import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined';
import BalconyOutlinedIcon from '@mui/icons-material/BalconyOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useTheme } from '@mui/material/styles';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  deleteDoc,
  orderBy,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  limit,
  where,
  setDoc,
  serverTimestamp,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage } from '../firebase';
import { useAuth } from '../AuthContext';
import { useTemplateStatuses } from './whatsapp/hubTemplates';
import { TEMPLATE_LANG_CODES } from '../utils/whatsappTemplateDrafts';
import type { AutoReplyLang } from '../utils/whatsappAutoReplyFlows';
import { usePaymentDetail } from '../contexts/PaymentDetailContext';
import { useAddPayment }    from '../contexts/AddPaymentContext';
import BankTransactionDrawer from './BankTransactionDrawer';
import BrokerDrawer from './BrokerDrawer';
import {
  DEFAULT_OFFICE_LOAN_TEMPLATE_ID,
  DEFAULT_OFFICE_MAIN_TEMPLATE_ID,
  OFFICE_LOAN_STEP_KEYS,
  OFFICE_MAIN_STEP_KEYS,
  computeWorkflowSteps,
  defaultOfficeLoanTemplate,
  defaultOfficeMainTemplate,
  type WorkflowCaseDoc,
  type WorkflowStepComputed,
  type WorkflowStepDef,
  type WorkflowStepFieldType,
  type WorkflowTemplateDoc,
} from '../office/workflows';
import { allotmentLetterTemplate } from '../office/documentTemplates';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { applyTemplateTokens } from '../office/templateTokens';
import PaymentConditionEditor from './PaymentConditionEditor';
import BookingPlanPanel from './bookingForm/BookingPlanPanel';
import { shrinkIdFile } from '../office/idUpload';
import { buyersBlock, committeeBoxes, documentsChecklist, flatLine, gujaratiStatus, layoutClass, mainSignatures, printBuyerFromCustomer, signatureLines, termsPageNumber } from '../office/bookingFormBlocks';
import PaymentRoadmap from './PaymentRoadmap';
import { newPaymentCondition, paymentConditionHtml, type PaymentCondition, validatePaymentCondition } from '../office/paymentCondition';
import { BUC_DEP_KEY, FULL_PAYMENT_DEP_KEY, PRE_BUC_DEP_KEY, PRE_DEED_STEP_KEYS, buildingBucReceived, fullPaymentReceived, hasRegisteredDeedEvidence, nearFullPayment } from '../office/deedPossessionGates';
import { yesAnswerDependency, yesAnswerForStep, yesAnswerStepKey } from '../office/workflowAnswerGate';
import { downloadFitoutAccessForm } from '../office/fitoutAccessForm';
import { amountToIndianWords, buildNetReceiptRows, buildPaymentTemplateTokens } from '../office/paymentPlaceholders';
import {
  buildBookingShortRefNo,
  ensureBookingShortCode,
  generateUniqueBookingShortCode,
  normalizeOfficeRefPrefix,
} from '../office/documentRefs';
import { buildUniversalOfficeTemplateTokens } from '../office/universalTemplateTokens';
import { optimizePdfToMaxBytes } from '../office/pdfReducer';
import {
  CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS,
  CHANGE_ORDER_STATUS_OPTIONS,
  isOpenChangeOrderStatus,
  type ChangeOrderCustomerApproval,
  type ChangeOrderStatus,
} from '../office/changeOrders';

const GOLD = '#9d6f32';

type UnitBookingStatus = 'available' | 'blocked' | 'booked' | 'sold';

type RelatedChangeOrderRow = {
  id: string;
  coNo: string;
  title: string;
  description: string;
  category: string;
  agency: string;
  status: ChangeOrderStatus;
  customerApproval: ChangeOrderCustomerApproval;
  costImpact: boolean;
  updatedAt?: any;
  createdAt?: any;
};

const resolveProjectLogoForTemplate = (projectRow: any, templateRow: any): string => {
  const prefersBw = Boolean(templateRow?.useProjectLogoBw ?? templateRow?.visualModel?.useProjectLogoBw);
  const bwLogo = String(projectRow?.logoBwUrl || '').trim();
  const colorLogo = String(projectRow?.logoUrl || '').trim();
  if (prefersBw && bwLogo) return bwLogo;
  return colorLogo;
};

const resolveCompanyLogoForTemplate = (companyRow: any, templateRow: any): string => {
  const prefersBw = Boolean(templateRow?.useProjectLogoBw ?? templateRow?.visualModel?.useProjectLogoBw);
  const bwLogo = String(companyRow?.logoBwUrl || '').trim();
  const colorLogo = String(companyRow?.logoUrl || '').trim();
  if (prefersBw && bwLogo) return bwLogo;
  return colorLogo;
};

type UnitRow = {
  id: string;
  unitNumber?: string | null;
  blockType?: string | null;
  usage?: string | null;
  unitAmount?: number | null;
  carpetArea?: number | null;
  exclusiveBalconyArea?: number | null;
  exclusiveWashArea?: number | null;
  openTerraceArea?: number | null;
  bookingStatus?: UnitBookingStatus | null;
  activeBookingId?: string | null;
  createdAt?: any;
  createdBy?: string | null;
  updatedAt?: any;
  updatedBy?: string | null;
};

type BankAccount = {
  id: string;
  name?: string | null;
  bankName?: string | null;
  accountRole?: string | null;
  allowCustomerReceipts?: boolean | null;
  allowGstReceipts?: boolean | null;
  allowPayments?: boolean | null;
  restrictedOutflow?: boolean | null;
  projectIds?: Array<string | number> | null;
};

type BrokerOption = {
  id: string;
  brokerName?: string | null;
  agencyName?: string | null;
  displayName?: string | null;
};

type BookingDoc = {
  id: string;
  shortBookingCode?: string | null;
  status?: 'active' | 'cancelled' | 'completed';
  bookingStatus?: UnitBookingStatus | null;
  customerName?: string | null;
  paymentTerms?: string | null;
  paymentCondition?: PaymentCondition | null;
  bookingFormDate?: string | null;
  refByBrokerId?: string | null;
  refByBrokerName?: string | null;
  rank?: number | null;
  schemeEnabled?: boolean | null;
  schemeRemark?: string | null;
  leadId?: string | null;
  notes?: string | null;
  createdAt?: any;
  createdBy?: string | null;
  updatedAt?: any;
  updatedBy?: string | null;
  closedAt?: any;
  closedBy?: string | null;
};

type BookingDocumentDoc = {
  id: string;
  type?: string | null;
  files?: Array<{
    name?: string | null;
    originalName?: string | null;
    url?: string | null;
    path?: string | null;
    uploadedAtMs?: number | null;
    uploadedBy?: string | null;
  }>;
  createdAt?: any;
  createdBy?: string | null;
  updatedAt?: any;
  updatedBy?: string | null;
};

type CustomerDoc = {
  id: string;
  name: string;
  displayName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  mobile: string;
  email?: string | null;
  occupation?: string | null;
  gender?: string | null;
  pan?: string | null;
  dateOfBirth?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  role?: string | null;
  createdAt?: any;
  createdBy?: string | null;
  updatedAt?: any;
  updatedBy?: string | null;
};

type KycDoc = {
  id: string;
  type: string;
  docNumber?: string | null;
  status?: 'pending' | 'verified' | 'rejected' | null;
  files?: Array<{
    name: string;
    originalName?: string | null;
    url: string;
    path: string;
    uploadedAtMs?: number;
    uploadedBy?: string | null;
    side?: 'front' | 'back' | null;
  }> | null;
  createdAt?: any;
  createdBy?: string | null;
  updatedAt?: any;
  updatedBy?: string | null;
};

const occupationOptions = [
  'Salaried (Private)',
  'Salaried (Government)',
  'Business Owner',
  'Self-Employed',
  'Professional (Doctor/CA/Lawyer)',
  'Freelancer/Consultant',
  'Retired',
  'Student',
  'Homemaker',
  'NRI/Overseas',
  'Other',
];

const genderOptions = ['Male', 'Female'];

const indiaStatesAndUTs = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
];

const kycTypeOptions = ['PAN', 'AADHAAR'];
const BOOKING_FORM_SIGNED_DOC_KEY = 'booking_form_signed';
const LEGACY_BOOKING_FORM_SIGNED_DOC_KEY = 'bookingFormSigned';
const SALE_AGREEMENT_SIGNED_DOC_KEY = 'sale_agreement';
const LEGACY_SALE_AGREEMENT_SIGNED_DOC_KEY = 'sale_agreement_signed';
const ALLOTMENT_LETTER_SIGNED_DOC_KEY = 'allotment_letter_signed';
const LOAN_COLLECT_DOC_SUBTASKS: Array<{ key: string; label: string }> = [
  { key: 'pan', label: 'PAN' },
  { key: 'aadhaar', label: 'Aadhaar' },
  { key: 'itr_3_year', label: 'Last 3 years ITR Return' },
  { key: 'bank_stmt_6_month', label: 'Last 6 months bank statement' },
  { key: 'salary_slip_3_month', label: 'Last 3 months salary slip (if any)' },
  { key: 'loan_stmt_6_month', label: 'Last 6 months loan statement (if any)' },
];

function normalizePhoneDigits(phone?: string | null): string {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function normalizeRelatedChangeOrderStatus(value: unknown): ChangeOrderStatus {
  const raw = String(value || 'requested')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (raw === 'inprogress') return 'in_progress';
  if (raw === 'customerconfirmed') return 'customer_confirmed';
  if (raw === 'onhold') return 'on_hold';
  return (
    CHANGE_ORDER_STATUS_OPTIONS.some((option) => option.value === raw)
      ? raw
      : 'requested'
  ) as ChangeOrderStatus;
}

function normalizeRelatedChangeOrderApproval(value: unknown): ChangeOrderCustomerApproval {
  const raw = String(value || 'pending')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return (
    CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS.some((option) => option.value === raw)
      ? raw
      : 'pending'
  ) as ChangeOrderCustomerApproval;
}

function relatedChangeOrderStatusLabel(value: ChangeOrderStatus): string {
  return CHANGE_ORDER_STATUS_OPTIONS.find((option) => option.value === value)?.label || value || '—';
}

function relatedChangeOrderApprovalLabel(value: ChangeOrderCustomerApproval): string {
  return CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS.find((option) => option.value === value)?.label || value || '—';
}

function formatPhoneDisplay(phone?: string | null): string {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return String(phone);
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (digits.length > 10) {
    const cc = digits.slice(0, digits.length - 10);
    const n = digits.slice(-10);
    return `+${cc} ${n.slice(0, 5)} ${n.slice(5)}`;
  }
  return String(phone);
}

function normalizeBankContactNumber(value?: string | null): string {
  return String(value || '').replace(/\D/g, '').slice(0, 10);
}

function isValidBankContactNumber(value?: string | null): boolean {
  const digits = normalizeBankContactNumber(value);
  if (digits.length === 10) return true;
  if (digits.length >= 2 && digits.length % 2 === 0 && /^0+$/.test(digits)) return true;
  return false;
}

async function copyToClipboard(text: string) {
  const t = text.trim();
  if (!t) return;
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(t);
    return;
  }
  const el = document.createElement('textarea');
  el.value = t;
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

function sqmToSqft(value: number): number {
  return value * 10.7639;
}

function formatDDMMYYYY(input: number | Date): string {
  const d = typeof input === 'number' ? new Date(input) : input;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

function formatYMD(input: number | Date): string {
  const d = typeof input === 'number' ? new Date(input) : input;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateTimeAmPm(input: number | Date): string {
  const d = typeof input === 'number' ? new Date(input) : input;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  const hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hh = String((hours % 12) || 12).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} ${hh}:${minutes} ${ampm}`;
}
function formatDateTimeAmPmCompact(input: number | Date): string {
  return formatDateTimeAmPm(input).replace(/[-:\s]/g, '');
}

function ymdToday(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${yyyy}-${mm}-${dd}`;
}

function coerceTsToMs(value: any): number | null {
  if (!value) return null;
  if (typeof value?.toMillis === 'function') {
    const ms = Number(value.toMillis() || 0);
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }
  if (typeof value?.seconds === 'number') {
    const ms = Number(value.seconds || 0) * 1000;
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

const USA_TIMEZONE = 'America/New_York';

function ymdNowUsa(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: USA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value || '1970';
  const month = parts.find((part) => part.type === 'month')?.value || '01';
  const day = parts.find((part) => part.type === 'day')?.value || '01';
  return `${year}-${month}-${day}`;
}

function hmNowUsa(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: USA_TIMEZONE,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date());
  const hour = parts.find((part) => part.type === 'hour')?.value || '00';
  const minute = parts.find((part) => part.type === 'minute')?.value || '00';
  return `${hour}:${minute}`;
}

function dateFromYMD(ymd: string): Date | null {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const da = Number(m[3]);
  const d = new Date(y, mo, da);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== da) return null;
  return d;
}

function dateFromDDMMYYYY(text: string): Date | null {
  const m = String(text || '').match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (!m) return null;
  const da = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const y = Number(m[3]);
  const d = new Date(y, mo, da);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== da) return null;
  return d;
}

type PaymentDuplicateMatchKind = 'exact' | 'likely';

type PaymentDuplicateMatch = {
  kind: PaymentDuplicateMatchKind;
  row: any;
  dateText: string;
  dayDiff: number;
};

function normalizePaymentDupText(value: any): string {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizePaymentDupRef(value: any): string {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function formatDateDDMMYYYY(value: Date): string {
  const dd = String(value.getDate()).padStart(2, '0');
  const mm = String(value.getMonth() + 1).padStart(2, '0');
  const yyyy = String(value.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

function dayDiffAbsForPayment(a: Date, b: Date): number {
  const dayMs = 24 * 60 * 60 * 1000;
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.abs(Math.round((ua - ub) / dayMs));
}

const qaCardSx = {
  bgcolor: '#ffffff',
  color: 'text.primary',
  borderRadius: 3,
  p: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 0 rgba(15,23,42,0.04)',
} as const;

const qaOptionButtonSx = {
  borderRadius: 2,
  px: 1.5,
  py: 0.75,
  fontWeight: 800,
  textTransform: 'none',
  bgcolor: '#f1f5f9',
  border: '1px solid rgba(15,23,42,0.12)',
  color: 'text.primary',
  '&:hover': { bgcolor: '#e2e8f0' },
} as const;

const qaCardCompactSx = {
  ...qaCardSx,
  p: 1.25,
  borderRadius: 2,
  boxShadow: 'none',
} as const;

const qaOptionButtonCompactSx = {
  ...qaOptionButtonSx,
  px: 1.25,
  py: 0.5,
  minHeight: 34,
  fontSize: '0.875rem',
} as const;

const UNIT_BOOKED_DEP_KEY = '__unit_booked__';
const LOAN_REQUIRED_DEP_KEY = '__loan_required_yes__';
const normalizeWorkflowTokenKey = (value: any) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

type WorkflowDefinitionStep = {
  key: string;
  title: string;
  order: number;
  enabled?: boolean;
  showInDrawer?: boolean;
  showInOtherWorkflow?: boolean;
  keepActive?: boolean;
  dependsOnSteps?: string[];
  dependsMode?: 'all' | 'any';
  actionType?: string;
  docKey?: string;
  paymentType?: string;
  fieldKey?: string;
  inputType?: WorkflowStepFieldType;
  helpText?: string;
};

const isInputValueStepComplete = (
  step: WorkflowDefinitionStep,
  stepData: Record<string, any> | null | undefined,
  bookingData?: Record<string, any> | null
): boolean => {
  const fieldKey = String((step as any)?.fieldKey || (step as any)?.key || '').trim();
  if (!fieldKey) return false;
  const row = (stepData || {}) as Record<string, any>;
  const bookingRow = (bookingData || {}) as Record<string, any>;
  const raw =
    row?.[fieldKey] ??
    row?.[String((step as any)?.key || '').trim()] ??
    bookingRow?.[fieldKey] ??
    bookingRow?.[String((step as any)?.key || '').trim()];
  const inputType = String((step as any)?.inputType || 'text').trim().toLowerCase();
  if (inputType === 'number') {
    return typeof raw === 'number' ? Number.isFinite(raw) : Number.isFinite(Number(raw));
  }
  return String(raw ?? '').trim().length > 0;
};

const normalizeDefinitionSteps = (input: any[]): WorkflowDefinitionStep[] => {
  const raw = Array.isArray(input) ? input : [];
  return raw
    .map((s: any, idx: number): WorkflowDefinitionStep => ({
      key: String(s?.key || '').trim(),
      title: String(s?.title || s?.key || `Step ${idx + 1}`).trim(),
      order: Number(s?.order || idx + 1),
      enabled: s?.enabled !== false,
      showInDrawer: s?.showInDrawer !== false,
      showInOtherWorkflow: !!s?.showInOtherWorkflow,
      keepActive: !!s?.keepActive,
      dependsOnSteps: (Array.isArray(s?.dependsOnSteps) ? s.dependsOnSteps : [])
        .map((v: any) => String(v || '').trim())
        .filter(Boolean),
      dependsMode: String(s?.dependsMode || 'all') === 'any' ? 'any' : 'all',
      actionType: String(s?.actionType || 'mark_done').trim(),
      docKey: String(s?.docKey || '').trim(),
      paymentType: String(s?.paymentType || '').trim(),
      fieldKey: String(s?.fieldKey || '').trim(),
      inputType: String(s?.inputType || 'text').trim().toLowerCase() as WorkflowStepFieldType,
      helpText: String(s?.helpText || '').trim(),
    }))
    .filter((s) => s.key)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

const getTemplateGeneratedOutputType = (templateRow: any): 'draft' | 'final' => {
  const raw = String(templateRow?.visualModel?.generatedOutputType || '').trim().toLowerCase();
  return raw === 'final' ? 'final' : 'draft';
};

const getTemplateShowInSection = (
  templateRow: any,
  generatedOutputType?: 'draft' | 'final'
): 'main_documents' | 'draft_documents' | 'loan_documents' | 'hidden' => {
  const raw = String(templateRow?.visualModel?.showInSection || '').trim().toLowerCase();
  if (raw === 'main_documents' || raw === 'draft_documents' || raw === 'loan_documents' || raw === 'hidden') {
    return raw;
  }
  return (generatedOutputType || getTemplateGeneratedOutputType(templateRow)) === 'final'
    ? 'main_documents'
    : 'draft_documents';
};


export type OfficeBookingDrawerComputedData = {
  customerName: string | null;
  bookingId: string | null;
  customerReceiptTotal: number;
  loanDisbursementReceiptTotal: number;
  loanToDeveloperAmount: number;
  gstReceiptTotal: number;
  saleAgreementDate: string | null;
  saleDeedDate: string | null;
  customers: Array<{ name: string; initials: string; role: string; mobile: string; gender?: string | null }>;
  gstDueAmount: number;
  stampDutyReceiptTotal: number;
  registrationReceiptTotal: number;
  hasDraftDocuments: boolean;
  bookingFormHoverDetails: {
    generatedAt: string;
    enteredDateTime: string;
    refBy: string;
    rank: string;
    terms: string;
    scheme: string;
    schemeRemark: string;
    fileName: string;
  } | null;
  bookingFormUrl: string | null;
  bookingFormFileName: string | null;
  sectionAvailability: {
    customers: boolean;
    changeOrders: boolean;
    payments: boolean;
    paymentRoadmap: boolean;
    documentsLoan: boolean;
  };
};
export type OfficeBookingDrawerHandle = {
  triggerAddPayment: () => void;
  triggerBookingForm: () => void;
  triggerDownloadBookingForm: () => void;
  triggerEditStatus: () => void;
  triggerAdminMenu: (anchor: HTMLElement) => void;
};

const OfficeBookingDrawer = React.forwardRef<OfficeBookingDrawerHandle, {
  open: boolean;
  onClose: () => void;
  onOpenUnitPage?: () => void;
  onDesktopOffsetChange?: (px: number) => void;
  projectId: string;
  unit: UnitRow | null;
  onRequestUnitStatusChange?: (unit: UnitRow, next: UnitBookingStatus) => Promise<void> | void;
  actionRequest?: { type: 'add_customer' | 'add_kyc' | 'upload_booking_form' | 'booking_form' | 'download_receipt' | 'start_loan' | 'stop_loan' | 'regenerate_doc' | 'add_dp' | 'add_gst' | 'loan_sanction_form' | 'edit_sanction' | 'auto_booking_form' | 'add_advocate_fee'; nonce: number; unitId?: string; txnId?: string; docKey?: string; count?: number } | null;
  actionOnly?: boolean;
  pageMode?: boolean;
  pageModeActiveSection?: string;
  /** Drawer mode only: show just this section (same names as page mode), e.g. 'documents' or 'payments'. */
  focusSection?: string;
  onComputedData?: (data: OfficeBookingDrawerComputedData) => void;
}>(function OfficeBookingDrawer({
  open,
  onClose,
  onOpenUnitPage,
  onDesktopOffsetChange,
  projectId,
  unit,
  onRequestUnitStatusChange,
  actionRequest,
  actionOnly = false,
  pageMode = false,
  pageModeActiveSection = 'overview',
  focusSection,
  onComputedData,
}, drawerRef) {
  const { currentUser, roleId } = useAuth();
  const { openPaymentDetail } = usePaymentDetail();
  const { openAddPayment }    = useAddPayment();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAdmin =
    String(roleId || '') === 'super-admin' || String(roleId || '') === 'admin';
  const canRemoveOtherBankLead =
    isAdmin;
  const db = useMemo(() => getFirestore(), []);

  useEffect(() => {
    if (!open || !projectId || !unit?.id) {
      setRelatedChangeOrders([]);
      return;
    }
    const unitNo = String(unit?.unitNumber || '').trim();
    const byUnitId = new Map<string, RelatedChangeOrderRow>();
    const byUnitNo = new Map<string, RelatedChangeOrderRow>();
    const mapRow = (row: any, id: string): RelatedChangeOrderRow => ({
      id,
      coNo: String(row?.coNo || '').trim() || `CO-${id.slice(-6).toUpperCase()}`,
      title: String(row?.title || '').trim(),
      description: String(row?.description || '').trim(),
      category: String(row?.category || '').trim(),
      agency: String(row?.agency || '').trim(),
      status: normalizeRelatedChangeOrderStatus(row?.status),
      customerApproval: normalizeRelatedChangeOrderApproval(row?.customerApproval),
      costImpact: !!row?.costImpact,
      updatedAt: row?.updatedAt || null,
      createdAt: row?.createdAt || null,
    });
    const publish = () => {
      const merged = new Map<string, RelatedChangeOrderRow>();
      byUnitNo.forEach((row, id) => merged.set(id, row));
      byUnitId.forEach((row, id) => merged.set(id, row));
      setRelatedChangeOrders(
        Array.from(merged.values()).sort((left, right) => {
          const leftMs =
            (typeof left.updatedAt?.toMillis === 'function' ? left.updatedAt.toMillis() : 0) ||
            (typeof left.createdAt?.toMillis === 'function' ? left.createdAt.toMillis() : 0);
          const rightMs =
            (typeof right.updatedAt?.toMillis === 'function' ? right.updatedAt.toMillis() : 0) ||
            (typeof right.createdAt?.toMillis === 'function' ? right.createdAt.toMillis() : 0);
          return rightMs - leftMs;
        })
      );
    };
    const unsubs: Array<() => void> = [];
    unsubs.push(
      onSnapshot(
        query(collection(db, 'projects', projectId, 'changeOrders'), where('unitId', '==', unit.id)),
        (snap) => {
          byUnitId.clear();
          snap.docs.forEach((row) => byUnitId.set(row.id, mapRow(row.data(), row.id)));
          publish();
        },
        (err) => {
          console.error('load related change orders by unit id failed', err);
        }
      )
    );
    if (unitNo) {
      unsubs.push(
        onSnapshot(
          query(collection(db, 'projects', projectId, 'changeOrders'), where('unitNo', '==', unitNo)),
          (snap) => {
            byUnitNo.clear();
            snap.docs.forEach((row) => byUnitNo.set(row.id, mapRow(row.data(), row.id)));
            publish();
          },
          (err) => {
            console.error('load related change orders by unit no failed', err);
          }
        )
      );
    }
    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [db, open, pageMode, projectId, unit?.id, unit?.unitNumber]);

  useEffect(() => {
    if (!open || isMobile || actionOnly) return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverscroll = (document.body.style as any).overscrollBehavior;
    const prevHtmlOverscroll = (document.documentElement.style as any).overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    (document.body.style as any).overscrollBehavior = 'none';
    (document.documentElement.style as any).overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      (document.body.style as any).overscrollBehavior = prevBodyOverscroll;
      (document.documentElement.style as any).overscrollBehavior = prevHtmlOverscroll;
    };
  }, [open, isMobile, actionOnly]);
  const [booking, setBooking] = useState<BookingDoc | null>(null);
  const [customers, setCustomers] = useState<CustomerDoc[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [kycDocs, setKycDocs] = useState<KycDoc[]>([]);
  const [bookingDocuments, setBookingDocuments] = useState<BookingDocumentDoc[]>([]);
  const [bookingDraftDocuments, setBookingDraftDocuments] = useState<BookingDocumentDoc[]>([]);
  const [, setUserDirectory] = useState<Array<{ id: string; email: string | null; name: string | null }>>([]);

  const [loading, setLoading] = useState(false);
  const [workflowHydration, setWorkflowHydration] = useState<{
    booking: boolean;
    customers: boolean;
    uploaded: boolean;
    draft: boolean;
    mainCase: boolean;
    loanCase: boolean;
  }>({
    booking: false,
    customers: false,
    uploaded: false,
    draft: false,
    mainCase: false,
    loanCase: false,
  });
  // Which unit each live listener last delivered data for. When the drawer moves to another unit, for a moment the
  // new unit is on screen while buyers / documents / workflow still belong to the previous one; documents must not be
  // generated then (that is how C-101's Builder NOC got A-502's buyers).
  type UnitDataPart = 'customers' | 'uploaded' | 'draft' | 'mainCase' | 'loanCase';
  const [loadedForUnit, setLoadedForUnit] = useState<Record<UnitDataPart, string>>({ customers: '', uploaded: '', draft: '', mainCase: '', loanCase: '' });
  const markLoadedFor = useCallback((part: UnitDataPart, unitId: string) => {
    setLoadedForUnit((prev) => (prev[part] === unitId ? prev : { ...prev, [part]: unitId }));
  }, []);
  const unitDataReady = !!unit?.id && Object.values(loadedForUnit).every((id) => id === unit.id);
  // Stale: some data still belongs to another unit. Read through a ref so every generator can check it.
  const unitDataStale = !!unit?.id && Object.values(loadedForUnit).some((id) => !!id && id !== unit.id);
  const unitDataStaleRef = useRef(false);
  unitDataStaleRef.current = unitDataStale;
  const assertUnitDataCurrent = useCallback(() => {
    if (unitDataStaleRef.current) throw new Error('Buyer details for this unit are still loading. Try again in a moment.');
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingKyc, setSavingKyc] = useState(false);
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [paymentDetailDrawerOpen, setPaymentDetailDrawerOpen] = useState(false);
  const [paymentDetailTxn, setPaymentDetailTxn] = useState<any | null>(null);
  const [paymentDetailBankAccountId, setPaymentDetailBankAccountId] = useState('');
  const [paymentDetailDateYMD, setPaymentDetailDateYMD] = useState(ymdToday());
  const [paymentDetailAmount, setPaymentDetailAmount] = useState('');
  const [paymentDetailTxnType, setPaymentDetailTxnType] = useState<'receipt' | 'payment'>('receipt');
  const [paymentDetailReceiptCategory, setPaymentDetailReceiptCategory] = useState<'customer' | 'gst' | 'advocate' | 'other'>('customer');
  const [paymentDetailPartyName, setPaymentDetailPartyName] = useState('');
  const [paymentDetailPayerBankName, setPaymentDetailPayerBankName] = useState('');
  const [paymentDetailMode, setPaymentDetailMode] = useState('');
  const [paymentDetailReferenceNo, setPaymentDetailReferenceNo] = useState('');
  const [paymentDetailNotes, setPaymentDetailNotes] = useState('');
  const [paymentDetailIsLoanDisbursement, setPaymentDetailIsLoanDisbursement] = useState(false);
  const [paymentDetailSaving, setPaymentDetailSaving] = useState(false);
  const [paymentDetailError, setPaymentDetailError] = useState<string | null>(null);
  const [paymentDetailEditMode, setPaymentDetailEditMode] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [documentUploadTypes, setDocumentUploadTypes] = useState<any[]>([]);
  const [officeDocumentLinkedBankNames, setOfficeDocumentLinkedBankNames] = useState<string[]>([]);

  const [projectWorkflowIds, setProjectWorkflowIds] = useState<{ mainId: string; loanId: string }>({
    mainId: DEFAULT_OFFICE_MAIN_TEMPLATE_ID,
    loanId: DEFAULT_OFFICE_LOAN_TEMPLATE_ID,
  });
  const [projectWorkflowDefinitionIds, setProjectWorkflowDefinitionIds] = useState<{ mainId: string; loanId: string }>({
    mainId: '',
    loanId: '',
  });
  const [projectName, setProjectName] = useState('');
  const [projectBuc, setProjectBuc] = useState<{ bucReceivedAt: string; bucReceivedBuildingNos: string[] }>({ bucReceivedAt: '', bucReceivedBuildingNos: [] });
  const [projectAdvocateAgreementFee, setProjectAdvocateAgreementFee] = useState(0);
  const [projectSbAreaPercent, setProjectSbAreaPercent] = useState(0);
  const [projectGstPercentByUnitType, setProjectGstPercentByUnitType] = useState<Record<string, number>>({});
  const [projectDefaultGstPercent, setProjectDefaultGstPercent] = useState(1);
  const [mainCase, setMainCase] = useState<(WorkflowCaseDoc & { id: string }) | null>(null);
  const [loanCase, setLoanCase] = useState<(WorkflowCaseDoc & { id: string }) | null>(null);
  const [mainTemplate, setMainTemplate] = useState<WorkflowTemplateDoc | null>(null);
  const [loanTemplate, setLoanTemplate] = useState<WorkflowTemplateDoc | null>(null);
  const [mainWorkflowDefinition, setMainWorkflowDefinition] = useState<any | null>(null);
  const [loanWorkflowDefinition, setLoanWorkflowDefinition] = useState<any | null>(null);

  const [kycSummaryByCustomer, setKycSummaryByCustomer] = useState<Record<string, { hasPAN: boolean; hasAADHAAR: boolean; hasPHOTO: boolean }>>({});
  const [bookingFormDoc, setBookingFormDoc] = useState<any | null>(null);
  const [bookingFormDraftDialogOpen, setBookingFormDraftDialogOpen] = useState(false);
  const [bookingFormDraftDate, setBookingFormDraftDate] = useState(ymdNowUsa());
  const [bookingFormDraftTime, setBookingFormDraftTime] = useState(hmNowUsa());
  const [bookingFormDraftGenerating, setBookingFormDraftGenerating] = useState(false);
  const [bookingFormTerms, setBookingFormTerms] = useState('');
  const [bookingFormCondition, setBookingFormCondition] = useState<PaymentCondition>(newPaymentCondition);
  const [bookingFormUseLegacyTerms, setBookingFormUseLegacyTerms] = useState(false);
  const [bookingFormRefByBroker, setBookingFormRefByBroker] = useState<BrokerOption | null>(null);
  const [bookingFormRank, setBookingFormRank] = useState('');
  const [bookingFormScheme, setBookingFormScheme] = useState<'yes' | 'no'>('no');
  const [bookingFormSchemeRemark, setBookingFormSchemeRemark] = useState('');
  // Office-only details printed on the booking form.
  const [bookingFormTermMonths, setBookingFormTermMonths] = useState('');
  const [bookingFormEarnest, setBookingFormEarnest] = useState('');
  const [bookingFormEarnestTouched, setBookingFormEarnestTouched] = useState(false);
  const [bookingFormCommittee, setBookingFormCommittee] = useState<'yes' | 'no' | ''>('');
  const [bookingFormDocs, setBookingFormDocs] = useState({ photos: false, pan: false, aadhaar: false });
  const [bookingFormError, setBookingFormError] = useState('');
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [brokerCreateDrawerOpen, setBrokerCreateDrawerOpen] = useState(false);
  const [availableUnitDraftHeaderFile, setAvailableUnitDraftHeaderFile] = useState<any | null>(null);
  const [paymentDetailsDraftGenerating, setPaymentDetailsDraftGenerating] = useState(false);
  const [paymentWhatsAppAnchor, setPaymentWhatsAppAnchor] = useState<HTMLElement | null>(null);
  const [paymentWhatsAppSending, setPaymentWhatsAppSending] = useState(false);
  const [paymentWhatsAppError, setPaymentWhatsAppError] = useState<string | null>(null);
  const [signedBookingWhatsAppAnchor, setSignedBookingWhatsAppAnchor] = useState<HTMLElement | null>(null);
  const [signedBookingWhatsAppSending, setSignedBookingWhatsAppSending] = useState(false);
  const [signedBookingWhatsAppError, setSignedBookingWhatsAppError] = useState<string | null>(null);
  const templateStatuses = useTemplateStatuses();
  const [adminDocumentActionMode, setAdminDocumentActionMode] = useState(false);
  const [headerEditAnchorEl, setHeaderEditAnchorEl] = useState<HTMLElement | null>(null);
  const [unitStatusDialogOpen, setUnitStatusDialogOpen] = useState(false);
  const [unitStatusSaving, setUnitStatusSaving] = useState(false);
  const [unitStatusDraft, setUnitStatusDraft] = useState<UnitBookingStatus>('available');
  const [saleAgreementDateEditYmd, setSaleAgreementDateEditYmd] = useState('');
  const [saleAgreementDateSaving, setSaleAgreementDateSaving] = useState(false);
  const [saleDeedDateEditYmd, setSaleDeedDateEditYmd] = useState('');
  const [saleDeedDateSaving, setSaleDeedDateSaving] = useState(false);
  const [isMultiSelectReceipts, setIsMultiSelectReceipts] = useState(false);
  const [selectedReceiptTxnIds, setSelectedReceiptTxnIds] = useState<Set<string>>(new Set());
  const [multiReceiptDownloading, setMultiReceiptDownloading] = useState(false);
  const [multiReceiptProgressText, setMultiReceiptProgressText] = useState('');
  const [stepDocPickedFiles, setStepDocPickedFiles] = useState<Record<string, File | null>>({});
  const [stepDocUploadPhase, setStepDocUploadPhase] = useState<
    Record<string, 'idle' | 'processing' | 'reducing' | 'uploading' | 'uploaded' | 'error'>
  >({});
  const [stepGeneratePhase, setStepGeneratePhase] = useState<
    Record<string, 'idle' | 'processing' | 'done' | 'error'>
  >({});
  const [stepEmailPhase, setStepEmailPhase] = useState<
    Record<string, 'idle' | 'processing' | 'done' | 'error'>
  >({});
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [emailConfirmLoading, setEmailConfirmLoading] = useState(false);
  const [emailConfirmSending, setEmailConfirmSending] = useState(false);
  const [emailConfirmError, setEmailConfirmError] = useState<string | null>(null);
  const [emailConfirmTarget, setEmailConfirmTarget] = useState<{ which: 'main' | 'loan'; step: WorkflowDefinitionStep } | null>(null);
  const [emailConfirmPreview, setEmailConfirmPreview] = useState<{
    templateName: string;
    to: string[];
    subject: string;
    bodyHtml: string;
    attachments: Array<{ key: string; name: string; source: 'uploaded' | 'draft' }>;
  } | null>(null);
  const [emailConfirmToDraft, setEmailConfirmToDraft] = useState('');
  const [demandGenerateDialogOpen, setDemandGenerateDialogOpen] = useState(false);
  const [demandGenerateContext, setDemandGenerateContext] = useState<{ which: 'main' | 'loan'; step: WorkflowDefinitionStep } | null>(null);
  const [demandConstructionStageInput, setDemandConstructionStageInput] = useState('');
  const [demandAmountInput, setDemandAmountInput] = useState('');
  const [demandDueDateInput, setDemandDueDateInput] = useState('');
  const [demandGenerateSubmitting, setDemandGenerateSubmitting] = useState(false);
  const [stepFieldDrafts, setStepFieldDrafts] = useState<Record<string, string>>({});
  const [step1ApplicantsEditMode, setStep1ApplicantsEditMode] = useState(false);
  const handledActionNonceRef = useRef<number>(0);
  const autoInitMainCaseRef = useRef<string>('');
  const quickBookingFormUploadInputRef = useRef<HTMLInputElement | null>(null);
  const openPaymentDrawerRef = useRef<(kind: 'dp' | 'gst' | 'advocateAgreement') => void>(() => {});

  // Payment drawer form
  const [paymentBankAccountId, setPaymentBankAccountId] = useState('');
  const [paymentDateYMD, setPaymentDateYMD] = useState(ymdToday());
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentTxnType, setPaymentTxnType] = useState<'receipt' | 'payment'>('receipt');
  const [paymentReceiptCategory, setPaymentReceiptCategory] = useState<'customer' | 'gst' | 'advocate' | 'other'>('customer');
  const [paymentIsLoanDisbursement, setPaymentIsLoanDisbursement] = useState(false);
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentReferenceNo, setPaymentReferenceNo] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentPartyName, setPaymentPartyName] = useState('');
  const [paymentPayerBankName, setPaymentPayerBankName] = useState('');
  const [sendToSbiHint, setSendToSbiHint] = useState<string>('');
  const [loanOtherBankName, setLoanOtherBankName] = useState('');
  const [loanOtherBankContactName, setLoanOtherBankContactName] = useState('');
  const [loanOtherBankContactNumber, setLoanOtherBankContactNumber] = useState('');
  const [loanOtherBankSentOn, setLoanOtherBankSentOn] = useState(ymdToday());
  const [loanOtherBankRemarks, setLoanOtherBankRemarks] = useState('');
  const [loanSanctionDialogOpen, setLoanSanctionDialogOpen] = useState(false);
  const [loanSanctionStepKey, setLoanSanctionStepKey] = useState('');
  const [loanSanctionStepTitle, setLoanSanctionStepTitle] = useState('Upload sanction letter');
  const [loanSanctionDocKey, setLoanSanctionDocKey] = useState('sanction_letter');
  const [loanSanctionBankName, setLoanSanctionBankName] = useState('');
  const [loanSanctionAgentName, setLoanSanctionAgentName] = useState('');
  const [loanSanctionAgentMobile, setLoanSanctionAgentMobile] = useState('');
  const [loanSanctionLoanToUnitAmount, setLoanSanctionLoanToUnitAmount] = useState('');
  const [loanSanctionAmount, setLoanSanctionAmount] = useState('');
  const [loanSanctionDate, setLoanSanctionDate] = useState('');
  const [loanSanctionNonSbiReason, setLoanSanctionNonSbiReason] = useState('');
  const [loanSanctionSaving, setLoanSanctionSaving] = useState(false);
  const [switchLoanBankDialogOpen, setSwitchLoanBankDialogOpen] = useState(false);
  const [switchLoanBankSaving, setSwitchLoanBankSaving] = useState(false);
  const [switchLoanBankName, setSwitchLoanBankName] = useState('');
  const [switchLoanBankReason, setSwitchLoanBankReason] = useState('');
  const [switchLoanBankError, setSwitchLoanBankError] = useState<string | null>(null);
  const [loanOtherBankRemarkEditId, setLoanOtherBankRemarkEditId] = useState('');
  const [loanOtherBankStatusEdit, setLoanOtherBankStatusEdit] = useState('sent');
  const [loanOtherBankRemarkDraft, setLoanOtherBankRemarkDraft] = useState('');
  const [loanOtherBankTimelineExpanded, setLoanOtherBankTimelineExpanded] = useState<Record<string, boolean>>({});
  const [loanOtherBankFormOpen, setLoanOtherBankFormOpen] = useState(false);
  const [paymentVoucherFile, setPaymentVoucherFile] = useState<File | null>(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentUploading, setPaymentUploading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const paymentVoucherInputRef = useRef<HTMLInputElement | null>(null);
  const autoFinalizingStepRef = useRef<string>('');
  const autoGeneratingAllotmentRef = useRef(false);
  const autoSyncSbiLeadRef = useRef(false);
  const autoSyncLoanBankLeadDoneRef = useRef(false);
  const [customerReceiptTotal, setCustomerReceiptTotal] = useState(0);
  const [gstReceiptTotal, setGstReceiptTotal] = useState(0);
  const [stampDutyReceiptTotal, setStampDutyReceiptTotal] = useState(0);
  const [registrationReceiptTotal, setRegistrationReceiptTotal] = useState(0);
  const [advocateDeedReceiptTotal, setAdvocateDeedReceiptTotal] = useState(0);
  const [relatedChangeOrders, setRelatedChangeOrders] = useState<RelatedChangeOrderRow[]>([]);

  const activeSection = pageMode ? pageModeActiveSection : focusSection || 'overview';
  useEffect(() => {
    if (!open || pageMode || !focusSection) return undefined;
    const t = window.setTimeout(() => {
      document.querySelector(`[data-section="${focusSection}"]`)?.scrollIntoView({ block: 'start' });
    }, 450);
    return () => window.clearTimeout(t);
  }, [focusSection, open, pageMode]);
  // Section ids: 'loan-documents' = Loan Documents, 'drafts' = Draft Documents. Documents (classic "Documents & Loan")
  // shows both; the unit page's Loan file ('loan') shows loan documents and loan workflow steps only.
  const isPageSectionVisible = (section: string) =>
    activeSection === 'overview' ||
    activeSection === section ||
    (activeSection === 'payments' && section === 'gst') ||
    (activeSection === 'documents' && (section === 'loan-documents' || section === 'drafts')) ||
    (activeSection === 'loan' && (section === 'loan-documents' || section === 'pending-work'));
  const [advocateAgreementReceiptTotal, setAdvocateAgreementReceiptTotal] = useState(0);
  const [customerReceiptRows, setCustomerReceiptRows] = useState<any[]>([]);
  const [customerRefundRows, setCustomerRefundRows] = useState<any[]>([]);
  const [receiptGeneratingTxnId, setReceiptGeneratingTxnId] = useState('');
  const [gstReceiptRows, setGstReceiptRows] = useState<any[]>([]);
  const [unitBookingHistoryIds, setUnitBookingHistoryIds] = useState<string[]>([]);
  const [paymentDuplicateCandidates, setPaymentDuplicateCandidates] = useState<any[]>([]);
  const [paymentDuplicateDialogOpen, setPaymentDuplicateDialogOpen] = useState(false);
  const [paymentDuplicateMatches, setPaymentDuplicateMatches] = useState<PaymentDuplicateMatch[]>([]);
  const [paymentDuplicateProceeding, setPaymentDuplicateProceeding] = useState(false);
  const [paymentLiveDuplicateMatches, setPaymentLiveDuplicateMatches] = useState<PaymentDuplicateMatch[]>([]);
  const [lastPayerBankByCategory, setLastPayerBankByCategory] = useState<{ customer: string; gst: string }>({
    customer: '',
    gst: '',
  });
  const [pendingPaymentStep, setPendingPaymentStep] = useState<{ which: 'main' | 'loan'; key: string } | null>(null);
  const [projectSbPercentByUnitType, setProjectSbPercentByUnitType] = useState<Record<string, number>>({});

  const customerRefundAmountByReceiptId = useMemo(() => {
    const map = new Map<string, number>();
    customerRefundRows.forEach((row: any) => {
      const totalAmount = Number(row?.amount || 0);
      const allocations = Array.isArray(row?.refundAllocations) ? row.refundAllocations : [];
      if (allocations.length > 0) {
        allocations.forEach((allocation: any) => {
          const txnId = String(allocation?.txnId || '').trim();
          const amount = Number(allocation?.amount || 0);
          if (!txnId || !Number.isFinite(amount) || amount <= 0) return;
          map.set(txnId, (map.get(txnId) || 0) + amount);
        });
        return;
      }

      const ids = [
        String(row?.refundAgainstTxnId || '').trim(),
        ...(Array.isArray(row?.refundAgainstTxnIds) ? row.refundAgainstTxnIds.map((id: any) => String(id || '').trim()) : []),
      ].filter(Boolean);
      if (ids.length !== 1 || !Number.isFinite(totalAmount) || totalAmount <= 0) return;
      map.set(ids[0], (map.get(ids[0]) || 0) + totalAmount);
    });
    return map;
  }, [customerRefundRows]);

  const hydratePaymentDetailForm = useCallback((entry: any) => {
    const txnType = String(entry?.txnType || '').toLowerCase() === 'payment' ? 'payment' : 'receipt';
    const receiptCategoryRaw = String(entry?.receiptCategory || '').toLowerCase();
    const receiptCategory: 'customer' | 'gst' | 'advocate' | 'other' =
      receiptCategoryRaw === 'gst' || receiptCategoryRaw === 'advocate' || receiptCategoryRaw === 'other'
        ? (receiptCategoryRaw as any)
        : 'customer';
    setPaymentDetailTxnType(txnType);
    setPaymentDetailReceiptCategory(receiptCategory);
    setPaymentDetailBankAccountId(String(entry?.bankAccountId || ''));
    const explicitDate = String(entry?.txnDateYMD || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(explicitDate)) {
      setPaymentDetailDateYMD(explicitDate);
    } else {
      const atMs =
        coerceTsToMs(entry?.chqDate) ||
        coerceTsToMs(entry?.txnDate) ||
        coerceTsToMs(entry?.createdAt) ||
        coerceTsToMs(entry?.updatedAt) ||
        null;
      if (!atMs) {
        setPaymentDetailDateYMD(ymdToday());
      } else {
        const d = new Date(atMs);
        if (Number.isNaN(d.getTime())) {
          setPaymentDetailDateYMD(ymdToday());
        } else {
          setPaymentDetailDateYMD(
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          );
        }
      }
    }
    const amt = Number(entry?.amount || 0);
    setPaymentDetailAmount(Number.isFinite(amt) && amt > 0 ? String(amt) : '');
    setPaymentDetailPartyName(String(entry?.partyName || ''));
    setPaymentDetailPayerBankName(String(entry?.payerBankName || ''));
    setPaymentDetailMode(String(entry?.mode || ''));
    setPaymentDetailReferenceNo(String(entry?.referenceNo || ''));
    setPaymentDetailNotes(String(entry?.notes || ''));
    setPaymentDetailIsLoanDisbursement(entry?.isLoanDisbursement === true);
  }, []);

  const openPaymentDetailDrawer = useCallback((row: any) => {
    const entry = row || null;
    setPaymentDetailTxn(entry);
    setPaymentDetailEditMode(false);
    setPaymentDetailError(null);
    hydratePaymentDetailForm(entry);
    setPaymentDetailDrawerOpen(true);
  }, [hydratePaymentDetailForm]);

  const paymentPayerBankNameOptions = useMemo(() => {
    const values = new Map<string, string>();
    const addValue = (raw: unknown) => {
      const value = String(raw || '').trim();
      const key = value.toLowerCase();
      if (value && !values.has(key)) values.set(key, value);
    };

    addValue(lastPayerBankByCategory.customer);
    addValue(lastPayerBankByCategory.gst);
    addValue(paymentPayerBankName);
    addValue(loanSanctionBankName);

    customerReceiptRows.forEach((row: any) => {
      addValue(row?.payerBankName);
      addValue(row?.bankName);
    });
    gstReceiptRows.forEach((row: any) => {
      addValue(row?.payerBankName);
      addValue(row?.bankName);
    });

    return Array.from(values.values()).sort((a, b) => a.localeCompare(b));
  }, [
    customerReceiptRows,
    gstReceiptRows,
    lastPayerBankByCategory.customer,
    lastPayerBankByCategory.gst,
    loanSanctionBankName,
    paymentPayerBankName,
  ]);

  const mergeBankNameOptions = useCallback((...lists: Array<unknown[] | undefined>) => {
    const values = new Map<string, string>();
    lists.forEach((list) => {
      (Array.isArray(list) ? list : []).forEach((raw) => {
        const value = String(raw || '').trim();
        const key = value.toLowerCase();
        if (value && !values.has(key)) values.set(key, value);
      });
    });
    return Array.from(values.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, []);

  const loanToDeveloperPendingAmount = useMemo(() => {
    const unitAmount = Number((unit as any)?.unitAmount || 0);
    if (!Number.isFinite(unitAmount) || unitAmount <= 0) return 0;
    return Math.max(0, unitAmount - Number(customerReceiptTotal || 0));
  }, [customerReceiptTotal, unit]);

  const loanToDeveloperNinetyPercentAmount = useMemo(() => {
    const unitAmount = Number((unit as any)?.unitAmount || 0);
    if (!Number.isFinite(unitAmount) || unitAmount <= 0) return 0;
    return Math.max(0, Math.round(unitAmount * 0.9));
  }, [unit]);

  const currentUnitTypeForGst = useMemo(() => {
    const rawCandidates = [
      (unit as any)?.usage,
      (unit as any)?.['USAGE'],
      (unit as any)?.customFields?.usage,
      (unit as any)?.customFields?.USAGE,
      (unit as any)?.blockType,
      (unit as any)?.['BLOCK TYPE'],
      (unit as any)?.unitType,
      (unit as any)?.type,
      (unit as any)?.customFields?.blockType,
      (unit as any)?.customFields?.['BLOCK TYPE'],
    ];
    for (const raw of rawCandidates) {
      const key = String(raw || '').trim();
      if (key) return key;
    }
    return '';
  }, [unit]);

  const currentGstPercentForUnit = useMemo(() => {
    const byType = projectGstPercentByUnitType || {};
    const key = String(currentUnitTypeForGst || '').trim();
    const mapped = key ? Number(byType[key]) : NaN;
    if (Number.isFinite(mapped) && mapped >= 0) return mapped;
    const fallback = Number(projectDefaultGstPercent || 0);
    return Number.isFinite(fallback) && fallback >= 0 ? fallback : 1;
  }, [currentUnitTypeForGst, projectDefaultGstPercent, projectGstPercentByUnitType]);

  const currentSbPercentForUnit = useMemo(() => {
    const byType = projectSbPercentByUnitType || {};
    const key = String(currentUnitTypeForGst || '').trim();
    const mapped = key ? Number(byType[key]) : NaN;
    if (Number.isFinite(mapped) && mapped >= 0) return mapped;
    const fallback = Number(projectSbAreaPercent || 0);
    return Number.isFinite(fallback) && fallback >= 0 ? fallback : 0;
  }, [currentUnitTypeForGst, projectSbAreaPercent, projectSbPercentByUnitType]);

  const computeGstAmount = useCallback(
    (unitAmountValue: number) => {
      const amount = Number(unitAmountValue || 0);
      if (!(amount > 0)) return 0;
      return Math.ceil((amount * currentGstPercentForUnit) / 100);
    },
    [currentGstPercentForUnit]
  );

  const normalizePaymentTerms = useCallback((value: string): string => {
    const compact = String(value || '').trim().replace(/\s+/g, '').replace(/#/g, '|');
    if (!compact) return '';
    const parts = compact.split('|').filter(Boolean);
    return parts
      .map((part) => {
        const [percent, triggerRaw] = String(part || '').split('-');
        const trigger = /^\d+$/.test(String(triggerRaw || '')) ? String(triggerRaw) : String(triggerRaw || '').toUpperCase();
        return `${String(percent || '')}-${trigger}`;
      })
      .join('|');
  }, []);

  const validatePaymentTerms = useCallback((value: string): string | null => {
    const normalized = normalizePaymentTerms(value);
    if (!normalized) return null;
    const parts = normalized.split('|').filter(Boolean);
    if (!parts.length) return 'Invalid terms format.';
    for (const part of parts) {
      const match = part.match(/^(\d{1,3}(?:\.\d+)?)-(\d+|PO|LO)$/i);
      if (!match) {
        return 'Use format like 30-90|30-150|40-PO (PO=Possession, LO=Loan).';
      }
      const pct = Number(match[1]);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        return 'Each percentage must be > 0 and <= 100.';
      }
      if (/^\d+$/.test(match[2]) && Number(match[2]) <= 0) {
        return 'Days must be greater than 0.';
      }
    }
    return null;
  }, [normalizePaymentTerms]);

  // Customer form
  const [custFirstName, setCustFirstName] = useState('');
  const [custMiddleName, setCustMiddleName] = useState('');
  const [custLastName, setCustLastName] = useState('');
  const [custDisplayName, setCustDisplayName] = useState('');
  const [custMobile, setCustMobile] = useState('');
  const [custEmail, setCustEmail] = useState('');
  const [custDateOfBirth, setCustDateOfBirth] = useState('');
  const [custOccupation, setCustOccupation] = useState('');
  const [custGender, setCustGender] = useState('');
  const [custPan, setCustPan] = useState('');
  const [custRole, setCustRole] = useState('primary');
  const [custAddr1, setCustAddr1] = useState('');
  const [custAddr2, setCustAddr2] = useState('');
  const [custCity, setCustCity] = useState('');
  const [custState, setCustState] = useState('');
  const [custPincode, setCustPincode] = useState('');
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);

	  // KYC upload
	  const fileInputRef = useRef<HTMLInputElement | null>(null);
	  const aadhaarFrontInputRef = useRef<HTMLInputElement | null>(null);
	  const aadhaarBackInputRef = useRef<HTMLInputElement | null>(null);
  const [kycType, setKycType] = useState('PAN');
  const [kycDocNumber, setKycDocNumber] = useState('');
  const [kycFiles, setKycFiles] = useState<File[]>([]);
  const [aadhaarFrontFile, setAadhaarFrontFile] = useState<File | null>(null);
  const [aadhaarBackFile, setAadhaarBackFile] = useState<File | null>(null);
  const [showExtraKycUpload, setShowExtraKycUpload] = useState(false);
  const [fallbackBookingId, setFallbackBookingId] = useState<string>('');

  const bookingId = fallbackBookingId || (unit?.activeBookingId ? String(unit.activeBookingId) : '');
  const bookingPanelWidth = 560;
  const paymentPanelWidth = 520;
  const panelGap = 12;

  useEffect(() => {
    if (!open) return;
    setWorkflowHydration({
      booking: false,
      customers: false,
      uploaded: false,
      draft: false,
      mainCase: false,
      loanCase: false,
    });
  }, [bookingId, open, projectId, unit?.id]);

  const selectedCustomer = useMemo(() => {
    if (!selectedCustomerId) return null;
    return customers.find((c) => c.id === selectedCustomerId) || null;
  }, [customers, selectedCustomerId]);

  useEffect(() => {
    if (!kycDialogOpen) {
      setShowExtraKycUpload(false);
    }
  }, [kycDialogOpen, selectedCustomerId]);

  useEffect(() => {
    setFallbackBookingId('');
  }, [unit?.id]);

  const primaryCustomerName = useMemo(() => {
    if (!customers.length) return '';
    const primary = customers.find((c) => String(c.role || '').toLowerCase() === 'primary') || customers[0];
    return String(primary.displayName || primary.name || '').trim();
  }, [customers]);

  const firstCustomerForAddress = useMemo(() => {
    if (!customers.length) return null;
    return customers.find((c) => String(c.role || '').toLowerCase() === 'primary') || customers[0];
  }, [customers]);

  const paymentModeOptions = useMemo(
    () => [
      'Cash',
      'Cheque',
      'NEFT/RTGS',
      'UPI',
      'IMPS',
      'Card',
      'Net Banking',
      'PG/Online',
      'Other',
    ],
    []
  );

  const allowedCustomerAccounts = useMemo(() => {
    return bankAccounts.filter((b) => {
      const role = String(b.accountRole || '').toLowerCase();
      if (typeof b.allowCustomerReceipts === 'boolean') return !!b.allowCustomerReceipts;
      return role === 'collection';
    });
  }, [bankAccounts]);

  const allowedGstAccounts = useMemo(() => {
    return bankAccounts.filter((b) => {
      const role = String(b.accountRole || '').toLowerCase();
      if (typeof b.allowGstReceipts === 'boolean') return !!b.allowGstReceipts;
      return role !== 'rera70' && role !== 'collection' && role !== 'dummy_advocate';
    });
  }, [bankAccounts]);

  const allowedAdvocateAccounts = useMemo(() => {
    return bankAccounts.filter((b) => {
      const role = String(b.accountRole || '').toLowerCase();
      if (role === 'dummy_advocate') return true;
      if (typeof b.allowPayments === 'boolean') return !!b.allowPayments;
      return false;
    });
  }, [bankAccounts]);

  const loanOtherBankHistory = useMemo(() => {
    const bankSet = new Set<string>();
    const contactNameSet = new Set<string>();
    const contactNumberSet = new Set<string>();
    const nameToNumber = new Map<string, string>();
    const numberToName = new Map<string, string>();
    const stepData =
      (((loanCase as any)?.stepData || {}) as Record<string, any>)?.[OFFICE_LOAN_STEP_KEYS.sendToBanks] || {};
    const rows = Array.isArray(stepData?.otherBankLeads) ? (stepData.otherBankLeads as Array<Record<string, any>>) : [];
    for (const row of rows) {
      const bankName = String(row?.bankName || '').trim();
      const contactName = String(row?.contactName || '').trim();
      const contactNumber = String(row?.contactNumber || '').trim();
      if (bankName) bankSet.add(bankName);
      if (contactName) contactNameSet.add(contactName);
      if (contactNumber) contactNumberSet.add(contactNumber);
      if (contactName && contactNumber) {
        const nameKey = contactName.toLowerCase();
        if (!nameToNumber.has(nameKey)) nameToNumber.set(nameKey, contactNumber);
        if (!numberToName.has(contactNumber)) numberToName.set(contactNumber, contactName);
      }
    }
    return {
      bankNames: Array.from(bankSet).sort((a, b) => a.localeCompare(b)),
      contactNames: Array.from(contactNameSet).sort((a, b) => a.localeCompare(b)),
      contactNumbers: Array.from(contactNumberSet).sort((a, b) => a.localeCompare(b)),
      nameToNumber,
      numberToName,
    };
  }, [loanCase]);

  const loanSanctionHistory = useMemo(() => {
    const bankSet = new Set<string>();
    const agentNameSet = new Set<string>();
    const agentMobileSet = new Set<string>();
    const nameToMobile = new Map<string, string>();
    const mobileToName = new Map<string, string>();

    const stepDataMap = ((((loanCase as any)?.stepData || {}) as Record<string, any>) || {});
    Object.values(stepDataMap).forEach((row: any) => {
      if (!row || typeof row !== 'object') return;
      const sanctionBank = String(row?.sanctionBankName || '').trim();
      const sanctionAgent = String(row?.sanctionAgentName || '').trim();
      const sanctionMobile = normalizeBankContactNumber(String(row?.sanctionAgentMobile || '').trim());
      if (sanctionBank) bankSet.add(sanctionBank);
      if (sanctionAgent) agentNameSet.add(sanctionAgent);
      if (sanctionMobile) agentMobileSet.add(sanctionMobile);
      if (sanctionAgent && sanctionMobile) {
        const key = sanctionAgent.toLowerCase();
        if (!nameToMobile.has(key)) nameToMobile.set(key, sanctionMobile);
        if (!mobileToName.has(sanctionMobile)) mobileToName.set(sanctionMobile, sanctionAgent);
      }
      const otherBankLeads = Array.isArray(row?.otherBankLeads) ? (row.otherBankLeads as Array<Record<string, any>>) : [];
      otherBankLeads.forEach((lead) => {
        const bankName = String(lead?.bankName || '').trim();
        const contactName = String(lead?.contactName || '').trim();
        const contactNumber = normalizeBankContactNumber(String(lead?.contactNumber || '').trim());
        if (bankName) bankSet.add(bankName);
        if (contactName) agentNameSet.add(contactName);
        if (contactNumber) agentMobileSet.add(contactNumber);
        if (contactName && contactNumber) {
          const key = contactName.toLowerCase();
          if (!nameToMobile.has(key)) nameToMobile.set(key, contactNumber);
          if (!mobileToName.has(contactNumber)) mobileToName.set(contactNumber, contactName);
        }
      });
    });

    return {
      bankNames: Array.from(bankSet).sort((a, b) => a.localeCompare(b)),
      agentNames: Array.from(agentNameSet).sort((a, b) => a.localeCompare(b)),
      agentMobiles: Array.from(agentMobileSet).sort((a, b) => a.localeCompare(b)),
      nameToMobile,
      mobileToName,
    };
  }, [loanCase]);

  const primaryCustomerMobile = useMemo(() => {
    const primary = (customers.find((c) => String(c.role || '').toLowerCase() === 'primary') || customers[0] || null) as any;
    return String(
      primary?.mobile ||
      primary?.mobileNo ||
      primary?.phone ||
      primary?.phoneNumber ||
      ''
    ).trim();
  }, [customers]);

  const latestLoanSanctionData = useMemo(() => {
    const steps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []).filter(
      (step) => String(step?.actionType || '').toLowerCase() === 'loan_sanction_form'
    );
    const stepDataMap = ((((loanCase as any)?.stepData || {}) as Record<string, any>) || {});
    const stepStateMap = ((((loanCase as any)?.stepState || {}) as Record<string, any>) || {});
    const candidates = steps
      .map((step) => {
        const row = (stepDataMap?.[step.key] || {}) as Record<string, any>;
        const sanctionDate = String(row?.sanctionDate || '').trim();
        const rawDoneAtMs = Number(stepStateMap?.[step.key]?.doneAtMs || 0);
        const doneAtMs = Number.isFinite(rawDoneAtMs) ? rawDoneAtMs : 0;
        return {
          stepKey: step.key,
          row,
          sanctionDate,
          doneAtMs,
        };
      })
      .filter((item) => Object.keys(item.row || {}).length > 0);

    candidates.sort((a, b) => {
      if (a.sanctionDate !== b.sanctionDate) return String(b.sanctionDate).localeCompare(String(a.sanctionDate));
      return Number(b.doneAtMs || 0) - Number(a.doneAtMs || 0);
    });

    return candidates[0]?.row || {};
  }, [loanCase, loanWorkflowDefinition?.steps]);

  const loanToDeveloperAmount = useMemo(() => {
    const rawAmount = Number((latestLoanSanctionData as any)?.loanToUnitAmount || 0);
    return Number.isFinite(rawAmount) ? Math.max(0, rawAmount) : 0;
  }, [latestLoanSanctionData]);

  const customerDpReceiptTotal = useMemo(
    () =>
      customerReceiptRows
        .filter((row: any) => row?.isLoanDisbursement !== true)
        .reduce((sum: number, row: any) => sum + Math.max(0, Number(row?.amount || 0) || 0), 0),
    [customerReceiptRows]
  );
  const loanDisbursementReceiptTotal = useMemo(
    () =>
      customerReceiptRows
        .filter((row: any) => row?.isLoanDisbursement === true)
        .reduce((sum: number, row: any) => sum + Math.max(0, Number(row?.amount || 0) || 0), 0),
    [customerReceiptRows]
  );

  const customerPaymentPendingAmount = useMemo(() => {
    const total = Math.max(0, Number((unit as any)?.unitAmount || 0) || 0);
    if (total <= 0) return 0;
    return Math.max(0, total - Number(customerReceiptTotal || 0));
  }, [customerReceiptTotal, unit]);

  const resolveWorkflowStepTitle = useCallback(
    (step: any, which: 'main' | 'loan') => {
      const rawTitle = String(step?.title || '').trim();
      if (!rawTitle) return '';
      if (!/\{\{[^}]+\}\}/.test(rawTitle)) return rawTitle;

      let sanctionBankName = String((latestLoanSanctionData as any)?.sanctionBankName || '').trim();
      if (which === 'loan') {
        const stepKey = String(step?.key || '').trim();
        const row = (((loanCase as any)?.stepData || {}) as Record<string, any>)?.[stepKey] || {};
        sanctionBankName = String(row?.sanctionBankName || sanctionBankName).trim();
      }

      return rawTitle
        .replace(/\{\{\s*loan_sanction_bank_name\s*\}\}/gi, sanctionBankName || 'Bank')
        .replace(/\s{2,}/g, ' ')
        .trim();
    },
    [latestLoanSanctionData, loanCase]
  );

  const resolveWorkflowStepHelpText = useCallback(
    (step: any, which: 'main' | 'loan') => {
      const rawHelpText = String(step?.helpText || '').trim();
      if (!rawHelpText) return '';
      if (!/\{\{[^}]+\}\}/.test(rawHelpText)) return rawHelpText;

      let sanctionBankName = String((latestLoanSanctionData as any)?.sanctionBankName || '').trim();
      if (which === 'loan') {
        const stepKey = String(step?.key || '').trim();
        const row = (((loanCase as any)?.stepData || {}) as Record<string, any>)?.[stepKey] || {};
        sanctionBankName = String(row?.sanctionBankName || sanctionBankName).trim();
      }

      return rawHelpText
        .replace(/\{\{\s*loan_sanction_bank_name\s*\}\}/gi, sanctionBankName || 'Bank')
        .replace(/\s{2,}/g, ' ')
        .trim();
    },
    [latestLoanSanctionData, loanCase]
  );

  const resetLocalState = () => {
    setLoadedForUnit({ customers: '', uploaded: '', draft: '', mainCase: '', loanCase: '' });
    setBooking(null);
    setCustomers([]);
    setSelectedCustomerId('');
    setKycDocs([]);
    setBookingDocuments([]);
    setBookingDraftDocuments([]);
    setDocumentUploadTypes([]);
    setBrokers([]);
    setUserDirectory([]);
    setMainCase(null);
    setLoanCase(null);
    setMainTemplate(null);
    setLoanTemplate(null);
    setKycSummaryByCustomer({});
    setBookingFormDoc(null);
    setAvailableUnitDraftHeaderFile(null);
    setPaymentDetailsDraftGenerating(false);
    setAdminDocumentActionMode(false);
    setHeaderEditAnchorEl(null);
    setUnitStatusDialogOpen(false);
    setUnitStatusSaving(false);
    setBookingFormTerms('');
    setBookingFormRefByBroker(null);
    setBookingFormRank('');
    setBookingFormScheme('no');
    setBookingFormSchemeRemark('');
    setError(null);
    setEditingCustomerId(null);
    setCustomerDrawerOpen(false);
    setPaymentDrawerOpen(false);
    setPaymentTxnType('receipt');
    setPaymentReceiptCategory('customer');
    setPaymentIsLoanDisbursement(false);
    setPaymentBankAccountId('');
    setPaymentDateYMD(ymdToday());
    setPaymentAmount('');
    setPaymentMode('');
    setPaymentReferenceNo('');
    setPaymentNotes('');
    setPaymentPartyName('');
    setPaymentPayerBankName('');
    setSendToSbiHint('');
    setLoanOtherBankName('');
    setLoanOtherBankContactName('');
    setLoanOtherBankContactNumber('');
    setLoanOtherBankSentOn(ymdToday());
    setLoanOtherBankRemarks('');
    setLoanOtherBankRemarkEditId('');
    setLoanOtherBankStatusEdit('sent');
    setLoanOtherBankRemarkDraft('');
    setLoanOtherBankFormOpen(false);
    setPaymentVoucherFile(null);
    setPaymentSaving(false);
    setPaymentUploading(false);
    setPaymentError(null);
    setCustFirstName('');
    setCustMiddleName('');
    setCustLastName('');
    setCustDisplayName('');
    setCustMobile('');
    setCustEmail('');
    setCustOccupation('');
    setCustGender('');
    setCustPan('');
    setCustRole('primary');
    setCustAddr1('');
    setCustAddr2('');
    setCustCity('');
    setCustState('');
    setCustPincode('');
    setKycType('PAN');
    setKycDocNumber('');
    setKycFiles([]);
    setAadhaarFrontFile(null);
    setAadhaarBackFile(null);
    setStepDocPickedFiles({});
    setStepDocUploadPhase({});
    setStepGeneratePhase({});
    setStepFieldDrafts({});
    setStep1ApplicantsEditMode(false);
    if (paymentVoucherInputRef.current) paymentVoucherInputRef.current.value = '';
    setFallbackBookingId('');
  };

  const detailRow = (
    icon: React.ReactNode,
    label: string,
    value: string | number | null | undefined,
    opts?: { valueColor?: string; valueWeight?: number; rowBgColor?: string; rowBorderColor?: string }
  ) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1,
        py: 1.25,
        borderBottom: '1px solid #f0f0f0',
        bgcolor: opts?.rowBgColor || 'transparent',
      }}
    >
      <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>{icon}</Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: opts?.valueWeight ?? 500, color: opts?.valueColor || 'text.primary' }}
      >
        {label}: {value ?? '—'}
      </Typography>
    </Box>
  );

  const saleAgreementDateChanged = Boolean(String(saleAgreementDateEditYmd || '').trim());
  const saleAgreementDatePreview = dateFromYMD(String(saleAgreementDateEditYmd || '').trim());
  const renderSaleAgreementDateRow = () => {
    if (!(isAdmin && adminDocumentActionMode && bookingId)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 1.25, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ color: 'rgba(157,111,50,0.6)', display: 'flex' }}>
            <CalendarTodayOutlinedIcon fontSize="small" />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>Sale Agreement Date:</Typography>{''}
            {' '}{saleAgreementDateDisplay ?? '—'}
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 1.25,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
          <CalendarTodayOutlinedIcon fontSize="small" />
        </Box>
        <TextField
          size="small"
          type="date"
          label="Sale Agreement Date"
          value={saleAgreementDateEditYmd}
          onChange={(e) => setSaleAgreementDateEditYmd(String(e.target.value || ''))}
          InputLabelProps={{ shrink: true }}
          helperText={saleAgreementDatePreview ? formatDDMMYYYY(saleAgreementDatePreview) : '—'}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            void saveSaleAgreementDateFromAdmin();
          }}
          disabled={!saleAgreementDateChanged || saleAgreementDateSaving}
          sx={{ textTransform: 'none', fontWeight: 700, minWidth: 84 }}
        >
          {saleAgreementDateSaving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    );
  };

  const saleDeedDateChanged = Boolean(String(saleDeedDateEditYmd || '').trim());
  const saleDeedDatePreview = dateFromYMD(String(saleDeedDateEditYmd || '').trim());
  const renderSaleDeedDateRow = () => {
    if (!(isAdmin && adminDocumentActionMode && bookingId)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 1.25, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ color: 'rgba(157,111,50,0.6)', display: 'flex' }}>
            <CalendarTodayOutlinedIcon fontSize="small" />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>Sale Deed Date:</Typography>{''}
            {' '}{saleDeedDateDisplay ?? '—'}
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 1.25,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
          <CalendarTodayOutlinedIcon fontSize="small" />
        </Box>
        <TextField
          size="small"
          type="date"
          label="Sale Deed Date"
          value={saleDeedDateEditYmd}
          onChange={(e) => setSaleDeedDateEditYmd(String(e.target.value || ''))}
          InputLabelProps={{ shrink: true }}
          helperText={saleDeedDatePreview ? formatDDMMYYYY(saleDeedDatePreview) : '—'}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            void saveSaleDeedDateFromAdmin();
          }}
          disabled={!saleDeedDateChanged || saleDeedDateSaving}
          sx={{ textTransform: 'none', fontWeight: 700, minWidth: 84 }}
        >
          {saleDeedDateSaving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    );
  };

  const areaRow = (args: { icon: React.ReactNode; label: string; sqm?: number | null }) => {
    const sqm = typeof args.sqm === 'number' && Number.isFinite(args.sqm) ? args.sqm : null;
    const sqft = sqm != null ? sqmToSqft(sqm) : null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          px: 1,
          py: 1.25,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>{args.icon}</Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {args.label}:
            </Typography>{' '}
            {sqm != null ? `${sqm} sq mt` : '—'}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
          {sqft != null ? `${sqft.toFixed(2)} sq ft` : '—'}
        </Typography>
      </Box>
    );
  };

  const amountBreakupRow = (amount: number | null | undefined) => {
    const total = typeof amount === 'number' && Number.isFinite(amount) ? Math.max(0, amount) : 0;
    const customerReceived = Math.max(0, Number(customerReceiptTotal || 0));
    const loanReceived = Math.max(0, Number(loanDisbursementReceiptTotal || 0));
    const received = customerReceived + loanReceived;
    const loanAmount = total > 0 ? Math.min(loanToDeveloperAmount, total) : 0;
    const pending = total > 0 ? Math.max(0, total - received) : 0;
    const isFullyReceived = total > 0 && received >= total;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          px: 1,
          py: 1.25,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ color: 'rgba(157,111,50,0.6)', display: 'flex' }}>
            <CurrencyRupeeOutlinedIcon fontSize="small" />
          </Box>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  Amount:
                </Typography>{' '}
                ₹ {total.toLocaleString('en-IN')}
              </Typography>
              {isFullyReceived && <CheckCircleOutlinedIcon sx={{ fontSize: 15, color: '#2a7d4b' }} />}
            </Stack>
            {(received > 0 || loanAmount > 0) && pending > 0 ? (
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                  Received ₹ {received.toLocaleString('en-IN')}
                </Typography>
                {loanAmount > 0 && (
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    Loan ₹ {loanAmount.toLocaleString('en-IN')}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: 'warning.main',
                    fontWeight: 500,
                  }}
                >
                  Pending ₹ {pending.toLocaleString('en-IN')}
                </Typography>
              </Stack>
            ) : received > 0 || loanAmount > 0 ? (
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                {received > 0 && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                    Received ₹ {received.toLocaleString('en-IN')}
                  </Typography>
                )}
                {loanAmount > 0 && (
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    Loan ₹ {loanAmount.toLocaleString('en-IN')}
                  </Typography>
                )}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={paymentDetailsDraftGenerating ? 'Generating…' : 'Generate payment details'}>
            <span>
              <IconButton
                size="small"
                disabled={paymentDetailsDraftGenerating}
                onClick={async () => {
                  try {
                    setPaymentDetailsDraftGenerating(true);
                    setError(null);
                    const effectiveBookingId = bookingId || (await ensureBookingForDraft());
                    await generatePaymentDetailsV1Draft(effectiveBookingId);
                  } catch (err: any) {
                    setError(err?.message || 'Failed to generate payment details.');
                  } finally {
                    setPaymentDetailsDraftGenerating(false);
                  }
                }}
                sx={{ ml: 0.5 }}
              >
                {paymentDetailsDraftGenerating ? <CircularProgress size={16} /> : <AutorenewOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  const collectionSnapshot = (amount: number | null | undefined, compact = false) => {
    const totalAmount = Math.max(0, Number(amount || 0) || 0);
    const loanAmount = Math.min(totalAmount, Math.max(0, Number(loanToDeveloperAmount || 0) || 0));
    const dpAmount = Math.max(0, totalAmount - loanAmount);
    const dpReceivedAmount = Math.max(0, Number(customerReceiptTotal || 0) || 0);
    const loanReceivedAmount = Math.max(0, Number(loanDisbursementReceiptTotal || 0) || 0);
    const totalReceivedAmount = dpReceivedAmount + loanReceivedAmount;
    const rows = [
      {
        label: 'Total',
        amount: totalAmount,
        received: totalReceivedAmount,
        pending: Math.max(0, totalAmount - totalReceivedAmount),
        emphasis: true,
      },
      ...(loanAmount > 0
        ? [
            {
              label: 'Customer DP',
              amount: dpAmount,
              received: dpReceivedAmount,
              pending: Math.max(0, dpAmount - dpReceivedAmount),
              emphasis: false,
            },
            {
              label: 'Loan to Builder',
              amount: loanAmount,
              received: loanReceivedAmount,
              pending: Math.max(0, loanAmount - loanReceivedAmount),
              emphasis: false,
            },
          ]
        : []),
    ];
    const formatCurrency = (value: number) => `₹ ${value.toLocaleString('en-IN')}`;
    const formatPercentage = (value: number, total: number) =>
      total > 0 ? `${((Math.max(0, value) / total) * 100).toFixed(1)}%` : '0.0%';

    if (compact) {
      const totalRow = rows[0];
      return (
        <Box sx={{ py: 0.25 }}>
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase', mb: 0.75 }}
          >
            Collection
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={0.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Amount</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatCurrency(totalRow.amount)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Collected</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: 'success.main' }}>{formatCurrency(totalRow.received)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: totalRow.pending > 0 ? 'warning.main' : 'text.primary' }}>
                {formatCurrency(totalRow.pending)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      );
    }

    return (
      <Box sx={{ pt: 1.25, pb: 1.25, mb: 0.5, borderBottom: rows.length > 1 ? '1px solid #e7e0d7' : 'none' }}>
        <Typography
          sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase', mb: 0.75 }}
        >
          Collection Snapshot
        </Typography>
        <Box>
          <Table size="small" aria-label="Collection snapshot">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.85, px: 1, fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                  Collection
                </TableCell>
                <TableCell align="right" sx={{ py: 0.85, px: 0.75, fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  Amount
                </TableCell>
                <TableCell align="right" sx={{ py: 0.85, px: 0.75, fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  Collected
                </TableCell>
                <TableCell align="right" sx={{ py: 0.85, px: 1, fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  Pending
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.label}
                  sx={row.emphasis ? { '& td': { borderBottom: rows.length > 1 ? '1px solid #d9d1c5' : 'none' } } : undefined}
                >
                  <TableCell sx={{ py: 0.9, px: 1, fontSize: '0.75rem', fontWeight: row.emphasis ? 800 : 600, whiteSpace: 'nowrap' }}>
                    {row.label}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.9, px: 0.75, fontSize: '0.75rem', fontWeight: row.emphasis ? 800 : 600, whiteSpace: 'nowrap' }}>
                    {formatCurrency(row.amount)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.9, px: 0.75, color: 'success.main', whiteSpace: 'nowrap' }}>
                    <Stack spacing={0} alignItems="flex-end">
                      <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: row.emphasis ? 800 : 600 }}>
                        {formatCurrency(row.received)}
                      </Typography>
                      {!row.emphasis ? (
                        <Typography component="span" sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>
                          {formatPercentage(row.received, row.amount)} collected
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.9, px: 1, color: row.pending > 0 ? 'warning.main' : 'text.primary', whiteSpace: 'nowrap' }}>
                    <Stack spacing={0} alignItems="flex-end">
                      <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: row.emphasis ? 800 : 600 }}>
                        {formatCurrency(row.pending)}
                      </Typography>
                      {!row.emphasis ? (
                        <Typography component="span" sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>
                          {formatPercentage(row.pending, row.amount)} balance
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  };

  const chargeBreakupRow = (
    label: string,
    totalAmount: number | null | undefined,
    receivedAmount: number | null | undefined,
    opts?: { fullBg?: boolean }
  ) => {
    const total = typeof totalAmount === 'number' && Number.isFinite(totalAmount) ? Math.max(0, totalAmount) : 0;
    const received = Math.max(0, Number(receivedAmount || 0));
    const pending = total > 0 ? Math.max(0, total - received) : 0;
    const isFullyReceived = total > 0 && pending <= 0;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1,
          py: 1.25,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Box sx={{ color: 'rgba(157,111,50,0.6)', display: 'flex' }}>
          <CurrencyRupeeOutlinedIcon fontSize="small" />
        </Box>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {label}:
              </Typography>{' '}
              ₹ {total.toLocaleString('en-IN')}
            </Typography>
            {opts?.fullBg && isFullyReceived && <CheckCircleOutlinedIcon sx={{ fontSize: 15, color: '#2a7d4b' }} />}
          </Stack>
          {received > 0 && pending > 0 ? (
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                Received ₹ {received.toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                Pending ₹ {pending.toLocaleString('en-IN')}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Box>
    );
  };

  const gstBreakupRow = (gstAmount: number | null | undefined) => {
    const total = typeof gstAmount === 'number' && Number.isFinite(gstAmount) ? Math.max(0, gstAmount) : 0;
    const received = Math.max(0, Number(gstReceiptTotal || 0));
    const pending = total > 0 ? Math.max(0, total - received) : 0;
    const isFullyReceived = total > 0 && pending <= 0;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1,
          py: 1.25,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Box sx={{ color: 'rgba(157,111,50,0.6)', display: 'flex' }}>
          <CurrencyRupeeOutlinedIcon fontSize="small" />
        </Box>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <Typography component="span" sx={{ fontWeight: 700 }}>
                GST:
              </Typography>{' '}
              ₹ {total.toLocaleString('en-IN')}
            </Typography>
            {isFullyReceived && <CheckCircleOutlinedIcon sx={{ fontSize: 15, color: '#2a7d4b' }} />}
          </Stack>
          {received > 0 && pending > 0 ? (
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                Received ₹ {received.toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                Pending ₹ {pending.toLocaleString('en-IN')}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Box>
    );
  };

  useEffect(() => {
    if (!open) return;
    resetLocalState();
  }, [open, projectId, unit?.id]);

  useEffect(() => {
    if (!open) return undefined;
    // Map uploadedBy email -> display name for document/timeline labels.
    const q = query(collection(db, 'users'), orderBy('name', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            email: typeof v?.email === 'string' ? v.email : null,
            name: typeof v?.name === 'string' ? v.name : null,
          };
        });
        setUserDirectory((prev) => {
          if (!Array.isArray(prev) || prev.length !== next.length) return next;
          for (let i = 0; i < next.length; i += 1) {
            const a = prev[i];
            const b = next[i];
            if (!a || !b) return next;
            if (String(a.id) !== String(b.id)) return next;
            if (String(a.email || '') !== String(b.email || '')) return next;
            if (String(a.name || '') !== String(b.name || '')) return next;
          }
          return prev;
        });
      },
      () => setUserDirectory([])
    );
  }, [db, open]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId) {
      setBankAccounts([]);
      return undefined;
    }
    const q = query(collection(db, 'bankAccounts'), orderBy('name', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as BankAccount[];
        const pid = String(projectId || '');
        const filtered = all.filter((b: any) =>
          Array.isArray(b.projectIds) && b.projectIds.map((x: any) => String(x)).includes(pid)
        );
        setBankAccounts(filtered);
      },
      () => setBankAccounts([])
    );
  }, [db, open, projectId]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId) {
      setDocumentUploadTypes([]);
      return undefined;
    }
    const refCol = collection(db, 'officeDocumentUploadTypes');
    return onSnapshot(
      refCol,
      (snap) => {
        const pid = String(projectId || '');
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((row: any) => {
            const assigned = Array.isArray(row?.assignedProjectIds)
              ? row.assignedProjectIds.map((x: any) => String(x))
              : [];
            return assigned.length === 0 || assigned.includes(pid);
          });
        setDocumentUploadTypes(rows);
      },
      () => setDocumentUploadTypes([])
    );
  }, [db, open, projectId]);

  useEffect(() => {
    if (!open) return undefined;
    return onSnapshot(
      collection(db, 'officeDocumentTemplates'),
      (snap) => {
        const values = new Map<string, string>();
        const addValue = (raw: unknown) => {
          const value = String(raw || '').trim();
          const key = value.toLowerCase();
          if (value && !values.has(key)) values.set(key, value);
        };
        snap.docs.forEach((docSnap) => {
          const row = docSnap.data() as any;
          const linkedBanks = Array.isArray(row?.linkedBanks)
            ? row.linkedBanks
            : Array.isArray(row?.visualModel?.linkedBanks)
              ? row.visualModel.linkedBanks
              : [];
          linkedBanks.forEach(addValue);
        });
        setOfficeDocumentLinkedBankNames(
          Array.from(values.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        );
      },
      () => setOfficeDocumentLinkedBankNames([])
    );
  }, [db, open]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId || !unit?.id) {
      setUnitBookingHistoryIds([]);
      return undefined;
    }
    const q = query(collection(db, 'projects', projectId, 'units', unit.id, 'bookings'), limit(50));
    return onSnapshot(
      q,
      (snap) => {
        const ids = snap.docs.map((d) => String(d.id || '').trim()).filter(Boolean);
        setUnitBookingHistoryIds(ids);
      },
      () => setUnitBookingHistoryIds([])
    );
  }, [db, open, projectId, unit?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const brokersRef = collection(db, 'brokers');
    const q = query(brokersRef, orderBy('displayNameLower', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .map((row: any) => ({
            id: String(row?.id || ''),
            brokerName: String(row?.brokerName || '').trim(),
            agencyName: String(row?.agencyName || '').trim(),
            displayName: String(row?.displayName || row?.brokerName || '').trim(),
          }))
          .filter((row) => !!row.id)
          .sort((a, b) => {
            const left = String(a.brokerName || a.displayName || '').toLowerCase();
            const right = String(b.brokerName || b.displayName || '').toLowerCase();
            return left.localeCompare(right);
          });
        const seen = new Set<string>();
        const uniqueRows = rows.filter((row) => {
          const nameKey = String(row.brokerName || row.displayName || '').trim().toLowerCase();
          if (!nameKey) return true;
          if (seen.has(nameKey)) return false;
          seen.add(nameKey);
          return true;
        });
        setBrokers(uniqueRows.filter((row) => String(row.brokerName || row.displayName || '').trim().length > 0));
      },
      (err) => {
        console.warn('Load brokers failed', err);
        setBrokers([]);
      }
    );
  }, [db, open]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId || !unit?.id) {
      setCustomerReceiptTotal(0);
      setGstReceiptTotal(0);
      setCustomerReceiptRows([]);
      setCustomerRefundRows([]);
      setGstReceiptRows([]);
      setPaymentDuplicateCandidates([]);
      setLastPayerBankByCategory({ customer: '', gst: '' });
      return undefined;
    }
    const q = query(collection(db, 'projects', projectId, 'bankTransactions'), where('unitId', '==', unit.id));
    return onSnapshot(
      q,
      (snap) => {
        const allRows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          // Always exclude transactions that were stamped cancelled during a booking wipe.
          .filter((row: any) => !row.cancelledAt && row.excludeFromBookingReport !== true);
        const activeBookingId = String(bookingId || '').trim();
        const activeBookingStartMs = coerceTsToMs((booking as any)?.createdAt) || 0;
        const hasSingleKnownBooking =
          !!activeBookingId &&
          unitBookingHistoryIds.length === 1 &&
          String(unitBookingHistoryIds[0] || '').trim() === activeBookingId;
        // No active booking → show nothing. A unit with no booking has no current
        // payment context; showing historical rows from cancelled bookings is always wrong.
        const rows = !activeBookingId
          ? []
          : allRows.filter((row: any) => {
              const rowBookingId = String(row?.bookingId || '').trim();
              if (rowBookingId) return rowBookingId === activeBookingId;
              if (hasSingleKnownBooking) return true;
              if (activeBookingStartMs <= 0) return true;
              const rowMs =
                coerceTsToMs(row?.chqDate) ||
                coerceTsToMs(row?.txnDate) ||
                coerceTsToMs(row?.createdAt) ||
                coerceTsToMs(row?.updatedAt) ||
                0;
              return rowMs >= activeBookingStartMs;
            });
        const sortedRows = [...rows].sort((a, b) => {
          const aAt =
            (typeof a?.chqDate?.toMillis === 'function' ? a.chqDate.toMillis() : null) ||
            (typeof a?.txnDate?.toMillis === 'function' ? a.txnDate.toMillis() : null) ||
            (typeof a?.createdAt?.toMillis === 'function' ? a.createdAt.toMillis() : null) ||
            0;
          const bAt =
            (typeof b?.chqDate?.toMillis === 'function' ? b.chqDate.toMillis() : null) ||
            (typeof b?.txnDate?.toMillis === 'function' ? b.txnDate.toMillis() : null) ||
            (typeof b?.createdAt?.toMillis === 'function' ? b.createdAt.toMillis() : null) ||
            0;
          return bAt - aAt;
        });
        setCustomerReceiptRows(
          sortedRows.filter(
            (v: any) =>
              String(v?.txnType || '').toLowerCase() === 'receipt' &&
              String(v?.receiptCategory || '').toLowerCase() === 'customer'
          )
        );
        setCustomerRefundRows(
          sortedRows.filter(
            (v: any) =>
              String(v?.txnType || '').toLowerCase() === 'payment' &&
              String(v?.paymentCategory || '').toLowerCase() === 'customer_refund'
          )
        );
        setGstReceiptRows(
          sortedRows.filter(
            (v: any) =>
              String(v?.txnType || '').toLowerCase() === 'receipt' &&
              String(v?.receiptCategory || '').toLowerCase() === 'gst'
          )
        );
        setPaymentDuplicateCandidates(sortedRows);
        let customer = 0;
        let customerRefund = 0;
        let gst = 0;
        let customerBank = '';
        let gstBank = '';
        let customerBankAt = -1;
        let gstBankAt = -1;
        rows.forEach((v: any) => {
          const amt = Number(v?.amount || 0);
          if (!Number.isFinite(amt)) return;
          const txnType = String(v?.txnType || '').toLowerCase();
          const category = String(v?.receiptCategory || '').toLowerCase();
          const paymentCategory = String(v?.paymentCategory || '').toLowerCase();
          const payerBankName = String(v?.payerBankName || '').trim();
          const at =
            (typeof v?.updatedAt?.toMillis === 'function' ? v.updatedAt.toMillis() : null) ||
            (typeof v?.createdAt?.toMillis === 'function' ? v.createdAt.toMillis() : null) ||
            (typeof v?.chqDate?.toMillis === 'function' ? v.chqDate.toMillis() : null) ||
            (typeof v?.txnDate?.toMillis === 'function' ? v.txnDate.toMillis() : null) ||
            0;
          if (txnType === 'receipt' && category === 'gst') {
            gst += amt;
            if (payerBankName && at >= gstBankAt) {
              gstBankAt = at;
              gstBank = payerBankName;
            }
          }
          if (txnType === 'receipt' && category === 'customer' && v?.isLoanDisbursement !== true) {
            customer += amt;
            if (payerBankName && at >= customerBankAt) {
              customerBankAt = at;
              customerBank = payerBankName;
            }
          }
          if (txnType === 'payment' && paymentCategory === 'customer_refund') {
            customerRefund += amt;
          }
        });
        setCustomerReceiptTotal(customer - customerRefund);
        setGstReceiptTotal(gst);
        setLastPayerBankByCategory({ customer: customerBank, gst: gstBank });
      },
      () => {
        setCustomerReceiptTotal(0);
        setGstReceiptTotal(0);
        setCustomerReceiptRows([]);
        setCustomerRefundRows([]);
        setGstReceiptRows([]);
        setPaymentDuplicateCandidates([]);
        setLastPayerBankByCategory({ customer: '', gst: '' });
      }
    );
  }, [booking, bookingId, db, open, projectId, unit?.id, unitBookingHistoryIds]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId || !unit?.id) {
      setAdvocateAgreementReceiptTotal(0);
      setStampDutyReceiptTotal(0);
      setRegistrationReceiptTotal(0);
      setAdvocateDeedReceiptTotal(0);
      return undefined;
    }
    const q = query(
      collection(db, 'projects', projectId, 'bankTransactions'),
      where('unitId', '==', unit.id),
      where('txnType', '==', 'receipt'),
      where('receiptCategory', '==', 'advocate')
    );
    return onSnapshot(
      q,
      (snap) => {
        let agreementTotal = 0;
        let stampDutyTotal = 0;
        let registrationTotal = 0;
        let deedTotal = 0;
        snap.docs.forEach((d) => {
          const row = d.data() as any;
          const chargeType = String(row?.legalChargeType || row?.chargeType || '').trim().toLowerCase();
          const amount = Number(row?.amount || 0);
          if (!Number.isFinite(amount) || amount <= 0) return;
          if (chargeType === 'agreement_fee') {
            agreementTotal += amount;
            return;
          }
          if (chargeType === 'stamp_duty') {
            stampDutyTotal += amount;
            return;
          }
          if (chargeType === 'registration_charges') {
            registrationTotal += amount;
          }
          if (chargeType === 'deed_fee') deedTotal += amount;
        });
        setAdvocateAgreementReceiptTotal(agreementTotal);
        setStampDutyReceiptTotal(stampDutyTotal);
        setRegistrationReceiptTotal(registrationTotal);
        setAdvocateDeedReceiptTotal(deedTotal);
      },
      () => {
        setAdvocateAgreementReceiptTotal(0);
        setStampDutyReceiptTotal(0);
        setRegistrationReceiptTotal(0);
        setAdvocateDeedReceiptTotal(0);
      }
    );
  }, [db, open, projectId, unit?.id]);

		  useEffect(() => {
		    if (kycType !== 'AADHAAR') return;
		    // Aadhaar requires front+back; avoid mixing with the generic multi-file picker.
		    setKycFiles([]);
		  }, [kycType]);

	  useEffect(() => {
	    if (!open) return undefined;
	    if (!projectId) {
        setProjectSbAreaPercent(0);
        setProjectSbPercentByUnitType({});
        return undefined;
      }
	    const projectRef = doc(db, 'projects', projectId);
	    const unsub = onSnapshot(
	      projectRef,
      (snap) => {
        const d = (snap.data() as any) || {};
        const next = {
          mainId: String(d.officeMainWorkflowTemplateId || DEFAULT_OFFICE_MAIN_TEMPLATE_ID),
          loanId: String(d.officeLoanWorkflowTemplateId || DEFAULT_OFFICE_LOAN_TEMPLATE_ID),
        };
        const nextDef = {
          mainId: String(d.officeMainWorkflowDefinitionId || '').trim(),
          loanId: String(d.officeLoanWorkflowDefinitionId || '').trim(),
        };
        setProjectName(String(d.name || d.projectName || projectId || 'Project'));
        setProjectBuc({ bucReceivedAt: String(d.bucReceivedAt || ''), bucReceivedBuildingNos: Array.isArray(d.bucReceivedBuildingNos) ? d.bucReceivedBuildingNos : [] });
        setProjectWorkflowIds((prev) => (prev.mainId === next.mainId && prev.loanId === next.loanId ? prev : next));
        setProjectWorkflowDefinitionIds((prev) =>
          prev.mainId === nextDef.mainId && prev.loanId === nextDef.loanId ? prev : nextDef
        );
        setProjectAdvocateAgreementFee(Math.max(0, Number(d.advocateAgreementFee || 0) || 0));
        setProjectSbAreaPercent(Math.max(0, Number(d.sbAreaPercent || 0) || 0));
        const rawSbByType = ({
          ...(d.sbAreaPercentByUnitType || {}),
          ...(d.sbAreaPercentByUsage || {}),
        }) as Record<string, any>;
        const nextSbByType: Record<string, number> = {};
        Object.keys(rawSbByType || {}).forEach((k) => {
          const key = String(k || '').trim();
          const val = Number(rawSbByType[k]);
          if (!key) return;
          if (!Number.isFinite(val) || val < 0) return;
          nextSbByType[key] = val;
        });
        setProjectSbPercentByUnitType(nextSbByType);
        const rawGstByType = ({
          ...(d.gstPercentByUnitType || {}),
          ...(d.gstPercentByUsage || {}),
        }) as Record<string, any>;
        const nextGstByType: Record<string, number> = {};
        Object.keys(rawGstByType || {}).forEach((k) => {
          const key = String(k || '').trim();
          const val = Number(rawGstByType[k]);
          if (!key) return;
          if (!Number.isFinite(val) || val < 0) return;
          nextGstByType[key] = val;
        });
        setProjectGstPercentByUnitType(nextGstByType);
        const rawDefaultGst = Number(d.gstDefaultPercent ?? 1);
        setProjectDefaultGstPercent(Number.isFinite(rawDefaultGst) && rawDefaultGst >= 0 ? rawDefaultGst : 1);
      },
	      (err) => {
	        console.error('Load project workflow ids failed', err);
	      }
	    );
	    return () => unsub();
	  }, [db, open, projectId]);

  const mainTemplateIdToUse = useMemo(
    () => String(projectWorkflowIds.mainId || DEFAULT_OFFICE_MAIN_TEMPLATE_ID),
    [projectWorkflowIds.mainId]
  );
  const loanTemplateIdToUse = useMemo(
    () => String(projectWorkflowIds.loanId || DEFAULT_OFFICE_LOAN_TEMPLATE_ID),
    [projectWorkflowIds.loanId]
  );

  useEffect(() => {
    if (!open) return undefined;
    if (!unit?.id) return undefined;
    if (!projectId) return undefined;
    if (!bookingId) return undefined;
    const mainCaseRef = doc(db, 'projects', projectId, 'units', unit.id, 'cases', bookingId);
    const unsub = onSnapshot(
      mainCaseRef,
      (snap) => {
        setWorkflowHydration((prev) => ({ ...prev, mainCase: true }));
        markLoadedFor('mainCase', String(unit?.id || ''));
        if (!snap.exists()) {
          setMainCase(null);
          return;
        }
        setMainCase({ id: snap.id, ...(snap.data() as any) } as any);
      },
      (err) => {
        console.error('Load main workflow case failed', err);
        setWorkflowHydration((prev) => ({ ...prev, mainCase: true }));
      }
    );
    return () => unsub();
  }, [bookingId, db, markLoadedFor, open, projectId, unit?.id]);

  useEffect(() => {
    if (!open) return undefined;
    if (!unit?.id) return undefined;
    if (!projectId) return undefined;
    if (!bookingId) return undefined;
    const loanCaseRef = doc(db, 'projects', projectId, 'units', unit.id, 'cases', `loan_${bookingId}`);
    const unsub = onSnapshot(
      loanCaseRef,
      (snap) => {
        setWorkflowHydration((prev) => ({ ...prev, loanCase: true }));
        markLoadedFor('loanCase', String(unit?.id || ''));
        if (!snap.exists()) {
          setLoanCase(null);
          return;
        }
        setLoanCase({ id: snap.id, ...(snap.data() as any) } as any);
      },
      (err) => {
        console.error('Load loan workflow case failed', err);
        setWorkflowHydration((prev) => ({ ...prev, loanCase: true }));
      }
    );
    return () => unsub();
  }, [bookingId, db, markLoadedFor, open, projectId, unit?.id]);

	  useEffect(() => {
	    if (!open) return undefined;
	    if (!projectId) return undefined;
	    const refDoc = doc(db, 'projects', projectId, 'workflowTemplates', mainTemplateIdToUse);
	    const unsub = onSnapshot(
	      refDoc,
	      (snap) => {
	        if (!snap.exists()) {
	          setMainTemplate(null);
          return;
        }
        setMainTemplate(snap.data() as any);
      },
      (err) => {
        console.error('Load main workflow template failed', err);
      }
	    );
	    return () => unsub();
	  }, [db, mainTemplateIdToUse, open, projectId]);

	  useEffect(() => {
	    if (!open) return undefined;
	    if (!projectId) return undefined;
	    const refDoc = doc(db, 'projects', projectId, 'workflowTemplates', loanTemplateIdToUse);
	    const unsub = onSnapshot(
	      refDoc,
	      (snap) => {
	        if (!snap.exists()) {
	          setLoanTemplate(null);
          return;
        }
        setLoanTemplate(snap.data() as any);
      },
      (err) => {
        console.error('Load loan workflow template failed', err);
      }
	    );
	    return () => unsub();
	  }, [db, loanTemplateIdToUse, open, projectId]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId) return undefined;
    const definitionId = String(projectWorkflowDefinitionIds.mainId || '').trim();
    if (!definitionId) {
      setMainWorkflowDefinition(null);
      return undefined;
    }
    const projectDefRef = doc(db, 'projects', projectId, 'workflowDefinitions', definitionId);
    const unsub = onSnapshot(
      projectDefRef,
      (snap) => {
        if (snap.exists()) {
          setMainWorkflowDefinition({ id: snap.id, ...(snap.data() as any) });
          return;
        }
        getDoc(doc(db, 'officeWorkflowDefinitions', definitionId))
          .then((globalSnap) => {
            if (!globalSnap.exists()) {
              setMainWorkflowDefinition(null);
              return;
            }
            setMainWorkflowDefinition({ id: globalSnap.id, ...(globalSnap.data() as any) });
          })
          .catch(() => setMainWorkflowDefinition(null));
      },
      () => setMainWorkflowDefinition(null)
    );
    return () => unsub();
  }, [db, open, projectId, projectWorkflowDefinitionIds.mainId]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId) return undefined;
    const definitionId = String(projectWorkflowDefinitionIds.loanId || '').trim();
    if (!definitionId) {
      setLoanWorkflowDefinition(null);
      return undefined;
    }
    const projectDefRef = doc(db, 'projects', projectId, 'workflowDefinitions', definitionId);
    const unsub = onSnapshot(
      projectDefRef,
      (snap) => {
        if (snap.exists()) {
          setLoanWorkflowDefinition({ id: snap.id, ...(snap.data() as any) });
          return;
        }
        getDoc(doc(db, 'officeWorkflowDefinitions', definitionId))
          .then((globalSnap) => {
            if (!globalSnap.exists()) {
              setLoanWorkflowDefinition(null);
              return;
            }
            setLoanWorkflowDefinition({ id: globalSnap.id, ...(globalSnap.data() as any) });
          })
          .catch(() => setLoanWorkflowDefinition(null));
      },
      () => setLoanWorkflowDefinition(null)
    );
    return () => unsub();
  }, [db, open, projectId, projectWorkflowDefinitionIds.loanId]);

  useEffect(() => {
    if (!open) return undefined;
    if (!unit?.id) return undefined;
    if (!projectId) return undefined;
    if (!bookingId) return undefined;

    setLoading(true);
    const bookingRef = doc(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingId);
    const unsubBooking = onSnapshot(
      bookingRef,
      (snap) => {
        setWorkflowHydration((prev) => ({ ...prev, booking: true }));
        if (!snap.exists()) {
          setBooking(null);
          setLoading(false);
          return;
        }
        setBooking({ id: snap.id, ...(snap.data() as any) });
        setLoading(false);
      },
      (err) => {
        console.error('Load booking failed', err);
        setError(err?.message || 'Failed to load booking.');
        setLoading(false);
        setWorkflowHydration((prev) => ({ ...prev, booking: true }));
      }
    );

    const customersCol = collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingId, 'customers');
    const unsubCustomers = onSnapshot(
      customersCol,
      (snap) => {
        setWorkflowHydration((prev) => ({ ...prev, customers: true }));
        markLoadedFor('customers', String(unit?.id || ''));
        const createdAtMs = (value: any): number => {
          if (!value) return 0;
          if (typeof value?.toMillis === 'function') return Number(value.toMillis() || 0);
          if (typeof value?.seconds === 'number') return Number(value.seconds) * 1000;
          if (typeof value === 'number') return value;
          return 0;
        };
        const rolePriority = (role: string | null | undefined): number =>
          String(role || '').toLowerCase() === 'primary' ? 0 : 1;
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((c: any) => c?.name && c?.mobile) as CustomerDoc[];
        rows.sort((a, b) => {
          // Buyer order from the buyer details form wins; older records fall back to primary first.
          const orderDiff = Number((a as any).order ?? 99) - Number((b as any).order ?? 99);
          if (orderDiff !== 0) return orderDiff;
          const roleDiff = rolePriority(a.role) - rolePriority(b.role);
          if (roleDiff !== 0) return roleDiff;
          const createdDiff = createdAtMs((a as any).createdAt) - createdAtMs((b as any).createdAt);
          if (createdDiff !== 0) return createdDiff;
          return String(a.id).localeCompare(String(b.id));
        });
        setCustomers(rows);
        setSelectedCustomerId((prev) => {
          if (prev && rows.some((r) => r.id === prev)) return prev;
          return rows[0]?.id || '';
        });
      },
      (err) => {
        console.error('Load customers failed', err);
        setError(err?.message || 'Failed to load customers.');
        setWorkflowHydration((prev) => ({ ...prev, customers: true }));
      }
    );

    return () => {
      unsubBooking();
      unsubCustomers();
    };
  }, [bookingId, db, markLoadedFor, open, projectId, unit?.id]);

  useEffect(() => {
    setBookingDocuments([]);
    if (!open) return undefined;
    if (!unit?.id) return undefined;
    if (!projectId) return undefined;
    if (!bookingId) return undefined;
    const refCol = collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingId, 'uploaded');
    const unsub = onSnapshot(
      query(refCol, orderBy('createdAt', 'desc')),
      (snap) => {
        setWorkflowHydration((prev) => ({ ...prev, uploaded: true }));
        markLoadedFor('uploaded', String(unit?.id || ''));
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as BookingDocumentDoc[];
        setBookingDocuments(rows);
        const bookingFormRow =
          rows.find((r) => String((r as any)?.id || '') === BOOKING_FORM_SIGNED_DOC_KEY) ||
          rows.find((r) => String((r as any)?.id || '') === LEGACY_BOOKING_FORM_SIGNED_DOC_KEY) ||
          null;
        setBookingFormDoc(bookingFormRow);
      },
      (err) => {
        console.error('Load booking documents failed', err);
        setWorkflowHydration((prev) => ({ ...prev, uploaded: true }));
      }
    );
    return () => unsub();
  }, [bookingId, db, markLoadedFor, open, projectId, unit?.id]);

  useEffect(() => {
    setBookingDraftDocuments([]);
    if (!open) return undefined;
    if (!unit?.id) return undefined;
    if (!projectId) return undefined;
    if (!bookingId) return undefined;
    const refCol = collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingId, 'draft');
    const unsub = onSnapshot(
      refCol,
      (snap) => {
        setWorkflowHydration((prev) => ({ ...prev, draft: true }));
        markLoadedFor('draft', String(unit?.id || ''));
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as BookingDocumentDoc[];
        setBookingDraftDocuments(rows);
      },
      (err) => {
        console.error('Load booking draft documents failed', err);
        setWorkflowHydration((prev) => ({ ...prev, draft: true }));
      }
    );
    return () => unsub();
  }, [bookingId, db, markLoadedFor, open, projectId, unit?.id]);

  useEffect(() => {
    if (!open) return undefined;
    if (!unit?.id) return undefined;
    if (!projectId) return undefined;
    if (!bookingId) return undefined;
    const ids = customers.map((c) => c.id).filter(Boolean);
    if (ids.length === 0) {
      setKycSummaryByCustomer({});
      return undefined;
    }
    const unsubs: Array<() => void> = [];
    ids.forEach((cid) => {
      const colRef = collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingId, 'customers', cid, 'kycDocs');
      const unsub = onSnapshot(
        colRef,
        (snap) => {
          const hasPAN = snap.docs.some((d) => {
            const data = d.data() as any;
            const keyMatch = d.id === 'PAN' || String(data?.type || '').toUpperCase() === 'PAN';
            return keyMatch && Array.isArray(data?.files) && data.files.length > 0;
          });
          const hasAADHAAR = snap.docs.some((d) => {
            const data = d.data() as any;
            const keyMatch = d.id === 'AADHAAR' || String(data?.type || '').toUpperCase() === 'AADHAAR';
            return keyMatch && Array.isArray(data?.files) && data.files.length > 0;
          });
          const hasPHOTO = snap.docs.some((d) => {
            const data = d.data() as any;
            const keyMatch = d.id === 'PHOTO' || String(data?.type || '').toUpperCase() === 'PHOTO';
            return keyMatch && Array.isArray(data?.files) && data.files.length > 0;
          });
          setKycSummaryByCustomer((prev) => ({
            ...prev,
            [cid]: { hasPAN, hasAADHAAR, hasPHOTO },
          }));
        },
        (err) => {
          console.error('Load customer KYC summary failed', err);
        }
      );
      unsubs.push(unsub);
    });
    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [bookingId, customers, db, open, projectId, unit?.id]);

  useEffect(() => {
    if (!open) return;
    if (!projectId || !unit?.id || !booking?.id) return;
    if (String(booking.shortBookingCode || '').trim()) return;
    ensureBookingShortCode({
      db,
      projectId,
      unitId: unit.id,
      bookingId: booking.id,
      currentUserEmail: currentUser?.email || null,
      existingBooking: booking as any,
    })
      .then((code) => {
        if (!code) return;
        setBooking((prev) => (prev && prev.id === booking.id ? { ...prev, shortBookingCode: code } : prev));
      })
      .catch((err) => console.warn('Ensure short booking code failed', err));
  }, [booking, currentUser?.email, db, open, projectId, unit?.id]);

  useEffect(() => {
    if (!open) return undefined;
    if (!unit?.id) return undefined;
    if (!projectId) return undefined;
    if (!bookingId) return undefined;
    if (!selectedCustomerId) {
      setKycDocs([]);
      return undefined;
    }
    const kycCol = collection(
      db,
      'projects',
      projectId,
      'units',
      unit.id,
      'bookings',
      bookingId,
      'customers',
      selectedCustomerId,
      'kycDocs'
    );
    const unsub = onSnapshot(
      kycCol,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as KycDoc[];
        rows.sort((a, b) => String(a.type || '').localeCompare(String(b.type || '')));
        setKycDocs(rows);
      },
      (err) => {
        console.error('Load KYC docs failed', err);
        setError(err?.message || 'Failed to load KYC docs.');
      }
    );
    return () => unsub();
  }, [bookingId, db, open, projectId, selectedCustomerId, unit?.id]);

  const primaryCustomerHeaderName = useMemo(() => {
    if (!customers.length) return '';
    const primary = customers.find((c) => String(c.role || '').toLowerCase() === 'primary') || customers[0];
    const first = String((primary as any)?.firstName || '').trim();
    const last =
      String((primary as any)?.lastName || '').trim() ||
      String((primary as any)?.surname || '').trim() ||
      String((primary as any)?.sirName || '').trim();
    if (first || last) return `${first} ${last}`.trim();
    const full = String(primary.displayName || primary.name || '').trim();
    if (!full) return '';
    const parts = full.split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return full;
    return `${parts[0]} ${parts[parts.length - 1]}`.trim();
  }, [customers]);

  const unitTitle = `${unit?.unitNumber || 'Unit'}`;
  const currentUnitStatus = ((String(unit?.bookingStatus || 'available').toLowerCase() as UnitBookingStatus) || 'available');

  useEffect(() => {
    setUnitStatusDraft(currentUnitStatus);
  }, [currentUnitStatus, unit?.id]);

  const saveUnitStatusFromDrawer = useCallback(async () => {
    if (!unit) return;
    if (!onRequestUnitStatusChange) {
      setError('Status change is available from Units page only.');
      return;
    }
    if (unitStatusDraft === currentUnitStatus) {
      setUnitStatusDialogOpen(false);
      return;
    }
    try {
      setUnitStatusSaving(true);
      await onRequestUnitStatusChange(unit, unitStatusDraft);
      setUnitStatusDialogOpen(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to update unit status.');
    } finally {
      setUnitStatusSaving(false);
    }
  }, [currentUnitStatus, onRequestUnitStatusChange, unit, unitStatusDraft]);

  const statusChip = (() => {
    const s = (unit?.bookingStatus || 'available') as UnitBookingStatus;
    const label =
      s === 'available' ? 'Available' :
      s === 'blocked' ? 'Blocked' :
      s === 'booked' ? 'Booked' :
      'Sold';
    const bg =
      s === 'available' ? 'rgba(148, 163, 184, 0.22)' :
      s === 'blocked' ? 'rgba(245, 158, 11, 0.18)' :
      s === 'booked' ? 'rgba(34, 197, 94, 0.16)' :
      'rgba(168, 85, 247, 0.14)';
    const border =
      s === 'available' ? 'rgba(148, 163, 184, 0.40)' :
      s === 'blocked' ? 'rgba(245, 158, 11, 0.35)' :
      s === 'booked' ? 'rgba(34, 197, 94, 0.30)' :
      'rgba(168, 85, 247, 0.30)';
    const textColor =
      s === 'available' ? '#475569' :
      s === 'blocked' ? '#b45309' :
      s === 'booked' ? '#166534' :
      '#6b21a8';
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1,
          py: 0.25,
          borderRadius: 10,
          bgcolor: bg,
          border: `1px solid ${border}`,
          fontSize: 11,
          fontWeight: 800,
          color: textColor,
          lineHeight: 1.4,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Box>
    );
  })();

  const toDraftDisplayLabel = useCallback(
    (rawName: string, fallback: string) => {
      const unitNumberRaw = String(unit?.unitNumber || '').trim();
      const normalizeUnitTokenPlaceholders = (input: string) =>
        String(input || '')
          .replace(/\{\{\s*unit[_\s-]*no\s*\}\}/gi, unitNumberRaw)
          .replace(/\{\{\s*unit[_\s-]*number\s*\}\}/gi, unitNumberRaw);
      const source = normalizeUnitTokenPlaceholders(String(rawName || fallback || '').trim());
      if (!source) return 'Draft Document';
      const withoutExt = source.replace(/\.pdf$/i, '').trim();
      if (!unitNumberRaw) return withoutExt.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
      const escapedUnit = unitNumberRaw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const stripPrefixPattern = new RegExp(`^${escapedUnit}[\\s_\\-:.]*`, 'i');
      return withoutExt
        .replace(stripPrefixPattern, '')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim() || withoutExt.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    },
    [unit?.unitNumber]
  );

  const extractFileExt = useCallback((nameOrUrl: string): string => {
    const raw = String(nameOrUrl || '').trim();
    if (!raw) return '';
    const cleaned = raw.split('#')[0].split('?')[0];
    const fileName = cleaned.split('/').pop() || cleaned;
    const dot = fileName.lastIndexOf('.');
    if (dot <= 0 || dot === fileName.length - 1) return '';
    return fileName.slice(dot);
  }, []);

  const sanitizeDownloadName = useCallback((value: string): string => {
    return String(value || '')
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const openDocumentInNewTab = useCallback((url: string) => {
    const safeUrl = String(url || '').trim();
    if (!safeUrl) return;
    const opened = window.open(safeUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      const a = document.createElement('a');
      a.href = safeUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, []);

  const triggerNamedDownload = useCallback(
    (url: string, displayName: string, fallbackName?: string | null) => {
      const safeUrl = String(url || '').trim();
      if (!safeUrl) return;
      const safeDisplay = (sanitizeDownloadName(displayName) || 'document')
        .replace(/^\d{10,}[_-]+/, '')
        .trim();
      const ext =
        extractFileExt(String(fallbackName || '')) ||
        extractFileExt(safeUrl) ||
        '.pdf';
      const nameWithExt =
        safeDisplay.toLowerCase().endsWith(ext.toLowerCase()) ? safeDisplay : `${safeDisplay}${ext}`;
      const sep = safeUrl.includes('?') ? '&' : '?';
      const forced = `${safeUrl}${sep}response-content-disposition=${encodeURIComponent(`attachment; filename="${nameWithExt}"`)}`;
      const a = document.createElement('a');
      a.href = forced;
      a.download = nameWithExt;
      a.rel = 'noopener noreferrer';
      a.target = isMobile ? '_self' : '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [extractFileExt, isMobile, sanitizeDownloadName]
  );

  const triggerPdfBytesDownload = useCallback((pdfBytes: Uint8Array, fileName: string) => {
    if (!(pdfBytes instanceof Uint8Array) || !pdfBytes.length) return;
    const safeNameBase = sanitizeDownloadName(fileName || 'document') || 'document';
    const safeName = /\.pdf$/i.test(safeNameBase) ? safeNameBase : `${safeNameBase}.pdf`;
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = safeName;
    a.target = isMobile ? '_self' : '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1200);
  }, [isMobile, sanitizeDownloadName]);

  const isDraftDocumentKey = useCallback((rawKey: string) => {
    const key = String(rawKey || '').trim().toLowerCase();
    if (!key) return false;
    if (key === 'allotmentletterdraft' || key === 'bookingformdraft') return true;
    if (key.startsWith('requestdraft_')) return true;
    if (key.endsWith('draft')) return true;
    if (key.includes('_draft')) return true;
    return false;
  }, []);

  const normalizeSpaces = useCallback((s: string) => String(s || '').trim().replace(/\s+/g, ' '), []);
  const buildFullName = useCallback(
    (first: string, middle: string, last: string) => {
      return normalizeSpaces([first, middle, last].filter((x) => normalizeSpaces(x)).join(' '));
    },
    [normalizeSpaces]
  );

  const displayNameOptions = useMemo(() => {
    const first = normalizeSpaces(custFirstName);
    const middle = normalizeSpaces(custMiddleName);
    const last = normalizeSpaces(custLastName);
    const out: string[] = [];
    const a = normalizeSpaces([first, last].filter(Boolean).join(' '));
    const b = buildFullName(first, middle, last);
    if (a) out.push(a);
    if (b && b !== a) out.push(b);
    // PAN card name is often uppercase; offer uppercase variants for quick selection.
    if (a) out.push(a.toUpperCase());
    if (b && b !== a) out.push(b.toUpperCase());
    return Array.from(new Set(out)).filter(Boolean).slice(0, 8);
  }, [buildFullName, custFirstName, custLastName, custMiddleName, normalizeSpaces]);

  const loanRequired = (mainCase?.loanRequired || null) as any;
  const loanSanctionDone = !!loanCase?.completedStepKeys?.includes(OFFICE_LOAN_STEP_KEYS.sanctionReceived);
  const pendingHydrationReady = useMemo(() => {
    if (!open || !projectId || !unit?.id || !bookingId) return false;
    const baseReady =
      workflowHydration.booking &&
      workflowHydration.customers &&
      workflowHydration.uploaded &&
      workflowHydration.draft &&
      workflowHydration.mainCase;
    if (!baseReady) return false;
    const needsLoanCase = String(loanRequired || '').toLowerCase() === 'yes';
    return !needsLoanCase || workflowHydration.loanCase;
  }, [bookingId, loanRequired, open, projectId, unit?.id, workflowHydration]);
  const derivedMainDoneKeys = useMemo(() => {
    const v = String(loanRequired || '').toLowerCase();
    if (v !== 'yes') return [OFFICE_MAIN_STEP_KEYS.loanGateSanction];
    return loanSanctionDone ? [OFFICE_MAIN_STEP_KEYS.loanGateSanction] : [];
  }, [loanRequired, loanSanctionDone]);

  const normalizeUploadedDocKey = useCallback((rawKey: string): string => {
    const key = String(rawKey || '').trim();
    if (!key) return '';
    if (key === LEGACY_BOOKING_FORM_SIGNED_DOC_KEY) return BOOKING_FORM_SIGNED_DOC_KEY;
    if (key === LEGACY_SALE_AGREEMENT_SIGNED_DOC_KEY) return SALE_AGREEMENT_SIGNED_DOC_KEY;
    return key;
  }, []);

  const getStoredDocDisplaySection = useCallback(
    (row: any): 'main_documents' | 'draft_documents' | 'loan_documents' | 'hidden' => {
      const raw = String(row?.displaySection || '').trim().toLowerCase();
      if (raw === 'main_documents' || raw === 'draft_documents' || raw === 'loan_documents' || raw === 'hidden') {
        return raw;
      }
      if (Boolean(row?.generatedFinal)) return 'main_documents';
      if (isDraftDocumentKey(String(row?.id || row?.type || row?.templateId || ''))) return 'draft_documents';
      return 'main_documents';
    },
    [isDraftDocumentKey]
  );

  const configuredUploadKeys = useMemo(() => {
    return new Set(
      (documentUploadTypes || [])
        .map((row) => normalizeUploadedDocKey(String((row as any)?.key || (row as any)?.id || '').trim()))
        .filter(Boolean)
    );
  }, [documentUploadTypes, normalizeUploadedDocKey]);

  const bookingDocsByKey = useMemo(() => {
    const m: Record<string, BookingDocumentDoc> = {};
    for (const d of bookingDocuments || []) {
      const k = normalizeUploadedDocKey(String((d as any)?.id || ''));
      if (!k) continue;
      if (!m[k]) m[k] = d;
    }
    for (const d of bookingDraftDocuments || []) {
      const k = String((d as any)?.id || '');
      if (!k) continue;
      m[k] = d;
      if (k === 'allotment_letter_v1') m.allotmentLetterDraft = d;
      if (k === 'booking_form_v1') m.bookingFormDraft = d;
      if (k === 'payment_details_v1' || k === 'payment_details_v2') {
        if (!m.paymentDetailsDraft || k === 'payment_details_v2') m.paymentDetailsDraft = d;
      }
    }
    if (bookingFormDoc) {
      m[BOOKING_FORM_SIGNED_DOC_KEY] = bookingFormDoc;
      m[LEGACY_BOOKING_FORM_SIGNED_DOC_KEY] = bookingFormDoc;
      m.bookingFormSigned = bookingFormDoc;
    }
    return m;
  }, [bookingDocuments, bookingDraftDocuments, bookingFormDoc, normalizeUploadedDocKey]);

  const sendPaymentDetailsWhatsApp = useCallback(async (lang: AutoReplyLang) => {
    const customer = customers.find((c) => String(c.role || '').toLowerCase() === 'primary') || customers[0];
    const rawPhone = String(customer?.mobile || '').replace(/\D/g, '');
    const phone10 = rawPhone.length === 12 && rawPhone.startsWith('91') ? rawPhone.slice(2) : rawPhone.slice(-10);
    const file = bookingDocsByKey.paymentDetailsDraft?.files?.find((row: any) => !!row?.url);
    const sendingTemplate = templateStatuses.versionsFor('swarna_unit_payment_details', lang).sending;
    if (!customer || !phone10 || phone10.length !== 10) {
      setPaymentWhatsAppError('The primary unit holder does not have a valid mobile number.');
      return;
    }
    if (!file?.url) {
      setPaymentWhatsAppError('Generate the payment-details PDF before sending it.');
      return;
    }
    if (!sendingTemplate) {
      setPaymentWhatsAppError(`The ${lang.toUpperCase()} payment-details template is not approved by Meta yet.`);
      return;
    }
    setPaymentWhatsAppSending(true);
    setPaymentWhatsAppError(null);
    try {
      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
      const customerName = String(customer.displayName || customer.name || '').trim();
      const values = [customerName.split(/\s+/)[0] || customerName, unitNo, projectName || projectId || 'Swarna Group'];
      const filename = String(file.originalName || file.name || `Payment_Details_${unitNo || 'Unit'}.pdf`).trim();
      await httpsCallable(getFunctions(), 'whatsappSendMessage')({
        to: `91${phone10}`,
        projectId,
        bookingId,
        templateName: sendingTemplate,
        templateLanguage: TEMPLATE_LANG_CODES[lang],
        templateComponents: [
          { type: 'header', parameters: [{ type: 'document', document: { link: file.url, filename } }] },
          { type: 'body', parameters: values.map((value) => ({ type: 'text', text: value })) },
        ],
        metadata: { source: 'office_payment_details', template: 'swarna_unit_payment_details', unitId: unit?.id || '', customerId: customer.id, documentUrl: file.url },
      });
      setPaymentWhatsAppAnchor(null);
    } catch (err: any) {
      setPaymentWhatsAppError(err?.message || 'Could not send the payment details on WhatsApp.');
    } finally {
      setPaymentWhatsAppSending(false);
    }
  }, [bookingDocsByKey.paymentDetailsDraft?.files, bookingId, customers, projectId, projectName, templateStatuses, unit?.id, unit?.unitNumber]);

  const signedBookingFormPdf = (bookingFormDoc?.files || []).find((file: any) => {
    if (!file?.url) return false;
    const mime = String(file.contentType || file.mimeType || '').toLowerCase();
    const name = String(file.originalName || file.name || file.url || '').split('?')[0].toLowerCase();
    return mime === 'application/pdf' || name.endsWith('.pdf');
  }) || null;
  const signedBookingPrimaryMobile = customers.find(
    (customer) => String(customer.role || '').toLowerCase() === 'primary'
  )?.mobile;

  const sendSignedBookingFormWhatsApp = useCallback(async (lang: AutoReplyLang) => {
    const customer = customers.find((item) => String(item.role || '').toLowerCase() === 'primary');
    const rawPhone = String(customer?.mobile || '').replace(/\D/g, '');
    const phone10 = rawPhone.length === 12 && rawPhone.startsWith('91') ? rawPhone.slice(2) : rawPhone;
    const sendingTemplate = templateStatuses.versionsFor('swarna_signed_booking_form', lang).sending;
    if (!customer || !/^\d{10}$/.test(phone10)) {
      setSignedBookingWhatsAppError('The primary unit holder does not have a valid mobile number.');
      return;
    }
    if (!signedBookingFormPdf?.url) {
      setSignedBookingWhatsAppError('Upload the signed booking form as a PDF before sending it.');
      return;
    }
    if (!sendingTemplate) {
      setSignedBookingWhatsAppError(`The ${lang.toUpperCase()} signed booking form template is not approved by Meta yet.`);
      return;
    }
    setSignedBookingWhatsAppSending(true);
    setSignedBookingWhatsAppError(null);
    try {
      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
      const customerName = String(customer.displayName || customer.name || '').trim();
      const values = [customerName.split(/\s+/)[0] || customerName, unitNo, projectName || projectId || 'Swarna Group'];
      const filename = String(signedBookingFormPdf.originalName || signedBookingFormPdf.name || `Signed_Booking_Form_${unitNo}.pdf`).trim();
      await httpsCallable(getFunctions(), 'whatsappSendMessage')({
        to: `91${phone10}`,
        projectId,
        bookingId,
        templateName: sendingTemplate,
        templateLanguage: TEMPLATE_LANG_CODES[lang],
        templateComponents: [
          { type: 'header', parameters: [{ type: 'document', document: { link: signedBookingFormPdf.url, filename } }] },
          { type: 'body', parameters: values.map((value) => ({ type: 'text', text: value })) },
        ],
        metadata: { source: 'office_signed_booking_form', template: 'swarna_signed_booking_form', unitId: unit?.id || '', customerId: customer.id, documentUrl: signedBookingFormPdf.url },
      });
      setSignedBookingWhatsAppAnchor(null);
    } catch (err: any) {
      setSignedBookingWhatsAppError(err?.message || 'Could not send the signed booking form on WhatsApp.');
    } finally {
      setSignedBookingWhatsAppSending(false);
    }
  }, [bookingId, customers, projectId, projectName, signedBookingFormPdf, templateStatuses, unit?.id, unit?.unitNumber]);

  const toMillisSafe = useCallback((raw: any): number => {
    if (!raw) return 0;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (raw instanceof Date) return raw.getTime();
    if (typeof raw?.toMillis === 'function') return raw.toMillis();
    if (typeof raw?.seconds === 'number') return raw.seconds * 1000;
    const parsed = Date.parse(String(raw || ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const getDocEvidenceDoneAtMs = useCallback(
    (docKey: string): number => {
      const row = bookingDocsByKey?.[String(docKey || '').trim()];
      if (!row) return 0;
      const files = Array.isArray(row.files) ? row.files : [];
      const fileMs = files.reduce((maxMs, file) => {
        const ms = Number((file as any)?.uploadedAtMs || 0);
        return Number.isFinite(ms) && ms > maxMs ? ms : maxMs;
      }, 0);
      const updatedMs = toMillisSafe((row as any)?.updatedAt);
      const createdMs = toMillisSafe((row as any)?.createdAt);
      return Math.max(fileMs, updatedMs, createdMs, 0);
    },
    [bookingDocsByKey, toMillisSafe]
  );

  const inferStepEvidenceDoneAtMs = useCallback(
    (step: WorkflowDefinitionStep): number => {
      const actionType = String(step?.actionType || '').toLowerCase();
      if (actionType === 'upload_document' || actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return 0;
        return getDocEvidenceDoneAtMs(docKey);
      }
      if (actionType === 'loan_sanction_form') {
        const docKey = String((step as any)?.docKey || '').trim() || 'sanction_letter';
        return getDocEvidenceDoneAtMs(docKey);
      }
      return 0;
    },
    [getDocEvidenceDoneAtMs]
  );

  const draftSubcollectionKeys = useMemo(() => {
    return new Set(
      (bookingDraftDocuments || [])
        .map((d) => String((d as any)?.id || '').trim())
        .filter(Boolean)
    );
  }, [bookingDraftDocuments]);

  const uploadedDocsByKey = useMemo(() => {
    const m: Record<string, BookingDocumentDoc> = {};
    for (const d of bookingDocuments || []) {
      const k = normalizeUploadedDocKey(String((d as any)?.id || ''));
      const type = String((d as any)?.type || '');
      const generatedFinal = Boolean((d as any)?.generatedFinal);
      if (!k) continue;
      if (draftSubcollectionKeys.has(k)) continue;
      if (isDraftDocumentKey(k) || /draft/i.test(type)) continue;
      if (configuredUploadKeys.size > 0 && !configuredUploadKeys.has(k) && !generatedFinal) continue;
      if (!m[k]) m[k] = d;
    }
    if (bookingFormDoc) {
      m[BOOKING_FORM_SIGNED_DOC_KEY] = bookingFormDoc;
      m[LEGACY_BOOKING_FORM_SIGNED_DOC_KEY] = bookingFormDoc;
      m.bookingFormSigned = bookingFormDoc;
    }
    return m;
  }, [bookingDocuments, bookingFormDoc, configuredUploadKeys, draftSubcollectionKeys, isDraftDocumentKey, normalizeUploadedDocKey]);

  const bookingDraftDocumentRows = useMemo(() => {
    const m = new Map<string, BookingDocumentDoc>();
    for (const d of bookingDraftDocuments || []) {
      const key = String((d as any)?.id || '').trim();
      if (!key) continue;
      m.set(key, d);
    }
    for (const d of bookingDocuments || []) {
      const key = String((d as any)?.id || '').trim();
      const type = String((d as any)?.type || '');
      if (!key) continue;
      if (!(isDraftDocumentKey(key) || /draft/i.test(type))) continue;
      if (!m.has(key)) m.set(key, d);
    }
    return Array.from(m.values());
  }, [bookingDocuments, bookingDraftDocuments, isDraftDocumentKey]);

  const visibleDraftDocumentRows = useMemo(() => {
    const hiddenDraftKeys = new Set<string>();
    const normalizeLabel = (value: string) =>
      String(value || '')
        .toLowerCase()
        .replace(/draft/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    const uploadTypeLabelByKey = new Map<string, string>();
    for (const row of documentUploadTypes || []) {
      const k = normalizeUploadedDocKey(String((row as any)?.key || (row as any)?.id || '').trim());
      if (!k) continue;
      uploadTypeLabelByKey.set(k, String((row as any)?.label || k));
    }
    for (const row of documentUploadTypes || []) {
      const uploadKey = normalizeUploadedDocKey(String((row as any)?.key || (row as any)?.id || '').trim());
      const linkedDraftKey = String((row as any)?.linkedDraftTemplateId || '').trim();
      if (!uploadKey || !linkedDraftKey) continue;
      const uploaded = uploadedDocsByKey[uploadKey];
      const hasUploadedFile = Array.isArray(uploaded?.files) && uploaded!.files!.some((f) => !!f?.url);
      if (hasUploadedFile) hiddenDraftKeys.add(linkedDraftKey);
    }
    const uploadedLabelBases = new Set<string>();
    for (const [uploadKey, row] of Object.entries(uploadedDocsByKey || {})) {
      const hasFile = Array.isArray((row as any)?.files) && (row as any).files.some((f: any) => !!f?.url);
      if (!hasFile) continue;
      const label = String((row as any)?.displayName || uploadTypeLabelByKey.get(String(uploadKey || '')) || uploadKey || '');
      const base = normalizeLabel(label);
      if (base) uploadedLabelBases.add(base);
    }
    const hasPaymentDetailsV2 = bookingDraftDocumentRows.some((row) => {
      const key = String((row as any)?.id || (row as any)?.templateId || '').trim();
      return key === 'payment_details_v2' && (row?.files || []).some((file) => !!file?.url);
    });
    return bookingDraftDocumentRows.filter((row) => {
      const key = String((row as any)?.id || '').trim();
      if (key === 'payment_details_v1' && hasPaymentDetailsV2) return false;
      if (key && hiddenDraftKeys.has(key)) return false;
      const first = ((row as any)?.files || []).find((f: any) => !!f?.url) || null;
      const draftLabel = toDraftDisplayLabel(
        String(first?.name || first?.originalName || (row as any)?.type || (row as any)?.templateId || key || ''),
        String((row as any)?.type || (row as any)?.templateId || key || '')
      );
      const draftBase = normalizeLabel(draftLabel);
      if (draftBase && uploadedLabelBases.has(draftBase)) return false;
      return true;
    });
  }, [bookingDraftDocumentRows, documentUploadTypes, normalizeUploadedDocKey, toDraftDisplayLabel, uploadedDocsByKey]);

  const uploadTypeByKey = useMemo(() => {
    const map = new Map<string, any>();
    for (const row of documentUploadTypes || []) {
      const key = String((row as any)?.key || (row as any)?.id || '').trim();
      if (!key) continue;
      map.set(key, row);
    }
    return map;
  }, [documentUploadTypes]);

  const renderUploadDisplayNameTemplate = useCallback(
    (rawTemplate: string, docLabel: string) => {
      const unitNo = String(unit?.unitNumber || unit?.id || 'Unit').trim();
      const normalizedLabel = String(docLabel || '').trim();
      return String(rawTemplate || '')
        .replace(/\{\{\s*unit[_\s-]*no\s*\}\}/gi, unitNo)
        .replace(/\{\{\s*unit[_\s-]*number\s*\}\}/gi, unitNo)
        .replace(/\{\{\s*unit_no\s*\}\}/gi, unitNo)
        .replace(/\{\{\s*unit_number\s*\}\}/gi, unitNo)
        .replace(/\{\{\s*doc_label\s*\}\}/gi, normalizedLabel)
        .trim();
    },
    [unit?.id, unit?.unitNumber]
  );

  const parseAllowedExtensions = useCallback((raw: string | null | undefined): string[] => {
    return String(raw || '')
      .split(/[,\s]+/g)
      .map((v) => v.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean);
  }, []);

  const validateUploadByConfig = useCallback(
    (args: { file: File; docKey: string; fallbackLabel?: string }) => {
      const config = uploadTypeByKey.get(String(args.docKey).trim()) || null;
      if (!config) return null;
      const label = String(config?.label || args.fallbackLabel || args.docKey || 'document');
      const allowed = parseAllowedExtensions(config?.allowedExtensions);
      const ext = String(args.file.name || '').includes('.')
        ? String(args.file.name || '').split('.').pop()!.toLowerCase()
        : '';
      if (allowed.length > 0 && ext && !allowed.includes(ext)) {
        return `${label}: file type .${ext} is not allowed. Allowed: ${allowed.join(', ')}`;
      }
      if (allowed.length > 0 && !ext) {
        return `${label}: file has no extension. Allowed: ${allowed.join(', ')}`;
      }
      const maxSizeMb = Math.max(1, Number(config?.maxSizeMb || 0) || 0);
      if (maxSizeMb > 0) {
        const maxBytes = maxSizeMb * 1024 * 1024;
        if (Number(args.file.size || 0) > maxBytes) {
          return `${label}: file size exceeds ${maxSizeMb} MB limit.`;
        }
      }
      return null;
    },
    [parseAllowedExtensions, uploadTypeByKey]
  );

  const getUploadMaxBytesByConfig = useCallback(
    (docKey: string): number => {
      const config = uploadTypeByKey.get(String(docKey).trim()) || null;
      const maxSizeMb = Math.max(1, Number(config?.maxSizeMb || 0) || 0);
      return maxSizeMb > 0 ? maxSizeMb * 1024 * 1024 : 0;
    },
    [uploadTypeByKey]
  );

  const formatBytes = useCallback((bytes: number) => {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    if (value < 1024) return `${Math.round(value)} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  }, []);

  const tryAutoCompressPdfForUpload = useCallback(
    async (file: File, docKey: string, fallbackLabel?: string): Promise<{ file: File; notice: string | null }> => {
      const config = uploadTypeByKey.get(String(docKey).trim()) || null;
      if (!config) return { file, notice: null };
      const maxSizeMb = Math.max(1, Number(config?.maxSizeMb || 0) || 0);
      if (maxSizeMb <= 0) return { file, notice: null };
      const maxBytes = maxSizeMb * 1024 * 1024;
      const ext = String(file.name || '').includes('.')
        ? String(file.name || '').split('.').pop()!.toLowerCase()
        : '';
      if (ext !== 'pdf') return { file, notice: null };
      if (Number(file.size || 0) <= maxBytes) return { file, notice: null };

      const label = String(config?.label || fallbackLabel || docKey || 'document');
      return optimizePdfToMaxBytes({
        file,
        maxBytes,
        label,
        formatBytes,
      });
    },
    [formatBytes, uploadTypeByKey]
  );

  const bookingDocumentDefs = useMemo(() => {
    const map = new Map<string, { key: string; label: string }>();
    const push = (key: string, label: string) => {
      const k = String(key || '').trim();
      if (!k) return;
      if (map.has(k)) return;
      map.set(k, { key: k, label: String(label || k) });
    };

    for (const row of documentUploadTypes || []) {
      const key = normalizeUploadedDocKey(String((row as any)?.key || (row as any)?.id || '').trim());
      if (!key) continue;
      push(key, String((row as any)?.label || key));
    }

    for (const row of bookingDocuments || []) {
      const key = normalizeUploadedDocKey(String((row as any)?.id || '').trim());
      if (!key) continue;
      if (!(row as any)?.generatedFinal) continue;
      push(
        key,
        String((row as any)?.displayName || (row as any)?.templateName || (row as any)?.templateId || key)
      );
    }

    return Array.from(map.values());
  }, [bookingDocuments, documentUploadTypes, normalizeUploadedDocKey]);

  const visibleMainDocumentDefs = useMemo(
    () =>
      bookingDocumentDefs
        .filter((d) => !isDraftDocumentKey(d.key))
        .filter((d) => {
          const row = uploadedDocsByKey[d.key];
          return (row?.files || []).some((f) => !!f?.url) && getStoredDocDisplaySection(row) === 'main_documents';
        }),
    [bookingDocumentDefs, getStoredDocDisplaySection, isDraftDocumentKey, uploadedDocsByKey]
  );

  const visibleLoanDocumentDefs = useMemo(
    () =>
      bookingDocumentDefs
        .filter((d) => !isDraftDocumentKey(d.key))
        .filter((d) => {
          const row = uploadedDocsByKey[d.key];
          return (row?.files || []).some((f) => !!f?.url) && getStoredDocDisplaySection(row) === 'loan_documents';
        }),
    [bookingDocumentDefs, getStoredDocDisplaySection, isDraftDocumentKey, uploadedDocsByKey]
  );

  const visibleDraftSectionRows = useMemo(
    () => visibleDraftDocumentRows.filter((row) => getStoredDocDisplaySection(row) === 'draft_documents'),
    [getStoredDocDisplaySection, visibleDraftDocumentRows]
  );

  const visibleLoanSectionDraftRows = useMemo(
    () => visibleDraftDocumentRows.filter((row) => getStoredDocDisplaySection(row) === 'loan_documents'),
    [getStoredDocDisplaySection, visibleDraftDocumentRows]
  );

  const mainSteps = useMemo(() => {
    if (!mainTemplate || !mainCase) return [];
    return computeWorkflowSteps({
      template: mainTemplate,
      completedStepKeys: mainCase.completedStepKeys || [],
      ctx: { loanRequired: (loanRequired || null) as any },
      derivedDoneKeys: derivedMainDoneKeys,
    });
  }, [derivedMainDoneKeys, loanRequired, mainCase, mainTemplate]);

  const derivedLoanDoneKeys = useMemo(() => {
    const out = new Set<string>();
    const stepData = ((loanCase as any)?.stepData || {}) as Record<string, any>;
    for (const [stepKey, stepEntry] of Object.entries(stepData)) {
      if (!stepKey) continue;
      if (Boolean((stepEntry as any)?.taskDone)) out.add(stepKey);
    }
    return Array.from(out);
  }, [loanCase]);

  const loanSteps = useMemo(() => {
    if (!loanTemplate || !loanCase) return [];
    const sourceSteps = loanTemplate.steps || [];
    const hasLegacyCollectSend = sourceSteps.some(
      (step) => step.key === OFFICE_LOAN_STEP_KEYS.collectSendDocsLegacy
    );
    const hasCollectDocsStep = sourceSteps.some((step) => step.key === OFFICE_LOAN_STEP_KEYS.collectDocs);
    const hasSendToBanksStep = sourceSteps.some((step) => step.key === OFFICE_LOAN_STEP_KEYS.sendToBanks);
    let normalizedSteps = sourceSteps;
    if (hasLegacyCollectSend && !hasCollectDocsStep) {
      const legacyStep =
        sourceSteps.find((step) => step.key === OFFICE_LOAN_STEP_KEYS.collectSendDocsLegacy) || null;
      const legacyOrder = Number(legacyStep?.order || 1);
      const collectStep: WorkflowStepDef = {
        ...(legacyStep || {
          key: OFFICE_LOAN_STEP_KEYS.collectDocs,
          title: 'Collect Documents for Loan',
          order: legacyOrder,
          dependsOn: [],
          kind: 'task',
          fields: [],
          requiredDocs: [],
        }),
        key: OFFICE_LOAN_STEP_KEYS.collectDocs,
        title: 'Collect Documents for Loan',
        order: legacyOrder,
        dependsOn: [],
        kind: 'task',
        requiredDocs: [],
        fields: [],
      };
      const sendToSbiStep: WorkflowStepDef = {
        key: OFFICE_LOAN_STEP_KEYS.sendToBanks,
        title: 'Send Lead to SBI',
        order: legacyOrder + 0.1,
        dependsOn: [],
        kind: 'task',
        requiredDocs: [],
        fields: [],
      };
      normalizedSteps = [
        ...sourceSteps
          .filter((step) => step.key !== OFFICE_LOAN_STEP_KEYS.collectSendDocsLegacy)
          .map((step) => ({
            ...step,
            dependsOn: (step.dependsOn || []).map((dep) =>
              dep === OFFICE_LOAN_STEP_KEYS.collectSendDocsLegacy
                ? OFFICE_LOAN_STEP_KEYS.collectDocs
                : dep
            ),
          })),
        collectStep,
        sendToSbiStep,
      ];
    } else if (hasCollectDocsStep && !hasSendToBanksStep) {
      const collectStep = sourceSteps.find((step) => step.key === OFFICE_LOAN_STEP_KEYS.collectDocs) || null;
      const collectOrder = Number(collectStep?.order || 1);
      const sendToSbiStep: WorkflowStepDef = {
        key: OFFICE_LOAN_STEP_KEYS.sendToBanks,
        title: 'Send Lead to SBI',
        order: collectOrder + 0.1,
        dependsOn: [],
        kind: 'task',
        requiredDocs: [],
        fields: [],
      };
      normalizedSteps = [
        ...sourceSteps.map((step) => {
          if (step.key === OFFICE_LOAN_STEP_KEYS.collectDocs || step.key === OFFICE_LOAN_STEP_KEYS.sendToBanks) {
            return step;
          }
          if (step.key === OFFICE_LOAN_STEP_KEYS.finaliseBank) {
            const deps = Array.from(
              new Set([...(step.dependsOn || []), OFFICE_LOAN_STEP_KEYS.collectDocs, OFFICE_LOAN_STEP_KEYS.sendToBanks])
            ).filter(Boolean);
            return { ...step, dependsOn: deps };
          }
          return step;
        }),
        sendToSbiStep,
      ];
    }
    normalizedSteps = normalizedSteps.map((step) => {
      if (step.key !== OFFICE_LOAN_STEP_KEYS.finaliseBank) return step;
      const deps = Array.from(
        new Set([...(step.dependsOn || []), OFFICE_LOAN_STEP_KEYS.collectDocs, OFFICE_LOAN_STEP_KEYS.sendToBanks])
      ).filter(Boolean);
      return { ...step, dependsOn: deps };
    });
    normalizedSteps = normalizedSteps
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((step, idx) => ({ ...step, order: idx + 1 }));
    const normalizedLoanTemplate: WorkflowTemplateDoc = {
      ...loanTemplate,
      steps: normalizedSteps,
    };
    const templateStepKeys = (normalizedLoanTemplate.steps || []).map((step) => String(step.key || '')).filter(Boolean);
    let completedStepKeys = (loanCase.completedStepKeys || []).filter(Boolean);
    if (
      completedStepKeys.includes(OFFICE_LOAN_STEP_KEYS.collectSendDocsLegacy) &&
      !completedStepKeys.includes(OFFICE_LOAN_STEP_KEYS.collectDocs)
    ) {
      completedStepKeys = [
        ...completedStepKeys,
        OFFICE_LOAN_STEP_KEYS.collectDocs,
        OFFICE_LOAN_STEP_KEYS.sendToBanks,
      ];
    }
    const sanctionLetterUploaded = !!bookingDocsByKey?.sanctionLetter?.files?.length;
    const normalizeCompletedForBuggedOldCases =
      templateStepKeys.length > 0 &&
      completedStepKeys.length >= templateStepKeys.length &&
      templateStepKeys.every((key) => completedStepKeys.includes(key)) &&
      !sanctionLetterUploaded;
    return computeWorkflowSteps({
      template: normalizedLoanTemplate,
      completedStepKeys: normalizeCompletedForBuggedOldCases ? [] : completedStepKeys,
      ctx: { loanRequired: 'yes' },
      derivedDoneKeys: derivedLoanDoneKeys,
    });
  }, [bookingDocsByKey?.sanctionLetter?.files?.length, derivedLoanDoneKeys, loanCase, loanTemplate]);

  const [recentDoneTick, setRecentDoneTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setRecentDoneTick((v) => v + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const mainWorkflowV2Pending = useMemo<
    Array<
      WorkflowDefinitionStep & {
        status: 'ready' | 'blocked' | 'recent_done' | 'done';
        blockedBy: string[];
        hideAtMs: number;
      }
    >
  >(() => {
    // Tick forces re-evaluation so "recently done" rows disappear after 15 minutes.
    const tick = recentDoneTick;
    const steps = normalizeDefinitionSteps(mainWorkflowDefinition?.steps || []);
    const loanSteps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []);
    if (!steps.length) return [];
    const completedKeys = Array.isArray((mainCase as any)?.completedStepKeys)
      ? ((mainCase as any).completedStepKeys as any[])
      : [];
    const completed = new Set(
      completedKeys
        .map((k: any) => String(k || '').trim())
        .filter(Boolean)
    );
    const isBookedOrSold = ['booked', 'sold'].includes(String(unit?.bookingStatus || '').toLowerCase());
    const stepState = (((mainCase as any)?.stepState || {}) as Record<string, any>) || {};
    const loanCompletedKeys = Array.isArray((loanCase as any)?.completedStepKeys)
      ? ((loanCase as any).completedStepKeys as any[])
      : [];
    const loanCompleted = new Set(
      loanCompletedKeys
        .map((k: any) => String(k || '').trim())
        .filter(Boolean)
    );
    const loanStepState = (((loanCase as any)?.stepState || {}) as Record<string, any>) || {};
    const isStepCompleteByCondition = (step: WorkflowDefinitionStep): boolean => {
      if (completed.has(step.key)) return true;
      if (PRE_DEED_STEP_KEYS.has(step.key) && hasRegisteredDeedEvidence(booking, bookingDocsByKey)) return true;
      const actionType = String(step?.actionType || '').toLowerCase();
      const doneSource = String(stepState?.[step.key]?.doneSource || '').trim().toLowerCase();
      const hasDoneAt = !!stepState?.[step.key]?.doneAt || !!stepState?.[step.key]?.doneAtMs;
      const hasDoneFlag = stepState?.[step.key]?.done === true;
      // `add_customer` is manual-only: never auto-complete it just because a doneAt exists.
      if (actionType === 'add_customer') {
        return (hasDoneFlag || hasDoneAt) && doneSource !== 'auto';
      }
      if (hasDoneFlag || hasDoneAt) return true;
      if (actionType === 'upload_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'input_value') {
        const stepData = ((((mainCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        return isInputValueStepComplete(step, stepData, booking as any);
      }
      if (actionType === 'add_payment') {
        const paymentType = String((step as any)?.paymentType || '').toLowerCase();
        if (paymentType === 'gst') return gstReceiptTotal > 0;
        return customerReceiptTotal > 0;
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return advocateAgreementReceiptTotal > 0;
      }
      if (actionType === 'add_kyc') {
        if (!customers.length) return false;
        return customers.every((customer) => {
          const summary = (kycSummaryByCustomer as any)?.[customer.id];
          return !!summary?.hasPAN && !!summary?.hasAADHAAR;
        });
      }
      return false;
    };
    const isLoanStepCompleteByCondition = (step: WorkflowDefinitionStep): boolean => {
      if (loanCompleted.has(step.key)) return true;
      const actionType = String(step?.actionType || '').toLowerCase();
      const doneSource = String(loanStepState?.[step.key]?.doneSource || '').trim().toLowerCase();
      const hasDoneAt = !!loanStepState?.[step.key]?.doneAt || !!loanStepState?.[step.key]?.doneAtMs;
      const hasDoneFlag = loanStepState?.[step.key]?.done === true;
      if (actionType === 'add_customer') {
        return (hasDoneFlag || hasDoneAt) && doneSource !== 'auto';
      }
      if (hasDoneFlag || hasDoneAt) return true;
      if (actionType === 'upload_document' || actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'loan_sanction_form') {
        const docKey = String((step as any)?.docKey || '').trim() || 'sanction_letter';
        const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        const bankName = String(stepData?.sanctionBankName || '').trim();
        const sanctionDate = String(stepData?.sanctionDate || '').trim();
        const sanctionAmount = Number(stepData?.sanctionAmount);
        const loanToUnitAmount = Number(stepData?.loanToUnitAmount);
        const isSbi = bankName.toLowerCase() === 'state bank of india';
        const nonSbiReason = String(stepData?.nonSbiReason || '').trim();
        const hasDoc = !!bookingDocsByKey?.[docKey]?.files?.length;
        if (!hasDoc) return false;
        if (!bankName || !sanctionDate) return false;
        if (!Number.isFinite(sanctionAmount) || sanctionAmount <= 0) return false;
        if (!Number.isFinite(loanToUnitAmount) || loanToUnitAmount <= 0) return false;
        if (!isSbi && !nonSbiReason) return false;
        return true;
      }
      if (actionType === 'input_value') {
        const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        return isInputValueStepComplete(step, stepData, booking as any);
      }
      if (actionType === 'add_payment') {
        const paymentType = String((step as any)?.paymentType || '').toLowerCase();
        if (paymentType === 'gst') return gstReceiptTotal > 0;
        return customerReceiptTotal > 0;
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return advocateAgreementReceiptTotal > 0;
      }
      if (actionType === 'add_kyc') {
        if (!customers.length) return false;
        return customers.every((customer) => {
          const summary = (kycSummaryByCustomer as any)?.[customer.id];
          return !!summary?.hasPAN && !!summary?.hasAADHAAR;
        });
      }
      return false;
    };
    const stepByKey = new Map(
      steps
        .map((s) => ({ key: String(s?.key || '').trim(), step: s }))
        .filter((x) => x.key)
        .map((x) => [x.key, x.step] as const)
    );
    const loanStepByKey = new Map(
      loanSteps
        .map((s) => ({ key: String(s?.key || '').trim(), step: s }))
        .filter((x) => x.key)
        .map((x) => [x.key, x.step] as const)
    );

    const now = Date.now() + tick * 0;
    const graceMs = 15 * 60 * 1000;
    const doneAtMsForKey = (step: WorkflowDefinitionStep): number => {
      const key = String(step?.key || '').trim();
      const rawMs = stepState?.[key]?.doneAtMs;
      const doneSource = String(stepState?.[key]?.doneSource || '').trim().toLowerCase();
      if (typeof rawMs === 'number' && Number.isFinite(rawMs) && rawMs > 0) return rawMs;
      const raw = stepState?.[key]?.doneAt;
      const rawDoneMs =
        typeof raw === 'number'
          ? raw
          : typeof raw?.toMillis === 'function'
            ? raw.toMillis()
            : typeof raw?.seconds === 'number'
              ? raw.seconds * 1000
              : 0;
      const evidenceMs = inferStepEvidenceDoneAtMs(step);
      if (doneSource === 'auto' && evidenceMs > 0) return evidenceMs;
      return rawDoneMs;
    };
    const result = steps
      .filter((step) => step.enabled !== false)
      .map((step) => {
        const doneAtMs = doneAtMsForKey(step);
        const isComplete = isStepCompleteByCondition(step);
        if (isComplete) {
          const withinGrace = doneAtMs > 0 && doneAtMs <= now && now - doneAtMs <= graceMs;
          const status: 'recent_done' | 'done' = withinGrace ? 'recent_done' : 'done';
          return {
            ...step,
            status,
            blockedBy: [] as string[],
            hideAtMs: withinGrace ? doneAtMs + graceMs : 0,
          };
        }
        const deps = (step.dependsOnSteps || []).filter(Boolean);
        if (!deps.length) return { ...step, status: 'ready' as const, blockedBy: [] as string[], hideAtMs: 0 };
        const isDepDone = (dep: string) => {
          if (yesAnswerStepKey(dep)) return yesAnswerForStep(dep, steps, mainCase as any);
          if (dep === UNIT_BOOKED_DEP_KEY) return isBookedOrSold;
          if (dep === LOAN_REQUIRED_DEP_KEY) return String(loanRequired || '').toLowerCase() === 'yes';
          if (dep === FULL_PAYMENT_DEP_KEY) return fullPaymentReceived(unit?.unitAmount, customerReceiptTotal + loanDisbursementReceiptTotal);
          if (dep === BUC_DEP_KEY) return buildingBucReceived(projectBuc, unit);
          if (dep === PRE_BUC_DEP_KEY) return !buildingBucReceived(projectBuc, unit);
          if (dep.startsWith('loan:')) {
            const depKey = String(dep.split(':').slice(1).join(':') || '').trim();
            if (!depKey) return false;
            if (loanCompleted.has(depKey)) return true;
            if (loanStepState?.[depKey]?.done === true) return true;
            if (loanStepState?.[depKey]?.doneAt || loanStepState?.[depKey]?.doneAtMs) return true;
            const depStep = loanStepByKey.get(depKey);
            return depStep ? isLoanStepCompleteByCondition(depStep) : false;
          }
          if (completed.has(dep)) return true;
          if (stepState?.[dep]?.done === true) return true;
          if (stepState?.[dep]?.doneAt || stepState?.[dep]?.doneAtMs) return true;
          const depStep = stepByKey.get(dep);
          return depStep ? isStepCompleteByCondition(depStep) : false;
        };
        const blockedBy = deps.filter((dep) => !isDepDone(dep));
        const modeAny = String(step.dependsMode || 'all') === 'any';
        const ready = modeAny ? blockedBy.length < deps.length : blockedBy.length === 0;
        const status: 'ready' | 'blocked' = ready ? 'ready' : 'blocked';
        return { ...step, status, blockedBy, hideAtMs: 0 };
      })
      .filter((step) => step.showInDrawer !== false && (step.status === 'ready' || step.status === 'recent_done'))
      .sort((a, b) => {
        if (a.status === 'recent_done' && b.status !== 'recent_done') return -1;
        if (a.status !== 'recent_done' && b.status === 'recent_done') return 1;
        return (a.order || 0) - (b.order || 0);
      });
    return result;
  }, [
    advocateAgreementReceiptTotal,
    booking,
    bookingDocsByKey,
	    customerReceiptTotal,
	    customers,
	    gstReceiptTotal,
    loanDisbursementReceiptTotal,
    projectBuc,
    inferStepEvidenceDoneAtMs,
    kycSummaryByCustomer,
    loanCase,
    loanRequired,
    loanWorkflowDefinition?.steps,
    mainCase,
    mainWorkflowDefinition?.steps,
    recentDoneTick,
    unit,
  ]);

  const loanWorkflowV2Pending = useMemo<
    Array<
      WorkflowDefinitionStep & {
        status: 'ready' | 'blocked' | 'recent_done' | 'done';
        blockedBy: string[];
        hideAtMs: number;
      }
    >
  >(() => {
    const tick = recentDoneTick;
    const steps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []);
    const mainSteps = normalizeDefinitionSteps(mainWorkflowDefinition?.steps || []);
    if (!steps.length || !loanCase) return [];
    const completedKeys = Array.isArray((loanCase as any)?.completedStepKeys)
      ? ((loanCase as any).completedStepKeys as any[])
      : [];
    const completed = new Set(
      completedKeys
        .map((k: any) => String(k || '').trim())
        .filter(Boolean)
    );
    const isBookedOrSold = ['booked', 'sold'].includes(String(unit?.bookingStatus || '').toLowerCase());
    const stepState = (((loanCase as any)?.stepState || {}) as Record<string, any>) || {};
    const mainCompletedKeys = Array.isArray((mainCase as any)?.completedStepKeys)
      ? ((mainCase as any).completedStepKeys as any[])
      : [];
    const mainCompleted = new Set(
      mainCompletedKeys
        .map((k: any) => String(k || '').trim())
        .filter(Boolean)
    );
    const mainStepState = (((mainCase as any)?.stepState || {}) as Record<string, any>) || {};
    const isStepCompleteByCondition = (step: WorkflowDefinitionStep): boolean => {
      if (completed.has(step.key)) return true;
      const actionType = String(step?.actionType || '').toLowerCase();
      const doneSource = String(stepState?.[step.key]?.doneSource || '').trim().toLowerCase();
      const hasDoneAt = !!stepState?.[step.key]?.doneAt || !!stepState?.[step.key]?.doneAtMs;
      const hasDoneFlag = stepState?.[step.key]?.done === true;
      if (actionType === 'add_customer') {
        return (hasDoneFlag || hasDoneAt) && doneSource !== 'auto';
      }
      if (hasDoneFlag || hasDoneAt) return true;
      if (actionType === 'upload_document' || actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'loan_sanction_form') {
        const docKey = String((step as any)?.docKey || '').trim() || 'sanction_letter';
        const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        const bankName = String(stepData?.sanctionBankName || '').trim();
        const sanctionDate = String(stepData?.sanctionDate || '').trim();
        const sanctionAmount = Number(stepData?.sanctionAmount);
        const loanToUnitAmount = Number(stepData?.loanToUnitAmount);
        const isSbi = bankName.toLowerCase() === 'state bank of india';
        const nonSbiReason = String(stepData?.nonSbiReason || '').trim();
        const hasDoc = !!bookingDocsByKey?.[docKey]?.files?.length;
        if (!hasDoc) return false;
        if (!bankName || !sanctionDate) return false;
        if (!Number.isFinite(sanctionAmount) || sanctionAmount <= 0) return false;
        if (!Number.isFinite(loanToUnitAmount) || loanToUnitAmount <= 0) return false;
        if (!isSbi && !nonSbiReason) return false;
        return true;
      }
      if (actionType === 'input_value') {
        const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        return isInputValueStepComplete(step, stepData, booking as any);
      }
      if (actionType === 'add_payment') {
        const paymentType = String((step as any)?.paymentType || '').toLowerCase();
        if (paymentType === 'gst') return gstReceiptTotal > 0;
        return customerReceiptTotal > 0;
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return advocateAgreementReceiptTotal > 0;
      }
      if (actionType === 'add_kyc') {
        if (!customers.length) return false;
        return customers.every((customer) => {
          const summary = (kycSummaryByCustomer as any)?.[customer.id];
          return !!summary?.hasPAN && !!summary?.hasAADHAAR;
        });
      }
      return false;
    };
    const isMainStepCompleteByCondition = (step: WorkflowDefinitionStep): boolean => {
      if (mainCompleted.has(step.key)) return true;
      const actionType = String(step?.actionType || '').toLowerCase();
      const doneSource = String(mainStepState?.[step.key]?.doneSource || '').trim().toLowerCase();
      const hasDoneAt = !!mainStepState?.[step.key]?.doneAt || !!mainStepState?.[step.key]?.doneAtMs;
      const hasDoneFlag = mainStepState?.[step.key]?.done === true;
      if (actionType === 'add_customer') {
        return (hasDoneFlag || hasDoneAt) && doneSource !== 'auto';
      }
      if (hasDoneFlag || hasDoneAt) return true;
      if (actionType === 'upload_document' || actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'input_value') {
        const stepData = ((((mainCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        return isInputValueStepComplete(step, stepData, booking as any);
      }
      if (actionType === 'add_payment') {
        const paymentType = String((step as any)?.paymentType || '').toLowerCase();
        if (paymentType === 'gst') return gstReceiptTotal > 0;
        return customerReceiptTotal > 0;
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return advocateAgreementReceiptTotal > 0;
      }
      if (actionType === 'add_kyc') {
        if (!customers.length) return false;
        return customers.every((customer) => {
          const summary = (kycSummaryByCustomer as any)?.[customer.id];
          return !!summary?.hasPAN && !!summary?.hasAADHAAR;
        });
      }
      return false;
    };
    const stepByKey = new Map(
      steps
        .map((s) => ({ key: String(s?.key || '').trim(), step: s }))
        .filter((x) => x.key)
        .map((x) => [x.key, x.step] as const)
    );
    const mainStepByKey = new Map(
      mainSteps
        .map((s) => ({ key: String(s?.key || '').trim(), step: s }))
        .filter((x) => x.key)
        .map((x) => [x.key, x.step] as const)
    );
    const now = Date.now() + tick * 0;
    const graceMs = 15 * 60 * 1000;
    const doneAtMsForKey = (step: WorkflowDefinitionStep): number => {
      const key = String(step?.key || '').trim();
      const rawMs = stepState?.[key]?.doneAtMs;
      const doneSource = String(stepState?.[key]?.doneSource || '').trim().toLowerCase();
      if (typeof rawMs === 'number' && Number.isFinite(rawMs) && rawMs > 0) return rawMs;
      const raw = stepState?.[key]?.doneAt;
      const rawDoneMs =
        typeof raw === 'number'
          ? raw
          : typeof raw?.toMillis === 'function'
            ? raw.toMillis()
            : typeof raw?.seconds === 'number'
              ? raw.seconds * 1000
              : 0;
      const evidenceMs = inferStepEvidenceDoneAtMs(step);
      if (doneSource === 'auto' && evidenceMs > 0) return evidenceMs;
      return rawDoneMs;
    };
    return steps
      .filter((step) => step.enabled !== false)
      .map((step) => {
        const doneAtMs = doneAtMsForKey(step);
        const isComplete = isStepCompleteByCondition(step);
        if (isComplete) {
          const withinGrace = doneAtMs > 0 && doneAtMs <= now && now - doneAtMs <= graceMs;
          const status: 'recent_done' | 'done' = withinGrace ? 'recent_done' : 'done';
          return {
            ...step,
            status,
            blockedBy: [] as string[],
            hideAtMs: withinGrace ? doneAtMs + graceMs : 0,
          };
        }
        const deps = (step.dependsOnSteps || []).filter(Boolean);
        if (!deps.length) return { ...step, status: 'ready' as const, blockedBy: [] as string[], hideAtMs: 0 };
        const isDepDone = (dep: string) => {
          if (yesAnswerStepKey(dep)) return yesAnswerForStep(dep, steps, loanCase as any);
          if (dep === UNIT_BOOKED_DEP_KEY) return isBookedOrSold;
          if (dep === LOAN_REQUIRED_DEP_KEY) return String(loanRequired || '').toLowerCase() === 'yes';
          if (dep.startsWith('main:')) {
            const depKey = String(dep.split(':').slice(1).join(':') || '').trim();
            if (!depKey) return false;
            if (mainCompleted.has(depKey)) return true;
            if (mainStepState?.[depKey]?.done === true) return true;
            if (mainStepState?.[depKey]?.doneAt || mainStepState?.[depKey]?.doneAtMs) return true;
            const depStep = mainStepByKey.get(depKey);
            return depStep ? isMainStepCompleteByCondition(depStep) : false;
          }
          if (completed.has(dep)) return true;
          if (stepState?.[dep]?.done === true) return true;
          if (stepState?.[dep]?.doneAt || stepState?.[dep]?.doneAtMs) return true;
          const depStep = stepByKey.get(dep);
          return depStep ? isStepCompleteByCondition(depStep) : false;
        };
        const blockedBy = deps.filter((dep) => !isDepDone(dep));
        const modeAny = String(step.dependsMode || 'all') === 'any';
        const ready = modeAny ? blockedBy.length < deps.length : blockedBy.length === 0;
        const status: 'ready' | 'blocked' = ready ? 'ready' : 'blocked';
        return { ...step, status, blockedBy, hideAtMs: 0 };
      })
      .filter((step) => step.showInDrawer !== false && (step.status === 'ready' || step.status === 'recent_done'))
      .sort((a, b) => {
        if (a.status === 'recent_done' && b.status !== 'recent_done') return -1;
        if (a.status !== 'recent_done' && b.status === 'recent_done') return 1;
        return (a.order || 0) - (b.order || 0);
      });
  }, [
    advocateAgreementReceiptTotal,
    booking,
    bookingDocsByKey,
    customerReceiptTotal,
    customers,
    gstReceiptTotal,
    inferStepEvidenceDoneAtMs,
    kycSummaryByCustomer,
    mainCase,
    mainWorkflowDefinition?.steps,
    loanCase,
    loanRequired,
    loanWorkflowDefinition?.steps,
    recentDoneTick,
    unit?.bookingStatus,
  ]);

  const [draftGenerationStatus, setDraftGenerationStatus] = useState<'idle' | 'processing' | 'done' | 'error'>('idle');

  const drawerWorkflowV2Sections = useMemo(
    () =>
      [
        { key: 'main', title: '', steps: mainWorkflowV2Pending, which: 'main' as const },
        {
          key: 'loan',
          title: String(loanRequired || '').toLowerCase() === 'yes' ? 'Loan Workflow' : '',
          steps: loanWorkflowV2Pending,
          which: 'loan' as const,
        },
      ].filter((section) => section.steps.length > 0 && (focusSection !== 'loan' || section.which === 'loan')),
    [focusSection, loanRequired, loanWorkflowV2Pending, mainWorkflowV2Pending]
  );

  const getChecklistItemsForStep = useCallback((which: 'main' | 'loan', step: WorkflowDefinitionStep) => {
    const fieldKey = String((step as any)?.fieldKey || '').trim().toLowerCase();
    const stepKey = String(step?.key || '').trim().toLowerCase();
    if (
      which === 'loan' &&
      (fieldKey === 'loan_docs_checklist' ||
        stepKey === 'collect_documents_for_loan' ||
        stepKey === String(OFFICE_LOAN_STEP_KEYS.collectDocs).trim().toLowerCase())
    ) {
      return LOAN_COLLECT_DOC_SUBTASKS;
    }
    return [] as Array<{ key: string; label: string }>;
  }, []);

  const currentMainStep = useMemo(() => {
    if (!mainCase) return null;
    const completed = new Set(mainCase.completedStepKeys || []);
    const step1DataRaw = (mainCase as any)?.stepData?.[OFFICE_MAIN_STEP_KEYS.customerKycBookingForm] || {};
    const expectedApplicantsCount = Number(step1DataRaw.expectedApplicantsCount || 1) || 1;
    const stage1CustomersOk = customers.length >= expectedApplicantsCount;
    const stage1DobOk =
      customers.length === 0 ||
      customers.every((customer) => {
        const dob = String(
          (customer as any)?.dateOfBirth || (customer as any)?.dob || (customer as any)?.birthDate || ''
        ).trim();
        return !!dob;
      });
    const stage1KycOk =
      customers.length > 0 &&
      customers.every((customer) => {
        const summary = kycSummaryByCustomer[customer.id];
        return !!summary?.hasPAN && !!summary?.hasAADHAAR;
      });
    const stage1BookingFormOk = !!bookingFormDoc?.files?.length;
    const stage1PrereqReady = stage1CustomersOk && stage1DobOk && stage1KycOk && stage1BookingFormOk;

    const step2Template = mainSteps.find((step) => step.key === OFFICE_MAIN_STEP_KEYS.downpaymentGst) || null;
    const receiptType = String((step2Template as any)?.paymentReceiptType || 'both').toLowerCase();
    const step2Data = (mainCase as any)?.stepData?.[OFFICE_MAIN_STEP_KEYS.downpaymentGst] || {};
    const gstSkippedForNow = Boolean(step2Data?.gstSkippedForNow);
    const needsCustomer = receiptType === 'both' || receiptType === 'customer';
    const needsGst = receiptType === 'both' || receiptType === 'gst';
    const stage2PrereqReady =
      (!needsCustomer || customerReceiptTotal > 0) &&
      (!needsGst || gstReceiptTotal > 0 || gstSkippedForNow);

    const stage3GeneratedAt = Number(
      (mainCase as any)?.stepData?.[OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter]
        ?.allotmentLetterDraftGeneratedAt ||
        (mainCase as any)?.stepData?.[OFFICE_MAIN_STEP_KEYS.sendToAdvocate]
          ?.allotmentLetterDraftGeneratedAt ||
        0
    );

    const stage1Done = completed.has(OFFICE_MAIN_STEP_KEYS.customerKycBookingForm) && stage1PrereqReady;
    const stage2Done = completed.has(OFFICE_MAIN_STEP_KEYS.downpaymentGst) && stage2PrereqReady;
    const stage3PrereqReady = stage1PrereqReady && stage2PrereqReady && customerReceiptTotal > 0;
    const stage3Done =
      (completed.has(OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter) || stage3GeneratedAt > 0) &&
      stage3PrereqReady;
    const stage4Done = completed.has(OFFICE_MAIN_STEP_KEYS.sendToAdvocate);
    const stage5Data = (mainCase as any)?.stepData?.[OFFICE_MAIN_STEP_KEYS.signAtAdvocate] || {};
    const stage5SentForSign = !!stage5Data?.customerSentForSign;
    const stage5AdvocateAgreementFeePaid = advocateAgreementReceiptTotal > 0;
    const stage5Done = stage4Done && stage5SentForSign;
    const saleAgreementUploaded = !!bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.length;
    const allotmentLetterUploaded = !!bookingDocsByKey[ALLOTMENT_LETTER_SIGNED_DOC_KEY]?.files?.length;
    const stage6Done =
      completed.has(OFFICE_MAIN_STEP_KEYS.uploadSignedDocs) ||
      (stage5Done && saleAgreementUploaded && allotmentLetterUploaded && stage5AdvocateAgreementFeePaid);

    const staged: WorkflowStepComputed[] = [
      {
        key: OFFICE_MAIN_STEP_KEYS.customerKycBookingForm,
        title: 'Customer details + KYC + signed booking form',
        order: 1,
        kind: 'document',
        fields: [],
        requiredDocs: [],
        status: stage1Done ? 'done' : 'ready',
      },
      {
        key: OFFICE_MAIN_STEP_KEYS.downpaymentGst,
        title: 'Down payment + GST payment + cheque/receipt upload',
        order: 2,
        kind: 'payment',
        fields: [],
        requiredDocs: [],
        dependsOn: [OFFICE_MAIN_STEP_KEYS.customerKycBookingForm],
        status: stage2Done ? 'done' : stage1PrereqReady ? 'ready' : 'blocked',
      },
      {
        key: OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter,
        title: 'Create Allotment Letter',
        order: 3,
        kind: 'task',
        fields: [],
        requiredDocs: [],
        dependsOn: [OFFICE_MAIN_STEP_KEYS.downpaymentGst],
        status: stage3Done ? 'done' : stage3PrereqReady ? 'ready' : 'blocked',
      },
      {
        key: OFFICE_MAIN_STEP_KEYS.sendToAdvocate,
        title: 'Send documents to advocate for sale agreement',
        order: 4,
        kind: 'task',
        fields: [],
        requiredDocs: [],
        dependsOn: [OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter],
        status: stage4Done ? 'done' : stage3Done ? 'ready' : 'blocked',
      },
      {
        key: OFFICE_MAIN_STEP_KEYS.signAtAdvocate,
        title: 'Send customer to sign sale agreement',
        order: 5,
        kind: 'task',
        fields: [],
        requiredDocs: [],
        dependsOn: [OFFICE_MAIN_STEP_KEYS.sendToAdvocate],
        status: stage5Done ? 'done' : stage4Done ? 'ready' : 'blocked',
      },
      {
        key: OFFICE_MAIN_STEP_KEYS.uploadSignedDocs,
        title: 'Upload signed sale agreement + signed allotment letter + add advocate sale agreement fee receipt',
        order: 6,
        kind: 'task',
        fields: [],
        requiredDocs: [
          { key: SALE_AGREEMENT_SIGNED_DOC_KEY, label: 'Signed Sale Agreement', required: true },
          { key: ALLOTMENT_LETTER_SIGNED_DOC_KEY, label: 'Signed Allotment Letter', required: true },
        ],
        dependsOn: [OFFICE_MAIN_STEP_KEYS.signAtAdvocate],
        status: stage6Done ? 'done' : stage5Done ? 'ready' : 'blocked',
      },
    ];

    return staged.find((s) => s.status !== 'done' && s.status !== 'skipped') || null;
  }, [
    bookingFormDoc?.files?.length,
    customerReceiptTotal,
    customers,
    gstReceiptTotal,
    kycSummaryByCustomer,
    mainCase,
    mainSteps,
    advocateAgreementReceiptTotal,
    bookingDocsByKey,
  ]);

  const currentLoanStep = useMemo(() => {
    const sanctionStep = loanSteps.find((step) => step.key === OFFICE_LOAN_STEP_KEYS.sanctionReceived) || null;
    const sendToBanksStep = loanSteps.find((step) => step.key === OFFICE_LOAN_STEP_KEYS.sendToBanks) || null;
    if (sendToBanksStep && sanctionStep?.status !== 'done') {
      return sendToBanksStep;
    }
    const pending = loanSteps.filter((step) => step.status !== 'done' && step.status !== 'skipped');
    return pending.sort((a, b) => (a.order || 0) - (b.order || 0))[0] || null;
  }, [loanSteps]);

  const stepStatusChip = (s: any) => {
    if (s?.status === 'ready') return null;
    const color: any =
      s?.status === 'done' ? 'success' :
      s?.status === 'ready' ? 'primary' :
      s?.status === 'blocked' ? 'warning' :
      'default';
    const label =
      s?.status === 'done' ? 'Done' :
      s?.status === 'ready' ? 'Pending' :
      s?.status === 'blocked' ? 'Blocked' :
      s?.status === 'skipped' ? 'Skipped' : '—';
    return <Chip size="small" label={label} color={color} variant={s?.status === 'done' ? 'filled' : 'outlined'} sx={{ fontWeight: 900 }} />;
  };

  const ensureProjectWorkflowTemplates = useCallback(async (): Promise<{ mainId: string; loanId: string }> => {
    if (!projectId) return { mainId: DEFAULT_OFFICE_MAIN_TEMPLATE_ID, loanId: DEFAULT_OFFICE_LOAN_TEMPLATE_ID };
    if (!currentUser?.email) throw new Error('Not signed in.');
    const by = currentUser.email;
    const projectRef = doc(db, 'projects', projectId);
    const snap = await getDoc(projectRef);
    const d = (snap.data() as any) || {};
    const mainId = String(d.officeMainWorkflowTemplateId || DEFAULT_OFFICE_MAIN_TEMPLATE_ID);
    const loanId = String(d.officeLoanWorkflowTemplateId || DEFAULT_OFFICE_LOAN_TEMPLATE_ID);
    // Only seed templates if missing. Do not overwrite customized workflows.
    const mainRef = doc(db, 'projects', projectId, 'workflowTemplates', mainId);
    const mainSnap = await getDoc(mainRef);
    if (!mainSnap.exists()) {
      await setDoc(
        mainRef,
        { ...defaultOfficeMainTemplate(), createdAt: serverTimestamp(), createdBy: by, updatedAt: serverTimestamp(), updatedBy: by },
        { merge: true }
      );
    }
    const loanRef = doc(db, 'projects', projectId, 'workflowTemplates', loanId);
    const loanSnap = await getDoc(loanRef);
    if (!loanSnap.exists()) {
      await setDoc(
        loanRef,
        { ...defaultOfficeLoanTemplate(), createdAt: serverTimestamp(), createdBy: by, updatedAt: serverTimestamp(), updatedBy: by },
        { merge: true }
      );
    }
    if (!d.officeMainWorkflowTemplateId || !d.officeLoanWorkflowTemplateId) {
      await setDoc(
        projectRef,
        { officeMainWorkflowTemplateId: mainId, officeLoanWorkflowTemplateId: loanId, updatedAt: serverTimestamp(), updatedBy: by },
        { merge: true }
      );
    }
    setProjectWorkflowIds({ mainId, loanId });
    return { mainId, loanId };
  }, [currentUser?.email, db, projectId]);

  const initMainWorkflowCase = useCallback(async () => {
    if (!unit?.id || !projectId || !bookingId) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    const { mainId } = await ensureProjectWorkflowTemplates();
    const by = currentUser.email;
    const templateRef = doc(db, 'projects', projectId, 'workflowTemplates', mainId);
    const tSnap = await getDoc(templateRef);
    const v = Number((tSnap.data() as any)?.version || 1);
    const caseRef = doc(db, 'projects', projectId, 'units', unit.id, 'cases', bookingId);
    const cSnap = await getDoc(caseRef);
    if (cSnap.exists()) return;
    await setDoc(caseRef, {
      caseType: 'main',
      status: 'active',
      bookingId,
      unitId: unit.id,
      templateId: mainId,
      templateVersion: v,
      loanRequired: null,
      completedStepKeys: [],
      stepData: {},
      startedAt: serverTimestamp(),
      startedBy: by,
      updatedAt: serverTimestamp(),
      updatedBy: by,
    });
  }, [bookingId, currentUser?.email, db, ensureProjectWorkflowTemplates, projectId, unit?.id]);

  useEffect(() => {
    if (!open || !projectId || !unit?.id || !bookingId) return;
    const status = String((unit as any)?.bookingStatus || '').trim().toLowerCase();
    if (status !== 'booked' && status !== 'sold') return;
    if (mainCase) return;
    const autoKey = `${projectId}:${unit.id}:${bookingId}`;
    if (autoInitMainCaseRef.current === autoKey) return;
    autoInitMainCaseRef.current = autoKey;
    initMainWorkflowCase().catch((err: any) => {
      autoInitMainCaseRef.current = '';
      setError(err?.message || 'Failed to auto-start workflow.');
    });
  }, [bookingId, initMainWorkflowCase, mainCase, open, projectId, unit, unit?.bookingStatus, unit?.id]);

  const setLoanRequiredValue = async (value: 'yes' | 'no') => {
    const unitId = String(unit?.id || '').trim();
    const pid = String(projectId || '').trim();
    const bid = String(bookingId || '').trim();
    if (!unitId || !pid || !bid) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    const caseRef = doc(db, 'projects', pid, 'units', unitId, 'cases', bid);
    await updateDoc(caseRef, { loanRequired: value, updatedAt: serverTimestamp(), updatedBy: currentUser.email });
  };

  // force: the caller has just set Loan Required = Yes (unit page "Start loan file"), so skip the check.
  const startLoanWorkflowCase = async (force = false) => {
    const unitId = String(unit?.id || '').trim();
    const pid = String(projectId || '').trim();
    const bid = String(bookingId || '').trim();
    if (!unitId || !pid || !bid) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    if (!force && String(loanRequired || '').toLowerCase() !== 'yes') {
      setError('Set Loan Required = Yes first.');
      return;
    }
    const { loanId } = await ensureProjectWorkflowTemplates();
    const by = currentUser.email;
    const templateRef = doc(db, 'projects', pid, 'workflowTemplates', loanId);
    const tSnap = await getDoc(templateRef);
    const v = Number((tSnap.data() as any)?.version || 1);
    const caseRef = doc(db, 'projects', pid, 'units', unitId, 'cases', `loan_${bid}`);
    const cSnap = await getDoc(caseRef);
    if (cSnap.exists()) return;
    await setDoc(caseRef, {
      caseType: 'loan',
      status: 'active',
      bookingId: bid,
      unitId,
      templateId: loanId,
      templateVersion: v,
      parentCaseId: bid,
      attemptNo: 1,
      completedStepKeys: [],
      stepData: {},
      startedAt: serverTimestamp(),
      startedBy: by,
      updatedAt: serverTimestamp(),
      updatedBy: by,
    });
  };

  const openSwitchLoanBankDialog = useCallback(() => {
    setSwitchLoanBankError(null);
    setSwitchLoanBankName(String((latestLoanSanctionData as any)?.sanctionBankName || '').trim());
    setSwitchLoanBankReason('');
    setSwitchLoanBankDialogOpen(true);
  }, [latestLoanSanctionData]);

  const archiveLoanDocumentsForBankSwitch = useCallback(
    async (effectiveBookingId: string, archiveSuffix: string) => {
      const unitId = String(unit?.id || '').trim();
      const pid = String(projectId || '').trim();
      const by = String(currentUser?.email || '').trim();
      if (!unitId || !pid || !effectiveBookingId || !by) return;

      const collectionsToArchive: Array<'uploaded' | 'draft'> = ['uploaded', 'draft'];
      const loanDocKeys = new Set(
        normalizeDefinitionSteps(loanWorkflowDefinition?.steps || [])
          .map((step) => String((step as any)?.docKey || '').trim())
          .filter(Boolean)
      );
      for (const collectionName of collectionsToArchive) {
        const collectionRef = collection(
          db,
          'projects',
          pid,
          'units',
          unitId,
          'bookings',
          effectiveBookingId,
          collectionName
        );
        const snap = await getDocs(collectionRef);
        const targetDocs = snap.docs.filter((docSnap) => {
          const row = docSnap.data() || {};
          const rowDisplaySection = String((row as any)?.displaySection || '').trim().toLowerCase();
          return rowDisplaySection === 'loan_documents' || loanDocKeys.has(String(docSnap.id || '').trim());
        });
        for (const docSnap of targetDocs) {
          const row = docSnap.data() || {};
          const archiveDocKey = `${docSnap.id}__loan_history_${archiveSuffix}`;
          const archiveRef = doc(collectionRef, archiveDocKey);
          await setDoc(archiveRef, {
            ...row,
            displaySection: 'hidden',
            archivedAt: serverTimestamp(),
            archivedBy: by,
            archivedReason: 'loan_bank_switched',
            archivedFromDocKey: docSnap.id,
            archivedCollection: collectionName,
          });
          await deleteDoc(docSnap.ref);
        }
      }
    },
    [currentUser?.email, db, loanWorkflowDefinition?.steps, projectId, unit?.id]
  );

  const switchLoanBankAttempt = useCallback(async () => {
    const unitId = String(unit?.id || '').trim();
    const pid = String(projectId || '').trim();
    const bid = String(bookingId || '').trim();
    const by = String(currentUser?.email || '').trim();
    const newBankName = String(switchLoanBankName || '').trim();
    const switchReason = String(switchLoanBankReason || '').trim();
    if (!isAdmin) throw new Error('Only admin can switch loan bank.');
    if (!unitId || !pid || !bid) throw new Error('Unit booking is missing.');
    if (!by) throw new Error('Not signed in.');
    if (!loanCase?.id) throw new Error('Loan workflow is not started yet.');
    if (!newBankName) throw new Error('New bank is required.');
    if (!switchReason) throw new Error('Reason for bank switch is required.');

    setSwitchLoanBankSaving(true);
    setSwitchLoanBankError(null);
    try {
      const currentCaseId = String(loanCase.id || `loan_${bid}`).trim() || `loan_${bid}`;
      const archiveSuffix = String(Date.now());
      const archiveCaseId = `${currentCaseId}__history_${archiveSuffix}`;
      const currentAttemptNo = Math.max(1, Number((loanCase as any)?.attemptNo || 1) || 1);
      const currentBankName = String((latestLoanSanctionData as any)?.sanctionBankName || '').trim();
      const templateId = String((loanCase as any)?.templateId || loanTemplateIdToUse || DEFAULT_OFFICE_LOAN_TEMPLATE_ID).trim();
      const templateVersion = Math.max(1, Number((loanCase as any)?.templateVersion || 1) || 1);
      const currentCaseSnapshot = { ...(loanCase as any) };
      delete (currentCaseSnapshot as any).id;

      await archiveLoanDocumentsForBankSwitch(bid, archiveSuffix);
      await setDoc(doc(db, 'projects', pid, 'units', unitId, 'cases', archiveCaseId), {
        ...currentCaseSnapshot,
        status: 'closed',
        attemptNo: currentAttemptNo,
        archivedAt: serverTimestamp(),
        archivedBy: by,
        archivedFromCaseId: currentCaseId,
        closedAt: serverTimestamp(),
        closedBy: by,
        closeReason: 'loan_bank_switched',
        switchedToBankName: newBankName,
        switchedFromBankName: currentBankName || '',
        switchReason,
        updatedAt: serverTimestamp(),
        updatedBy: by,
      });

      await setDoc(doc(db, 'projects', pid, 'units', unitId, 'cases', currentCaseId), {
        caseType: 'loan',
        status: 'active',
        bookingId: bid,
        unitId,
        templateId,
        templateVersion,
        parentCaseId: bid,
        attemptNo: currentAttemptNo + 1,
        preferredBankName: newBankName,
        switchedFromCaseId: archiveCaseId,
        switchedFromBankName: currentBankName || '',
        switchReason,
        completedStepKeys: [],
        stepData: {},
        stepState: {},
        startedAt: serverTimestamp(),
        startedBy: by,
        updatedAt: serverTimestamp(),
        updatedBy: by,
      });
      setSwitchLoanBankDialogOpen(false);
    } catch (err: any) {
      setSwitchLoanBankError(err?.message || 'Failed to switch loan bank.');
      throw err;
    } finally {
      setSwitchLoanBankSaving(false);
    }
  }, [
    archiveLoanDocumentsForBankSwitch,
    bookingId,
    currentUser?.email,
    db,
    isAdmin,
    latestLoanSanctionData,
    loanCase,
    loanTemplateIdToUse,
    projectId,
    switchLoanBankName,
    switchLoanBankReason,
    unit?.id,
  ]);

  const setStepDone = useCallback(
    async (which: 'main' | 'loan', stepKey: string, done: boolean) => {
      const unitId = String(unit?.id || '').trim();
      const pid = String(projectId || '').trim();
      const bid = String(bookingId || '').trim();
      if (!unitId || !pid || !bid) return;
      if (!currentUser?.email) throw new Error('Not signed in.');
      const id = which === 'main' ? bid : `loan_${bid}`;
      const caseRef = doc(db, 'projects', pid, 'units', unitId, 'cases', id);
      try {
        await updateDoc(caseRef, {
          completedStepKeys: done ? arrayUnion(stepKey) : arrayRemove(stepKey),
          [`stepData.${stepKey}.taskDone`]: done,
          [`stepState.${stepKey}.done`]: done,
          [`stepState.${stepKey}.doneAt`]: done ? serverTimestamp() : deleteField(),
          [`stepState.${stepKey}.doneAtMs`]: done ? Date.now() : deleteField(),
          [`stepState.${stepKey}.doneBy`]: done ? currentUser.email : deleteField(),
          [`stepState.${stepKey}.doneSource`]: done ? 'manual' : deleteField(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
        });
      } catch {
        await updateDoc(caseRef, {
          [`stepData.${stepKey}.taskDone`]: done,
          [`stepState.${stepKey}.done`]: done,
          [`stepState.${stepKey}.doneAt`]: done ? serverTimestamp() : deleteField(),
          [`stepState.${stepKey}.doneAtMs`]: done ? Date.now() : deleteField(),
          [`stepState.${stepKey}.doneBy`]: done ? currentUser.email : deleteField(),
          [`stepState.${stepKey}.doneSource`]: done ? 'manual' : deleteField(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
        });
      }
    },
    [bookingId, currentUser?.email, db, projectId, unit?.id]
  );

  useEffect(() => {
    if (!open) return;
    if (!mainWorkflowDefinition) return;
    if (!mainCase) return;
    if (!unit?.id || !projectId || !bookingId) return;
    if (!currentUser?.email) return;
    const steps = normalizeDefinitionSteps(mainWorkflowDefinition?.steps || []).filter((s) => s.enabled !== false);
    if (!steps.length) return;
    const completedKeys = Array.isArray((mainCase as any)?.completedStepKeys)
      ? ((mainCase as any).completedStepKeys as any[])
      : [];
    const completed = new Set(completedKeys.map((k: any) => String(k || '').trim()).filter(Boolean));
    const stepState = (((mainCase as any)?.stepState || {}) as Record<string, any>) || {};
    const isStepCompleteByCondition = (step: WorkflowDefinitionStep): boolean => {
      if (completed.has(step.key)) return true;
      const actionType = String(step?.actionType || '').toLowerCase();
      if (actionType === 'add_customer') return false;
      if (actionType === 'upload_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'input_value') {
        const stepData = ((((mainCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        return isInputValueStepComplete(step, stepData, booking as any);
      }
      if (actionType === 'add_payment') {
        const paymentType = String((step as any)?.paymentType || '').toLowerCase();
        if (paymentType === 'gst') return gstReceiptTotal > 0;
        return customerReceiptTotal > 0;
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return advocateAgreementReceiptTotal > 0;
      }
      if (actionType === 'add_kyc') {
        if (!customers.length) return false;
        return customers.every((customer) => {
          const summary = (kycSummaryByCustomer as any)?.[customer.id];
          return !!summary?.hasPAN && !!summary?.hasAADHAAR;
        });
      }
      return false;
    };

    const updates: Record<string, any> = {};
    steps.forEach((step) => {
      const key = String(step.key || '').trim();
      if (!key) return;
      const state = stepState?.[key] || {};
      const doneSource = String(state?.doneSource || '').trim().toLowerCase();
      const hasDoneAt = !!state?.doneAt || !!state?.doneAtMs;
      const hasDone = state?.done === true || hasDoneAt;
      const isComplete = isStepCompleteByCondition(step) || completed.has(key);

      if (isComplete && !hasDone) {
        const evidenceDoneAtMs = inferStepEvidenceDoneAtMs(step);
        updates[`stepState.${key}.done`] = true;
        if (evidenceDoneAtMs > 0) {
          updates[`stepState.${key}.doneAt`] = Timestamp.fromMillis(evidenceDoneAtMs);
          updates[`stepState.${key}.doneAtMs`] = evidenceDoneAtMs;
          updates[`stepState.${key}.doneBy`] = currentUser.email;
        }
        updates[`stepState.${key}.doneSource`] = completed.has(key) ? 'manual' : 'auto';
        return;
      }
      if (!isComplete && hasDone && !completed.has(key) && doneSource === 'auto') {
        updates[`stepState.${key}.done`] = false;
        updates[`stepState.${key}.doneAt`] = deleteField();
        updates[`stepState.${key}.doneAtMs`] = deleteField();
        updates[`stepState.${key}.doneBy`] = deleteField();
        updates[`stepState.${key}.doneSource`] = deleteField();
      }
    });
    if (!Object.keys(updates).length) return;
    const caseRef = doc(db, 'projects', projectId, 'units', unit.id, 'cases', bookingId);
    updateDoc(caseRef, {
      ...updates,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    }).catch(() => {
      // ignore
    });
  }, [
    advocateAgreementReceiptTotal,
    booking,
    bookingDocsByKey,
    bookingId,
    customerReceiptTotal,
    customers,
    currentUser?.email,
    db,
    gstReceiptTotal,
    inferStepEvidenceDoneAtMs,
    kycSummaryByCustomer,
    mainCase,
    mainWorkflowDefinition,
    open,
    projectId,
    unit?.id,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!loanWorkflowDefinition) return;
    if (!loanCase) return;
    if (!unit?.id || !projectId || !bookingId) return;
    if (!currentUser?.email) return;
    const steps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []).filter((s) => s.enabled !== false);
    if (!steps.length) return;
    const completedKeys = Array.isArray((loanCase as any)?.completedStepKeys)
      ? ((loanCase as any).completedStepKeys as any[])
      : [];
    const completed = new Set(completedKeys.map((k: any) => String(k || '').trim()).filter(Boolean));
    const stepState = (((loanCase as any)?.stepState || {}) as Record<string, any>) || {};
    const isStepCompleteByCondition = (step: WorkflowDefinitionStep): boolean => {
      if (completed.has(step.key)) return true;
      const actionType = String(step?.actionType || '').toLowerCase();
      if (actionType === 'add_customer') return false;
      if (actionType === 'upload_document' || actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        if (!docKey) return false;
        return !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'loan_sanction_form') {
        const docKey = String((step as any)?.docKey || '').trim() || 'sanction_letter';
        const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        const bankName = String(stepData?.sanctionBankName || '').trim();
        const sanctionDate = String(stepData?.sanctionDate || '').trim();
        const sanctionAmount = Number(stepData?.sanctionAmount);
        const loanToUnitAmount = Number(stepData?.loanToUnitAmount);
        const isSbi = bankName.toLowerCase() === 'state bank of india';
        const nonSbiReason = String(stepData?.nonSbiReason || '').trim();
        const hasDoc = !!bookingDocsByKey?.[docKey]?.files?.length;
        if (!hasDoc) return false;
        if (!bankName || !sanctionDate) return false;
        if (!Number.isFinite(sanctionAmount) || sanctionAmount <= 0) return false;
        if (!Number.isFinite(loanToUnitAmount) || loanToUnitAmount <= 0) return false;
        if (!isSbi && !nonSbiReason) return false;
        return true;
      }
      if (actionType === 'input_value') {
        const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        return isInputValueStepComplete(step, stepData, booking as any);
      }
      if (actionType === 'add_payment') {
        const paymentType = String((step as any)?.paymentType || '').toLowerCase();
        if (paymentType === 'gst') return gstReceiptTotal > 0;
        return customerReceiptTotal > 0;
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return advocateAgreementReceiptTotal > 0;
      }
      if (actionType === 'add_kyc') {
        if (!customers.length) return false;
        return customers.every((customer) => {
          const summary = (kycSummaryByCustomer as any)?.[customer.id];
          return !!summary?.hasPAN && !!summary?.hasAADHAAR;
        });
      }
      return false;
    };

    const updates: Record<string, any> = {};
    steps.forEach((step) => {
      const key = String(step.key || '').trim();
      if (!key) return;
      const state = stepState?.[key] || {};
      const doneSource = String(state?.doneSource || '').trim().toLowerCase();
      const hasDoneAt = !!state?.doneAt || !!state?.doneAtMs;
      const hasDone = state?.done === true || hasDoneAt;
      const isComplete = isStepCompleteByCondition(step) || completed.has(key);

      if (isComplete && !hasDone) {
        const evidenceDoneAtMs = inferStepEvidenceDoneAtMs(step);
        updates[`stepState.${key}.done`] = true;
        if (evidenceDoneAtMs > 0) {
          updates[`stepState.${key}.doneAt`] = Timestamp.fromMillis(evidenceDoneAtMs);
          updates[`stepState.${key}.doneAtMs`] = evidenceDoneAtMs;
          updates[`stepState.${key}.doneBy`] = currentUser.email;
        }
        updates[`stepState.${key}.doneSource`] = completed.has(key) ? 'manual' : 'auto';
        return;
      }
      if (!isComplete && hasDone && !completed.has(key) && doneSource === 'auto') {
        updates[`stepState.${key}.done`] = false;
        updates[`stepState.${key}.doneAt`] = deleteField();
        updates[`stepState.${key}.doneAtMs`] = deleteField();
        updates[`stepState.${key}.doneBy`] = deleteField();
        updates[`stepState.${key}.doneSource`] = deleteField();
      }
    });
    if (!Object.keys(updates).length) return;
    const caseRef = doc(db, 'projects', projectId, 'units', unit.id, 'cases', `loan_${bookingId}`);
    updateDoc(caseRef, {
      ...updates,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    }).catch(() => {
      // ignore
    });
  }, [
    advocateAgreementReceiptTotal,
    booking,
    bookingDocsByKey,
    bookingId,
    customerReceiptTotal,
    customers,
    currentUser?.email,
    db,
    gstReceiptTotal,
    inferStepEvidenceDoneAtMs,
    kycSummaryByCustomer,
    loanCase,
    loanWorkflowDefinition,
    open,
    projectId,
    unit?.id,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!loanCase?.id) return;
    if (!bookingId) return;
    if (autoSyncSbiLeadRef.current) return;
    const stepData =
      (((loanCase as any)?.stepData || {}) as Record<string, any>)?.[OFFICE_LOAN_STEP_KEYS.sendToBanks] || {};
    const otherBankLeads = Array.isArray(stepData?.otherBankLeads)
      ? (stepData.otherBankLeads as Array<Record<string, any>>)
      : [];
    const hasSbiLead = otherBankLeads.some(
      (lead) => String(lead?.bankName || '').trim().toLowerCase() === 'state bank of india'
    );
    const completed = new Set<string>([
      ...((loanCase as any)?.completedStepKeys || []),
      ...(stepData?.taskDone ? [OFFICE_LOAN_STEP_KEYS.sendToBanks] : []),
    ]);
    const isDone = completed.has(OFFICE_LOAN_STEP_KEYS.sendToBanks);
    if (hasSbiLead === isDone) return;
    autoSyncSbiLeadRef.current = true;
    setStepDone('loan', OFFICE_LOAN_STEP_KEYS.sendToBanks, hasSbiLead)
      .then(() => {
        if (hasSbiLead) setSendToSbiHint('');
      })
      .catch((err: any) => {
        setError(err?.message || 'Failed to sync Send Lead to SBI.');
      })
      .finally(() => {
        autoSyncSbiLeadRef.current = false;
      });
  }, [bookingId, loanCase, open, setStepDone]);

  useEffect(() => {
    if (!open) return;
    if (!loanCase?.id) return;
    if (!loanWorkflowDefinition) return;
    if (autoSyncLoanBankLeadDoneRef.current) return;
    const steps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []).filter((s) => s.enabled !== false);
    if (!steps.length) return;
    const bankLeadStep = steps.find((s) => String(s?.actionType || '').toLowerCase() === 'add_bank_lead');
    const sanctionStep = steps.find((s) => String(s?.actionType || '').toLowerCase() === 'loan_sanction_form');
    if (!bankLeadStep?.key || !sanctionStep?.key) return;

    const completed = new Set<string>(
      (((loanCase as any)?.completedStepKeys || []) as any[])
        .map((k: any) => String(k || '').trim())
        .filter(Boolean)
    );
    const stepState = (((loanCase as any)?.stepState || {}) as Record<string, any>) || {};
    const sanctionData =
      ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[sanctionStep.key] || {}) as Record<string, any>;
    const sanctionDocKey = String((sanctionStep as any)?.docKey || '').trim() || 'sanction_letter';
    const sanctionBankName = String(sanctionData?.sanctionBankName || '').trim();
    const sanctionDate = String(sanctionData?.sanctionDate || '').trim();
    const sanctionAmount = Number(sanctionData?.sanctionAmount);
    const loanToUnitAmount = Number(sanctionData?.loanToUnitAmount);
    const isSbi = sanctionBankName.toLowerCase() === 'state bank of india';
    const nonSbiReason = String(sanctionData?.nonSbiReason || '').trim();
    const hasSanctionDoc = !!bookingDocsByKey?.[sanctionDocKey]?.files?.length;
    const sanctionDone =
      completed.has(sanctionStep.key) ||
      stepState?.[sanctionStep.key]?.done === true ||
      !!stepState?.[sanctionStep.key]?.doneAt ||
      !!stepState?.[sanctionStep.key]?.doneAtMs ||
      (
        hasSanctionDoc &&
        !!sanctionBankName &&
        !!sanctionDate &&
        Number.isFinite(sanctionAmount) &&
        sanctionAmount > 0 &&
        Number.isFinite(loanToUnitAmount) &&
        loanToUnitAmount > 0 &&
        (isSbi || !!nonSbiReason)
      );
    if (!sanctionDone) return;

    const bankLeadDone =
      completed.has(bankLeadStep.key) ||
      stepState?.[bankLeadStep.key]?.done === true ||
      !!stepState?.[bankLeadStep.key]?.doneAt ||
      !!stepState?.[bankLeadStep.key]?.doneAtMs;
    if (bankLeadDone) return;

    autoSyncLoanBankLeadDoneRef.current = true;
    setStepDone('loan', bankLeadStep.key, true)
      .catch((err: any) => {
        setError(err?.message || 'Failed to sync Send Lead to Bank.');
      })
      .finally(() => {
        autoSyncLoanBankLeadDoneRef.current = false;
      });
  }, [bookingDocsByKey, loanCase, loanWorkflowDefinition, open, setStepDone]);

  const step1Data = useMemo(() => {
    const k = OFFICE_MAIN_STEP_KEYS.customerKycBookingForm;
    const d = (mainCase as any)?.stepData?.[k] || {};
    return {
      expectedApplicantsCount: Number(d.expectedApplicantsCount || 1) || 1,
      hasApplicantsCount: Number.isFinite(Number(d.expectedApplicantsCount)) && Number(d.expectedApplicantsCount) > 0,
    };
  }, [mainCase]);

  useEffect(() => {
    if (!open) return;
    if (!step1ApplicantsEditMode) return;
    if (!currentMainStep || currentMainStep.key !== OFFICE_MAIN_STEP_KEYS.customerKycBookingForm) {
      setStep1ApplicantsEditMode(false);
    }
  }, [currentMainStep, open, step1ApplicantsEditMode]);

  const step1CustomersCount = customers.length;
  const step1CustomersOk = step1CustomersCount >= step1Data.expectedApplicantsCount;
  const step1CustomersMissingDob = useMemo(
    () =>
      customers.filter((c) => {
        const dob = String((c as any)?.dateOfBirth || (c as any)?.dob || (c as any)?.birthDate || '').trim();
        return !dob;
      }),
    [customers]
  );
  const step1DobOk = step1CustomersCount === 0 || step1CustomersMissingDob.length === 0;
  const step1KycOk =
    step1CustomersCount > 0 &&
    customers.every((c) => {
      const s = kycSummaryByCustomer[c.id];
      return !!s?.hasPAN && !!s?.hasAADHAAR;
    });
  const step1BookingFormOk = !!bookingFormDoc?.files?.length;
  const step1ReadyToComplete = step1CustomersOk && step1DobOk && step1KycOk && step1BookingFormOk;

  const isPaymentStepSatisfied = useCallback(
    (
      which: 'main' | 'loan',
      stepKey: string,
      customerTotal: number,
      gstTotal: number
    ) => {
      const step =
        (which === 'main' ? mainSteps : loanSteps).find((item) => item.key === stepKey) ||
        null;
      const receiptType = String((step as any)?.paymentReceiptType || 'both').toLowerCase();
      const caseStepData =
        (((which === 'main' ? (mainCase as any) : (loanCase as any))?.stepData || {}) as Record<string, any>)?.[
          stepKey
        ] || {};
      const needsCustomer = receiptType === 'both' || receiptType === 'customer';
      const needsGstBase = receiptType === 'both' || receiptType === 'gst';
      const needsGst = !(
        which === 'main' &&
        stepKey === OFFICE_MAIN_STEP_KEYS.downpaymentGst &&
        Boolean(caseStepData?.gstSkippedForNow)
      ) && needsGstBase;
      const customerOk = !needsCustomer || customerTotal > 0;
      const gstOk = !needsGst || gstTotal > 0;
      return customerOk && gstOk;
    },
    [loanCase, loanSteps, mainCase, mainSteps]
  );

  const getCaseStepData = useCallback(
    (which: 'main' | 'loan', stepKey: string) => {
      const c = which === 'main' ? (mainCase as any) : (loanCase as any);
      return (c?.stepData?.[stepKey] || {}) as Record<string, any>;
    },
    [loanCase, mainCase]
  );

  const getWorkflowInputValueByFieldKey = useCallback(
    (...candidateKeys: string[]): any => {
      const targetKeys = new Set(
        candidateKeys
          .map((key) => normalizeWorkflowTokenKey(key))
          .filter(Boolean)
      );
      if (!targetKeys.size) return null;

      const readCase = (caseDoc: any, definitionDoc: any): any => {
        const stepDataMap = ((caseDoc?.stepData || {}) as Record<string, any>) || {};
        const definitionSteps = normalizeDefinitionSteps(((definitionDoc?.steps || []) as any[]) || []);
        for (const step of definitionSteps) {
          if (String(step.actionType || '').trim().toLowerCase() !== 'input_value') continue;
          const fieldKey = String((step as any)?.fieldKey || step.key || '').trim();
          if (!fieldKey) continue;
          const stepKeyNorm = normalizeWorkflowTokenKey(step.key);
          const fieldKeyNorm = normalizeWorkflowTokenKey(fieldKey);
          if (!targetKeys.has(fieldKeyNorm) && !targetKeys.has(stepKeyNorm)) continue;
          const row = (stepDataMap?.[step.key] || {}) as Record<string, any>;
          const fieldValue = row?.[fieldKey];
          if (fieldValue != null && String(fieldValue).trim()) return fieldValue;
          const stepValue = row?.[step.key];
          if (stepValue != null && String(stepValue).trim()) return stepValue;
        }
        for (const row of Object.values(stepDataMap)) {
          if (!row || typeof row !== 'object') continue;
          for (const [key, value] of Object.entries(row as Record<string, any>)) {
            if (!targetKeys.has(normalizeWorkflowTokenKey(key))) continue;
            if (value == null) continue;
            if (typeof value === 'number') {
              if (Number.isFinite(value)) return value;
              continue;
            }
            if (typeof value === 'boolean') return value;
            if (String(value).trim()) return value;
          }
        }
        return null;
      };

      return (
        readCase(mainCase, mainWorkflowDefinition) ??
        readCase(loanCase, loanWorkflowDefinition) ??
        null
      );
    },
    [loanCase, loanWorkflowDefinition, mainCase, mainWorkflowDefinition]
  );

  const resolveBookingTextToken = useCallback(
    (...keys: string[]) => {
      const bookingAny = (booking as any) || {};
      for (const key of keys) {
        const raw = String(bookingAny?.[key] || '').trim();
        if (raw) return raw;
      }
      return '';
    },
    [booking]
  );

  const resolveDateTokenText = useCallback((raw: any): string => {
    if (!raw) return '';
    if (typeof raw?.toDate === 'function') {
      const dt = raw.toDate();
      if (dt instanceof Date && !Number.isNaN(dt.getTime())) return formatDDMMYYYY(dt);
    }
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return formatDDMMYYYY(raw);
    const text = String(raw || '').trim();
    if (!text) return '';
    const fromYmd = dateFromYMD(text);
    if (fromYmd) return formatDDMMYYYY(fromYmd);
    const fromDdMmYyyy = dateFromDDMMYYYY(text);
    if (fromDdMmYyyy) return formatDDMMYYYY(fromDdMmYyyy);
    if (/^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}$/.test(text)) return '';
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return formatDDMMYYYY(parsed);
    return text;
  }, []);

  const resolveBookingDateToken = useCallback(
    (...keys: string[]) => {
      const bookingAny = (booking as any) || {};
      for (const key of keys) {
        const tokenText = resolveDateTokenText(bookingAny?.[key]);
        if (tokenText) return tokenText;
      }
      return '';
    },
    [booking, resolveDateTokenText]
  );

  const resolveWorkflowInputTextToken = useCallback(
    (...keys: string[]) => {
      const raw = getWorkflowInputValueByFieldKey(...keys);
      if (raw == null) return '';
      if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : '';
      if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
      return String(raw).trim();
    },
    [getWorkflowInputValueByFieldKey]
  );

  const resolveWorkflowInputDateToken = useCallback(
    (...keys: string[]) => resolveDateTokenText(getWorkflowInputValueByFieldKey(...keys)),
    [getWorkflowInputValueByFieldKey, resolveDateTokenText]
  );

  const saleAgreementDateDisplay = useMemo(
    () =>
      resolveWorkflowInputDateToken(
        'agreement_date',
        'agreementDate',
        'sale_agreement_date',
        'saleAgreementDate'
      ) ||
      resolveBookingDateToken('agreementDate', 'agreement_date', 'sale_agreement_date', 'saleAgreementDate') ||
      '—',
    [resolveBookingDateToken, resolveWorkflowInputDateToken]
  );

  const saleDeedDateDisplay = useMemo(
    () =>
      resolveWorkflowInputDateToken(
        'deed_date',
        'deedDate',
        'sale_deed_date',
        'saleDeedDate'
      ) ||
      resolveBookingDateToken('deedDate', 'deed_date', 'sale_deed_date', 'saleDeedDate') ||
      '—',
    [resolveBookingDateToken, resolveWorkflowInputDateToken]
  );

  const bookingFormDraftDoc = useMemo(() => {
    const byId =
      (bookingDraftDocuments || []).find(
        (row: any) => String((row as any)?.id || '').trim() === 'booking_form_v1'
      ) || null;
    if (byId) return byId as any;
    const byTemplate =
      (bookingDraftDocuments || []).find(
        (row: any) => String((row as any)?.templateId || '').trim() === 'booking_form_v1'
      ) || null;
    if (byTemplate) return byTemplate as any;
    const byType =
      (bookingDraftDocuments || []).find(
        (row: any) => String((row as any)?.type || '').trim().toLowerCase() === 'bookingformdraft'
      ) || null;
    if (byType) return byType as any;
    return ((bookingDocsByKey.bookingFormDraft as any) || (bookingDocsByKey['booking_form_v1'] as any) || null) as any;
  }, [bookingDocsByKey, bookingDraftDocuments]);

  const bookingFormDraftFirstFile = useMemo(() => {
    return (bookingFormDraftDoc as any)?.files?.[0] || null;
  }, [bookingFormDraftDoc]);

  const headerBookingFormFile = bookingFormDraftFirstFile || availableUnitDraftHeaderFile;

  const bookingFormCreateHoverDetails = useMemo(() => {
    const hasDraft = !!(bookingFormDraftFirstFile?.url || (bookingFormDraftDoc as any)?.files?.length);
    if (!hasDraft) return null;
    const toMs = (value: any): number | null => {
      if (!value) return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      if (value instanceof Date) return value.getTime();
      if (typeof value?.toMillis === 'function') return Number(value.toMillis() || 0) || null;
      const parsed = Date.parse(String(value));
      return Number.isFinite(parsed) ? parsed : null;
    };
    const generatedAtMs =
      Number(bookingFormDraftFirstFile?.uploadedAtMs || 0) ||
      toMs((bookingFormDraftDoc as any)?.updatedAt) ||
      toMs((bookingFormDraftDoc as any)?.createdAt) ||
      toMs((booking as any)?.updatedAt) ||
      null;
    const bookingFormDateRaw = String((booking as any)?.bookingFormDate || '').trim();
    const bookingFormTimeRaw = String((booking as any)?.bookingFormTime || '').trim();
    const bookingFormDateObj = dateFromYMD(bookingFormDateRaw);
    const enteredDateTime = bookingFormDateObj
      ? `${formatDDMMYYYY(bookingFormDateObj)}${bookingFormTimeRaw ? ` ${bookingFormTimeRaw}` : ''}`
      : '';
    const refById = String((booking as any)?.refByBrokerId || '').trim();
    const refByNameDirect = String((booking as any)?.refByBrokerName || '').trim();
    const refByFromBroker =
      refById
        ? String(
            brokers.find((b) => String(b?.id || '').trim() === refById)?.brokerName ||
            brokers.find((b) => String(b?.id || '').trim() === refById)?.displayName ||
            ''
          ).trim()
        : '';
    const refBy = refByNameDirect || refByFromBroker;
    const rankRaw = (booking as any)?.rank;
    const rank =
      typeof rankRaw === 'number' && Number.isFinite(rankRaw)
        ? String(rankRaw)
        : String(rankRaw || '').trim();
    const terms = String((booking as any)?.paymentTerms || '').trim();
    const schemeEnabled = Boolean((booking as any)?.schemeEnabled);
    const schemeRemark = String((booking as any)?.schemeRemark || '').trim();
    const fileName = String(
      bookingFormDraftFirstFile?.originalName || bookingFormDraftFirstFile?.name || 'BOOKING FORM DRAFT'
    ).trim();
    return {
      generatedAt: generatedAtMs ? formatDateTimeAmPm(generatedAtMs) : '',
      enteredDateTime,
      refBy,
      rank,
      terms,
      scheme: schemeEnabled ? 'Yes' : 'No',
      schemeRemark,
      fileName,
    };
  }, [booking, bookingFormDraftDoc, bookingFormDraftFirstFile, brokers]);

  // Notify parent page of live computed data (for sidebar display)
  useEffect(() => {
    if (!onComputedData) return;
    onComputedData({
      customerName: primaryCustomerHeaderName,
      bookingId: bookingId || null,
      customerReceiptTotal,
      loanDisbursementReceiptTotal,
      loanToDeveloperAmount,
      gstReceiptTotal,
      saleAgreementDate: saleAgreementDateDisplay === '—' ? null : saleAgreementDateDisplay,
      saleDeedDate: saleDeedDateDisplay === '—' ? null : saleDeedDateDisplay,
      customers: customers.map((c, idx) => ({
        name: c.name,
        initials: c.name.split(' ').filter(Boolean).map(p => p[0] || '').join('').slice(0, 2).toUpperCase(),
        role: c.role || (idx === 0 ? 'Primary applicant' : 'Co-applicant'),
        mobile: c.mobile || '',
        gender: c.gender || null,
      })),
      gstDueAmount: computeGstAmount(typeof unit?.unitAmount === 'number' ? unit.unitAmount : 0),
      stampDutyReceiptTotal,
      registrationReceiptTotal,
      hasDraftDocuments: visibleDraftSectionRows.filter((row: any) => (row?.files || []).some((f: any) => !!f?.url)).length > 0,
      bookingFormHoverDetails: bookingFormCreateHoverDetails,
      bookingFormUrl: String(headerBookingFormFile?.url || '') || null,
      bookingFormFileName: String(headerBookingFormFile?.originalName || headerBookingFormFile?.name || 'BOOKING_FORM_DRAFT.pdf') || null,
      sectionAvailability: {
        customers: customers.length > 0,
        changeOrders: relatedChangeOrders.length > 0,
        payments: customerReceiptRows.length > 0 || customerRefundRows.length > 0 || gstReceiptRows.length > 0,
        paymentRoadmap: Number(unit?.unitAmount || 0) > 0 || Boolean((booking as any)?.paymentCondition),
        documentsLoan:
          visibleMainDocumentDefs.length > 0 ||
          visibleLoanDocumentDefs.length > 0 ||
          visibleDraftSectionRows.some((row: any) => (row?.files || []).some((file: any) => !!file?.url)) ||
          visibleLoanSectionDraftRows.some((row: any) => (row?.files || []).some((file: any) => !!file?.url)) ||
          Boolean(loanRequired),
      },
    });
  }, [onComputedData, primaryCustomerHeaderName, bookingId, customerReceiptTotal, loanDisbursementReceiptTotal, loanToDeveloperAmount, gstReceiptTotal, stampDutyReceiptTotal, registrationReceiptTotal, computeGstAmount, unit?.unitAmount, saleAgreementDateDisplay, saleDeedDateDisplay, customers, relatedChangeOrders, customerReceiptRows, customerRefundRows, gstReceiptRows, booking?.paymentCondition, visibleMainDocumentDefs, visibleLoanDocumentDefs, visibleDraftSectionRows, visibleLoanSectionDraftRows, loanRequired, bookingFormCreateHoverDetails, headerBookingFormFile]);

  const coerceAnyDateToYmd = useCallback((raw: any): string => {
    if (!raw) return '';
    if (typeof raw?.toDate === 'function') {
      const dt = raw.toDate();
      if (dt instanceof Date && !Number.isNaN(dt.getTime())) return formatYMD(dt);
    }
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return formatYMD(raw);
    const text = String(raw || '').trim();
    if (!text) return '';
    const fromYmd = dateFromYMD(text);
    if (fromYmd) return formatYMD(fromYmd);
    const fromDdMmYyyy = dateFromDDMMYYYY(text);
    if (fromDdMmYyyy) return formatYMD(fromDdMmYyyy);
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return formatYMD(parsed);
    return '';
  }, []);

  const saleAgreementDateInputYmd = useMemo(() => {
    const workflowRaw = getWorkflowInputValueByFieldKey(
      'agreement_date',
      'agreementDate',
      'sale_agreement_date',
      'saleAgreementDate'
    );
    const fromWorkflow = coerceAnyDateToYmd(workflowRaw);
    if (fromWorkflow) return fromWorkflow;
    const bookingAny = (booking as any) || {};
    return (
      coerceAnyDateToYmd(bookingAny?.agreementDate) ||
      coerceAnyDateToYmd(bookingAny?.agreement_date) ||
      coerceAnyDateToYmd(bookingAny?.sale_agreement_date) ||
      coerceAnyDateToYmd(bookingAny?.saleAgreementDate) ||
      ''
    );
  }, [booking, coerceAnyDateToYmd, getWorkflowInputValueByFieldKey]);

  useEffect(() => {
    setSaleAgreementDateEditYmd(saleAgreementDateInputYmd);
  }, [saleAgreementDateInputYmd]);

  const saleDeedDateInputYmd = useMemo(() => {
    const workflowRaw = getWorkflowInputValueByFieldKey(
      'deed_date',
      'deedDate',
      'sale_deed_date',
      'saleDeedDate'
    );
    const fromWorkflow = coerceAnyDateToYmd(workflowRaw);
    if (fromWorkflow) return fromWorkflow;
    const bookingAny = (booking as any) || {};
    return (
      coerceAnyDateToYmd(bookingAny?.deedDate) ||
      coerceAnyDateToYmd(bookingAny?.deed_date) ||
      coerceAnyDateToYmd(bookingAny?.sale_deed_date) ||
      coerceAnyDateToYmd(bookingAny?.saleDeedDate) ||
      ''
    );
  }, [booking, coerceAnyDateToYmd, getWorkflowInputValueByFieldKey]);

  useEffect(() => {
    setSaleDeedDateEditYmd(saleDeedDateInputYmd);
  }, [saleDeedDateInputYmd]);

  const buildWorkflowEmailTokenValues = useCallback(async () => {
    if (!projectId || !unit?.id) return {} as Record<string, string>;

    const [projectSnap, transactionSnap] = await Promise.all([
      getDoc(doc(db, 'projects', projectId)),
      getDocs(
        query(
          collection(db, 'projects', projectId, 'bankTransactions'),
          where('unitId', '==', unit.id),
          limit(300)
        )
      ).catch(() => ({ docs: [] } as any)),
    ]);

    const projectRow = projectSnap.exists() ? ({ id: projectSnap.id, ...(projectSnap.data() as any) }) : null;
    const cpId = String(projectRow?.companyProfileId || '').trim();
    const companySnap = cpId ? await getDoc(doc(db, 'companyProfiles', cpId)) : null;
    const companyProfileRow = companySnap?.exists()
      ? ({ id: companySnap.id, ...(companySnap.data() as any) })
      : null;

    const transactionRows = Array.isArray((transactionSnap as any)?.docs)
      ? (transactionSnap as any).docs.map((d: any) => ({ id: d.id, ...(d.data() as any) }))
      : [];
    const unitAmountValue = Number((unit as any)?.unitAmount || 0) || 0;
    const gstValue = computeGstAmount(unitAmountValue);
    const formatAmount = (value: number) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : '');
    const paymentTemplateTokens = buildPaymentTemplateTokens({
      transactions: transactionRows,
      unitAmount: unitAmountValue,
      gstAmount: gstValue,
      formatDate: formatDDMMYYYY,
      formatAmount,
    });

    const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
    const allotteeNames = customers
      .map((c) => String(c.displayName || c.name || '').trim())
      .filter(Boolean);
    const agreementNumberToken =
      resolveWorkflowInputTextToken('agreement_number', 'agreementNumber', 'sale_agreement_number', 'saleAgreementNumber') ||
      resolveBookingTextToken('agreementNumber', 'agreement_number', 'saleAgreementNumber');
    const deedNumberToken =
      resolveWorkflowInputTextToken('deed_number', 'deedNumber') ||
      resolveBookingTextToken('deedNumber', 'deed_number', 'saleDeedNumber');
    const agreementDateToken =
      resolveWorkflowInputDateToken('agreement_date', 'agreementDate', 'sale_agreement_date', 'saleAgreementDate') ||
      resolveBookingDateToken('agreementDate', 'agreement_date', 'sale_agreement_date', 'saleAgreementDate');
    const agreementDateResolved =
      String(agreementDateToken || '').trim() ||
      (saleAgreementDateDisplay === '—' ? '' : String(saleAgreementDateDisplay || '').trim());
    const deedDateToken =
      resolveWorkflowInputDateToken('deed_date', 'deedDate') ||
      resolveBookingDateToken('deedDate', 'deed_date', 'saleDeedDate');
    const agreementLinkToken = resolveBookingTextToken('agreementLink', 'agreement_link', 'saleAgreementLink');
    const deedLinkToken = resolveBookingTextToken('deedLink', 'deed_link', 'saleDeedLink');

    const joinNamesCommaAnd = (names: string[]) => {
      const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
      if (clean.length <= 1) return clean[0] || '';
      if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
      return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
    };

    return {
      ...paymentTemplateTokens,
      '{{project_name}}': String(projectRow?.name || projectName || ''),
      '{{unit_no}}': unitNo,
      '{{unit_number}}': unitNo,
      '{{allottee_names}}': allotteeNames.join(', '),
      '{{allottee_name}}': allotteeNames.join(', '),
      '{{allottee_primary_name}}': allotteeNames[0] || '',
      '{{allottee_names_lines}}': allotteeNames.join('<br/>'),
      '{{allottee_names_comma_and}}': joinNamesCommaAnd(allotteeNames),
      '{{amount}}': formatAmount(unitAmountValue),
      '{{unit_amount}}': formatAmount(unitAmountValue),
      '{{issue_date}}': formatDDMMYYYY(new Date()),
      '{{carpet_area}}': String((unit as any)?.carpetArea ?? ''),
      '{{area_of_exclusive_balcony}}': String((unit as any)?.exclusiveBalconyArea ?? ''),
      '{{area_of_exclusive_wash}}': String(
        (unit as any)?.exclusiveWashArea ??
          (unit as any)?.areaOfExclusiveWash ??
          (unit as any)?.area_of_exclusive_wash ??
          (unit as any)?.customFields?.area_of_exclusive_wash ??
          (unit as any)?.['AREA OF EXCLUSIVE WASH'] ??
          ''
      ),
      '{{area_of_exclusive_open_terrace}}': String(
        (unit as any)?.openTerraceArea ??
          (unit as any)?.areaOfExclusiveOpenTerrace ??
          (unit as any)?.area_of_exclusive_open_terrace ??
          (unit as any)?.customFields?.area_of_exclusive_open_terrace ??
          (unit as any)?.['AREA OF EXCLUSIVE OPEN TERRACE'] ??
          ''
      ),
      '{{vanvechayel_area}}': String((unit as any)?.vanvechayelArea || (unit as any)?.vanvechayel_area || ''),
      '{{built_up_area}}': String((unit as any)?.builtUpArea || (unit as any)?.built_up_area || ''),
      '{{building_no}}': String((unit as any)?.buildingNo || (unit as any)?.building_no || ''),
      '{{floor_eng}}': String((unit as any)?.floorEng || (unit as any)?.floor_eng || ''),
      ...buildUniversalOfficeTemplateTokens({
        primaryCustomerMobile,
        loanBankName: latestLoanSanctionData?.sanctionBankName,
        loanSanctionAmount: latestLoanSanctionData?.sanctionAmount,
        reasonNotTakingSbi: latestLoanSanctionData?.nonSbiReason,
        pendingAmount: unitAmountValue - customerReceiptTotal,
        formatAmount,
      }),
      '{{gst}}': formatAmount(gstValue),
      '{{agreement_number}}': agreementNumberToken,
      '{{deed_number}}': deedNumberToken,
      '{{agreement_date}}': agreementDateResolved,
      '{{sale_agreement_date}}': agreementDateResolved,
      '{{deed_date}}': deedDateToken,
      '{{agreement_link}}': agreementLinkToken,
      '{{deed_link}}': deedLinkToken,
      '{{company_logo_url}}': String(resolveCompanyLogoForTemplate(companyProfileRow, null) || ''),
      '{{project_logo_url}}': resolveProjectLogoForTemplate(projectRow, null),
      '{{project_document_image_url}}': String(projectRow?.documentImageUrl || ''),
      '{{company_address}}': String(companyProfileRow?.address || ''),
      '{{company_mobile}}': String(companyProfileRow?.mobile || ''),
      '{{company_email}}': String(companyProfileRow?.email || ''),
      '{{company_name}}': String(companyProfileRow?.name || ''),
    } as Record<string, string>;
  }, [
    computeGstAmount,
    customerReceiptTotal,
    customers,
    db,
    latestLoanSanctionData?.nonSbiReason,
    latestLoanSanctionData?.sanctionAmount,
    latestLoanSanctionData?.sanctionBankName,
    primaryCustomerMobile,
    projectId,
    projectName,
    resolveBookingDateToken,
    resolveBookingTextToken,
    resolveWorkflowInputDateToken,
    resolveWorkflowInputTextToken,
    saleAgreementDateDisplay,
    unit,
  ]);

  const isStepInputsComplete = useCallback(
    (which: 'main' | 'loan', step: WorkflowStepComputed) => {
      if (step.kind === 'payment') {
        return isPaymentStepSatisfied(which, step.key, customerReceiptTotal, gstReceiptTotal);
      }
      if (which === 'main' && step.key === OFFICE_MAIN_STEP_KEYS.customerKycBookingForm) return step1ReadyToComplete;
      if (
        which === 'main' &&
        step.key === OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter
      ) {
        const stepData = getCaseStepData(which, step.key);
        const stage2Satisfied = isPaymentStepSatisfied(
          'main',
          OFFICE_MAIN_STEP_KEYS.downpaymentGst,
          customerReceiptTotal,
          gstReceiptTotal
        );
        return (
          Number(stepData?.allotmentLetterDraftGeneratedAt || 0) > 0 &&
          step1ReadyToComplete &&
          stage2Satisfied &&
          customerReceiptTotal > 0
        );
      }
      if (which === 'main' && step.key === OFFICE_MAIN_STEP_KEYS.sendToAdvocate) {
        const stepData = getCaseStepData(which, step.key);
        const sent = !!stepData?.sentToAdvocate;
        const docRow = bookingDocsByKey.allotmentLetterDraft;
        const uploaded = !!docRow?.files?.length;
        return sent && uploaded;
      }
      if (which === 'main' && step.key === OFFICE_MAIN_STEP_KEYS.signAtAdvocate) {
        const stepData = getCaseStepData(which, step.key);
        const sentForSign = !!stepData?.customerSentForSign;
        return sentForSign;
      }
      if (which === 'main' && step.key === OFFICE_MAIN_STEP_KEYS.uploadSignedDocs) {
        const docRow = bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY];
        return !!docRow?.files?.length && advocateAgreementReceiptTotal > 0;
      }
      if (which === 'loan' && step.key === OFFICE_LOAN_STEP_KEYS.collectDocs) {
        const stepData = getCaseStepData(which, step.key);
        const checklist = (stepData?.loanDocChecklist || {}) as Record<string, boolean>;
        return LOAN_COLLECT_DOC_SUBTASKS.every((item) => Boolean(checklist[item.key]));
      }

      const stepData = getCaseStepData(which, step.key);
      const fields = step.fields || [];
      const docs = step.requiredDocs || [];

      for (const f of fields) {
        if (!f.required) continue;
        const v = stepData[f.key];
        if (f.type === 'boolean') {
          if (typeof v !== 'boolean') return false;
        } else if (f.type === 'number') {
          if (typeof v !== 'number' || !Number.isFinite(v)) return false;
        } else if (f.type === 'date') {
          if (!String(v || '').trim()) return false;
        } else {
          if (!String(v || '').trim()) return false;
        }
      }

      for (const d of docs) {
        if (d.required === false) continue;
        const docRow = bookingDocsByKey[d.key];
        if (!docRow?.files?.length) return false;
      }

      return true;
    },
    [
      advocateAgreementReceiptTotal,
      bookingDocsByKey,
      customerReceiptTotal,
      getCaseStepData,
      gstReceiptTotal,
      isPaymentStepSatisfied,
      step1ReadyToComplete,
    ]
  );

  const getStepDetails = useCallback(
    (which: 'main' | 'loan', stepKey: string) => {
      const stepData = (which === 'main' ? (mainCase as any)?.stepData?.[stepKey] : (loanCase as any)?.stepData?.[stepKey]) || {};
      const details: string[] = [];

      if (which === 'main' && stepKey === OFFICE_MAIN_STEP_KEYS.customerKycBookingForm) {
        if (customers.length) {
          details.push(...customers.map((c) => `${c.displayName || c.name} • ${c.mobile}`));
        }
        const kycTypes = Array.from(new Set((kycDocs || []).map((k) => String(k.type || k.id || '').toUpperCase()).filter(Boolean)));
        if (kycTypes.length) details.push(`KYC: ${kycTypes.join(', ')}`);
        const bookingFormName = bookingFormDoc?.files?.[0]?.name || null;
        if (bookingFormName) details.push(`Booking form: ${bookingFormName}`);
      }

      if (typeof stepData?.summary === 'string' && stepData.summary.trim()) details.push(stepData.summary.trim());
      if (Array.isArray(stepData?.details)) {
        for (const d of stepData.details) {
          if (typeof d === 'string' && d.trim()) details.push(d.trim());
        }
      }

      return details;
    },
    [bookingFormDoc?.files, customers, kycDocs, loanCase, mainCase]
  );

  const renderOptionsQuestion = (args: {
    title: string;
    value: string;
    options: Array<{ key: string; label: string }>;
    onSelect: (nextKey: string) => void;
    helperText?: string | null;
    compact?: boolean;
    inlineOptions?: boolean;
  }) => {
    return (
      <Paper sx={args.compact ? qaCardCompactSx : qaCardSx}>
        <Stack
          direction={args.inlineOptions ? { xs: 'column', sm: 'row' } : 'column'}
          spacing={args.inlineOptions ? 1 : 0}
          alignItems={args.inlineOptions ? { xs: 'stretch', sm: 'center' } : 'stretch'}
          justifyContent={args.inlineOptions ? 'space-between' : 'flex-start'}
        >
          <Box sx={{ minWidth: 0, flex: args.inlineOptions ? 1 : undefined }}>
            <Typography
              sx={{
                fontWeight: 700,
                mb: args.inlineOptions ? 0 : args.compact ? 0.5 : 1,
                ...(args.inlineOptions
                  ? { fontSize: '0.95rem', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
                  : {}),
              }}
            >
              {args.title}
            </Typography>
            {args.helperText ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: args.compact ? 0.5 : 1 }}>
                {args.helperText}
              </Typography>
            ) : null}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            rowGap={args.compact ? 0.5 : 1}
            sx={
              args.inlineOptions
                ? {
                    justifyContent: 'flex-end',
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    gap: 0.5,
                    minWidth: 0,
                    ml: { xs: 0, sm: 0.5 },
                    width: { xs: '100%', sm: 'auto' },
                  }
                : undefined
            }
          >
          {args.options.map((o) => {
            const selected = args.value === o.key;
            return (
              <Button
                key={o.key}
                size="small"
                onClick={() => args.onSelect(o.key)}
                startIcon={selected ? <CheckOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} /> : undefined}
                sx={{
                  ...(args.compact ? qaOptionButtonCompactSx : qaOptionButtonSx),
                  bgcolor: selected
                    ? 'rgba(25,118,210,0.10)'
                    : ((args.compact ? qaOptionButtonCompactSx : qaOptionButtonSx) as any).bgcolor,
                  borderColor: selected ? 'rgba(25,118,210,0.30)' : 'rgba(15,23,42,0.12)',
                  ...(args.inlineOptions
                    ? {
                        minWidth: 34,
                        minHeight: 28,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 1.25,
                        fontSize: '0.8rem',
                      }
                    : {}),
                }}
              >
                {o.label}
              </Button>
            );
          })}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderTextQuestion = (args: {
    title: string;
    value: string;
    onChange: (next: string) => void;
    onBlur?: () => void;
    multiline?: boolean;
    placeholder?: string;
    type?: 'text' | 'number' | 'date';
    compact?: boolean;
  }) => {
    return (
      <Paper sx={args.compact ? qaCardCompactSx : qaCardSx}>
        <Typography sx={{ fontWeight: 700, mb: args.compact ? 0.5 : 1 }}>
          {args.title}
        </Typography>
        <TextField
          type={args.type || 'text'}
          value={args.value}
          onChange={(e) => args.onChange(e.target.value)}
          onBlur={args.onBlur}
          fullWidth
          size="small"
          placeholder={args.placeholder}
          multiline={!!args.multiline}
          minRows={args.multiline ? 3 : undefined}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              color: 'text.primary',
              '& fieldset': { borderColor: 'rgba(15,23,42,0.12)' },
              '&:hover fieldset': { borderColor: 'rgba(15,23,42,0.18)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(25,118,210,0.55)' },
            },
            '& .MuiInputBase-input::placeholder': { color: 'rgba(15,23,42,0.45)' },
          }}
        />
      </Paper>
    );
  };

  const getDocUploadActionLabel = (docKey: string, uploaded: boolean) => {
    const phase = stepDocUploadPhase[docKey] || 'idle';
    if (phase === 'processing') return 'Processing…';
    if (phase === 'reducing') return 'Reducing…';
    if (phase === 'uploading') return 'Uploading…';
    if (phase === 'uploaded' || uploaded) return 'Uploaded';
    return 'Upload';
  };

  const isDocUploadBusy = (docKey: string) => {
    const phase = stepDocUploadPhase[docKey] || 'idle';
    return phase === 'processing' || phase === 'reducing' || phase === 'uploading';
  };

  const renderUploadQuestion = (args: {
    title: string;
    docKey: string;
    existing?: BookingDocumentDoc | null;
    compact?: boolean;
    hideStatusChip?: boolean;
    instantUpload?: boolean;
    subtitle?: string;
  }) => {
    const picked = stepDocPickedFiles[args.docKey] || null;
    const uploading = isDocUploadBusy(args.docKey);
    const uploaded = !!args.existing?.files?.length || stepDocUploadPhase[args.docKey] === 'uploaded';
    const uploadActionLabel = getDocUploadActionLabel(args.docKey, uploaded);
    return (
      <Paper sx={args.compact ? qaCardCompactSx : qaCardSx}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: args.compact ? 0.5 : 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>{args.title}</Typography>
            {args.subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.125, lineHeight: 1.2 }}>
                {args.subtitle}
              </Typography>
            ) : null}
          </Box>
          {args.instantUpload ? (
            <Button
              size="small"
              variant="contained"
              color={uploaded ? 'secondary' : 'primary'}
              component="label"
              startIcon={<UploadFileOutlinedIcon />}
              sx={{ textTransform: 'none', fontWeight: 700 }}
              disabled={uploading}
            >
              {uploadActionLabel === 'Upload' ? 'Upload' : uploadActionLabel}
              <input
                hidden
                type="file"
                accept="image/*,application/pdf"
                onChange={async (e) => {
                  const f = (e.target.files || [])[0] || null;
                  if (!f) return;
                  const input = e.target as HTMLInputElement;
                  if (input) input.value = '';
                  setStepDocPickedFiles((prev) => ({ ...prev, [args.docKey]: f }));
                  setStepDocUploadPhase((prev) => ({ ...prev, [args.docKey]: 'idle' }));
                  await uploadStepRequiredDoc(args.docKey, args.title, f);
                }}
              />
            </Button>
          ) : !args.hideStatusChip ? (
            <Chip
              size="small"
              label={uploaded ? 'Uploaded' : 'Pending'}
              color={uploaded ? 'success' : 'warning'}
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>
        {args.existing?.files?.length ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={args.compact ? 0.5 : 1} sx={{ mb: args.compact ? 0.5 : 1 }}>
            {args.existing.files.map((f, idx) => (
              <Button
                key={`${args.docKey}_file_${idx}`}
                size="small"
                variant="text"
                onClick={() =>
                  f.url
                    ? triggerNamedDownload(
                        f.url,
                        String(args.title || f.name || 'Document'),
                        String(f.originalName || f.name || '')
                      )
                    : undefined
                }
                sx={{ color: 'primary.main', textTransform: 'none' }}
              >
                Download: {f.name || 'file'}
              </Button>
            ))}
          </Stack>
        ) : null}
        {!args.instantUpload ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={args.compact ? 0.5 : 1} alignItems={{ sm: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                component="label"
                startIcon={<UploadFileOutlinedIcon />}
                sx={{
                  ...qaOptionButtonSx,
                }}
                disabled={uploading}
              >
                Choose file
                <input
                  hidden
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={async (e) => {
                    const f = (e.target.files || [])[0] || null;
                    if (!f) return;
                    const input = e.target as HTMLInputElement;
                    if (input) input.value = '';
                    setStepDocPickedFiles((prev) => ({ ...prev, [args.docKey]: f }));
                    setStepDocUploadPhase((prev) => ({ ...prev, [args.docKey]: 'idle' }));
                    await uploadStepRequiredDoc(args.docKey, args.title, f);
                  }}
                />
              </Button>
              {picked ? (
                <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {picked.name}
                </Typography>
              ) : null}
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="contained"
                color={uploaded ? 'secondary' : 'primary'}
                onClick={() => uploadStepRequiredDoc(args.docKey, args.title)}
                disabled={uploading || !picked}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {uploadActionLabel}
              </Button>
          </Stack>
        ) : null}
      </Paper>
    );
  };

  const tsToMs = (ts: any): number | null => {
    if (!ts) return null;
    if (typeof ts === 'number') return ts;
    if (typeof ts?.toMillis === 'function') return ts.toMillis();
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
    return null;
  };

  const sectionSx = useMemo(
    () => ({
      border: '1px solid #e4dfd6',
      borderRadius: '12px',
      p: 2,
      bgcolor: '#ffffff',
    }),
    []
  );

  const updateStep1Data = async (patch: Partial<{ expectedApplicantsCount: number }>) => {
    if (!unit?.id || !projectId || !bookingId) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    const key = OFFICE_MAIN_STEP_KEYS.customerKycBookingForm;
    const refDoc = doc(db, 'projects', projectId, 'units', unit.id, 'cases', bookingId);
    const existing = (mainCase as any)?.stepData?.[key] || {};
    await updateDoc(refDoc, {
      [`stepData.${key}`]: {
        ...existing,
        ...patch,
      },
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    });
  };

  const updateStepField = useCallback(async (which: 'main' | 'loan', stepKey: string, fieldKey: string, value: any) => {
    const unitId = String(unit?.id || '').trim();
    const pid = String(projectId || '').trim();
    const bid = String(bookingId || '').trim();
    if (!unitId || !pid || !bid) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    const caseId = which === 'main' ? bid : `loan_${bid}`;
    const refDoc = doc(db, 'projects', pid, 'units', unitId, 'cases', caseId);
    const existing = (which === 'main' ? (mainCase as any) : (loanCase as any))?.stepData?.[stepKey] || {};
    await updateDoc(refDoc, {
      [`stepData.${stepKey}`]: {
        ...existing,
        [fieldKey]: value,
      },
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    });
  }, [bookingId, currentUser?.email, db, loanCase, mainCase, projectId, unit?.id]);

  const updateStepFields = useCallback(async (which: 'main' | 'loan', stepKey: string, patch: Record<string, any>) => {
    const unitId = String(unit?.id || '').trim();
    const pid = String(projectId || '').trim();
    const bid = String(bookingId || '').trim();
    if (!unitId || !pid || !bid) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    const caseId = which === 'main' ? bid : `loan_${bid}`;
    const refDoc = doc(db, 'projects', pid, 'units', unitId, 'cases', caseId);
    const existing = (which === 'main' ? (mainCase as any) : (loanCase as any))?.stepData?.[stepKey] || {};
    await updateDoc(refDoc, {
      [`stepData.${stepKey}`]: {
        ...existing,
        ...patch,
      },
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    });
  }, [bookingId, currentUser?.email, db, loanCase, mainCase, projectId, unit?.id]);

  const saveYesNoAnswer = useCallback(async (which: 'main' | 'loan', step: WorkflowDefinitionStep, answer: boolean) => {
    const unitId = String(unit?.id || '').trim();
    const pid = String(projectId || '').trim();
    const bid = String(bookingId || '').trim();
    if (!unitId || !pid || !bid) throw new Error('Booking is not ready.');
    if (!currentUser?.email) throw new Error('Not signed in.');
    const workflowCase = (which === 'main' ? mainCase : loanCase) as any;
    const definition = which === 'main' ? mainWorkflowDefinition : loanWorkflowDefinition;
    const definitionSteps = normalizeDefinitionSteps(definition?.steps || []);
    const gate = yesAnswerDependency(step.key);
    const branchSteps = definitionSteps.filter((candidate) => candidate.dependsOnSteps?.includes(gate));
    const isDecision = branchSteps.length > 0;
    const done = answer || isDecision;
    const fieldKey = String(step.fieldKey || step.key).trim();
    const existing = workflowCase?.stepData?.[step.key] || {};
    const oldAnswer = existing[fieldKey];
    if (typeof oldAnswer === 'boolean' && oldAnswer !== answer) {
      const hasBranchRecord = branchSteps.some((candidate) =>
        workflowCase?.completedStepKeys?.includes(candidate.key) ||
        workflowCase?.stepState?.[candidate.key]?.done ||
        workflowCase?.stepState?.[candidate.key]?.doneAt ||
        (candidate.docKey && bookingDocsByKey?.[candidate.docKey]?.files?.length)
      );
      if (hasBranchRecord) throw new Error('Fit-out work is already recorded. Review or reverse those records before changing this answer.');
      if (!window.confirm(`Change this answer from ${oldAnswer ? 'Yes' : 'No'} to ${answer ? 'Yes' : 'No'}?`)) return;
    }
    const caseRef = doc(db, 'projects', pid, 'units', unitId, 'cases', which === 'main' ? bid : `loan_${bid}`);
    await updateDoc(caseRef, {
      [`stepData.${step.key}`]: { ...existing, [fieldKey]: answer, taskDone: done },
      completedStepKeys: done ? arrayUnion(step.key) : arrayRemove(step.key),
      [`stepState.${step.key}.done`]: done,
      [`stepState.${step.key}.doneAt`]: done ? serverTimestamp() : deleteField(),
      [`stepState.${step.key}.doneAtMs`]: done ? Date.now() : deleteField(),
      [`stepState.${step.key}.doneBy`]: done ? currentUser.email : deleteField(),
      [`stepState.${step.key}.doneSource`]: done ? 'manual' : deleteField(),
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    });
  }, [bookingDocsByKey, bookingId, currentUser?.email, db, loanCase, loanWorkflowDefinition, mainCase, mainWorkflowDefinition, projectId, unit?.id]);

  const refreshPaymentDetailsDraftAfterPaymentSave = useCallback(
    async (txnId: string) => {
      if (!projectId || !txnId) return null;
      const txnSnap = await getDoc(doc(db, 'projects', projectId, 'bankTransactions', txnId));
      const txn = txnSnap.exists() ? (txnSnap.data() as any) : null;
      const txnType = String(txn?.txnType || '').toLowerCase();
      const receiptCategory = String(txn?.receiptCategory || '').toLowerCase();
      return { txn, txnType, receiptCategory };
    },
    [db, projectId]
  );

  const handleUnifiedPaymentSaved = useCallback(
    async (txnId: string, stepConfig?: { which: 'main' | 'loan'; key: string }) => {
      if (!projectId || !txnId) return;
      try {
        const saved = await refreshPaymentDetailsDraftAfterPaymentSave(txnId);
        const receiptCategory = String(saved?.receiptCategory || '').toLowerCase();
        if (!stepConfig) {
          setPendingPaymentStep(null);
          return;
        }
        if (String(saved?.txnType || '').toLowerCase() !== 'receipt' || receiptCategory === 'advocate') {
          setPendingPaymentStep(null);
          return;
        }
        if (receiptCategory === 'gst' && stepConfig.which === 'main') {
          await updateStepField('main', stepConfig.key, 'gstSkippedForNow', false);
        }
        const savedAmount = Number(saved?.txn?.amount || 0);
        const nextCustomerTotal = customerReceiptTotal + (receiptCategory === 'customer' ? savedAmount : 0);
        const nextGstTotal = gstReceiptTotal + (receiptCategory === 'gst' ? savedAmount : 0);
        if (isPaymentStepSatisfied(stepConfig.which, stepConfig.key, nextCustomerTotal, nextGstTotal)) {
          await setStepDone(stepConfig.which, stepConfig.key, true);
        }
        setPendingPaymentStep(null);
      } catch (err: any) {
        setError(err?.message || 'Payment saved, but workflow step update failed.');
      }
    },
    [customerReceiptTotal, gstReceiptTotal, isPaymentStepSatisfied, projectId, refreshPaymentDetailsDraftAfterPaymentSave, setStepDone, updateStepField]
  );

  const openUnifiedPayment = useCallback(
    (kind: 'dp' | 'gst' | 'advocateAgreement', stepConfig?: { which: 'main' | 'loan'; key: string }) => {
      setCustomerDrawerOpen(false);
      setPaymentDrawerOpen(false);
      setPaymentError(null);
      setPendingPaymentStep(stepConfig || null);
      const gstAmt = typeof unit?.unitAmount === 'number' ? computeGstAmount(unit.unitAmount) : undefined;
      openAddPayment({
        unitId: unit?.id ?? undefined,
        unitNumber: unit?.unitNumber ?? undefined,
        bookingId: booking?.id ?? undefined,
        partyName: primaryCustomerName || undefined,
        receiptCategory: kind === 'gst' ? 'gst' : kind === 'advocateAgreement' ? 'advocate' : 'customer',
        amount:
          kind === 'gst'
            ? gstAmt && gstAmt > 0
              ? gstAmt
              : undefined
            : kind === 'advocateAgreement' && projectAdvocateAgreementFee > 0
              ? projectAdvocateAgreementFee
              : undefined,
        onSaved: (txnId) => {
          void handleUnifiedPaymentSaved(txnId, stepConfig);
        },
      });
    },
    [
      booking?.id,
      computeGstAmount,
      handleUnifiedPaymentSaved,
      openAddPayment,
      primaryCustomerName,
      projectAdvocateAgreementFee,
      unit?.id,
      unit?.unitAmount,
      unit?.unitNumber,
    ]
  );

  const sendWorkflowEmailForStep = useCallback(
    async (
      which: 'main' | 'loan',
      step: WorkflowDefinitionStep,
      opts?: { toOverride?: string[] }
    ) => {
      const unitId = String(unit?.id || '').trim();
      const pid = String(projectId || '').trim();
      const bid = String(bookingId || '').trim();
      const stepKey = String(step?.key || '').trim();
      const templateKey = String((step as any)?.docKey || '').trim();
      if (!unitId || !pid || !bid || !stepKey) return;
      if (!currentUser?.email) throw new Error('Not signed in.');
      if (!templateKey) throw new Error('Email template key is missing on this step.');
      const phaseKey = `${which}:${stepKey}`;
      if (stepEmailPhase[phaseKey] === 'processing') return;
      setStepEmailPhase((prev) => ({ ...prev, [phaseKey]: 'processing' }));
      try {
        const tokenValues = await buildWorkflowEmailTokenValues();
        const functions = getFunctions();
        const sendWorkflowEmail = httpsCallable(functions, 'sendWorkflowEmail');
        const result = await sendWorkflowEmail({
          projectId: pid,
          unitId,
          bookingId: bid,
          stepKey,
          workflowType: which,
          templateKey,
          tokenValues,
          toOverride: Array.isArray(opts?.toOverride) ? opts?.toOverride : [],
        });
        const data = (result?.data || {}) as any;
        await updateStepFields(which, stepKey, {
          emailTemplateKey: templateKey,
          emailSentAtMs: Date.now(),
          emailSentBy: currentUser.email,
          emailSentTo: Array.isArray(data?.to) ? data.to : [],
          emailSubject: String(data?.subject || ''),
          emailMessageId: String(data?.messageId || ''),
        });
        await setStepDone(which, stepKey, true);
        setStepEmailPhase((prev) => ({ ...prev, [phaseKey]: 'done' }));
        window.setTimeout(
          () => setStepEmailPhase((prev) => ({ ...prev, [phaseKey]: 'idle' })),
          1800
        );
      } catch (err) {
        setStepEmailPhase((prev) => ({ ...prev, [phaseKey]: 'error' }));
        throw err;
      }
    },
    [
      bookingId,
      buildWorkflowEmailTokenValues,
      currentUser?.email,
      projectId,
      setStepDone,
      stepEmailPhase,
      unit?.id,
      updateStepFields,
    ]
  );

  const previewWorkflowEmailForStep = useCallback(
    async (which: 'main' | 'loan', step: WorkflowDefinitionStep) => {
      const unitId = String(unit?.id || '').trim();
      const pid = String(projectId || '').trim();
      const bid = String(bookingId || '').trim();
      const stepKey = String(step?.key || '').trim();
      const templateKey = String((step as any)?.docKey || '').trim();
      if (!unitId || !pid || !bid || !stepKey) return;
      if (!templateKey) throw new Error('Email template key is missing on this step.');
      const tokenValues = await buildWorkflowEmailTokenValues();
      const functions = getFunctions();
      const previewWorkflowEmail = httpsCallable(functions, 'previewWorkflowEmail');
      const result = await previewWorkflowEmail({
        projectId: pid,
        unitId,
        bookingId: bid,
        stepKey,
        workflowType: which,
        templateKey,
        tokenValues,
      });
      const data = (result?.data || {}) as any;
      return {
        templateName: String(data?.templateName || templateKey),
        to: Array.isArray(data?.to) ? data.to.map((v: any) => String(v || '').trim()).filter(Boolean) : [],
        subject: String(data?.subject || ''),
        bodyHtml: String(data?.bodyHtml || ''),
        attachments: Array.isArray(data?.attachments)
          ? data.attachments.map((row: any) => ({
            key: String(row?.key || ''),
            name: String(row?.name || ''),
            source: String(row?.source || '').toLowerCase() === 'uploaded' ? 'uploaded' : 'draft',
          }))
          : [],
      };
    },
    [bookingId, buildWorkflowEmailTokenValues, projectId, unit?.id]
  );

  const openEmailConfirmForStep = useCallback(
    async (which: 'main' | 'loan', step: WorkflowDefinitionStep) => {
      setEmailConfirmTarget({ which, step });
      setEmailConfirmPreview(null);
      setEmailConfirmError(null);
      setEmailConfirmOpen(true);
      setEmailConfirmLoading(true);
      try {
        const preview = await previewWorkflowEmailForStep(which, step);
        setEmailConfirmPreview(preview || null);
        setEmailConfirmToDraft((preview?.to || []).join(', '));
      } catch (err: any) {
        setEmailConfirmError(err?.message || 'Failed to load email preview.');
      } finally {
        setEmailConfirmLoading(false);
      }
    },
    [previewWorkflowEmailForStep]
  );

  const confirmAndSendWorkflowEmail = useCallback(async () => {
    if (!emailConfirmTarget) return;
    setEmailConfirmError(null);
    setEmailConfirmSending(true);
    try {
      const overrideTo = String(emailConfirmToDraft || '')
        .split(',')
        .map((v) => String(v || '').trim())
        .filter(Boolean);
      await sendWorkflowEmailForStep(emailConfirmTarget.which, emailConfirmTarget.step, {
        toOverride: overrideTo,
      });
      setEmailConfirmOpen(false);
      setEmailConfirmTarget(null);
      setEmailConfirmPreview(null);
    } catch (err: any) {
      setEmailConfirmError(err?.message || 'Failed to send email.');
    } finally {
      setEmailConfirmSending(false);
    }
  }, [emailConfirmTarget, emailConfirmToDraft, sendWorkflowEmailForStep]);

  const persistInputValueToBooking = useCallback(async (fieldKey: string, value: any) => {
    const unitId = String(unit?.id || '').trim();
    const pid = String(projectId || '').trim();
    const bid = String(bookingId || '').trim();
    const key = String(fieldKey || '').trim();
    if (!unitId || !pid || !bid || !key) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    await setDoc(
      doc(db, 'projects', pid, 'units', unitId, 'bookings', bid),
      {
        [key]: value,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      },
      { merge: true }
    );
  }, [bookingId, currentUser?.email, db, projectId, unit?.id]);

  const saveInputValueStep = useCallback(async (args: {
    which: 'main' | 'loan';
    stepKey: string;
    fieldKey: string;
    inputType: string;
    rawValue: any;
    draftKey?: string;
  }) => {
    const fieldKey = String(args.fieldKey || '').trim();
    if (!fieldKey) {
      setError('This input step is missing field key.');
      return;
    }
    const inputType = String(args.inputType || 'text').trim().toLowerCase();
    const rawText = String(args.rawValue ?? '').trim();
    let parsedValue: any = rawText;
    if (inputType === 'number') {
      const n = Number(rawText);
      if (!Number.isFinite(n)) {
        setError('Enter a valid number.');
        return;
      }
      parsedValue = n;
    } else if (inputType === 'date') {
      if (rawText && !dateFromYMD(rawText)) {
        setError('Enter a valid date.');
        return;
      }
      parsedValue = rawText;
    }
    await updateStepField(args.which, args.stepKey, fieldKey, parsedValue);
    await persistInputValueToBooking(fieldKey, parsedValue);
    if (args.draftKey) {
      setStepFieldDrafts((prev) => {
        const next = { ...prev };
        delete next[args.draftKey as string];
        return next;
      });
    }
  }, [persistInputValueToBooking, updateStepField]);

  const saveSaleAgreementDateFromAdmin = useCallback(async () => {
    const nextYmd = String(saleAgreementDateEditYmd || '').trim();
    if (nextYmd && !dateFromYMD(nextYmd)) {
      setError('Enter a valid Sale Agreement Date.');
      return;
    }
    setSaleAgreementDateSaving(true);
    setError(null);
    try {
      await persistInputValueToBooking('sale_agreement_date', nextYmd || '');
      await persistInputValueToBooking('agreement_date', nextYmd || '');

      const targetFieldKeys = new Set(['sale_agreement_date', 'saleAgreementDate', 'agreement_date', 'agreementDate']);
      const mainSteps = normalizeDefinitionSteps(mainWorkflowDefinition?.steps || []).filter(
        (step) =>
          String(step?.actionType || '').trim().toLowerCase() === 'input_value' &&
          targetFieldKeys.has(String((step as any)?.fieldKey || '').trim())
      );
      const loanSteps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []).filter(
        (step) =>
          String(step?.actionType || '').trim().toLowerCase() === 'input_value' &&
          targetFieldKeys.has(String((step as any)?.fieldKey || '').trim())
      );
      if (mainCase) {
        for (const step of mainSteps) {
          const fieldKey = String((step as any)?.fieldKey || '').trim();
          if (!fieldKey) continue;
          await updateStepField('main', step.key, fieldKey, nextYmd || '');
        }
      }
      if (loanCase) {
        for (const step of loanSteps) {
          const fieldKey = String((step as any)?.fieldKey || '').trim();
          if (!fieldKey) continue;
          await updateStepField('loan', step.key, fieldKey, nextYmd || '');
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save Sale Agreement Date.');
    } finally {
      setSaleAgreementDateSaving(false);
    }
  }, [
    loanCase,
    loanWorkflowDefinition?.steps,
    mainCase,
    mainWorkflowDefinition?.steps,
    persistInputValueToBooking,
    saleAgreementDateEditYmd,
    updateStepField,
  ]);

  const saveSaleDeedDateFromAdmin = useCallback(async () => {
    const nextYmd = String(saleDeedDateEditYmd || '').trim();
    if (nextYmd && !dateFromYMD(nextYmd)) {
      setError('Enter a valid Sale Deed Date.');
      return;
    }
    setSaleDeedDateSaving(true);
    setError(null);
    try {
      await persistInputValueToBooking('sale_deed_date', nextYmd || '');
      await persistInputValueToBooking('deed_date', nextYmd || '');

      const targetFieldKeys = new Set(['sale_deed_date', 'saleDeedDate', 'deed_date', 'deedDate']);
      const mainSteps = normalizeDefinitionSteps(mainWorkflowDefinition?.steps || []).filter(
        (step) =>
          String(step?.actionType || '').trim().toLowerCase() === 'input_value' &&
          targetFieldKeys.has(String((step as any)?.fieldKey || '').trim())
      );
      const loanSteps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []).filter(
        (step) =>
          String(step?.actionType || '').trim().toLowerCase() === 'input_value' &&
          targetFieldKeys.has(String((step as any)?.fieldKey || '').trim())
      );
      if (mainCase) {
        for (const step of mainSteps) {
          const fieldKey = String((step as any)?.fieldKey || '').trim();
          if (!fieldKey) continue;
          await updateStepField('main', step.key, fieldKey, nextYmd || '');
        }
      }
      if (loanCase) {
        for (const step of loanSteps) {
          const fieldKey = String((step as any)?.fieldKey || '').trim();
          if (!fieldKey) continue;
          await updateStepField('loan', step.key, fieldKey, nextYmd || '');
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save Sale Deed Date.');
    } finally {
      setSaleDeedDateSaving(false);
    }
  }, [
    loanCase,
    loanWorkflowDefinition?.steps,
    mainCase,
    mainWorkflowDefinition?.steps,
    persistInputValueToBooking,
    saleDeedDateEditYmd,
    updateStepField,
  ]);

  const openLoanSanctionDialog = useCallback((step: WorkflowDefinitionStep) => {
    const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
    const preferredBankName = String((loanCase as any)?.preferredBankName || '').trim();
    setLoanSanctionStepKey(String(step.key || '').trim());
    setLoanSanctionStepTitle(String(step.title || 'Upload sanction letter').trim() || 'Upload sanction letter');
    setLoanSanctionDocKey(String((step as any)?.docKey || 'sanction_letter').trim() || 'sanction_letter');
    setLoanSanctionBankName(String(stepData?.sanctionBankName || preferredBankName).trim());
    setLoanSanctionAgentName(String(stepData?.sanctionAgentName || '').trim());
    setLoanSanctionAgentMobile(normalizeBankContactNumber(String(stepData?.sanctionAgentMobile || '').trim()));
    setLoanSanctionLoanToUnitAmount(
      stepData?.loanToUnitAmount == null || stepData?.loanToUnitAmount === '' ? '' : String(stepData.loanToUnitAmount)
    );
    setLoanSanctionAmount(
      stepData?.sanctionAmount == null || stepData?.sanctionAmount === '' ? '' : String(stepData.sanctionAmount)
    );
    setLoanSanctionDate(String(stepData?.sanctionDate || '').trim());
    setLoanSanctionNonSbiReason(String(stepData?.nonSbiReason || '').trim());
    setLoanSanctionSaving(false);
    setLoanSanctionDialogOpen(true);
  }, [loanCase]);

  const openSanctionFormForDocument = useCallback((docKey: string) => {
    const sanctionSteps = normalizeDefinitionSteps(loanWorkflowDefinition?.steps || []).filter(
      (step) => String(step.actionType || '').toLowerCase() === 'loan_sanction_form'
    );
    const normalizeDocKey = (value: string) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = sanctionSteps.find((step) => normalizeDocKey(step.docKey || 'sanction_letter') === normalizeDocKey(docKey)) || sanctionSteps[0];
    if (!target) {
      setError('The loan sanction form is not configured for this project.');
      return;
    }
    openLoanSanctionDialog(target);
  }, [loanWorkflowDefinition?.steps, openLoanSanctionDialog]);

  const saveLoanSanctionForm = async () => {
    const stepKey = String(loanSanctionStepKey || '').trim();
    const docKey = String(loanSanctionDocKey || '').trim() || 'sanction_letter';
    const bankName = String(loanSanctionBankName || '').trim();
    const agentName = String(loanSanctionAgentName || '').trim();
    const agentMobile = normalizeBankContactNumber(loanSanctionAgentMobile);
    const sanctionDate = String(loanSanctionDate || '').trim();
    const isSbi = bankName.toLowerCase() === 'state bank of india';
    const nonSbiReason = String(loanSanctionNonSbiReason || '').trim();
    const pickedFile = stepDocPickedFiles[docKey] || null;
    const existingFile = !!bookingDocsByKey?.[docKey]?.files?.length;
    const sanctionAmount = Number(loanSanctionAmount);
    const loanToUnitAmount = Number(loanSanctionLoanToUnitAmount);

    if (!stepKey) throw new Error('Sanction step is missing.');
    if (!bankName) throw new Error('Bank is required.');
    if (!sanctionDate) throw new Error('Date of sanction is required.');
    if (!Number.isFinite(sanctionAmount) || sanctionAmount <= 0) throw new Error('Sanction amount is required.');
    if (!Number.isFinite(loanToUnitAmount) || loanToUnitAmount <= 0) throw new Error('Loan to Unit amount is required.');
    if (agentMobile && !isValidBankContactNumber(agentMobile)) throw new Error('Agent mobile is invalid.');
    if (!isSbi && !nonSbiReason) throw new Error('Reason not taking loan from SBI is required.');
    if (!pickedFile && !existingFile) throw new Error('Upload sanction letter is required.');

    setLoanSanctionSaving(true);
    try {
      await updateStepFields('loan', stepKey, {
        sanctionBankName: bankName,
        sanctionAgentName: agentName,
        sanctionAgentMobile: agentMobile || '',
        loanToUnitAmount,
        sanctionAmount,
        sanctionDate,
        nonSbiReason: isSbi ? '' : nonSbiReason,
      });
      if (projectId && unit?.id) {
        await setDoc(
          doc(db, 'projects', projectId, 'units', unit.id),
          {
            loanToDeveloperAmount: loanToUnitAmount,
            updatedAt: serverTimestamp(),
            updatedBy: currentUser?.email || null,
          },
          { merge: true }
        );
      }
      if (pickedFile) {
        await uploadStepRequiredDoc(docKey, loanSanctionStepTitle || 'Sanction letter', pickedFile);
      }
      setLoanSanctionDialogOpen(false);
    } finally {
      setLoanSanctionSaving(false);
    }
  };

  const mapMainV2StepAction = useCallback(
    (step: WorkflowDefinitionStep): { label: string; run: () => void } | null => {
      const actionType = String(step?.actionType || '').toLowerCase();
      if (actionType === 'add_customer') return { label: 'Add', run: () => setCustomerDrawerOpen(true) };
      if (actionType === 'add_kyc') return { label: 'Add', run: () => setKycDialogOpen(true) };
      if (actionType === 'upload_document' && String(step?.docKey || '').toLowerCase() === 'booking_form_signed') {
        return {
          label: 'Upload',
          run: () => {
            setTimeout(() => quickBookingFormUploadInputRef.current?.click(), 60);
          },
        };
      }
      if (actionType === 'add_payment') {
        const paymentType = String(step?.paymentType || '').toLowerCase();
        return {
          label: paymentType === 'gst' ? 'Add GST' : 'Add DP',
          run: () => {
            openUnifiedPayment(paymentType === 'gst' ? 'gst' : 'dp', { which: 'main', key: step.key });
          },
        };
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return {
          label: 'Add Receipt',
          run: () => {
            openUnifiedPayment('advocateAgreement', { which: 'main', key: step.key });
          },
        };
      }
      if (actionType === 'deed_charges_paid') {
        return {
          label: 'Confirm charges',
          run: () => {
            if (stampDutyReceiptTotal <= 0 || registrationReceiptTotal <= 0 || advocateDeedReceiptTotal <= 0) {
              setError('Record stamp duty, registration charge and advocate deed fee receipts before confirming.');
              return;
            }
            setStepDone('main', step.key, true).catch((err: any) => setError(err?.message || 'Failed to confirm deed charges.'));
          },
        };
      }
      if (actionType === 'mark_done') {
        return {
          label: 'Mark done',
          run: () => {
            if (step.key === 'send_deed_package') {
              const hasBookingForm = !!(bookingDocsByKey[BOOKING_FORM_SIGNED_DOC_KEY]?.files?.length || bookingDocsByKey.bookingFormSigned?.files?.length);
              const hasPaymentDetails = !!bookingDocsByKey.paymentDetailsDraft?.files?.length;
              const hasAgreement = !!(bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.length || bookingDocsByKey.sale_agreement?.files?.length);
              if (!hasBookingForm || !hasPaymentDetails || !hasAgreement) {
                setError('Prepare the signed booking form, payment details PDF and registered sale agreement before confirming the advocate package.');
                return;
              }
            }
            setStepDone('main', step.key, true).catch((err: any) =>
              setError(err?.message || 'Failed to mark step done.')
            );
          },
        };
      }
      if (actionType === 'send_email') {
        const stepPhase = stepEmailPhase[`main:${step.key}`] || 'idle';
        return {
          label: stepPhase === 'processing' ? 'Sending…' : stepPhase === 'done' ? 'Sent' : 'Send Email',
          run: () => {
            if (stepPhase === 'processing') return;
            openEmailConfirmForStep('main', step).catch((err: any) =>
              setError(err?.message || 'Failed to open email preview.')
            );
          },
        };
      }
      if (actionType === 'loan_sanction_form') {
        return {
          label: 'Open Form',
          run: () => openLoanSanctionDialog(step),
        };
      }
      return null;
    },
    [advocateDeedReceiptTotal, bookingDocsByKey, openEmailConfirmForStep, openLoanSanctionDialog, openUnifiedPayment, registrationReceiptTotal, setStepDone, stampDutyReceiptTotal, stepEmailPhase, updateStepField]
  );

  const mapLoanV2StepAction = useCallback(
    (step: WorkflowDefinitionStep): { label: string; run: () => void } | null => {
      const actionType = String(step?.actionType || '').toLowerCase();
      if (actionType === 'upload_document') {
        const docKey = String(step?.docKey || '').trim();
        if (!docKey) return null;
        return {
          label: 'Upload',
          run: () => {
            const input = document.getElementById(`loan-v2-upload-${step.key}`) as HTMLInputElement | null;
            input?.click();
          },
        };
      }
      if (actionType === 'add_payment') {
        const paymentType = String(step?.paymentType || '').toLowerCase();
        return {
          label: paymentType === 'gst' ? 'Add GST' : 'Add DP',
          run: () => {
            openUnifiedPayment(paymentType === 'gst' ? 'gst' : 'dp', { which: 'loan', key: step.key });
          },
        };
      }
      if (actionType === 'advocate_agreement_fee_receipt') {
        return {
          label: 'Add Receipt',
          run: () => {
            openUnifiedPayment('advocateAgreement', { which: 'loan', key: step.key });
          },
        };
      }
      if (actionType === 'loan_sanction_form') {
        return {
          label: 'Open Form',
          run: () => openLoanSanctionDialog(step),
        };
      }
      if (actionType === 'mark_done') {
        return {
          label: 'Done',
          run: () => {
            setStepDone('loan', step.key, true).catch((err: any) =>
              setError(err?.message || 'Failed to mark step done.')
            );
          },
        };
      }
      if (actionType === 'send_email') {
        const stepPhase = stepEmailPhase[`loan:${step.key}`] || 'idle';
        return {
          label: stepPhase === 'processing' ? 'Sending…' : stepPhase === 'done' ? 'Sent' : 'Send Email',
          run: () => {
            if (stepPhase === 'processing') return;
            openEmailConfirmForStep('loan', step).catch((err: any) =>
              setError(err?.message || 'Failed to open email preview.')
            );
          },
        };
      }
      return null;
    },
    [openEmailConfirmForStep, openLoanSanctionDialog, openUnifiedPayment, setStepDone, stepEmailPhase, updateStepField]
  );

  const makeDraftKey = (which: 'main' | 'loan', stepKey: string, fieldKey: string) => `${which}:${stepKey}:${fieldKey}`;

  const commitDraftField = async (args: {
    which: 'main' | 'loan';
    stepKey: string;
    fieldKey: string;
    fieldType: WorkflowStepFieldType;
  }) => {
    const k = makeDraftKey(args.which, args.stepKey, args.fieldKey);
    if (!(k in stepFieldDrafts)) return;
    const raw = stepFieldDrafts[k];

    let value: any = raw;
    if (args.fieldType === 'number') {
      const n = Number(raw);
      value = Number.isFinite(n) ? n : null;
    } else if (args.fieldType === 'boolean') {
      const v = String(raw || '').toLowerCase();
      value = v === 'true' || v === 'yes' || v === '1';
    }

    await saveInputValueStep({
      which: args.which,
      stepKey: args.stepKey,
      fieldKey: args.fieldKey,
      inputType: args.fieldType,
      rawValue: value,
      draftKey: k,
    });
  };

  const resolveTemplateRefNo = useCallback(
    async ({
      visualModel,
      bookingId: targetBookingId,
      docStorageKey,
      existingRefNo,
    }: {
      visualModel?: Record<string, any> | null;
      bookingId: string;
      docStorageKey: string;
      existingRefNo?: string | null;
    }) => {
      const normalizedBookingId = String(targetBookingId || '').trim();
      if (!projectId || !unit?.id || !normalizedBookingId) return '';
      const refPrefix = normalizeOfficeRefPrefix(String(visualModel?.refPrefix || '').trim());
      // Booking drawer docs are unit-scoped, always use prefix/shortBookingCode.
      void visualModel;
      void docStorageKey;
      void existingRefNo;
      const shortBookingCode = await ensureBookingShortCode({
        db,
        projectId,
        unitId: unit.id,
        bookingId: normalizedBookingId,
        currentUserEmail: currentUser?.email || null,
        existingBooking: booking as any,
      });
      if (
        shortBookingCode &&
        booking?.id === normalizedBookingId &&
        String(booking.shortBookingCode || '').trim() !== shortBookingCode
      ) {
        setBooking((prev) =>
          prev && prev.id === normalizedBookingId ? { ...prev, shortBookingCode } : prev
        );
      }
      return buildBookingShortRefNo(refPrefix, shortBookingCode, normalizedBookingId);
    },
    [booking, currentUser?.email, db, projectId, unit?.id]
  );

  const injectRuntimeRefDateTokens = useCallback((html: string) => {
    let next = String(html || '');
    if (!next.trim()) return next;
    next = next.replace(/(Ref\s*No\s*:)\s*([^<\n\r]+)/i, (_match, label, value) => {
      const currentValue = String(value || '').trim();
      if (!currentValue || currentValue.includes('{{')) return `${label} ${currentValue}`;
      return `${label} {{ref_no}}`;
    });
    next = next.replace(/(Date\s*:)\s*([^<\n\r]+)/i, (_match, label, value) => {
      const currentValue = String(value || '').trim();
      if (!currentValue || currentValue.includes('{{')) return `${label} ${currentValue}`;
      return `${label} {{issue_date}}`;
    });
    return next;
  }, []);

  const generateAllotmentLetterDraft = useCallback(async (
    stepKey: string = OFFICE_MAIN_STEP_KEYS.sendToAdvocate
  ) => {
    if (!unit?.id || !projectId || !bookingId) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    assertUnitDataCurrent();
    const primaryCustomer = customers.find((c) => String(c.role || '').toLowerCase() === 'primary') || customers[0];
    const address = [
      String(primaryCustomer?.addressLine1 || '').trim(),
      String(primaryCustomer?.addressLine2 || '').trim(),
      String(primaryCustomer?.city || '').trim(),
      String(primaryCustomer?.state || '').trim(),
      String(primaryCustomer?.pincode || '').trim(),
    ]
      .filter(Boolean)
      .join(', ');
    const context = {
      projectName: projectName || projectId || 'Project',
      unitNumber: String(unit?.unitNumber || unit?.id || '—'),
      block: String(unit?.blockType || ''),
      amountText:
        typeof unit?.unitAmount === 'number' ? unit.unitAmount.toLocaleString('en-IN') : '—',
      issueDate: formatDDMMYYYY(new Date()),
      allotteeNames: customers.map((c) => String(c.displayName || c.name || '').trim()).filter(Boolean),
      primaryMobile: String(primaryCustomer?.mobile || '').trim(),
      addressText: address,
      carpetArea: String((unit as any)?.carpetArea ?? ''),
      exclusiveBalconyArea: String((unit as any)?.exclusiveBalconyArea ?? ''),
      exclusiveWashArea: String(
        (unit as any)?.exclusiveWashArea ??
        (unit as any)?.areaOfExclusiveWash ??
        (unit as any)?.area_of_exclusive_wash ??
        (unit as any)?.customFields?.area_of_exclusive_wash ??
        (unit as any)?.['AREA OF EXCLUSIVE WASH'] ??
        ''
      ),
      openTerraceArea: String(
        (unit as any)?.openTerraceArea ??
        (unit as any)?.areaOfExclusiveOpenTerrace ??
        (unit as any)?.area_of_exclusive_open_terrace ??
        (unit as any)?.customFields?.area_of_exclusive_open_terrace ??
        (unit as any)?.['AREA OF EXCLUSIVE OPEN TERRACE'] ??
        ''
      ),
      vanvechayelArea: String((unit as any)?.vanvechayelArea || (unit as any)?.vanvechayel_area || ''),
      builtUpArea: String((unit as any)?.builtUpArea || (unit as any)?.built_up_area || ''),
      buildingNo: String((unit as any)?.buildingNo || (unit as any)?.building_no || ''),
      floorEng: String((unit as any)?.floorEng || (unit as any)?.floor_eng || ''),
      requestDateTimeAmPm: formatDateTimeAmPmCompact(new Date()),
    };
    const unitAmountValue = Number((unit as any)?.unitAmount || 0) || 0;
    const ceilHundred = (value: number) => Math.ceil(value / 100) * 100;
    const hasMaleAllottee = customers.some(
      (c) => String(c?.gender || '').trim().toLowerCase() === 'male'
    );
    const gstValue = computeGstAmount(unitAmountValue);
    const stampDutyValue = unitAmountValue > 0 ? ceilHundred(ceilHundred(unitAmountValue) * 0.049) : 0;
    const registrationChargesValue =
      unitAmountValue <= 0
        ? 0
        : customers.length === 0 || hasMaleAllottee
          ? ceilHundred(ceilHundred(unitAmountValue) * 0.01) + 1200
          : 1200;
    const formatAmount = (value: number) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : '');
    const toMillis = (value: any): number => {
      if (!value) return 0;
      if (typeof value?.toMillis === 'function') return Number(value.toMillis() || 0);
      if (typeof value?.seconds === 'number') return Number(value.seconds || 0) * 1000;
      if (typeof value === 'number') return value;
      return 0;
    };
    let firstCustomerReceipt: any | null = null;
    let transactionRowsForTemplate: any[] = [];
    try {
      const txSnap = await getDocs(
        query(
          collection(db, 'projects', projectId, 'bankTransactions'),
          where('unitId', '==', unit.id),
          where('txnType', '==', 'receipt'),
          limit(200)
        )
      );
      const rows = txSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      transactionRowsForTemplate = rows;
      const customerRows = rows
        .filter((row) => String(row?.receiptCategory || '').toLowerCase() === 'customer')
        .sort((a, b) => {
          const aAt =
            toMillis(a?.chqDate) ||
            toMillis(a?.txnDate) ||
            toMillis(a?.createdAt) ||
            toMillis(a?.updatedAt) ||
            Number.MAX_SAFE_INTEGER;
          const bAt =
            toMillis(b?.chqDate) ||
            toMillis(b?.txnDate) ||
            toMillis(b?.createdAt) ||
            toMillis(b?.updatedAt) ||
            Number.MAX_SAFE_INTEGER;
          if (aAt !== bAt) return aAt - bAt;
          return String(a?.id || '').localeCompare(String(b?.id || ''));
        });
      firstCustomerReceipt = customerRows[0] || null;
    } catch (txErr) {
      console.warn('Could not load bank transactions for allotment placeholders', txErr);
    }
    const joinNamesCommaAnd = (names: string[]) => {
      const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
      if (clean.length <= 1) return clean[0] || '';
      if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
      return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
    };
    const buildAllotteeSignatureBlock = (names: string[]) => {
      const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
      if (!clean.length) return '';
      const esc = (value: string) =>
        String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      return clean
        .map(
          (name) => `
<div style="margin-top:16px;">
  <div style="height:36px;"></div>
  <div style="border-top:1px solid #000;max-width:260px;"></div>
  <div style="margin-top:4px;">${esc(name)}</div>
</div>`
        )
        .join('');
    };
    let htmlToRender = '';
    let selectedTemplate: any = null;
    let projectRow: any = null;
    let companyProfileRow: any = null;
    let tokenValues: Record<string, string> = {};
    const allotmentDraftDocRef = doc(
      db,
      'projects',
      projectId,
      'units',
      unit.id,
      'bookings',
      bookingId,
      'draft',
      'allotment_letter_v1'
    );
    const allotmentExistingSnap = await getDoc(allotmentDraftDocRef);
    try {
      const templatesSnap = await getDocs(
        query(
          collection(db, 'officeDocumentTemplates'),
          where('templateId', '==', 'allotment_letter_v1')
        )
      );
      const templates = templatesSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      const projectMatched = templates.filter((t: any) =>
        Array.isArray(t.assignedProjectIds) &&
        t.assignedProjectIds.map((x: any) => String(x)).includes(String(projectId || ''))
      );
      const toMillis = (value: any) =>
        typeof value?.toMillis === 'function'
          ? value.toMillis()
          : typeof value?.seconds === 'number'
          ? value.seconds * 1000
          : 0;
      const pickNewest = (rows: any[]) =>
        [...rows].sort((a, b) => {
          const versionA = Number(a?.version || 0) || 0;
          const versionB = Number(b?.version || 0) || 0;
          if (versionA !== versionB) return versionB - versionA;
          const updatedA = toMillis(a?.updatedAt || a?.createdAt);
          const updatedB = toMillis(b?.updatedAt || b?.createdAt);
          if (updatedA !== updatedB) return updatedB - updatedA;
          return String(b?.id || '').localeCompare(String(a?.id || ''));
        })[0] || null;
      selectedTemplate = pickNewest(projectMatched) || pickNewest(templates) || null;

      const projectSnap = await getDoc(doc(db, 'projects', projectId));
      projectRow = projectSnap.exists() ? { id: projectSnap.id, ...(projectSnap.data() as any) } : null;
      const cpId = String(projectRow?.companyProfileId || '').trim();
      if (cpId) {
        const companySnap = await getDoc(doc(db, 'companyProfiles', cpId));
        companyProfileRow = companySnap.exists() ? { id: companySnap.id, ...(companySnap.data() as any) } : null;
      }

      const bodyHtml = String(
        selectedTemplate?.renderHtml ||
        selectedTemplate?.visualModel?.renderHtml ||
        selectedTemplate?.visualModel?.body ||
        selectedTemplate?.bodyHtml ||
        ''
      ).trim();

      if (bodyHtml) {
        const digits = String(companyProfileRow?.mobile || '').replace(/\D/g, '');
        const formattedMobile = digits.length > 5 ?
          `+91 ${digits.slice(-10, -5)} ${digits.slice(-5)}` :
          (companyProfileRow?.mobile || '');
        const paymentTemplateTokens = buildPaymentTemplateTokens({
          transactions: transactionRowsForTemplate,
          unitAmount: unitAmountValue,
          gstAmount: gstValue,
          formatDate: formatDDMMYYYY,
          formatAmount,
        });
        const stampDutyToken = String(paymentTemplateTokens['{{stamp_duty}}'] || '').trim();
        const registrationChargesToken = String(paymentTemplateTokens['{{registration_charges}}'] || '').trim();
        const advocateAgreementFeeToken = String(paymentTemplateTokens['{{advocate_agreement_fee}}'] || '').trim();
        const advocateDeedFeeToken = String(paymentTemplateTokens['{{advocate_deed_fee}}'] || '').trim();
        const advocateFeeToken = String(paymentTemplateTokens['{{advocate_fee}}'] || '').trim();
        const advocateAgreementFeeValue = Number(projectRow?.advocateAgreementFee || 0) || 0;
        const advocateDeedFeeValue = Number(projectRow?.advocateDeedFee || 0) || 0;
        const resolvedRefNo = await resolveTemplateRefNo({
          visualModel: selectedTemplate?.visualModel || {},
          bookingId,
          docStorageKey: String(selectedTemplate?.templateId || 'allotment_letter_v1'),
          existingRefNo: String((allotmentExistingSnap.data() as any)?.refNo || '').trim(),
        });
        const agreementNumberToken =
          resolveWorkflowInputTextToken('agreement_number', 'agreementNumber', 'sale_agreement_number', 'saleAgreementNumber') ||
          resolveBookingTextToken('agreementNumber', 'agreement_number', 'saleAgreementNumber');
        const deedNumberToken =
          resolveWorkflowInputTextToken('deed_number', 'deedNumber') ||
          resolveBookingTextToken('deedNumber', 'deed_number', 'saleDeedNumber');
        const agreementDateToken =
          resolveWorkflowInputDateToken('agreement_date', 'agreementDate', 'sale_agreement_date', 'saleAgreementDate') ||
          resolveBookingDateToken('agreementDate', 'agreement_date', 'sale_agreement_date', 'saleAgreementDate');
        const agreementDateResolved =
          String(agreementDateToken || '').trim() ||
          (saleAgreementDateDisplay === '—' ? '' : String(saleAgreementDateDisplay || '').trim());
        const deedDateToken =
          resolveWorkflowInputDateToken('deed_date', 'deedDate') ||
          resolveBookingDateToken('deedDate', 'deed_date', 'saleDeedDate');
        const agreementLinkToken = resolveBookingTextToken('agreementLink', 'agreement_link', 'saleAgreementLink');
        const deedLinkToken = resolveBookingTextToken('deedLink', 'deed_link', 'saleDeedLink');
        const replacements: Record<string, string> = {
          ...paymentTemplateTokens,
          '{{project_name}}': context.projectName,
          '{{unit_no}}': context.unitNumber,
          '{{unit_number}}': context.unitNumber,
          '{{allottee_names}}': context.allotteeNames.join(', '),
          '{{allottee_name}}': context.allotteeNames.join(', '),
          '{{allottee_primary_name}}': context.allotteeNames[0] || '',
          '{{allottee_names_lines}}': context.allotteeNames.join('<br/>'),
          '{{allottee_names_comma_and}}': joinNamesCommaAnd(context.allotteeNames),
          '{{allottee_names_signature_block}}': buildAllotteeSignatureBlock(context.allotteeNames),
          '{{amount}}': context.amountText,
          '{{unit_amount}}': context.amountText,
          '{{issue_date}}': context.issueDate,
          '{{ref_no}}': resolvedRefNo,
          '{{receipt_no}}': `${resolvedRefNo}-${String(paymentTemplateTokens['{{payment_sequence_no}}'] || '1')}`,
          '{{carpet_area}}': context.carpetArea,
          '{{area_of_exclusive_balcony}}': context.exclusiveBalconyArea,
          '{{area_of_exclusive_wash}}': context.exclusiveWashArea,
          '{{area_of_exclusive_open_terrace}}': context.openTerraceArea,
          '{{vanvechayel_area}}': context.vanvechayelArea,
          '{{built_up_area}}': context.builtUpArea,
          '{{building_no}}': context.buildingNo,
          '{{floor_eng}}': context.floorEng,
          ...buildUniversalOfficeTemplateTokens({
            primaryCustomerMobile,
            loanBankName: latestLoanSanctionData?.sanctionBankName,
            loanSanctionAmount: latestLoanSanctionData?.sanctionAmount,
            reasonNotTakingSbi: latestLoanSanctionData?.nonSbiReason,
            pendingAmount: unitAmountValue - customerReceiptTotal,
            formatAmount,
          }),
          '{{gst}}': formatAmount(gstValue),
          '{{stamp_duty}}': stampDutyToken || formatAmount(stampDutyValue),
          '{{registration_charges}}': registrationChargesToken || formatAmount(registrationChargesValue),
          '{{advocate_agreement_fee}}': advocateAgreementFeeToken || formatAmount(advocateAgreementFeeValue),
          '{{advocate_deed_fee}}': advocateDeedFeeToken || formatAmount(advocateDeedFeeValue),
          '{{advocate_fee}}': advocateFeeToken || formatAmount(advocateAgreementFeeValue + advocateDeedFeeValue),
          '{{agreement_number}}': agreementNumberToken,
          '{{deed_number}}': deedNumberToken,
          '{{agreement_date}}': agreementDateResolved,
          '{{sale_agreement_date}}': agreementDateResolved,
          '{{deed_date}}': deedDateToken,
          '{{agreement_link}}': agreementLinkToken,
          '{{deed_link}}': deedLinkToken,
          '{{chq_date}}': (firstCustomerReceipt?.chqDate || firstCustomerReceipt?.txnDate)
            ? formatDDMMYYYY(
                (firstCustomerReceipt?.chqDate || firstCustomerReceipt?.txnDate).toDate
                  ? (firstCustomerReceipt?.chqDate || firstCustomerReceipt?.txnDate).toDate()
                  : (firstCustomerReceipt?.chqDate || firstCustomerReceipt?.txnDate)
              )
            : '',
          '{{ref_chq_no}}': String(firstCustomerReceipt?.referenceNo || ''),
          '{{bank}}': String(firstCustomerReceipt?.payerBankName || firstCustomerReceipt?.bankName || ''),
          '{{deposit}}': formatAmount(Number(firstCustomerReceipt?.amount || 0) || 0),
          '{{request_datetime_ampm}}': context.requestDateTimeAmPm,
          '{{company_logo_url}}': String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
          '{{project_logo_url}}': resolveProjectLogoForTemplate(projectRow, selectedTemplate),
          '{{project_document_image_url}}': String(projectRow?.documentImageUrl || ''),
          '{{company_address}}': String(companyProfileRow?.address || ''),
          '{{company_mobile}}': String(formattedMobile || companyProfileRow?.mobile || ''),
          '{{company_email}}': String(companyProfileRow?.email || ''),
          '{{company_name}}': String(companyProfileRow?.name || ''),
          '[Project Name]': context.projectName,
          '[Unit Number]': context.unitNumber,
          '[Allottee Name]': context.allotteeNames.join(', '),
        };
        tokenValues = replacements;
        let rendered = applyTemplateTokens(injectRuntimeRefDateTokens(bodyHtml), replacements);
        const isFullHtml = /<html[\s>]/i.test(rendered);
        htmlToRender = isFullHtml
          ? rendered
          : `<!doctype html><html><head><meta charset="utf-8" /><title>Allotment Letter</title></head><body>${rendered}</body></html>`;
      }
    } catch (err) {
      console.error('Load allotment_letter_v1 template failed', err);
    }

    if (!htmlToRender) {
      htmlToRender = allotmentLetterTemplate.renderHtml(context);
    }

    const functions = getFunctions();
    const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
    const vm = selectedTemplate?.visualModel || {};
    const runtimeVisualModel = {
      ...vm,
      backgroundImageUrl: String((vm as any)?.backgroundImageUrl || projectRow?.backgroundImageUrl || ''),
      backgroundOpacity:
        (vm as any)?.backgroundOpacity ??
        (String(projectRow?.backgroundImageUrl || '').trim() ? 0.21 : 0),
      issueDate: formatDDMMYYYY(new Date()),
      refNo: String(tokenValues['{{ref_no}}'] || '').trim(),
    };
    const result = await renderTemplatePdf(
      buildPdfPayloadFromTemplate({
        html: htmlToRender,
        tokenValues,
        visualModel: runtimeVisualModel,
        companyLogoUrl: String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
        projectLogoUrl: resolveProjectLogoForTemplate(projectRow, selectedTemplate),
        companyAddress: String(companyProfileRow?.address || ''),
        companyMobile: String(companyProfileRow?.mobile || ''),
        companyEmail: String(companyProfileRow?.email || ''),
        companyFontFamily: String(companyProfileRow?.fontFamily || ''),
        companyFontUrl: String(companyProfileRow?.fontUrl || ''),
        companyFontFormat: String(companyProfileRow?.fontFormat || ''),
        gujaratiFontFamily: String(companyProfileRow?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(companyProfileRow?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(companyProfileRow?.gujaratiFontFormat || ''),
        paperSize: String(vm.paperSize || 'A4'),
        fileName: `allotment_letter_${String(unit?.unitNumber || unit?.id || 'draft')}`,
      })
    );
    const data = (result?.data || {}) as {
      fileName?: string;
      mimeType?: string;
      pdfBytes?: number[] | Record<string, number>;
      pdfBase64?: string | { type?: string; data?: number[] };
    };
    const rawBytes = Array.isArray(data.pdfBytes)
      ? data.pdfBytes
      : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes)
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : [];
    let pdfBuffer = rawBytes.length ? Uint8Array.from(rawBytes) : new Uint8Array();
    if (!pdfBuffer.length) {
      const base64Raw =
        typeof data.pdfBase64 === 'string'
          ? data.pdfBase64
          : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
          ? (() => {
              const source = data.pdfBase64?.data || [];
              let chunk = '';
              for (let i = 0; i < source.length; i += 0x8000) {
                const slice = source.slice(i, i + 0x8000);
                chunk += String.fromCharCode(...slice);
              }
              return btoa(chunk);
            })()
          : '';
      const normalizedBase64 = base64Raw
        .replace(/^data:application\/pdf;base64,/i, '')
        .replace(/\s+/g, '');
      if (normalizedBase64) {
        const binary = atob(normalizedBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        pdfBuffer = bytes;
      }
    }
    if (!pdfBuffer.length) {
      throw new Error('Invalid PDF payload received from server.');
    }

    const by = currentUser.email;
    const uploadedAtMs = Date.now();
    const unitNo = String(unit?.unitNumber || unit?.id || 'Unit').trim();
    const outputType = getTemplateGeneratedOutputType(selectedTemplate);
    const displaySection = getTemplateShowInSection(selectedTemplate, outputType);
    const rawDraftNameTemplate = String(selectedTemplate?.draftFileNameTemplate || '')
      .replace(/\{\{\s*unit[_\s-]*no\s*\}\}/gi, '{{unit_no}}')
      .replace(/\{\{\s*unit[_\s-]*number\s*\}\}/gi, '{{unit_no}}')
      .trim();
    const resolvedDraftName = rawDraftNameTemplate ?
      applyTemplateTokens(rawDraftNameTemplate, tokenValues) :
      '';
    const fallbackBaseName = outputType === 'final' ? `${unitNo} - ALLOTMENT LETTER` : `${unitNo} - ALLOTMENT_LETTER_DRAFT`;
    const fileDisplayNameBase = String(resolvedDraftName || '').trim() || fallbackBaseName;
    const fileDisplayName = /\.pdf$/i.test(fileDisplayNameBase) ? fileDisplayNameBase : `${fileDisplayNameBase}.pdf`;
    const safeFileName = fileDisplayName.replace(/[^\w.-]+/g, '_');
    const finalDocRef = doc(
      db,
      'projects',
      projectId,
      'units',
      unit.id,
      'bookings',
      bookingId,
      'uploaded',
      'allotment_letter_v1'
    );
    const finalExistingSnap = await getDoc(finalDocRef);
    const docRef = outputType === 'final' ? finalDocRef : allotmentDraftDocRef;
    const existingSnap = outputType === 'final' ? finalExistingSnap : allotmentExistingSnap;
    const existingPath = String((existingSnap?.data() as any)?.files?.[0]?.path || '').trim();
    const path = `bookings/${bookingId}/${outputType === 'final' ? 'uploaded' : 'draft'}/allotment_letter_v1/${safeFileName}`;
    if (existingPath && existingPath !== path) {
      try {
        await deleteObject(ref(storage, existingPath));
      } catch (e) {
        console.warn(`Could not delete previous allotment ${outputType} file`, e);
      }
    }
    if (outputType === 'final' && allotmentExistingSnap.exists()) {
      const staleDraftPath = String((allotmentExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
      if (staleDraftPath) {
        try {
          await deleteObject(ref(storage, staleDraftPath));
        } catch (e) {
          console.warn('Could not delete stale allotment draft file', e);
        }
      }
      await deleteDoc(allotmentDraftDocRef).catch(() => undefined);
    }
    if (outputType === 'draft' && finalExistingSnap.exists()) {
      const staleFinalPath = String((finalExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
      if (staleFinalPath) {
        try {
          await deleteObject(ref(storage, staleFinalPath));
        } catch (e) {
          console.warn('Could not delete stale final allotment file', e);
        }
      }
      await deleteDoc(finalDocRef).catch(() => undefined);
    }
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, pdfBuffer, {
      contentType: data.mimeType || 'application/pdf',
      cacheControl: 'no-store, max-age=0',
    });
    const baseUrl = await getDownloadURL(storageRef);
    const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${uploadedAtMs}`;
    await setDoc(
      docRef,
      {
        type: outputType === 'final' ? 'allotment_letter_v1' : 'allotmentLetterDraft',
        templateId: 'allotment_letter_v1',
        templateName: String(selectedTemplate?.name || 'Allotment Letter'),
        displayName: String(selectedTemplate?.name || 'Allotment Letter'),
        generatedFinal: outputType === 'final',
        displaySection,
        refNo: String(tokenValues['{{ref_no}}'] || '').trim() || null,
        files: [
          {
            name: fileDisplayName,
            originalName: fileDisplayName,
            url,
            path,
            uploadedAtMs,
            uploadedBy: by,
          },
        ],
        updatedAt: serverTimestamp(),
        updatedBy: by,
        createdAt: serverTimestamp(),
        createdBy: by,
      },
      { merge: true }
    );
    await updateStepField('main', stepKey, 'allotmentLetterDraftGeneratedAt', Date.now());
  }, [
    assertUnitDataCurrent,
    bookingId,
    computeGstAmount,
    customerReceiptTotal,
    customers,
    currentUser?.email,
    db,
    injectRuntimeRefDateTokens,
    latestLoanSanctionData?.nonSbiReason,
    latestLoanSanctionData?.sanctionAmount,
    latestLoanSanctionData?.sanctionBankName,
    primaryCustomerMobile,
    projectId,
    projectName,
    resolveBookingDateToken,
    resolveBookingTextToken,
    resolveTemplateRefNo,
    resolveWorkflowInputDateToken,
    resolveWorkflowInputTextToken,
    saleAgreementDateDisplay,
    unit,
    updateStepField,
  ]);

  const runAllotmentDraftGeneration = useCallback(
    async (stepKey: string) => {
      setDraftGenerationStatus('processing');
      setError(null);
      try {
        await generateAllotmentLetterDraft(stepKey);
        setDraftGenerationStatus('done');
        window.setTimeout(() => setDraftGenerationStatus('idle'), 2200);
      } catch (err) {
        setDraftGenerationStatus('error');
        throw err;
      }
    },
    [generateAllotmentLetterDraft]
  );

  const ensureBookingForDraft = useCallback(async (): Promise<string> => {
    if (bookingId) return bookingId;
    if (!unit?.id || !projectId) throw new Error('Unit or project not selected.');
    if (!currentUser?.email) throw new Error('Not signed in.');
    const by = currentUser.email;
    const bookingRef = doc(collection(db, 'projects', projectId, 'units', unit.id, 'bookings'));
    const nextBookingId = String(bookingRef.id || '').trim();
    if (!nextBookingId) throw new Error('Failed to initialize booking.');
    const shortBookingCode = await generateUniqueBookingShortCode({
      db,
      projectId,
    });
    await setDoc(
      bookingRef,
      {
        projectId,
        unitId: unit.id,
        unitNumber: String(unit?.unitNumber || unit?.id || ''),
        shortBookingCode: shortBookingCode || null,
        bookingType: 'main',
        status: 'active',
        createdAt: serverTimestamp(),
        createdBy: by,
        updatedAt: serverTimestamp(),
        updatedBy: by,
      },
      { merge: true }
    );
    await setDoc(
      doc(db, 'projects', projectId, 'units', unit.id),
      {
        activeBookingId: nextBookingId,
        updatedAt: serverTimestamp(),
        updatedBy: by,
      },
      { merge: true }
    );
    setFallbackBookingId(nextBookingId);
    return nextBookingId;
  }, [bookingId, currentUser?.email, db, projectId, unit?.id, unit?.unitNumber]);

  const saveBookingFormMetadata = useCallback(
    async (effectiveBookingId: string) => {
      if (!unit?.id || !projectId) return;
      if (!currentUser?.email) return;
      const by = currentUser.email;
      const parsedRank = Number(bookingFormRank);
      const rankValue = bookingFormRank.trim() === '' || Number.isNaN(parsedRank) ? null : parsedRank;
      await setDoc(
        doc(db, 'projects', projectId, 'units', unit.id, 'bookings', effectiveBookingId),
        {
          paymentTerms: bookingFormUseLegacyTerms ? normalizePaymentTerms(bookingFormTerms) || null : null,
          paymentCondition: bookingFormUseLegacyTerms ? null : JSON.parse(JSON.stringify({
            ...bookingFormCondition,
            loan: bookingFormCondition.loan ? {
              requestedAmount: bookingFormCondition.loan.requestedAmount,
              requestedPercent: bookingFormCondition.loan.requestedPercent,
              trigger: bookingFormCondition.loan.trigger,
            } : undefined,
          })),
          refByBrokerId: bookingFormRefByBroker?.id || null,
          refByBrokerName: String(
            bookingFormRefByBroker?.brokerName ||
            bookingFormRefByBroker?.displayName ||
            ''
          ).trim() || null,
          rank: rankValue,
          schemeEnabled: bookingFormScheme === 'yes',
          schemeRemark: bookingFormScheme === 'yes' ? String(bookingFormSchemeRemark || '').trim() || null : null,
          bookingFormDate: String(bookingFormDraftDate || '').trim() || null,
          bookingFormTime: String(bookingFormDraftTime || '').trim() || null,
          agreementTermMonths: Number(bookingFormTermMonths) || null,
          earnestAmount: Number(bookingFormEarnest) || null,
          societyCommittee: bookingFormCommittee || null,
          bookingFormDocs,
          updatedAt: serverTimestamp(),
          updatedBy: by,
        },
        { merge: true }
      );
      await setDoc(
        doc(db, 'projects', projectId, 'units', unit.id),
        {
          hasBookingFormDate: true,
          latestBookingFormDate: String(bookingFormDraftDate || '').trim() || null,
          updatedAt: serverTimestamp(),
          updatedBy: by,
        },
        { merge: true }
      );
    },
    [
      bookingFormCommittee,
      bookingFormDocs,
      bookingFormEarnest,
      bookingFormTermMonths,
      bookingFormRank,
      bookingFormTerms,
      bookingFormCondition,
      bookingFormUseLegacyTerms,
      bookingFormRefByBroker,
      bookingFormScheme,
      bookingFormSchemeRemark,
      bookingFormDraftDate,
      bookingFormDraftTime,
      normalizePaymentTerms,
      currentUser?.email,
      db,
      projectId,
      unit?.id,
    ]
  );

  const handleFullBookingWipe = useCallback(async () => {
    if (!isAdmin) {
      setError('Only admin can reset booking.');
      return;
    }
    if (!projectId || !unit?.id) {
      setError('Project or unit is missing.');
      return;
    }
    if (!currentUser?.email) {
      setError('Not signed in.');
      return;
    }

    // If there's an active bookingId use it; otherwise discover all booking docs under the unit.
    const effectiveBookingId = String(bookingId || '').trim();

    const unitNo = String(unit?.unitNumber || unit?.id || 'this unit').trim();
    const confirmed = window.confirm(
      effectiveBookingId
        ? `Reset booking for ${unitNo}?\n\nThis permanently removes booking form data, customers, KYC docs, generated/uploaded booking documents, and workflow progress for this unit booking only.`
        : `Force-clear ALL stale booking data for ${unitNo}?\n\nNo active booking was found, but orphaned booking documents exist in Firestore. This will permanently delete all of them and reset the unit.`
    );
    if (!confirmed) return;

    setBookingFormDraftGenerating(true);
    setError(null);
    try {
      const deleteFilesFromRow = async (row: any) => {
        const files = Array.isArray(row?.files) ? row.files : [];
        await Promise.all(
          files.map(async (f: any) => {
            const path = String(f?.path || '').trim();
            if (!path) return;
            try {
              await deleteObject(ref(storage, path));
            } catch (err) {
              console.warn('Could not delete storage file during booking reset', path, err);
            }
          })
        );
      };

      // Collect all booking IDs to wipe — either just the known one, or all discovered ones.
      let bookingIdsToWipe: string[] = [];
      if (effectiveBookingId) {
        bookingIdsToWipe = [effectiveBookingId];
      } else {
        // No activeBookingId set — discover orphaned booking docs.
        const allBookingsSnap = await getDocs(
          collection(db, 'projects', projectId, 'units', unit.id, 'bookings')
        );
        bookingIdsToWipe = allBookingsSnap.docs.map((d) => d.id);
      }

      for (const bid of bookingIdsToWipe) {
        const draftSnap = await getDocs(
          collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bid, 'draft')
        );
        for (const draftDoc of draftSnap.docs) {
          await deleteFilesFromRow(draftDoc.data());
          await deleteDoc(draftDoc.ref).catch(() => undefined);
        }

        const uploadedSnap = await getDocs(
          collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bid, 'uploaded')
        );
        for (const uploadedDoc of uploadedSnap.docs) {
          await deleteFilesFromRow(uploadedDoc.data());
          await deleteDoc(uploadedDoc.ref).catch(() => undefined);
        }

        const customersSnap = await getDocs(
          collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bid, 'customers')
        );
        for (const customerDoc of customersSnap.docs) {
          const kycSnap = await getDocs(
            collection(
              db, 'projects', projectId, 'units', unit.id,
              'bookings', bid, 'customers', customerDoc.id, 'kycDocs'
            )
          );
          for (const kycDoc of kycSnap.docs) {
            await deleteFilesFromRow(kycDoc.data());
            await deleteDoc(kycDoc.ref).catch(() => undefined);
          }
          await deleteDoc(customerDoc.ref).catch(() => undefined);
        }

        await deleteDoc(
          doc(db, 'projects', projectId, 'units', unit.id, 'bookings', bid)
        ).catch(() => undefined);
        await deleteDoc(
          doc(db, 'projects', projectId, 'units', unit.id, 'cases', bid)
        ).catch(() => undefined);
        await deleteDoc(
          doc(db, 'projects', projectId, 'units', unit.id, 'cases', `loan_${bid}`)
        ).catch(() => undefined);
      }

      // Stamp cancelledAt on all bankTransactions belonging to the wiped bookings.
      // This preserves the audit trail while hiding them from the active view.
      try {
        const txnSnap = await getDocs(
          query(
            collection(db, 'projects', projectId, 'bankTransactions'),
            where('unitId', '==', unit.id)
          )
        );
        const wipedSet = new Set(bookingIdsToWipe);
        const toCancel = txnSnap.docs.filter((d) => {
          const rowBookingId = String(d.data()?.bookingId || '').trim();
          // Cancel if transaction is explicitly linked to a wiped booking,
          // OR if it has no bookingId (legacy data that belongs to this unit's only booking).
          return rowBookingId ? wipedSet.has(rowBookingId) : true;
        });
        // Batch update in chunks of 500 (Firestore limit).
        const CHUNK = 500;
        for (let i = 0; i < toCancel.length; i += CHUNK) {
          const batch = writeBatch(db);
          toCancel.slice(i, i + CHUNK).forEach((d) => {
            batch.update(d.ref, {
              cancelledAt: serverTimestamp(),
              cancelledBy: currentUser.email,
              cancelledForBookingIds: bookingIdsToWipe,
            });
          });
          await batch.commit();
        }
      } catch (txnErr) {
        console.warn('Could not stamp cancelledAt on transactions — non-fatal', txnErr);
      }

      await setDoc(
        doc(db, 'projects', projectId, 'units', unit.id),
        {
          activeBookingId: deleteField(),
          hasBookingFormDate: deleteField(),
          latestBookingFormDate: deleteField(),
          latestBookingFormTime: deleteField(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
        },
        { merge: true }
      );

      setFallbackBookingId('');
      setBookingFormDraftDialogOpen(false);
      resetLocalState();
    } catch (err: any) {
      setError(err?.message || 'Failed to reset booking.');
    } finally {
      setBookingFormDraftGenerating(false);
    }
  }, [bookingId, currentUser?.email, db, isAdmin, projectId, unit?.id, unit?.unitNumber]);

  const generateBookingFormV1Draft = useCallback(async (effectiveBookingIdOverride?: string, conditionOverride?: PaymentCondition | null) => {
    if (!unit?.id || !projectId) return;
    if (!currentUser?.email) throw new Error('Not signed in.');
    assertUnitDataCurrent();
    if (bookingFormDoc?.files?.length) throw new Error('Signed booking form is locked. Create a revised form before regenerating.');
    const effectiveBookingId = effectiveBookingIdOverride || (await ensureBookingForDraft());
    const context = {
      projectName: projectName || projectId || 'Project',
      unitNumber: String(unit?.unitNumber || unit?.id || '—'),
      amountText:
        typeof unit?.unitAmount === 'number' ? unit.unitAmount.toLocaleString('en-IN') : '—',
      issueDate: bookingFormDraftDate
        ? (() => {
            const dt = dateFromYMD(bookingFormDraftDate);
            return dt ? formatDDMMYYYY(dt) : formatDDMMYYYY(new Date());
          })()
        : formatDDMMYYYY(new Date()),
      allotteeNames: customers.map((c) => String(c.displayName || c.name || '').trim()).filter(Boolean),
      carpetArea: String((unit as any)?.carpetArea ?? ''),
      exclusiveBalconyArea: String((unit as any)?.exclusiveBalconyArea ?? ''),
      exclusiveWashArea: String(
        (unit as any)?.exclusiveWashArea ??
        (unit as any)?.areaOfExclusiveWash ??
        (unit as any)?.area_of_exclusive_wash ??
        (unit as any)?.customFields?.area_of_exclusive_wash ??
        (unit as any)?.['AREA OF EXCLUSIVE WASH'] ??
        ''
      ),
      openTerraceArea: String(
        (unit as any)?.openTerraceArea ??
        (unit as any)?.areaOfExclusiveOpenTerrace ??
        (unit as any)?.area_of_exclusive_open_terrace ??
        (unit as any)?.customFields?.area_of_exclusive_open_terrace ??
        (unit as any)?.['AREA OF EXCLUSIVE OPEN TERRACE'] ??
        ''
      ),
      vanvechayelArea: String((unit as any)?.vanvechayelArea || (unit as any)?.vanvechayel_area || ''),
      builtUpArea: String((unit as any)?.builtUpArea || (unit as any)?.built_up_area || ''),
      buildingNo: String((unit as any)?.buildingNo || (unit as any)?.building_no || ''),
      floorEng: String((unit as any)?.floorEng || (unit as any)?.floor_eng || ''),
      requestDate: bookingFormDraftDate || '',
      requestTime: bookingFormDraftTime || '',
      requestDateTimeAmPm: (() => {
        const dt = dateFromYMD(bookingFormDraftDate || '');
        const tm = String(bookingFormDraftTime || '').match(/^(\d{2}):(\d{2})$/);
        if (!dt || !tm) return '';
        dt.setHours(Number(tm[1] || 0), Number(tm[2] || 0), 0, 0);
        return formatDateTimeAmPm(dt).replace(/[-:\s]/g, '');
      })(),
    };
    const joinNamesCommaAnd = (names: string[]) => {
      const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
      if (clean.length <= 1) return clean[0] || '';
      if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
      return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
    };
    const buildAllotteeSignatureBlock = (names: string[]) => {
      const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
      if (!clean.length) return '';
      const esc = (value: string) =>
        String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      return clean
        .map(
          (name) => `
<div style="margin-top:16px;">
  <div style="height:36px;"></div>
  <div style="border-top:1px solid #000;max-width:260px;"></div>
  <div style="margin-top:4px;">${esc(name)}</div>
</div>`
        )
        .join('');
    };
    const unitAmountValue = Number((unit as any)?.unitAmount || 0) || 0;
    const ceilHundred = (value: number) => Math.ceil(value / 100) * 100;
    const hasMaleAllottee = customers.some(
      (c) => String(c?.gender || '').trim().toLowerCase() === 'male'
    );
    const gstValue = computeGstAmount(unitAmountValue);
    const stampDutyValue = unitAmountValue > 0 ? ceilHundred(ceilHundred(unitAmountValue) * 0.049) : 0;
    const registrationChargesValue =
      unitAmountValue <= 0
        ? 0
        : customers.length === 0 || hasMaleAllottee
          ? ceilHundred(ceilHundred(unitAmountValue) * 0.01) + 1200
          : 1200;
    const formatAmount = (value: number) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : '');

    const templatesSnap = await getDocs(
      query(collection(db, 'officeDocumentTemplates'), where('templateId', '==', 'booking_form_v1'))
    );
    const templates = templatesSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
    const projectMatched = templates.filter((t: any) =>
      Array.isArray(t.assignedProjectIds) &&
      t.assignedProjectIds.map((x: any) => String(x)).includes(String(projectId || ''))
    );
    const toMillis = (value: any) =>
      typeof value?.toMillis === 'function'
        ? value.toMillis()
        : typeof value?.seconds === 'number'
        ? value.seconds * 1000
        : 0;
    const pickNewest = (rows: any[]) =>
      [...rows].sort((a, b) => {
        const versionA = Number(a?.version || 0) || 0;
        const versionB = Number(b?.version || 0) || 0;
        if (versionA !== versionB) return versionB - versionA;
        const updatedA = toMillis(a?.updatedAt || a?.createdAt);
        const updatedB = toMillis(b?.updatedAt || b?.createdAt);
        if (updatedA !== updatedB) return updatedB - updatedA;
        return String(b?.id || '').localeCompare(String(a?.id || ''));
      })[0] || null;
    const selectedTemplate = pickNewest(projectMatched) || pickNewest(templates) || null;
    if (!selectedTemplate) throw new Error('booking_form_v1 template not found.');

    const projectSnap = await getDoc(doc(db, 'projects', projectId));
    const projectRow = projectSnap.exists() ? { id: projectSnap.id, ...(projectSnap.data() as any) } : null;
    const cpId = String(projectRow?.companyProfileId || '').trim();
    const companySnap = cpId ? await getDoc(doc(db, 'companyProfiles', cpId)) : null;
    const companyProfileRow = companySnap?.exists()
      ? ({ id: companySnap.id, ...(companySnap.data() as any) })
      : null;
    const digits = String(companyProfileRow?.mobile || '').replace(/\D/g, '');
    const formattedMobile = digits.length > 5 ?
      `+91 ${digits.slice(-10, -5)} ${digits.slice(-5)}` :
      (companyProfileRow?.mobile || '');
    let transactionRowsForTemplate: any[] = [];
    try {
      const txSnap = await getDocs(
        query(
          collection(db, 'projects', projectId, 'bankTransactions'),
          where('unitId', '==', unit.id),
          where('txnType', '==', 'receipt'),
          limit(200)
        )
      );
      transactionRowsForTemplate = txSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
    } catch (txErr) {
      console.warn('Could not load bank transactions for booking-form placeholders', txErr);
    }
    const paymentTemplateTokens = buildPaymentTemplateTokens({
      transactions: transactionRowsForTemplate,
      unitAmount: unitAmountValue,
      gstAmount: gstValue,
      formatDate: formatDDMMYYYY,
      formatAmount,
    });
    const stampDutyToken = String(paymentTemplateTokens['{{stamp_duty}}'] || '').trim();
    const registrationChargesToken = String(paymentTemplateTokens['{{registration_charges}}'] || '').trim();
    const advocateAgreementFeeToken = String(paymentTemplateTokens['{{advocate_agreement_fee}}'] || '').trim();
    const advocateDeedFeeToken = String(paymentTemplateTokens['{{advocate_deed_fee}}'] || '').trim();
    const advocateFeeToken = String(paymentTemplateTokens['{{advocate_fee}}'] || '').trim();
    const advocateAgreementFeeValue = Number(projectRow?.advocateAgreementFee || 0) || 0;
    const advocateDeedFeeValue = Number(projectRow?.advocateDeedFee || 0) || 0;
    const bookingFormDraftDocRef = doc(
      db,
      'projects',
      projectId,
      'units',
      unit.id,
      'bookings',
      effectiveBookingId,
      'draft',
      'booking_form_v1'
    );
    const bookingFormExistingSnap = await getDoc(bookingFormDraftDocRef);
    const resolvedRefNo = await resolveTemplateRefNo({
      visualModel: selectedTemplate?.visualModel || {},
      bookingId: effectiveBookingId,
      docStorageKey: String(selectedTemplate?.templateId || 'booking_form_v1'),
      existingRefNo: String((bookingFormExistingSnap.data() as any)?.refNo || '').trim(),
    });
    const agreementNumberToken =
      resolveWorkflowInputTextToken('agreement_number', 'agreementNumber', 'sale_agreement_number', 'saleAgreementNumber') ||
      resolveBookingTextToken('agreementNumber', 'agreement_number', 'saleAgreementNumber');
    const deedNumberToken =
      resolveWorkflowInputTextToken('deed_number', 'deedNumber') ||
      resolveBookingTextToken('deedNumber', 'deed_number', 'saleDeedNumber');
    const agreementDateToken =
      resolveWorkflowInputDateToken('agreement_date', 'agreementDate', 'sale_agreement_date', 'saleAgreementDate') ||
      resolveBookingDateToken('agreementDate', 'agreement_date', 'sale_agreement_date', 'saleAgreementDate');
    const agreementDateResolved =
      String(agreementDateToken || '').trim() ||
      (saleAgreementDateDisplay === '—' ? '' : String(saleAgreementDateDisplay || '').trim());
    const deedDateToken =
      resolveWorkflowInputDateToken('deed_date', 'deedDate') ||
      resolveBookingDateToken('deedDate', 'deed_date', 'saleDeedDate');
    const agreementLinkToken = resolveBookingTextToken('agreementLink', 'agreement_link', 'saleAgreementLink');
    const deedLinkToken = resolveBookingTextToken('deedLink', 'deed_link', 'saleDeedLink');

    const replacements: Record<string, string> = {
      ...paymentTemplateTokens,
      '{{project_name}}': context.projectName,
      '{{unit_no}}': context.unitNumber,
      '{{unit_number}}': context.unitNumber,
      '{{allottee_names}}': context.allotteeNames.join(', '),
      '{{allottee_name}}': context.allotteeNames.join(', '),
      '{{allottee_primary_name}}': context.allotteeNames[0] || '',
      '{{allottee_names_lines}}': context.allotteeNames.join('<br/>'),
      '{{allottee_names_comma_and}}': joinNamesCommaAnd(context.allotteeNames),
      '{{allottee_names_signature_block}}': buildAllotteeSignatureBlock(context.allotteeNames),
      '{{amount}}': context.amountText,
      '{{unit_amount}}': context.amountText,
      '{{issue_date}}': context.issueDate,
      '{{ref_no}}': resolvedRefNo,
      '{{receipt_no}}': `${resolvedRefNo}-${String(paymentTemplateTokens['{{payment_sequence_no}}'] || '1')}`,
      '{{carpet_area}}': context.carpetArea,
      '{{area_of_exclusive_balcony}}': context.exclusiveBalconyArea,
      '{{area_of_exclusive_wash}}': context.exclusiveWashArea,
      '{{area_of_exclusive_open_terrace}}': context.openTerraceArea,
      '{{vanvechayel_area}}': context.vanvechayelArea,
      '{{built_up_area}}': context.builtUpArea,
      '{{building_no}}': context.buildingNo,
      '{{floor_eng}}': context.floorEng,
      ...buildUniversalOfficeTemplateTokens({
        primaryCustomerMobile,
        loanBankName: latestLoanSanctionData?.sanctionBankName,
        loanSanctionAmount: latestLoanSanctionData?.sanctionAmount,
        reasonNotTakingSbi: latestLoanSanctionData?.nonSbiReason,
        pendingAmount: unitAmountValue - customerReceiptTotal,
        formatAmount,
      }),
      '{{gst}}': formatAmount(gstValue),
      '{{stamp_duty}}': stampDutyToken || formatAmount(stampDutyValue),
      '{{registration_charges}}': registrationChargesToken || formatAmount(registrationChargesValue),
      '{{advocate_agreement_fee}}': advocateAgreementFeeToken || formatAmount(advocateAgreementFeeValue),
      '{{advocate_deed_fee}}': advocateDeedFeeToken || formatAmount(advocateDeedFeeValue),
      '{{advocate_fee}}': advocateFeeToken || formatAmount(advocateAgreementFeeValue + advocateDeedFeeValue),
      '{{agreement_number}}': agreementNumberToken,
      '{{deed_number}}': deedNumberToken,
      '{{agreement_date}}': agreementDateResolved,
      '{{sale_agreement_date}}': agreementDateResolved,
      '{{deed_date}}': deedDateToken,
      '{{agreement_link}}': agreementLinkToken,
      '{{deed_link}}': deedLinkToken,
      '{{request_date}}': context.requestDate ? formatDDMMYYYY(dateFromYMD(context.requestDate) || new Date()) : '',
      '{{request_time}}': context.requestTime,
      '{{request_datetime}}': context.requestDateTimeAmPm,
      '{{request_datetime_ampm}}': context.requestDateTimeAmPm,
      '{{company_logo_url}}': String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
      '{{project_logo_url}}': resolveProjectLogoForTemplate(projectRow, selectedTemplate),
      '{{project_document_image_url}}': String(projectRow?.documentImageUrl || ''),
      '{{company_address}}': String(companyProfileRow?.address || ''),
      '{{company_mobile}}': String(formattedMobile || companyProfileRow?.mobile || ''),
      '{{company_email}}': String(companyProfileRow?.email || ''),
      '{{company_name}}': String(companyProfileRow?.name || ''),
    };
    // Buyer blocks, signatures and office details. From the pop-up these are the values just entered;
    // a regenerate without the pop-up uses what is saved on the booking.
    const fromDialog = conditionOverride !== undefined;
    const savedBooking = booking as any;
    const formDateYmd = String((fromDialog ? bookingFormDraftDate : savedBooking?.bookingFormDate || bookingFormDraftDate) || '').trim();
    const formDateText = (() => {
      const dt = dateFromYMD(formDateYmd);
      return dt ? formatDDMMYYYY(dt) : '';
    })();
    const printBuyers = customers.map((c) => printBuyerFromCustomer(c, formDateYmd));
    const earnestValue = Number(fromDialog ? bookingFormEarnest : savedBooking?.earnestAmount) || 0;
    Object.assign(replacements, {
      '{{form_date}}': formDateText,
      '{{booking_code}}': String(savedBooking?.shortBookingCode || '').trim(),
      '{{flat_line}}': flatLine(context.unitNumber, context.buildingNo, context.floorEng),
      '{{layout_class}}': layoutClass(printBuyers.length),
      '{{buyers_block}}': buyersBlock(printBuyers),
      '{{main_signatures}}': mainSignatures(printBuyers, formDateText),
      '{{buyer_signature_lines}}': signatureLines(printBuyers, formDateText),
      '{{buyer_signature_lines_blank}}': signatureLines(printBuyers, null),
      '{{agreement_term_months}}': String((fromDialog ? bookingFormTermMonths : savedBooking?.agreementTermMonths) || ''),
      '{{earnest_amount}}': earnestValue ? formatAmount(earnestValue) : '',
      '{{committee_boxes}}': committeeBoxes(fromDialog ? bookingFormCommittee : savedBooking?.societyCommittee),
      '{{documents_checklist}}': documentsChecklist(fromDialog ? bookingFormDocs : savedBooking?.bookingFormDocs),
    });
    const templateHtml = String(
      selectedTemplate?.visualModel?.body ||
      selectedTemplate?.bodyHtml ||
      selectedTemplate?.renderHtml ||
      selectedTemplate?.visualModel?.renderHtml ||
      ''
    ).trim();
    if (!templateHtml) throw new Error('Template content is empty.');
    const effectiveCondition = conditionOverride === undefined ? booking?.paymentCondition : conditionOverride;
    const effectiveConditionDate = conditionOverride === undefined
      ? String(booking?.bookingFormDate || bookingFormDraftDate)
      : bookingFormDraftDate;
    const conditionBlock = effectiveCondition ? paymentConditionHtml(effectiveCondition, effectiveConditionDate) : '';
    const roadmapToken = '{{payment_condition_roadmap}}';
    const hasConditionToken = /\{\{payment_condition_roadmap\}\}/i.test(templateHtml);
    const paymentSectionAnchor = '<div style="display:flex;justify-content:space-between;align-items:flex-end;margin:6mm 0 3mm;font-weight:700;">';
    let templateWithRoadmapToken = templateHtml;
    if (conditionBlock && !hasConditionToken) {
      const anchorCount = templateHtml.split(paymentSectionAnchor).length - 1;
      if (anchorCount !== 1) throw new Error('Could not locate the payment section in the booking form template.');
      templateWithRoadmapToken = templateHtml.replace(paymentSectionAnchor, `${roadmapToken}\n\n    ${paymentSectionAnchor}`);
    }
    const rendered = applyTemplateTokens(injectRuntimeRefDateTokens(templateWithRoadmapToken), {
      ...replacements,
      [roadmapToken]: conditionBlock,
    });
    const isFullHtml = /<html[\s>]/i.test(rendered);
    const htmlToRender = isFullHtml
      ? rendered
      : `<!doctype html><html><head><meta charset="utf-8" /><title>Booking Form</title></head><body>${rendered}</body></html>`;

    const functions = getFunctions();
    const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
    const vm = selectedTemplate?.visualModel || {};
    const runtimeVisualModel = {
      ...vm,
      backgroundImageUrl: String((vm as any)?.backgroundImageUrl || projectRow?.backgroundImageUrl || ''),
      backgroundOpacity:
        (vm as any)?.backgroundOpacity ??
        (String(projectRow?.backgroundImageUrl || '').trim() ? 0.21 : 0),
      issueDate: formatDDMMYYYY(new Date()),
      refNo: String(replacements['{{ref_no}}'] || '').trim(),
      // The date stamp in the footer belongs on the terms page, which moves with the number of buyers.
      ...(/\{\{\s*buyers_block\s*\}\}/.test(templateHtml) ? { footerOverlayPageNumbers: String(termsPageNumber(printBuyers)) } : {}),
    };
    const result = await renderTemplatePdf(
      buildPdfPayloadFromTemplate({
        html: htmlToRender,
        tokenValues: replacements,
        visualModel: runtimeVisualModel,
        companyLogoUrl: String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
        projectLogoUrl: resolveProjectLogoForTemplate(projectRow, selectedTemplate),
        companyAddress: String(companyProfileRow?.address || ''),
        companyMobile: String(companyProfileRow?.mobile || ''),
        companyEmail: String(companyProfileRow?.email || ''),
        companyFontFamily: String(companyProfileRow?.fontFamily || ''),
        companyFontUrl: String(companyProfileRow?.fontUrl || ''),
        companyFontFormat: String(companyProfileRow?.fontFormat || ''),
        gujaratiFontFamily: String(companyProfileRow?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(companyProfileRow?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(companyProfileRow?.gujaratiFontFormat || ''),
        paperSize: String(vm.paperSize || 'A4'),
        fileName: `booking_form_${String(unit?.unitNumber || unit?.id || 'draft')}`,
      })
    );

    const data = (result?.data || {}) as {
      fileName?: string;
      mimeType?: string;
      pdfBytes?: number[] | Record<string, number>;
      pdfBase64?: string | { type?: string; data?: number[] };
    };
    const rawBytes = Array.isArray(data.pdfBytes)
      ? data.pdfBytes
      : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes)
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : [];
    let pdfBuffer = rawBytes.length ? Uint8Array.from(rawBytes) : new Uint8Array();
    if (!pdfBuffer.length) {
      const base64Raw =
        typeof data.pdfBase64 === 'string'
          ? data.pdfBase64
          : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
          ? (() => {
              const source = data.pdfBase64?.data || [];
              let chunk = '';
              for (let i = 0; i < source.length; i += 0x8000) {
                const slice = source.slice(i, i + 0x8000);
                chunk += String.fromCharCode(...slice);
              }
              return btoa(chunk);
            })()
          : '';
      const normalizedBase64 = base64Raw
        .replace(/^data:application\/pdf;base64,/i, '')
        .replace(/\s+/g, '');
      if (normalizedBase64) {
        const binary = atob(normalizedBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        pdfBuffer = bytes;
      }
    }
    if (!pdfBuffer.length) throw new Error('Invalid PDF payload received from server.');

    const by = currentUser.email;
    const uploadedAtMs = Date.now();
    const unitNo = String(unit?.unitNumber || unit?.id || 'Unit').trim();
    const outputType = getTemplateGeneratedOutputType(selectedTemplate);
    const displaySection = getTemplateShowInSection(selectedTemplate, outputType);
    const rawDraftNameTemplate = String(selectedTemplate?.draftFileNameTemplate || '')
      .replace(/\{\{\s*unit[_\s-]*no\s*\}\}/gi, '{{unit_no}}')
      .replace(/\{\{\s*unit[_\s-]*number\s*\}\}/gi, '{{unit_no}}')
      .trim();
    const resolvedDraftName = rawDraftNameTemplate ?
      applyTemplateTokens(rawDraftNameTemplate, replacements) :
      '';
    const fallbackBaseName = outputType === 'final' ? `${unitNo} - BOOKING FORM` : `${unitNo} - BOOKING_FORM_DRAFT`;
    const fileDisplayNameBase = String(resolvedDraftName || '').trim() || fallbackBaseName;
    const fileDisplayName = /\.pdf$/i.test(fileDisplayNameBase) ? fileDisplayNameBase : `${fileDisplayNameBase}.pdf`;
    const safeFileName = fileDisplayName.replace(/[^\w.-]+/g, '_');
    const finalDocRef = doc(
      db,
      'projects',
      projectId,
      'units',
      unit.id,
      'bookings',
      effectiveBookingId,
      'uploaded',
      'booking_form_v1'
    );
    const finalExistingSnap = await getDoc(finalDocRef);
    const docRef = outputType === 'final' ? finalDocRef : bookingFormDraftDocRef;
    const existingSnap = outputType === 'final' ? finalExistingSnap : bookingFormExistingSnap;
    const existingPath = String((existingSnap?.data() as any)?.files?.[0]?.path || '').trim();
    const path = `bookings/${effectiveBookingId}/${outputType === 'final' ? 'uploaded' : 'draft'}/booking_form_v1/${safeFileName}`;
    if (existingPath && existingPath !== path) {
      try {
        await deleteObject(ref(storage, existingPath));
      } catch (e) {
        console.warn(`Could not delete previous booking form ${outputType} file`, e);
      }
    }
    if (outputType === 'final' && bookingFormExistingSnap.exists()) {
      const staleDraftPath = String((bookingFormExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
      if (staleDraftPath) {
        try {
          await deleteObject(ref(storage, staleDraftPath));
        } catch (e) {
          console.warn('Could not delete stale booking form draft file', e);
        }
      }
      await deleteDoc(bookingFormDraftDocRef).catch(() => undefined);
    }
    if (outputType === 'draft' && finalExistingSnap.exists()) {
      const staleFinalPath = String((finalExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
      if (staleFinalPath) {
        try {
          await deleteObject(ref(storage, staleFinalPath));
        } catch (e) {
          console.warn('Could not delete stale final booking form file', e);
        }
      }
      await deleteDoc(finalDocRef).catch(() => undefined);
    }
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, pdfBuffer, {
      contentType: data.mimeType || 'application/pdf',
      cacheControl: 'no-store, max-age=0',
    });
    const baseUrl = await getDownloadURL(storageRef);
    const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${uploadedAtMs}`;

    await setDoc(
      docRef,
        {
          type: outputType === 'final' ? 'booking_form_v1' : 'bookingFormDraft',
          templateId: 'booking_form_v1',
          templateName: String(selectedTemplate?.name || 'Booking Form'),
          displayName: String(selectedTemplate?.name || 'Booking Form'),
          generatedFinal: outputType === 'final',
          displaySection,
          refNo: String(replacements['{{ref_no}}'] || '').trim() || null,
          files: [
          {
            name: fileDisplayName,
            originalName: fileDisplayName,
            url,
            path,
            uploadedAtMs,
            uploadedBy: by,
          },
        ],
        updatedAt: serverTimestamp(),
        updatedBy: by,
        createdAt: serverTimestamp(),
        createdBy: by,
      },
      { merge: true }
    );
  }, [
    assertUnitDataCurrent,
    bookingFormCommittee,
    bookingFormDocs,
    bookingFormEarnest,
    bookingFormTermMonths,
    bookingFormDraftDate,
    bookingFormDraftTime,
    bookingFormDoc?.files?.length,
    booking?.paymentCondition,
    booking?.bookingFormDate,
    computeGstAmount,
    currentUser?.email,
    customerReceiptTotal,
    customers,
    db,
    ensureBookingForDraft,
    injectRuntimeRefDateTokens,
    latestLoanSanctionData?.nonSbiReason,
    latestLoanSanctionData?.sanctionAmount,
    latestLoanSanctionData?.sanctionBankName,
    primaryCustomerMobile,
    projectId,
    projectName,
    resolveBookingDateToken,
    resolveBookingTextToken,
    resolveTemplateRefNo,
    resolveWorkflowInputDateToken,
    resolveWorkflowInputTextToken,
    saleAgreementDateDisplay,
    unit,
  ]);

  const generatePaymentDetailsV1Draft = useCallback(
    async (effectiveBookingIdOverride?: string, templateIdOverride = 'payment_details_v2') => {
      if (!unit?.id || !projectId) return;
      if (!currentUser?.email) throw new Error('Not signed in.');
      assertUnitDataCurrent();
      const effectiveBookingId = effectiveBookingIdOverride || (await ensureBookingForDraft());
      const paymentDetailsTemplateId = String(templateIdOverride || 'payment_details_v2').trim() || 'payment_details_v2';

      const templatesSnap = await getDocs(
        query(collection(db, 'officeDocumentTemplates'), where('templateId', '==', paymentDetailsTemplateId))
      );
      const templates = templatesSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      if (!templates.length) return;
      const projectMatched = templates.filter((t: any) =>
        Array.isArray(t.assignedProjectIds) &&
        t.assignedProjectIds.map((x: any) => String(x)).includes(String(projectId || ''))
      );
      const toMillis = (value: any) =>
        typeof value?.toMillis === 'function'
          ? value.toMillis()
          : typeof value?.seconds === 'number'
          ? value.seconds * 1000
          : 0;
      const pickNewest = (rows: any[]) =>
        [...rows].sort((a, b) => {
          const versionA = Number(a?.version || 0) || 0;
          const versionB = Number(b?.version || 0) || 0;
          if (versionA !== versionB) return versionB - versionA;
          const updatedA = toMillis(a?.updatedAt || a?.createdAt);
          const updatedB = toMillis(b?.updatedAt || b?.createdAt);
          if (updatedA !== updatedB) return updatedB - updatedA;
          return String(b?.id || '').localeCompare(String(a?.id || ''));
        })[0] || null;
      const selectedTemplate = pickNewest(projectMatched) || pickNewest(templates) || null;
      if (!selectedTemplate) return;

      const projectSnap = await getDoc(doc(db, 'projects', projectId));
      const projectRow = projectSnap.exists() ? { id: projectSnap.id, ...(projectSnap.data() as any) } : null;
      const cpId = String(projectRow?.companyProfileId || '').trim();
      const companySnap = cpId ? await getDoc(doc(db, 'companyProfiles', cpId)) : null;
      const companyProfileRow = companySnap?.exists()
        ? ({ id: companySnap.id, ...(companySnap.data() as any) })
        : null;

      const txSnap = await getDocs(
        query(
          collection(db, 'projects', projectId, 'bankTransactions'),
          where('unitId', '==', unit.id),
          limit(300)
        )
      );
      const transactionRows = txSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      const paymentDetailsTransactionRows = transactionRows;

      const unitAmountValue = Number((unit as any)?.unitAmount || 0) || 0;
      const ceilHundred = (value: number) => Math.ceil(value / 100) * 100;
      const hasMaleAllottee = customers.some(
        (c) => String(c?.gender || '').trim().toLowerCase() === 'male'
      );
      const gstValue = computeGstAmount(unitAmountValue);
      const stampDutyValue = unitAmountValue > 0 ? ceilHundred(ceilHundred(unitAmountValue) * 0.049) : 0;
      const registrationChargesValue =
        unitAmountValue <= 0
          ? 0
          : customers.length === 0 || hasMaleAllottee
            ? ceilHundred(ceilHundred(unitAmountValue) * 0.01) + 1200
            : 1200;
      const formatAmount = (value: number) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : '');
      const paymentTemplateTokens = buildPaymentTemplateTokens({
        transactions: paymentDetailsTransactionRows,
        unitAmount: unitAmountValue,
        gstAmount: gstValue,
        formatDate: formatDDMMYYYY,
        formatAmount,
      });
      const netCustomerReceiptTotal = buildNetReceiptRows(paymentDetailsTransactionRows)
        .filter((row: any) => String(row?.receiptCategory || '').toLowerCase() === 'customer')
        .reduce((sum: number, row: any) => sum + (Number(row?.amount || 0) || 0), 0);
      const stampDutyToken = String(paymentTemplateTokens['{{stamp_duty}}'] || '').trim();
      const registrationChargesToken = String(paymentTemplateTokens['{{registration_charges}}'] || '').trim();
      const advocateAgreementFeeToken = String(paymentTemplateTokens['{{advocate_agreement_fee}}'] || '').trim();
      const advocateDeedFeeToken = String(paymentTemplateTokens['{{advocate_deed_fee}}'] || '').trim();
      const advocateFeeToken = String(paymentTemplateTokens['{{advocate_fee}}'] || '').trim();
      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
      const allotteeNames = customers
        .map((c) => String(c.displayName || c.name || '').trim())
        .filter(Boolean);
      const agreementNumberToken =
        resolveWorkflowInputTextToken('agreement_number', 'agreementNumber', 'sale_agreement_number', 'saleAgreementNumber') ||
        resolveBookingTextToken('agreementNumber', 'agreement_number', 'saleAgreementNumber');
      const deedNumberToken =
        resolveWorkflowInputTextToken('deed_number', 'deedNumber') ||
        resolveBookingTextToken('deedNumber', 'deed_number', 'saleDeedNumber');
      const agreementDateToken =
        resolveWorkflowInputDateToken('agreement_date', 'agreementDate', 'sale_agreement_date', 'saleAgreementDate') ||
        resolveBookingDateToken('agreementDate', 'agreement_date', 'sale_agreement_date', 'saleAgreementDate');
      const agreementDateResolved =
        String(agreementDateToken || '').trim() ||
        (saleAgreementDateDisplay === '—' ? '' : String(saleAgreementDateDisplay || '').trim());
      const deedDateToken =
        resolveWorkflowInputDateToken('deed_date', 'deedDate') ||
        resolveBookingDateToken('deedDate', 'deed_date', 'saleDeedDate');
      const agreementLinkToken = resolveBookingTextToken('agreementLink', 'agreement_link', 'saleAgreementLink');
      const deedLinkToken = resolveBookingTextToken('deedLink', 'deed_link', 'saleDeedLink');
      const constructionStageStatusToken =
        resolveWorkflowInputTextToken('construction_stage_status', 'constructionStageStatus') ||
        resolveBookingTextToken('construction_stage_status', 'constructionStageStatus') ||
        '_______';
      const demandAmountTokenRaw =
        resolveWorkflowInputTextToken('demand_amount', 'demandAmount') ||
        resolveBookingTextToken('demand_amount', 'demandAmount');
      const demandAmountToken = String(demandAmountTokenRaw || '').trim() || '____________________';
      const paymentDraftDocRef = doc(
        db,
        'projects',
        projectId,
        'units',
        unit.id,
        'bookings',
        effectiveBookingId,
        'draft',
        paymentDetailsTemplateId
      );
      const paymentExistingSnap = await getDoc(paymentDraftDocRef);
      const resolvedRefNo = await resolveTemplateRefNo({
        visualModel: selectedTemplate?.visualModel || {},
        bookingId: effectiveBookingId,
        docStorageKey: String(selectedTemplate?.templateId || paymentDetailsTemplateId),
        existingRefNo: String((paymentExistingSnap.data() as any)?.refNo || '').trim(),
      });

      const replacements: Record<string, string> = {
        ...paymentTemplateTokens,
        '{{project_name}}': String(projectRow?.name || projectName || ''),
        '{{unit_no}}': unitNo,
        '{{unit_number}}': unitNo,
        '{{allottee_names}}': allotteeNames.join(', '),
        '{{allottee_name}}': allotteeNames.join(', '),
        '{{allottee_primary_name}}': allotteeNames[0] || '',
        '{{allottee_names_lines}}': allotteeNames.join('<br/>'),
        '{{amount}}': formatAmount(unitAmountValue),
        '{{unit_amount}}': formatAmount(unitAmountValue),
        '{{issue_date}}': formatDDMMYYYY(new Date()),
        '{{ref_no}}': resolvedRefNo,
        '{{carpet_area}}': String((unit as any)?.carpetArea ?? ''),
        '{{area_of_exclusive_balcony}}': String((unit as any)?.exclusiveBalconyArea ?? ''),
        '{{area_of_exclusive_wash}}': String(
          (unit as any)?.exclusiveWashArea ??
            (unit as any)?.areaOfExclusiveWash ??
            (unit as any)?.area_of_exclusive_wash ??
            (unit as any)?.customFields?.area_of_exclusive_wash ??
            (unit as any)?.['AREA OF EXCLUSIVE WASH'] ??
            ''
        ),
        '{{area_of_exclusive_open_terrace}}': String(
          (unit as any)?.openTerraceArea ??
            (unit as any)?.areaOfExclusiveOpenTerrace ??
            (unit as any)?.area_of_exclusive_open_terrace ??
            (unit as any)?.customFields?.area_of_exclusive_open_terrace ??
            (unit as any)?.['AREA OF EXCLUSIVE OPEN TERRACE'] ??
            ''
        ),
        '{{vanvechayel_area}}': String((unit as any)?.vanvechayelArea || (unit as any)?.vanvechayel_area || ''),
        '{{built_up_area}}': String((unit as any)?.builtUpArea || (unit as any)?.built_up_area || ''),
        '{{building_no}}': String((unit as any)?.buildingNo || (unit as any)?.building_no || ''),
        '{{floor_eng}}': String((unit as any)?.floorEng || (unit as any)?.floor_eng || ''),
        ...buildUniversalOfficeTemplateTokens({
          primaryCustomerMobile,
          loanBankName: latestLoanSanctionData?.sanctionBankName,
          loanSanctionAmount: latestLoanSanctionData?.sanctionAmount,
          reasonNotTakingSbi: latestLoanSanctionData?.nonSbiReason,
          pendingAmount: unitAmountValue - netCustomerReceiptTotal,
          formatAmount,
        }),
        '{{gst}}': formatAmount(gstValue),
        '{{stamp_duty}}': stampDutyToken || formatAmount(stampDutyValue),
        '{{registration_charges}}': registrationChargesToken || formatAmount(registrationChargesValue),
        '{{advocate_agreement_fee}}': advocateAgreementFeeToken || formatAmount(Number(projectRow?.advocateAgreementFee || 0) || 0),
        '{{advocate_deed_fee}}': advocateDeedFeeToken || formatAmount(Number(projectRow?.advocateDeedFee || 0) || 0),
        '{{advocate_fee}}': advocateFeeToken,
        '{{agreement_number}}': agreementNumberToken,
        '{{deed_number}}': deedNumberToken,
        '{{agreement_date}}': agreementDateResolved,
        '{{sale_agreement_date}}': agreementDateResolved,
        '{{deed_date}}': deedDateToken,
        '{{agreement_link}}': agreementLinkToken,
        '{{deed_link}}': deedLinkToken,
        '{{construction_stage_status}}': constructionStageStatusToken,
        '{{demand_amount}}': demandAmountToken,
        '{{company_logo_url}}': String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
        '{{project_logo_url}}': resolveProjectLogoForTemplate(projectRow, selectedTemplate),
        '{{project_document_image_url}}': String(projectRow?.documentImageUrl || ''),
        '{{company_address}}': String(companyProfileRow?.address || ''),
        '{{company_mobile}}': String(companyProfileRow?.mobile || ''),
        '{{company_email}}': String(companyProfileRow?.email || ''),
        '{{company_name}}': String(companyProfileRow?.name || ''),
      };

      const templateHtml = String(
        selectedTemplate?.visualModel?.body ||
          selectedTemplate?.bodyHtml ||
          selectedTemplate?.renderHtml ||
          selectedTemplate?.visualModel?.renderHtml ||
          ''
      ).trim();
      if (!templateHtml) return;
      let rendered = applyTemplateTokens(injectRuntimeRefDateTokens(templateHtml), replacements);
      const isFullHtml = /<html[\s>]/i.test(rendered);
      const htmlToRender = isFullHtml
        ? rendered
        : `<!doctype html><html><head><meta charset="utf-8" /><title>Payment Details</title></head><body>${rendered}</body></html>`;

      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const vm = selectedTemplate?.visualModel || {};
      const runtimeVisualModel = {
        ...vm,
        backgroundImageUrl: String((vm as any)?.backgroundImageUrl || projectRow?.backgroundImageUrl || ''),
        backgroundOpacity:
          (vm as any)?.backgroundOpacity ??
          (String(projectRow?.backgroundImageUrl || '').trim() ? 0.21 : 0),
        issueDate: formatDDMMYYYY(new Date()),
        refNo: String(replacements['{{ref_no}}'] || '').trim(),
      };
      const result = await renderTemplatePdf(
        buildPdfPayloadFromTemplate({
          html: htmlToRender,
          tokenValues: replacements,
          visualModel: runtimeVisualModel,
          companyLogoUrl: String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
          projectLogoUrl: resolveProjectLogoForTemplate(projectRow, selectedTemplate),
          companyAddress: String(companyProfileRow?.address || ''),
          companyMobile: String(companyProfileRow?.mobile || ''),
          companyEmail: String(companyProfileRow?.email || ''),
          companyFontFamily: String(companyProfileRow?.fontFamily || ''),
          companyFontUrl: String(companyProfileRow?.fontUrl || ''),
          companyFontFormat: String(companyProfileRow?.fontFormat || ''),
          gujaratiFontFamily: String(companyProfileRow?.gujaratiFontFamily || ''),
          gujaratiFontUrl: String(companyProfileRow?.gujaratiFontUrl || ''),
          gujaratiFontFormat: String(companyProfileRow?.gujaratiFontFormat || ''),
          paperSize: String(vm.paperSize || 'A4'),
          fileName: `payment_details_${unitNo || unit.id}`,
        })
      );

      const data = (result?.data || {}) as {
        mimeType?: string;
        pdfBytes?: number[] | Record<string, number>;
        pdfBase64?: string | { type?: string; data?: number[] };
      };
      const rawBytes = Array.isArray(data.pdfBytes)
        ? data.pdfBytes
        : data.pdfBytes && typeof data.pdfBytes === 'object'
        ? Object.keys(data.pdfBytes)
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
        : [];
      let pdfBuffer = rawBytes.length ? Uint8Array.from(rawBytes) : new Uint8Array();
      if (!pdfBuffer.length) {
        const base64Raw =
          typeof data.pdfBase64 === 'string'
            ? data.pdfBase64
            : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
            ? (() => {
                const source = data.pdfBase64?.data || [];
                let chunk = '';
                for (let i = 0; i < source.length; i += 0x8000) {
                  chunk += String.fromCharCode(...source.slice(i, i + 0x8000));
                }
                return btoa(chunk);
              })()
            : '';
        const normalizedBase64 = base64Raw
          .replace(/^data:application\/pdf;base64,/i, '')
          .replace(/\s+/g, '');
        if (normalizedBase64) {
          const binary = atob(normalizedBase64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
          pdfBuffer = bytes;
        }
      }
      if (!pdfBuffer.length) throw new Error('Invalid PDF payload received from server.');

      const by = currentUser.email;
      const uploadedAtMs = Date.now();
      const outputType = getTemplateGeneratedOutputType(selectedTemplate);
      const displaySection = getTemplateShowInSection(selectedTemplate, outputType);
      const rawDraftNameTemplate = String(selectedTemplate?.draftFileNameTemplate || '')
        .replace(/\{\{\s*unit[_\s-]*no\s*\}\}/gi, '{{unit_no}}')
        .replace(/\{\{\s*unit[_\s-]*number\s*\}\}/gi, '{{unit_no}}')
        .trim();
      const resolvedDraftName = rawDraftNameTemplate ? applyTemplateTokens(rawDraftNameTemplate, replacements) : '';
      const fallbackBaseName = outputType === 'final' ? `${unitNo || 'Unit'} - PAYMENT DETAILS` : `${unitNo || 'Unit'} - PAYMENT DETAILS`;
      const fileDisplayNameBase = String(resolvedDraftName || '').trim() || fallbackBaseName;
      const fileDisplayName = /\.pdf$/i.test(fileDisplayNameBase) ? fileDisplayNameBase : `${fileDisplayNameBase}.pdf`;
      const safeFileName = fileDisplayName.replace(/[^\w.-]+/g, '_');
      const finalDocRef = doc(
        db,
        'projects',
        projectId,
        'units',
        unit.id,
        'bookings',
        effectiveBookingId,
        'uploaded',
        paymentDetailsTemplateId
      );
      const finalExistingSnap = await getDoc(finalDocRef);
      const docRef = outputType === 'final' ? finalDocRef : paymentDraftDocRef;
      const existingSnap = outputType === 'final' ? finalExistingSnap : paymentExistingSnap;
      const existingPath = String((existingSnap?.data() as any)?.files?.[0]?.path || '').trim();
      const path = `bookings/${effectiveBookingId}/${outputType === 'final' ? 'uploaded' : 'draft'}/${paymentDetailsTemplateId}/${safeFileName}`;
      if (existingPath && existingPath !== path) {
        try {
          await deleteObject(ref(storage, existingPath));
        } catch (e) {
          console.warn(`Could not delete previous payment details ${outputType} file`, e);
        }
      }
      if (outputType === 'final' && paymentExistingSnap.exists()) {
        const staleDraftPath = String((paymentExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
        if (staleDraftPath) {
          try {
            await deleteObject(ref(storage, staleDraftPath));
          } catch (e) {
            console.warn('Could not delete stale payment details draft file', e);
          }
        }
        await deleteDoc(paymentDraftDocRef).catch(() => undefined);
      }
      if (outputType === 'draft' && finalExistingSnap.exists()) {
        const staleFinalPath = String((finalExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
        if (staleFinalPath) {
          try {
            await deleteObject(ref(storage, staleFinalPath));
          } catch (e) {
            console.warn('Could not delete stale final payment details file', e);
          }
        }
        await deleteDoc(finalDocRef).catch(() => undefined);
      }
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pdfBuffer, {
        contentType: data.mimeType || 'application/pdf',
        cacheControl: 'no-store, max-age=0',
      });
      const baseUrl = await getDownloadURL(storageRef);
      const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${uploadedAtMs}`;

      await setDoc(
        docRef,
        {
          type: outputType === 'final' ? paymentDetailsTemplateId : 'paymentDetailsDraft',
          templateId: paymentDetailsTemplateId,
          templateName: String(selectedTemplate?.name || 'Payment Details'),
          displayName: String(selectedTemplate?.name || 'Payment Details'),
          generatedFinal: outputType === 'final',
          displaySection,
          refNo: String(replacements['{{ref_no}}'] || '').trim() || null,
          files: [
            {
              name: fileDisplayName,
              originalName: fileDisplayName,
              url,
              path,
              uploadedAtMs,
              uploadedBy: by,
            },
          ],
          updatedAt: serverTimestamp(),
          updatedBy: by,
          createdAt: serverTimestamp(),
          createdBy: by,
        },
        { merge: true }
      );
    },
    [assertUnitDataCurrent, 
      computeGstAmount,
      customers,
      currentUser?.email,
      db,
      ensureBookingForDraft,
      injectRuntimeRefDateTokens,
      latestLoanSanctionData?.nonSbiReason,
      latestLoanSanctionData?.sanctionAmount,
      latestLoanSanctionData?.sanctionBankName,
      primaryCustomerMobile,
      projectId,
      projectName,
      resolveBookingDateToken,
      resolveBookingTextToken,
      resolveTemplateRefNo,
      resolveWorkflowInputDateToken,
      resolveWorkflowInputTextToken,
      saleAgreementDateDisplay,
      unit,
    ]
  );

  const generateDocumentFromTemplateId = useCallback(
    async (
      templateId: string,
      options?: {
        effectiveBookingIdOverride?: string;
        stepKey?: string;
        which?: 'main' | 'loan';
        tokenOverrides?: Record<string, string>;
        downloadOnly?: boolean;
        returnPdfBytes?: boolean;
        transactionRow?: any;
      }
    ) => {
      if (!unit?.id || !projectId) return;
      if (!currentUser?.email) throw new Error('Not signed in.');
      assertUnitDataCurrent();
      const safeTemplateSelector = String(templateId || '').trim();
      if (!safeTemplateSelector) throw new Error('Template id is missing.');
      const effectiveBookingId = options?.effectiveBookingIdOverride || (await ensureBookingForDraft());

      const templatesByIdSnap = await getDocs(
        query(collection(db, 'officeDocumentTemplates'), where('templateId', '==', safeTemplateSelector))
      );
      let templates = templatesByIdSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      let selectedByGroup = false;
      if (!templates.length) {
        const templatesByGroupSnap = await getDocs(
          query(collection(db, 'officeDocumentTemplates'), where('documentGroupKey', '==', safeTemplateSelector))
        );
        templates = templatesByGroupSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        selectedByGroup = templates.length > 0;
      }
      if (!templates.length) throw new Error(`Template not found: ${safeTemplateSelector}`);
      const normalizeBankName = (value: any) =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ');
      const sanctionBankNameNormalized = normalizeBankName(latestLoanSanctionData?.sanctionBankName);
      const templateLinkToBankEnabled = (row: any) =>
        Boolean(row?.linkToBank ?? row?.visualModel?.linkToBank);
      const templateLinkedBanks = (row: any) =>
        (Array.isArray(row?.linkedBanks)
          ? row.linkedBanks
          : Array.isArray(row?.visualModel?.linkedBanks)
            ? row.visualModel.linkedBanks
            : []
        )
          .map((value: any) => normalizeBankName(value))
          .filter(Boolean);
      if (selectedByGroup) {
        const bankLinkedTemplates = templates.filter((row: any) => templateLinkToBankEnabled(row));
        if (bankLinkedTemplates.length) {
          if (!sanctionBankNameNormalized) {
            throw new Error('Sanction bank name is required for this bank-linked template group.');
          }
          const matchedBankTemplates = bankLinkedTemplates.filter((row: any) =>
            templateLinkedBanks(row).includes(sanctionBankNameNormalized)
          );
          if (matchedBankTemplates.length) {
            templates = matchedBankTemplates;
          } else {
            const fallbackTemplates = templates.filter((row: any) => !templateLinkToBankEnabled(row));
            if (fallbackTemplates.length) {
              templates = fallbackTemplates;
            } else {
              throw new Error(`No template mapped for bank: ${latestLoanSanctionData?.sanctionBankName || 'Unknown'}`);
            }
          }
        }
      }

      const projectMatched = templates.filter((t: any) =>
        Array.isArray(t.assignedProjectIds) &&
        t.assignedProjectIds.map((x: any) => String(x)).includes(String(projectId || ''))
      );
      const toMillis = (value: any) =>
        typeof value?.toMillis === 'function'
          ? value.toMillis()
          : typeof value?.seconds === 'number'
          ? value.seconds * 1000
          : 0;
      const pickNewest = (rows: any[]) =>
        [...rows].sort((a, b) => {
          const versionA = Number(a?.version || 0) || 0;
          const versionB = Number(b?.version || 0) || 0;
          if (versionA !== versionB) return versionB - versionA;
          const updatedA = toMillis(a?.updatedAt || a?.createdAt);
          const updatedB = toMillis(b?.updatedAt || b?.createdAt);
          if (updatedA !== updatedB) return updatedB - updatedA;
          return String(b?.id || '').localeCompare(String(a?.id || ''));
        })[0] || null;
      const selectedTemplate = pickNewest(projectMatched) || pickNewest(templates) || null;
      if (!selectedTemplate) throw new Error(`Template not found: ${safeTemplateSelector}`);
      const resolvedTemplateId = String(selectedTemplate?.templateId || safeTemplateSelector).trim() || safeTemplateSelector;

      const projectSnap = await getDoc(doc(db, 'projects', projectId));
      const projectRow = projectSnap.exists() ? { id: projectSnap.id, ...(projectSnap.data() as any) } : null;
      const cpId = String(projectRow?.companyProfileId || '').trim();
      const companySnap = cpId ? await getDoc(doc(db, 'companyProfiles', cpId)) : null;
      const companyProfileRow = companySnap?.exists()
        ? ({ id: companySnap.id, ...(companySnap.data() as any) })
        : null;

      let transactionRows: any[] = [];
      try {
        const txSnap = await getDocs(
          query(
            collection(db, 'projects', projectId, 'bankTransactions'),
            where('unitId', '==', unit.id),
            limit(300)
          )
        );
        transactionRows = txSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      } catch (txErr) {
        console.warn('Could not load bank transactions for generic document placeholders', txErr);
      }

      const unitAmountValue = Number((unit as any)?.unitAmount || 0) || 0;
      const ceilHundred = (value: number) => Math.ceil(value / 100) * 100;
      const hasMaleAllottee = customers.some(
        (c) => String(c?.gender || '').trim().toLowerCase() === 'male'
      );
      const gstValue = computeGstAmount(unitAmountValue);
      const stampDutyValue = unitAmountValue > 0 ? ceilHundred(ceilHundred(unitAmountValue) * 0.049) : 0;
      const registrationChargesValue =
        unitAmountValue <= 0
          ? 0
          : customers.length === 0 || hasMaleAllottee
            ? ceilHundred(ceilHundred(unitAmountValue) * 0.01) + 1200
            : 1200;
      const formatAmount = (value: number) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : '');
      const paymentTemplateTokens = buildPaymentTemplateTokens({
        transactions: transactionRows,
        unitAmount: unitAmountValue,
        gstAmount: gstValue,
        formatDate: formatDDMMYYYY,
        formatAmount,
      });
      // Customer and GST payments each get their own receipt number series (REC/… and GST/…).
      const receiptCategoryOf = (row: any) => String(row?.receiptCategory || '').toLowerCase();
      const selectedReceiptCategory = receiptCategoryOf(options?.transactionRow) === 'gst' ? 'gst' : 'customer';
      const receiptRowsSorted = transactionRows
        .filter((row) => {
          const txnType = String(row?.txnType || '').toLowerCase();
          return txnType === 'receipt' && receiptCategoryOf(row) === selectedReceiptCategory;
        })
        .sort((a, b) => {
          const aAt =
            toMillis(a?.chqDate) ||
            toMillis(a?.txnDate) ||
            toMillis(a?.createdAt) ||
            toMillis(a?.updatedAt) ||
            Number.MAX_SAFE_INTEGER;
          const bAt =
            toMillis(b?.chqDate) ||
            toMillis(b?.txnDate) ||
            toMillis(b?.createdAt) ||
            toMillis(b?.updatedAt) ||
            Number.MAX_SAFE_INTEGER;
          if (aAt !== bAt) return aAt - bAt;
          return String(a?.id || '').localeCompare(String(b?.id || ''));
        });
      const selectedTransactionId = String(options?.transactionRow?.id || '').trim();
      const selectedReceiptRow =
        receiptRowsSorted.find((row) => String(row?.id || '').trim() === selectedTransactionId) ||
        (options?.transactionRow && receiptCategoryOf(options.transactionRow) === selectedReceiptCategory
          ? options.transactionRow
          : null);
      const selectedReceiptSequence =
        selectedReceiptRow
          ? Math.max(
              1,
              receiptRowsSorted.findIndex(
                (row) => String(row?.id || '').trim() === String(selectedReceiptRow?.id || '').trim()
              ) + 1
            )
          : Number(paymentTemplateTokens['{{payment_sequence_no}}'] || 1) || 1;
      const selectedReceiptDate = (() => {
        if (!selectedReceiptRow) return null;
        const selectedAtMs =
          toMillis(selectedReceiptRow?.chqDate) ||
          toMillis(selectedReceiptRow?.txnDate) ||
          toMillis(selectedReceiptRow?.createdAt) ||
          toMillis(selectedReceiptRow?.updatedAt) ||
          0;
        if (!selectedAtMs) return null;
        const value = new Date(selectedAtMs);
        return Number.isNaN(value.getTime()) ? null : value;
      })();

      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
      const allotteeNames = customers
        .map((c) => String(c.displayName || c.name || '').trim())
        .filter(Boolean);
      const joinNamesCommaAnd = (names: string[]) => {
        const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
        if (clean.length <= 1) return clean[0] || '';
        if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
        return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
      };

      const draftDocRef = doc(
        db,
        'projects',
        projectId,
        'units',
        unit.id,
        'bookings',
        effectiveBookingId,
        'draft',
        safeTemplateSelector
      );
      const finalDocRef = doc(
        db,
        'projects',
        projectId,
        'units',
        unit.id,
        'bookings',
        effectiveBookingId,
        'uploaded',
        safeTemplateSelector
      );
      const [draftExistingSnap, finalExistingSnap] = await Promise.all([getDoc(draftDocRef), getDoc(finalDocRef)]);
      const resolvedRefNo = await resolveTemplateRefNo({
        visualModel: selectedTemplate?.visualModel || {},
        bookingId: effectiveBookingId,
        docStorageKey: safeTemplateSelector,
        existingRefNo: String(
          (draftExistingSnap.data() as any)?.refNo ||
          (finalExistingSnap.data() as any)?.refNo ||
          ''
        ).trim(),
      });
      const agreementNumberToken =
        resolveWorkflowInputTextToken('agreement_number', 'agreementNumber', 'sale_agreement_number', 'saleAgreementNumber') ||
        resolveBookingTextToken('agreementNumber', 'agreement_number', 'saleAgreementNumber');
      const deedNumberToken =
        resolveWorkflowInputTextToken('deed_number', 'deedNumber') ||
        resolveBookingTextToken('deedNumber', 'deed_number', 'saleDeedNumber');
      const agreementDateToken =
        resolveWorkflowInputDateToken('agreement_date', 'agreementDate', 'sale_agreement_date', 'saleAgreementDate') ||
        resolveBookingDateToken('agreementDate', 'agreement_date', 'sale_agreement_date', 'saleAgreementDate');
      const agreementDateResolved =
        String(agreementDateToken || '').trim() ||
        (saleAgreementDateDisplay === '—' ? '' : String(saleAgreementDateDisplay || '').trim());
      const deedDateToken =
        resolveWorkflowInputDateToken('deed_date', 'deedDate') ||
        resolveBookingDateToken('deedDate', 'deed_date', 'saleDeedDate');
      const agreementLinkToken = resolveBookingTextToken('agreementLink', 'agreement_link', 'saleAgreementLink');
      const deedLinkToken = resolveBookingTextToken('deedLink', 'deed_link', 'saleDeedLink');
      const hasConstructionOverride =
        Object.prototype.hasOwnProperty.call(options?.tokenOverrides || {}, '{{construction_stage_status}}');
      const hasDemandAmountOverride =
        Object.prototype.hasOwnProperty.call(options?.tokenOverrides || {}, '{{demand_amount}}');
      const constructionStageStatusResolved =
        String(
          hasConstructionOverride
            ? options?.tokenOverrides?.['{{construction_stage_status}}']
            : resolveWorkflowInputTextToken('construction_stage_status', 'constructionStageStatus') ||
                resolveBookingTextToken('construction_stage_status', 'constructionStageStatus') ||
                ''
        ).trim() || '_______';
      const demandAmountResolved =
        String(
          hasDemandAmountOverride
            ? options?.tokenOverrides?.['{{demand_amount}}']
            : resolveWorkflowInputTextToken('demand_amount', 'demandAmount') ||
                resolveBookingTextToken('demand_amount', 'demandAmount') ||
                ''
        ).trim() || '____________________';

      const replacements: Record<string, string> = {
        ...paymentTemplateTokens,
        '{{project_name}}': String(projectRow?.name || projectName || ''),
        '{{unit_no}}': unitNo,
        '{{unit_number}}': unitNo,
        '{{allottee_names}}': allotteeNames.join(', '),
        '{{allottee_name}}': allotteeNames.join(', '),
        '{{allottee_primary_name}}': allotteeNames[0] || '',
        '{{allottee_names_lines}}': allotteeNames.join('<br/>'),
        '{{allottee_names_comma_and}}': joinNamesCommaAnd(allotteeNames),
        '{{amount}}': formatAmount(unitAmountValue),
        '{{unit_amount}}': formatAmount(unitAmountValue),
        '{{issue_date}}': formatDDMMYYYY(new Date()),
        '{{ref_no}}': resolvedRefNo,
        '{{carpet_area}}': String((unit as any)?.carpetArea ?? ''),
        '{{area_of_exclusive_balcony}}': String((unit as any)?.exclusiveBalconyArea ?? ''),
        '{{area_of_exclusive_wash}}': String(
          (unit as any)?.exclusiveWashArea ??
            (unit as any)?.areaOfExclusiveWash ??
            (unit as any)?.area_of_exclusive_wash ??
            (unit as any)?.customFields?.area_of_exclusive_wash ??
            (unit as any)?.['AREA OF EXCLUSIVE WASH'] ??
            ''
        ),
        '{{area_of_exclusive_open_terrace}}': String(
          (unit as any)?.openTerraceArea ??
            (unit as any)?.areaOfExclusiveOpenTerrace ??
            (unit as any)?.area_of_exclusive_open_terrace ??
            (unit as any)?.customFields?.area_of_exclusive_open_terrace ??
            (unit as any)?.['AREA OF EXCLUSIVE OPEN TERRACE'] ??
            ''
        ),
        '{{vanvechayel_area}}': String((unit as any)?.vanvechayelArea || (unit as any)?.vanvechayel_area || ''),
        '{{built_up_area}}': String((unit as any)?.builtUpArea || (unit as any)?.built_up_area || ''),
        '{{building_no}}': String((unit as any)?.buildingNo || (unit as any)?.building_no || ''),
        '{{floor_eng}}': String((unit as any)?.floorEng || (unit as any)?.floor_eng || ''),
        ...buildUniversalOfficeTemplateTokens({
          primaryCustomerMobile,
          loanBankName: latestLoanSanctionData?.sanctionBankName,
          loanSanctionAmount: latestLoanSanctionData?.sanctionAmount,
          reasonNotTakingSbi: latestLoanSanctionData?.nonSbiReason,
          pendingAmount: unitAmountValue - customerReceiptTotal,
          formatAmount,
        }),
        '{{gst}}': formatAmount(gstValue),
        '{{stamp_duty}}': String(paymentTemplateTokens['{{stamp_duty}}'] || '').trim() || formatAmount(stampDutyValue),
        '{{registration_charges}}':
          String(paymentTemplateTokens['{{registration_charges}}'] || '').trim() || formatAmount(registrationChargesValue),
        '{{agreement_number}}': agreementNumberToken,
        '{{deed_number}}': deedNumberToken,
        '{{agreement_date}}': agreementDateResolved,
        '{{sale_agreement_date}}': agreementDateResolved,
        '{{deed_date}}': deedDateToken,
        '{{agreement_link}}': agreementLinkToken,
        '{{deed_link}}': deedLinkToken,
        '{{construction_stage_status}}': constructionStageStatusResolved,
        '{{demand_amount}}': demandAmountResolved,
        '{{company_logo_url}}': String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
        '{{project_logo_url}}': resolveProjectLogoForTemplate(projectRow, selectedTemplate),
        '{{project_document_image_url}}': String(projectRow?.documentImageUrl || ''),
        '{{company_address}}': String(companyProfileRow?.address || ''),
        '{{company_mobile}}': String(companyProfileRow?.mobile || ''),
        '{{company_email}}': String(companyProfileRow?.email || ''),
        '{{company_name}}': String(companyProfileRow?.name || ''),
        '{{receipt_no}}': [String(resolvedRefNo || '').trim(), String(selectedReceiptSequence || '').trim()]
          .filter(Boolean)
          .join('-'),
        '{{receipt_towards}}': 'Sale consideration of the above flat',
      };
      if (selectedReceiptRow) {
        const selectedAmount = Number(selectedReceiptRow?.amount || 0);
        const isGstReceipt = selectedReceiptCategory === 'gst';
        // GST receipts keep the booking code but swap the prefix, e.g. REC/583120 -> GST/583120.
        const receiptRefNo = !isGstReceipt
          ? String(resolvedRefNo || '').trim()
          : String(resolvedRefNo || '').includes('/')
            ? String(resolvedRefNo).trim().replace(/^[^/]*\//, 'GST/')
            : `GST/${String(resolvedRefNo || '').trim()}`;
        replacements['{{ref_no}}'] = receiptRefNo;
        replacements['{{receipt_towards}}'] = isGstReceipt
          ? 'GST on sale consideration of the above flat'
          : 'Sale consideration of the above flat';
        replacements['{{chq_date}}'] = selectedReceiptDate ? formatDDMMYYYY(selectedReceiptDate) : '';
        replacements['{{ref_chq_no}}'] = String(selectedReceiptRow?.referenceNo || '').trim();
        replacements['{{bank}}'] = String(
          selectedReceiptRow?.payerBankName || selectedReceiptRow?.bankName || ''
        ).trim();
        replacements['{{deposit}}'] = Number.isFinite(selectedAmount) ? formatAmount(selectedAmount) : '';
        replacements['{{deposit_in_words}}'] = amountToIndianWords(selectedAmount);
        replacements['{{payment_sequence_no}}'] = String(selectedReceiptSequence);
        replacements['{{payment_no}}'] = String(selectedReceiptSequence);
        replacements['{{no_of_payment}}'] = String(selectedReceiptSequence);
        replacements['{{receipt_no}}'] = [receiptRefNo, String(selectedReceiptSequence || '').trim()]
          .filter(Boolean)
          .join('-');
      }

      const templateHtml = String(
        selectedTemplate?.visualModel?.body ||
          selectedTemplate?.bodyHtml ||
          selectedTemplate?.renderHtml ||
          selectedTemplate?.visualModel?.renderHtml ||
          ''
      ).trim();
      if (!templateHtml) throw new Error(`Template content is empty: ${safeTemplateSelector}`);
      let rendered = applyTemplateTokens(injectRuntimeRefDateTokens(templateHtml), replacements);
      const isFullHtml = /<html[\s>]/i.test(rendered);
      const htmlToRender = isFullHtml
        ? rendered
        : `<!doctype html><html><head><meta charset="utf-8" /><title>${safeTemplateSelector}</title></head><body>${rendered}</body></html>`;

      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const vm = selectedTemplate?.visualModel || {};
      const runtimeVisualModel = {
        ...vm,
        backgroundImageUrl: String((vm as any)?.backgroundImageUrl || projectRow?.backgroundImageUrl || ''),
        backgroundOpacity:
          (vm as any)?.backgroundOpacity ??
          (String(projectRow?.backgroundImageUrl || '').trim() ? 0.21 : 0),
        issueDate: formatDDMMYYYY(new Date()),
        refNo: String(replacements['{{ref_no}}'] || '').trim(),
      };
      const result = await renderTemplatePdf(
        buildPdfPayloadFromTemplate({
          html: htmlToRender,
          tokenValues: replacements,
          visualModel: runtimeVisualModel,
          companyLogoUrl: String(resolveCompanyLogoForTemplate(companyProfileRow, selectedTemplate) || ''),
          projectLogoUrl: resolveProjectLogoForTemplate(projectRow, selectedTemplate),
          companyAddress: String(companyProfileRow?.address || ''),
          companyMobile: String(companyProfileRow?.mobile || ''),
          companyEmail: String(companyProfileRow?.email || ''),
          companyFontFamily: String(companyProfileRow?.fontFamily || ''),
          companyFontUrl: String(companyProfileRow?.fontUrl || ''),
          companyFontFormat: String(companyProfileRow?.fontFormat || ''),
          gujaratiFontFamily: String(companyProfileRow?.gujaratiFontFamily || ''),
          gujaratiFontUrl: String(companyProfileRow?.gujaratiFontUrl || ''),
          gujaratiFontFormat: String(companyProfileRow?.gujaratiFontFormat || ''),
          paperSize: String(vm.paperSize || 'A4'),
          fileName: `${resolvedTemplateId}_${unitNo || unit.id}`,
        })
      );

      const data = (result?.data || {}) as {
        mimeType?: string;
        pdfBytes?: number[] | Record<string, number>;
        pdfBase64?: string | { type?: string; data?: number[] };
      };
      const rawBytes = Array.isArray(data.pdfBytes)
        ? data.pdfBytes
        : data.pdfBytes && typeof data.pdfBytes === 'object'
          ? Object.keys(data.pdfBytes)
              .sort((a, b) => Number(a) - Number(b))
              .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
          : [];
      let pdfBuffer = rawBytes.length ? Uint8Array.from(rawBytes) : new Uint8Array();
      if (!pdfBuffer.length) {
        const base64Raw =
          typeof data.pdfBase64 === 'string'
            ? data.pdfBase64
            : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
              ? (() => {
                  const source = data.pdfBase64?.data || [];
                  let chunk = '';
                  for (let i = 0; i < source.length; i += 0x8000) {
                    chunk += String.fromCharCode(...source.slice(i, i + 0x8000));
                  }
                  return btoa(chunk);
                })()
              : '';
        const normalizedBase64 = base64Raw
          .replace(/^data:application\/pdf;base64,/i, '')
          .replace(/\s+/g, '');
        if (normalizedBase64) {
          const binary = atob(normalizedBase64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
          pdfBuffer = bytes;
        }
      }
      if (!pdfBuffer.length) throw new Error('Invalid PDF payload received from server.');

      const by = currentUser.email;
      const uploadedAtMs = Date.now();
      const outputType = getTemplateGeneratedOutputType(selectedTemplate);
      const displaySection = getTemplateShowInSection(selectedTemplate, outputType);
      const rawDraftNameTemplate = String(selectedTemplate?.draftFileNameTemplate || '')
        .replace(/\{\{\s*unit[_\s-]*no\s*\}\}/gi, '{{unit_no}}')
        .replace(/\{\{\s*unit[_\s-]*number\s*\}\}/gi, '{{unit_no}}')
        .trim();
      const resolvedDraftName = rawDraftNameTemplate ? applyTemplateTokens(rawDraftNameTemplate, replacements) : '';
      const fallbackBaseName =
        outputType === 'final'
          ? `${unitNo || 'Unit'} - ${String(selectedTemplate?.name || safeTemplateSelector)}`
          : `${unitNo || 'Unit'} - ${String(selectedTemplate?.name || safeTemplateSelector)} DRAFT`;
      const fileDisplayNameBase = String(resolvedDraftName || '').trim() || fallbackBaseName;
      const fileDisplayName = /\.pdf$/i.test(fileDisplayNameBase) ? fileDisplayNameBase : `${fileDisplayNameBase}.pdf`;
      if (options?.returnPdfBytes) {
        return { pdfBuffer, fileDisplayName };
      }
      if (options?.downloadOnly) {
        triggerPdfBytesDownload(pdfBuffer, fileDisplayName);
        return { pdfBuffer, fileDisplayName };
      }
      const safeFileName = fileDisplayName.replace(/[^\w.-]+/g, '_');
      const docRef = outputType === 'final' ? finalDocRef : draftDocRef;
      const existingSnap = outputType === 'final' ? finalExistingSnap : draftExistingSnap;
      const existingPath = String((existingSnap?.data() as any)?.files?.[0]?.path || '').trim();
      const path = `bookings/${effectiveBookingId}/${outputType === 'final' ? 'uploaded' : 'draft'}/${safeTemplateSelector}/${safeFileName}`;
      if (existingPath && existingPath !== path) {
        try {
          await deleteObject(ref(storage, existingPath));
        } catch (e) {
          console.warn(`Could not delete previous ${safeTemplateSelector} ${outputType} file`, e);
        }
      }
      if (outputType === 'final' && draftExistingSnap.exists()) {
        const staleDraftPath = String((draftExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
        if (staleDraftPath) {
          try {
            await deleteObject(ref(storage, staleDraftPath));
          } catch (e) {
            console.warn(`Could not delete stale ${safeTemplateSelector} draft file`, e);
          }
        }
        await deleteDoc(draftDocRef).catch(() => undefined);
      }
      if (outputType === 'draft' && finalExistingSnap.exists()) {
        const staleFinalPath = String((finalExistingSnap.data() as any)?.files?.[0]?.path || '').trim();
        if (staleFinalPath) {
          try {
            await deleteObject(ref(storage, staleFinalPath));
          } catch (e) {
            console.warn(`Could not delete stale ${safeTemplateSelector} final file`, e);
          }
        }
        await deleteDoc(finalDocRef).catch(() => undefined);
      }

      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pdfBuffer, {
        contentType: data.mimeType || 'application/pdf',
        cacheControl: 'no-store, max-age=0',
      });
      const baseUrl = await getDownloadURL(storageRef);
      const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${uploadedAtMs}`;

      await setDoc(
        docRef,
        {
          type: safeTemplateSelector,
          templateId: resolvedTemplateId,
          templateSelector: safeTemplateSelector,
          templateName: String(selectedTemplate?.name || safeTemplateSelector),
          displayName: String(selectedTemplate?.name || safeTemplateSelector),
          generatedFinal: outputType === 'final',
          displaySection,
          refNo: String(replacements['{{ref_no}}'] || '').trim() || null,
          files: [
            {
              name: fileDisplayName,
              originalName: fileDisplayName,
              url,
              path,
              uploadedAtMs,
              uploadedBy: by,
            },
          ],
          updatedAt: serverTimestamp(),
          updatedBy: by,
          createdAt: serverTimestamp(),
          createdBy: by,
        },
        { merge: true }
      );

      const stepKey = String(options?.stepKey || '').trim();
      if (stepKey) {
        await updateStepField(options?.which === 'loan' ? 'loan' : 'main', stepKey, 'generatedAtMs', Date.now());
      }
    },
    [assertUnitDataCurrent, 
      computeGstAmount,
      currentUser?.email,
      customerReceiptTotal,
      customers,
      db,
      ensureBookingForDraft,
      latestLoanSanctionData?.nonSbiReason,
      latestLoanSanctionData?.sanctionAmount,
      latestLoanSanctionData?.sanctionBankName,
      primaryCustomerMobile,
      projectId,
      projectName,
      injectRuntimeRefDateTokens,
      resolveBookingDateToken,
      resolveBookingTextToken,
      resolveTemplateRefNo,
      resolveWorkflowInputDateToken,
      resolveWorkflowInputTextToken,
      saleAgreementDateDisplay,
      triggerPdfBytesDownload,
      unit,
      updateStepField,
    ]
  );

  const downloadableReceiptRows = useMemo(() => {
    return customerReceiptRows.filter(
      (row: any) =>
        String(row?.txnType || '').toLowerCase() === 'receipt' &&
        String(row?.receiptCategory || '').toLowerCase() === 'customer'
    );
  }, [customerReceiptRows]);

  const toggleReceiptSelection = useCallback((txnId: string) => {
    setSelectedReceiptTxnIds((prev) => {
      const next = new Set(prev);
      if (next.has(txnId)) {
        next.delete(txnId);
      } else {
        next.add(txnId);
      }
      return next;
    });
  }, []);

  const handleDownloadCombinedReceipts = useCallback(async () => {
    if (selectedReceiptTxnIds.size === 0) return;
    const toMillis = (value: any) =>
      typeof value?.toMillis === 'function'
        ? value.toMillis()
        : typeof value?.toDate === 'function'
          ? value.toDate().getTime()
          : value instanceof Date
            ? value.getTime()
            : typeof value === 'number'
              ? value
              : 0;

    const selectedRows = customerReceiptRows
      .filter((row: any) => selectedReceiptTxnIds.has(String(row?.id || '')))
      .sort((a: any, b: any) => {
        const aAt =
          toMillis(a?.chqDate) ||
          toMillis(a?.txnDate) ||
          toMillis(a?.createdAt) ||
          toMillis(a?.updatedAt) ||
          Number.MAX_SAFE_INTEGER;
        const bAt =
          toMillis(b?.chqDate) ||
          toMillis(b?.txnDate) ||
          toMillis(b?.createdAt) ||
          toMillis(b?.updatedAt) ||
          Number.MAX_SAFE_INTEGER;
        if (aAt !== bAt) return aAt - bAt;
        return String(a?.id || '').localeCompare(String(b?.id || ''));
      });

    if (selectedRows.length === 0) return;

    setMultiReceiptDownloading(true);
    setMultiReceiptProgressText(`Generating 1 of ${selectedRows.length}…`);
    setError(null);

    try {
      const pdfBuffers: Uint8Array[] = [];
      for (let i = 0; i < selectedRows.length; i++) {
        const row = selectedRows[i];
        setMultiReceiptProgressText(`Generating receipt ${i + 1} of ${selectedRows.length}…`);
        const res: any = await generateDocumentFromTemplateId('payment_receipt_v1', {
          transactionRow: row,
          returnPdfBytes: true,
        });
        if (res?.pdfBuffer && res.pdfBuffer.length > 0) {
          pdfBuffers.push(res.pdfBuffer);
        }
      }

      if (pdfBuffers.length === 0) {
        throw new Error('No receipt PDF bytes generated.');
      }

      setMultiReceiptProgressText('Merging receipts into 1 PDF…');
      const { PDFDocument } = await import('pdf-lib');
      const mergedPdf = await PDFDocument.create();

      for (const buffer of pdfBuffers) {
        const pdf = await PDFDocument.load(buffer, { ignoreEncryption: true });
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const mergedBytes = await mergedPdf.save({ useObjectStreams: true });

      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
      const customerName = String(
        primaryCustomerHeaderName ||
        customers[0]?.name ||
        ''
      ).trim();

      const fileName = [unitNo, 'Payment Receipts', customerName]
        .filter(Boolean)
        .join(' - ') + '.pdf';

      triggerPdfBytesDownload(mergedBytes, fileName);
      setIsMultiSelectReceipts(false);
      setSelectedReceiptTxnIds(new Set());
    } catch (err: any) {
      setError(err?.message || 'Failed to download combined receipts.');
    } finally {
      setMultiReceiptDownloading(false);
      setMultiReceiptProgressText('');
    }
  }, [
    customerReceiptRows,
    customers,
    generateDocumentFromTemplateId,
    primaryCustomerHeaderName,
    selectedReceiptTxnIds,
    triggerPdfBytesDownload,
    unit?.id,
    unit?.unitNumber,
  ]);

  const autoGenerateStepAttemptRef = useRef<Map<string, number>>(new Map());
  const autoGenerateStepInFlightRef = useRef<Set<string>>(new Set());
  const isDemandLetterGenerateStep = useCallback((step: WorkflowDefinitionStep | null | undefined) => {
    const stepKey = String(step?.key || '').trim();
    const docKey = String((step as any)?.docKey || '').trim();
    return stepKey === 'generate_demand_letter' || docKey === 'demand_letter';
  }, []);

  const runGenerateDocumentStep = useCallback(
    async (
      which: 'main' | 'loan',
      step: WorkflowDefinitionStep,
      options?: { tokenOverrides?: Record<string, string> }
    ) => {
      const templateId = String((step as any)?.docKey || '').trim();
      if (!templateId) return;
      const stepTitle = resolveWorkflowStepTitle(step, which) || String(step?.title || step?.key || 'Unknown step');
      const stepKey = String(step?.key || '').trim();
      try {
        if (templateId === 'allotment_letter_v1') {
          await runAllotmentDraftGeneration(stepKey);
          return;
        }
        if (templateId === 'booking_form_v1') {
          await generateBookingFormV1Draft();
          return;
        }
        if (templateId === 'payment_details_v1' || templateId === 'payment_details_v2') {
          await generatePaymentDetailsV1Draft(undefined, templateId === 'payment_details_v1' ? 'payment_details_v1' : 'payment_details_v2');
          return;
        }
        await generateDocumentFromTemplateId(templateId, { stepKey, which, tokenOverrides: options?.tokenOverrides });
      } catch (err: any) {
        const base = String(err?.message || 'Failed to generate document.');
        throw new Error(`${stepTitle} (${templateId}): ${base}`);
      }
    },
    [
      generateBookingFormV1Draft,
      generateDocumentFromTemplateId,
      generatePaymentDetailsV1Draft,
      resolveWorkflowStepTitle,
      runAllotmentDraftGeneration,
    ]
  );

  const confirmGenerateDemandLetter = useCallback(async () => {
    if (!demandGenerateContext) return;
    const which = demandGenerateContext.which;
    const step = demandGenerateContext.step;
    const genKey = `${which}:${step.key}`;
    const constructionStageValue = String(demandConstructionStageInput || '').trim();
    const demandAmountValue = String(demandAmountInput || '').trim();
    const demandDueDateValue = String(demandDueDateInput || '').trim();
    try {
      setDemandGenerateSubmitting(true);
      setStepGeneratePhase((prev) => ({ ...prev, [genKey]: 'processing' }));
      await updateStepField(which, step.key, 'construction_stage_status', constructionStageValue);
      await updateStepField(which, step.key, 'demand_amount', demandAmountValue);
      // Due date drives the WhatsApp payment-due reminders (7 days and 1 day before).
      await updateStepField(which, step.key, 'demand_due_date', demandDueDateValue);
      await runGenerateDocumentStep(which, step, {
        tokenOverrides: {
          '{{construction_stage_status}}': constructionStageValue,
          '{{demand_amount}}': demandAmountValue,
        },
      });
      setStepGeneratePhase((prev) => ({ ...prev, [genKey]: 'done' }));
      window.setTimeout(
        () =>
          setStepGeneratePhase((prev) => ({
            ...prev,
            [genKey]: 'idle',
          })),
        2200
      );
      setDemandGenerateDialogOpen(false);
      setDemandGenerateContext(null);
    } catch (err: any) {
      setStepGeneratePhase((prev) => ({ ...prev, [genKey]: 'error' }));
      setError(err?.message || `Failed to generate ${resolveWorkflowStepTitle(step, which)}.`);
    } finally {
      setDemandGenerateSubmitting(false);
    }
  }, [
    demandAmountInput,
    demandDueDateInput,
    demandConstructionStageInput,
    demandGenerateContext,
    resolveWorkflowStepTitle,
    runGenerateDocumentStep,
    updateStepField,
  ]);

  const openGenerateWorkflowStep = useCallback(
    async (which: 'main' | 'loan', step: WorkflowDefinitionStep) => {
      const genKey = `${which}:${step.key}`;
      try {
        if (isDemandLetterGenerateStep(step)) {
          const currentStepData = getCaseStepData(which, step.key) as Record<string, any>;
          setDemandConstructionStageInput(
            String(currentStepData?.construction_stage_status || currentStepData?.constructionStageStatus || '').trim()
          );
          setDemandAmountInput(String(currentStepData?.demand_amount || currentStepData?.demandAmount || '').trim());
          setDemandDueDateInput(String(currentStepData?.demand_due_date || '').trim());
          setDemandGenerateContext({ which, step });
          setDemandGenerateDialogOpen(true);
          return;
        }
        setStepGeneratePhase((prev) => ({ ...prev, [genKey]: 'processing' }));
        await runGenerateDocumentStep(which, step);
        setStepGeneratePhase((prev) => ({ ...prev, [genKey]: 'done' }));
        window.setTimeout(
          () =>
            setStepGeneratePhase((prev) => ({
              ...prev,
              [genKey]: 'idle',
            })),
          2200
        );
      } catch (err: any) {
        setStepGeneratePhase((prev) => ({ ...prev, [genKey]: 'error' }));
        setError(err?.message || `Failed to generate ${resolveWorkflowStepTitle(step, which)}.`);
      }
    },
    [getCaseStepData, isDemandLetterGenerateStep, resolveWorkflowStepTitle, runGenerateDocumentStep]
  );

  useEffect(() => {
    if (!open) return;
    if (!bookingId) return;
    // Wait until buyers, documents and both workflow cases are loaded for this unit (never the previous unit's).
    if (!unitDataReady) return;
    const readyGenerateSteps: Array<{ which: 'main' | 'loan'; step: WorkflowDefinitionStep }> = [];
    mainWorkflowV2Pending.forEach((step) => {
      if (String(step?.status || '').toLowerCase() !== 'ready') return;
      if (String(step?.actionType || '').toLowerCase() !== 'generate_document') return;
      if (['payment_details_v1', 'payment_details_v2'].includes(String((step as any)?.docKey || '').trim())) return;
      readyGenerateSteps.push({ which: 'main', step });
    });
    loanWorkflowV2Pending.forEach((step) => {
      if (String(step?.status || '').toLowerCase() !== 'ready') return;
      if (String(step?.actionType || '').toLowerCase() !== 'generate_document') return;
      if (['payment_details_v1', 'payment_details_v2'].includes(String((step as any)?.docKey || '').trim())) return;
      readyGenerateSteps.push({ which: 'loan', step });
    });
    if (!readyGenerateSteps.length) return;

    const now = Date.now();
    const minRetryMs = 2 * 60 * 1000;
    readyGenerateSteps.forEach(({ which, step }) => {
      const stepKey = String(step?.key || '').trim();
      const docKey = String((step as any)?.docKey || '').trim();
      if (!stepKey || !docKey) return;
      if (stepKey === 'generate_demand_letter' || docKey === 'demand_letter') return;
      if (bookingDocsByKey?.[docKey]?.files?.length) return;

      const refKey = `${which}:${String(bookingId || '').trim()}:${stepKey}:${docKey}`;
      if (autoGenerateStepInFlightRef.current.has(refKey)) return;
      const lastAttempt = Number(autoGenerateStepAttemptRef.current.get(refKey) || 0);
      if (lastAttempt && now - lastAttempt < minRetryMs) return;
      autoGenerateStepAttemptRef.current.set(refKey, now);
      autoGenerateStepInFlightRef.current.add(refKey);

      runGenerateDocumentStep(which, step)
        .catch((err: any) => {
          setError(err?.message || `Failed to auto-generate document for ${step.title}.`);
        })
        .finally(() => {
          autoGenerateStepInFlightRef.current.delete(refKey);
        });
    });
  }, [bookingDocsByKey, bookingId, loanWorkflowV2Pending, mainWorkflowV2Pending, open, runGenerateDocumentStep, unitDataReady]);

  const uploadStepRequiredDoc = async (docKey: string, label: string, pickedFile?: File | null) => {
    try {
      if (!unit?.id || !projectId) {
        setError('Project or unit is missing.');
        return;
      }
      if (!currentUser?.email) {
        setError('Not signed in.');
        return;
      }
      setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'processing' }));
      const effectiveBookingId = bookingId || (await ensureBookingForDraft());
      let file = pickedFile || stepDocPickedFiles[docKey] || null;
      if (!file) {
        setError('Choose a file first.');
        setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'error' }));
        return;
      }
      setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'reducing' }));
      const compression = await tryAutoCompressPdfForUpload(file, docKey, label);
      file = compression.file;
      if (compression.notice && /optimized/i.test(compression.notice)) setError(compression.notice);
      setStepDocPickedFiles((prev) => ({ ...prev, [docKey]: file }));
      const validationError = validateUploadByConfig({ file, docKey, fallbackLabel: label });
      if (validationError) {
        const maxBytes = getUploadMaxBytesByConfig(docKey);
        const extra =
          compression.notice && /failed|did not reduce/i.test(compression.notice)
            ? ` ${compression.notice}`
            : '';
        setError(
          `${validationError}${maxBytes ? ` Current: ${formatBytes(file.size)}, Limit: ${formatBytes(maxBytes)}.` : ''}${extra}`
        );
        setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'error' }));
        return;
      }
      if (!compression.notice) setError(null);
      setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'uploading' }));

      const by = currentUser.email;
      const uploadedAtMs = Date.now();
      const uploadConfig = uploadTypeByKey.get(String(docKey).trim()) || null;
      const docLabel = String(uploadConfig?.label || label || docKey);
      const configuredDisplaySectionRaw = String((uploadConfig as any)?.displaySection || '').trim().toLowerCase();
      const configuredDisplaySection: 'main_documents' | 'loan_documents' | 'draft_documents' =
        configuredDisplaySectionRaw === 'loan_documents'
          ? 'loan_documents'
          : configuredDisplaySectionRaw === 'draft_documents'
            ? 'draft_documents'
            : 'main_documents';
      const configuredDisplayName = renderUploadDisplayNameTemplate(
        String(uploadConfig?.displayNameTemplate || ''),
        docLabel
      );
      const unitNo = String(unit?.unitNumber || unit?.id || 'Unit').trim();
      const safeExt = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
      const displayName = configuredDisplayName || `${unitNo} - ${docLabel}`;
      const nameBase = `${displayName}${safeExt}`;
      const safeName = nameBase.replace(/[^\w.-]+/g, '_');
      const path = `bookings/${effectiveBookingId}/uploaded/${docKey}/${uploadedAtMs}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const docRef = doc(db, 'projects', projectId, 'units', unit.id, 'bookings', effectiveBookingId, 'uploaded', docKey);
      await setDoc(
        docRef,
        {
          type: docKey,
          displayName: displayName || null,
          displaySection: configuredDisplaySection,
          files: [
            {
              name: nameBase,
              originalName: file.name,
              url,
              path,
              uploadedAtMs,
              uploadedBy: by,
            },
          ],
          updatedAt: serverTimestamp(),
          updatedBy: by,
          createdAt: serverTimestamp(),
          createdBy: by,
        },
        { merge: true }
      );
      if (docKey === 'allotmentLetterDraft') {
        const sent = !!(mainCase as any)?.stepData?.[OFFICE_MAIN_STEP_KEYS.sendToAdvocate]?.sentToAdvocate;
        if (sent) {
          await setStepDone('main', OFFICE_MAIN_STEP_KEYS.sendToAdvocate, true);
        }
      }
      setStepDocPickedFiles((prev) => ({ ...prev, [docKey]: null }));
      setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'uploaded' }));
    } catch (err: any) {
      console.error('Upload required doc failed', err);
      setError(err?.message || 'Failed to upload document.');
      setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'error' }));
    }
  };

  type RepeatableWorkflowAction = {
    which: 'main' | 'loan';
    step: WorkflowDefinitionStep;
  };

  const repeatableWorkflowActions = useMemo<RepeatableWorkflowAction[]>(() => {
    const supportedActionTypes = new Set([
      'generate_document',
      'send_email',
      'upload_document',
      'add_payment',
      'advocate_agreement_fee_receipt',
      'loan_sanction_form',
    ]);
    const isBookedOrSold = ['booked', 'sold'].includes(String(unit?.bookingStatus || (unit as any)?.status || '').toLowerCase());
    const hasActiveLoanCase =
      !!loanCase && !['cancelled', 'closed'].includes(String((loanCase as any)?.status || '').trim().toLowerCase());
    const getCase = (which: 'main' | 'loan') => (which === 'loan' ? loanCase : mainCase) as any;
    const getDefinitionSteps = (which: 'main' | 'loan') =>
      normalizeDefinitionSteps((which === 'loan' ? loanWorkflowDefinition : mainWorkflowDefinition)?.steps || []);
    const completedSet = (which: 'main' | 'loan') =>
      new Set(
        (Array.isArray(getCase(which)?.completedStepKeys) ? getCase(which).completedStepKeys : [])
          .map((key: any) => String(key || '').trim())
          .filter(Boolean)
      );
    const stepState = (which: 'main' | 'loan') => (((getCase(which)?.stepState || {}) as Record<string, any>) || {});
    const stepByKey = (which: 'main' | 'loan') => new Map(getDefinitionSteps(which).map((step) => [step.key, step]));
    const isCompleteByCondition = (which: 'main' | 'loan', step: WorkflowDefinitionStep): boolean => {
      const completed = completedSet(which);
      const state = stepState(which);
      if (completed.has(step.key)) return true;
      if (which === 'main' && PRE_DEED_STEP_KEYS.has(step.key) && hasRegisteredDeedEvidence(booking, bookingDocsByKey)) return true;
      if (state?.[step.key]?.done === true || state?.[step.key]?.doneAt || state?.[step.key]?.doneAtMs) return true;
      const actionType = String(step?.actionType || '').toLowerCase();
      if (actionType === 'upload_document' || actionType === 'generate_document') {
        const docKey = String((step as any)?.docKey || '').trim();
        return !!docKey && !!bookingDocsByKey?.[docKey]?.files?.length;
      }
      if (actionType === 'loan_sanction_form') {
        const docKey = String((step as any)?.docKey || '').trim() || 'sanction_letter';
        const stepData = ((((loanCase as any)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>;
        const bankName = String(stepData?.sanctionBankName || '').trim();
        const sanctionDate = String(stepData?.sanctionDate || '').trim();
        const sanctionAmount = Number(stepData?.sanctionAmount);
        const loanToUnitAmount = Number(stepData?.loanToUnitAmount);
        const isSbi = bankName.toLowerCase() === 'state bank of india';
        const nonSbiReason = String(stepData?.nonSbiReason || '').trim();
        return (
          !!bookingDocsByKey?.[docKey]?.files?.length &&
          !!bankName &&
          !!sanctionDate &&
          Number.isFinite(sanctionAmount) &&
          sanctionAmount > 0 &&
          Number.isFinite(loanToUnitAmount) &&
          loanToUnitAmount > 0 &&
          (isSbi || !!nonSbiReason)
        );
      }
      if (actionType === 'input_value') {
        const stepData = ((((getCase(which)?.stepData || {}) as Record<string, any>)?.[step.key] || {}) as Record<string, any>);
        return isInputValueStepComplete(step, stepData, booking as any);
      }
      if (actionType === 'add_payment') {
        const paymentType = String((step as any)?.paymentType || '').toLowerCase();
        return paymentType === 'gst' ? gstReceiptTotal > 0 : customerReceiptTotal > 0;
      }
      if (actionType === 'advocate_agreement_fee_receipt') return advocateAgreementReceiptTotal > 0;
      return false;
    };
    const isDependencyDone = (which: 'main' | 'loan', dep: string): boolean => {
      const depKeyRaw = String(dep || '').trim();
      if (!depKeyRaw) return true;
      if (yesAnswerStepKey(depKeyRaw)) return yesAnswerForStep(depKeyRaw, getDefinitionSteps(which), getCase(which));
      if (depKeyRaw === UNIT_BOOKED_DEP_KEY) return isBookedOrSold;
      if (depKeyRaw === FULL_PAYMENT_DEP_KEY) return fullPaymentReceived(unit?.unitAmount, customerReceiptTotal + loanDisbursementReceiptTotal);
      if (depKeyRaw === BUC_DEP_KEY) return buildingBucReceived(projectBuc, unit);
      if (depKeyRaw === PRE_BUC_DEP_KEY) return !buildingBucReceived(projectBuc, unit);
      if (depKeyRaw === LOAN_REQUIRED_DEP_KEY) {
        return String(loanRequired || '').toLowerCase() === 'yes' || hasActiveLoanCase;
      }
      const crossMatch = depKeyRaw.match(/^(main|loan):(.+)$/);
      const targetWhich = crossMatch ? (crossMatch[1] as 'main' | 'loan') : which;
      const depKey = crossMatch ? String(crossMatch[2] || '').trim() : depKeyRaw;
      const completed = completedSet(targetWhich);
      const state = stepState(targetWhich);
      if (completed.has(depKey)) return true;
      if (state?.[depKey]?.done === true || state?.[depKey]?.doneAt || state?.[depKey]?.doneAtMs) return true;
      const depStep = stepByKey(targetWhich).get(depKey);
      return depStep ? isCompleteByCondition(targetWhich, depStep) : false;
    };
    const isStepReady = (which: 'main' | 'loan', step: WorkflowDefinitionStep): boolean => {
      const deps = (step.dependsOnSteps || []).filter(Boolean);
      if (!deps.length) return true;
      const doneCount = deps.filter((dep) => isDependencyDone(which, dep)).length;
      return String(step.dependsMode || 'all') === 'any' ? doneCount > 0 : doneCount === deps.length;
    };
    const collect = (which: 'main' | 'loan'): RepeatableWorkflowAction[] => {
      if (which === 'main' && !mainCase) return [];
      if (which === 'loan' && !hasActiveLoanCase) return [];
      return getDefinitionSteps(which)
        .filter((step) => step.enabled !== false && step.showInDrawer !== false)
        .filter((step) => step.keepActive === true)
        .filter((step) => supportedActionTypes.has(String(step.actionType || '').toLowerCase()))
        .filter((step) => isStepReady(which, step))
        .map((step) => ({ which, step }));
    };
    return [...collect('main'), ...collect('loan')].sort((a, b) => {
      if (a.which !== b.which) return a.which === 'main' ? -1 : 1;
      return (a.step.order || 0) - (b.step.order || 0);
    });
  }, [
    advocateAgreementReceiptTotal,
    booking,
    bookingDocsByKey,
    customerReceiptTotal,
    gstReceiptTotal,
    loanDisbursementReceiptTotal,
    projectBuc,
    loanCase,
    loanRequired,
    loanWorkflowDefinition,
    mainCase,
    mainWorkflowDefinition,
    unit,
  ]);

  const runRepeatableWorkflowAction = async (which: 'main' | 'loan', step: WorkflowDefinitionStep) => {
    const actionType = String(step?.actionType || '').toLowerCase();
    if (actionType === 'generate_document') {
      await openGenerateWorkflowStep(which, step);
      return;
    }
    if (actionType === 'send_email') {
      await openEmailConfirmForStep(which, step);
      return;
    }
    if (actionType === 'upload_document') {
      const docKey = String((step as any)?.docKey || '').trim();
      if (!docKey) {
        setError('This upload step is missing document key.');
        return;
      }
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*,application/pdf';
      input.onchange = async () => {
        const file = (input.files || [])[0] || null;
        if (!file) return;
        setStepDocPickedFiles((prev) => ({ ...prev, [docKey]: file }));
        setStepDocUploadPhase((prev) => ({ ...prev, [docKey]: 'idle' }));
        await uploadStepRequiredDoc(docKey, resolveWorkflowStepTitle(step, which), file);
      };
      input.click();
      return;
    }
    if (actionType === 'add_payment') {
      const paymentType = String((step as any)?.paymentType || '').toLowerCase();
      openUnifiedPayment(paymentType === 'gst' ? 'gst' : 'dp', { which, key: step.key });
      return;
    }
    if (actionType === 'advocate_agreement_fee_receipt') {
      openUnifiedPayment('advocateAgreement', { which, key: step.key });
      return;
    }
    if (actionType === 'loan_sanction_form') {
      openLoanSanctionDialog(step);
    }
  };

  const getRepeatableWorkflowActionLabel = (which: 'main' | 'loan', step: WorkflowDefinitionStep): string => {
    const title = resolveWorkflowStepTitle(step, which) || String(step?.title || step?.key || 'Workflow action');
    const actionType = String(step?.actionType || '').toLowerCase();
    const docKey = String((step as any)?.docKey || '').trim();
    const hasDoc = !!docKey && !!bookingDocsByKey?.[docKey]?.files?.length;
    if (actionType === 'generate_document') return `${hasDoc ? 'Regenerate' : 'Generate'}: ${title}`;
    if (actionType === 'send_email') return `Send: ${title}`;
    if (actionType === 'upload_document') return `${hasDoc ? 'Re-upload' : 'Upload'}: ${title}`;
    if (actionType === 'add_payment') return `${String((step as any)?.paymentType || '').toLowerCase() === 'gst' ? 'Add GST' : 'Add payment'}: ${title}`;
    if (actionType === 'advocate_agreement_fee_receipt') return `Add receipt: ${title}`;
    if (actionType === 'loan_sanction_form') return `Open: ${title}`;
    return title;
  };

  const regenerateDocumentBySelector = useCallback(
    async (docSelector: string) => {
      const selector = String(docSelector || '').trim();
      if (!selector) throw new Error('Document key is missing.');
      const effectiveBookingId = bookingId || (await ensureBookingForDraft());
      if (selector === 'payment_details_v1' || selector === 'payment_details_v2') {
        await generatePaymentDetailsV1Draft(effectiveBookingId, selector);
        return;
      }
      if (selector === 'booking_form_v1') {
        if (bookingFormDoc?.files?.length) throw new Error('Signed booking form is locked. Create a revised form before regenerating.');
        await generateBookingFormV1Draft(effectiveBookingId);
        return;
      }
      if (selector === 'allotment_letter_v1') {
        await generateAllotmentLetterDraft('regenerate_allotment_letter');
        return;
      }
      await generateDocumentFromTemplateId(selector, {
        effectiveBookingIdOverride: effectiveBookingId,
      });
    },
    [
      bookingId,
      ensureBookingForDraft,
      generateAllotmentLetterDraft,
      generateBookingFormV1Draft,
      generateDocumentFromTemplateId,
      generatePaymentDetailsV1Draft,
      bookingFormDoc?.files?.length,
    ]
  );

  const loadCustomerIntoForm = async (id: string) => {
    if (!unit?.id || !projectId || !bookingId) return;
    setError(null);
    try {
      const refDoc = doc(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingId, 'customers', id);
      const snap = await getDoc(refDoc);
      if (!snap.exists()) return;
      const c = snap.data() as any;
      setEditingCustomerId(id);
      const display = String(c?.displayName || c?.name || '');
      const first = String(c?.firstName || '');
      const middle = String(c?.middleName || '');
      const last = String(c?.lastName || '');
      if (first || middle || last) {
        setCustFirstName(first);
        setCustMiddleName(middle);
        setCustLastName(last);
        setCustDisplayName(display || buildFullName(first, middle, last));
      } else {
        const parts = normalizeSpaces(String(c?.name || '')).split(' ').filter(Boolean);
        const f = parts[0] || '';
        const l = parts.length > 1 ? parts[parts.length - 1] : '';
        const m = parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
        setCustFirstName(f);
        setCustMiddleName(m);
        setCustLastName(l);
        setCustDisplayName(display || buildFullName(f, m, l));
      }
      setCustMobile(String(c?.mobile || ''));
      setCustEmail(String(c?.email || ''));
      setCustDateOfBirth(
        String(c?.dateOfBirth || c?.dob || c?.birthDate || '').trim()
      );
      setCustOccupation(String(c?.occupation || ''));
      setCustGender(String(c?.gender || ''));
      setCustPan(String(c?.pan || ''));
      setCustRole(String(c?.role || 'primary'));
      setCustAddr1(String(c?.addressLine1 || ''));
      setCustAddr2(String(c?.addressLine2 || ''));
      setCustCity(String(c?.city || ''));
      setCustState(String(c?.state || ''));
      setCustPincode(String(c?.pincode || ''));
    } catch (err: any) {
      console.error('Load customer failed', err);
      setError(err?.message || 'Failed to load customer.');
    }
  };

  const resetCustomerForm = () => {
    setEditingCustomerId(null);
    setCustFirstName('');
    setCustMiddleName('');
    setCustLastName('');
    setCustDisplayName('');
    setCustMobile('');
    setCustEmail('');
    setCustDateOfBirth('');
    setCustOccupation('');
    setCustGender('');
    setCustPan('');
    setCustRole('primary');
    setCustAddr1('');
    setCustAddr2('');
    setCustCity('');
    setCustState('');
    setCustPincode('');
  };

  const saveCustomer = async () => {
    if (!unit?.id) return;
    if (!projectId || !bookingId) return;
    setError(null);
    const firstName = normalizeSpaces(custFirstName);
    const middleName = normalizeSpaces(custMiddleName);
    const lastName = normalizeSpaces(custLastName);
    const computed = buildFullName(firstName, middleName, lastName);
    const displayName = normalizeSpaces(custDisplayName) || computed;
    const mobile = custMobile.trim();
    const dateOfBirth = String(custDateOfBirth || '').trim();
    if (!firstName) {
      setError('First name is required.');
      return;
    }
    if (!lastName) {
      setError('Surname is required.');
      return;
    }
    if (!mobile) {
      setError('Mobile is required.');
      return;
    }
    if (!dateOfBirth) {
      setError('Date of birth is required.');
      return;
    }
    if (!currentUser?.email) {
      setError('Not signed in.');
      return;
    }
    setSavingCustomer(true);
    try {
      const customersCol = collection(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingId, 'customers');
      const by = currentUser.email;
      const computedRole = editingCustomerId ? custRole : customers.length ? 'secondary' : 'primary';
      const payload: any = {
        name: displayName,
        displayName,
        firstName,
        middleName: middleName || null,
        lastName,
        mobile,
        email: custEmail.trim() || null,
        dateOfBirth,
        dob: dateOfBirth,
        occupation: custOccupation || null,
        gender: custGender || null,
        pan: custPan.trim() || null,
        role: computedRole || null,
        addressLine1: custAddr1.trim() || null,
        addressLine2: custAddr2.trim() || null,
        city: custCity.trim() || null,
        state: custState.trim() || null,
        pincode: custPincode.trim() || null,
        updatedAt: serverTimestamp(),
        updatedBy: by,
      };
      if (editingCustomerId) {
        await updateDoc(doc(customersCol, editingCustomerId), payload);
        setSelectedCustomerId(editingCustomerId);
      } else {
        const created = await addDoc(customersCol, {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: by,
        });
        setSelectedCustomerId(created.id);
      }
      resetCustomerForm();
      setCustomerDrawerOpen(false);
    } catch (err: any) {
      console.error('Save customer failed', err);
      setError(err?.message || 'Failed to save customer.');
    } finally {
      setSavingCustomer(false);
    }
  };

  const customerFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 999,
      bgcolor: '#ffffff',
      borderColor: 'rgba(15, 23, 42, 0.12)',
    },
  };
  const addCustomersReadyStep = mainWorkflowV2Pending.find(
    (step) => String(step.actionType || '').toLowerCase() === 'add_customer' && step.status === 'ready'
  );

  const customerPanelContent = (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
            {`${String(unit?.unitNumber || unit?.id || 'Unit')} : Customer`}
          </Typography>
          {addCustomersReadyStep ? (
            <Tooltip title={customers.length > 0 ? 'Mark done' : 'Add at least one customer first'}>
              <span>
                <IconButton
                  size="small"
                  disabled={customers.length <= 0}
                  onClick={() =>
                    setStepDone('main', addCustomersReadyStep.key, true).catch((err: any) =>
                      setError(err?.message || 'Failed to mark step done.')
                    )
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    border: '1px solid',
                    borderColor: customers.length > 0 ? '#86efac' : 'divider',
                    bgcolor: customers.length > 0 ? '#dcfce7' : '#f8fafc',
                    color: customers.length > 0 ? '#16a34a' : 'text.secondary',
                    borderRadius: '50%',
                  }}
                >
                  <CheckOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
        <IconButton onClick={() => setCustomerDrawerOpen(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Divider sx={{ my: 1.5 }} />

      <Stack spacing={1.5}>
        {customers.length > 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#f8fafc',
              borderColor: 'rgba(15, 23, 42, 0.12)',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Customer List
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  resetCustomerForm();
                  setEditingCustomerId(null);
                }}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
              >
                Add New Customer
              </Button>
            </Stack>
            <Stack spacing={1}>
              {customers.map((c) => (
                <Paper
                  key={c.id}
                  variant="outlined"
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    borderColor: 'rgba(15, 23, 42, 0.12)',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {String(c.displayName || c.name || '').trim() || 'Customer'}
                    </Typography>
                    {c.mobile ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatPhoneDisplay(c.mobile)}
                      </Typography>
                    ) : null}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedCustomerId(c.id);
                      loadCustomerIntoForm(c.id);
                      setEditingCustomerId(c.id);
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Paper>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 3,
            bgcolor: '#f8fafc',
            borderColor: 'rgba(15, 23, 42, 0.12)',
            maxWidth: { md: 980 },
            mx: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            {editingCustomerId ? 'Edit Customer' : 'Add Customer'}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              columnGap: 1.5,
              rowGap: '15px',
              alignItems: 'start',
            }}
          >
            <Box>
              <TextField label="First Name *" value={custFirstName} onChange={(e) => setCustFirstName(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box>
              <TextField label="Middle Name" value={custMiddleName} onChange={(e) => setCustMiddleName(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box>
              <TextField label="Surname *" value={custLastName} onChange={(e) => setCustLastName(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
              <Autocomplete
                freeSolo
                options={displayNameOptions}
                value={custDisplayName}
                onInputChange={(_, v) => setCustDisplayName(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Display Name (as per PAN)"
                    size="small"
                    helperText={displayNameOptions.length ? 'Pick a suggested name or type PAN name.' : ''}
                    sx={customerFieldSx}
                  />
                )}
              />
            </Box>
            <Box>
              <TextField label="Mobile *" value={custMobile} onChange={(e) => setCustMobile(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box>
              <TextField
                label="Date of Birth *"
                type="date"
                value={custDateOfBirth}
                onChange={(e) => setCustDateOfBirth(e.target.value)}
                size="small"
                fullWidth
                sx={customerFieldSx}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField label="Email (optional)" value={custEmail} onChange={(e) => setCustEmail(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box>
              <TextField label="PAN" value={custPan} onChange={(e) => setCustPan(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box>
              <FormControl size="small" sx={{ ...customerFieldSx, width: '100%' }}>
                <InputLabel>Occupation</InputLabel>
                <Select value={custOccupation} label="Occupation" onChange={(e) => setCustOccupation(e.target.value)}>
                  <MenuItem value="">Select</MenuItem>
                  {custOccupation && !occupationOptions.includes(custOccupation) ? (
                    <MenuItem value={custOccupation}>{custOccupation} (legacy)</MenuItem>
                  ) : null}
                  {occupationOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FormControl size="small" sx={{ ...customerFieldSx, width: '100%' }}>
                <InputLabel>Gender</InputLabel>
                <Select value={custGender} label="Gender" onChange={(e) => setCustGender(e.target.value)}>
                  <MenuItem value="">Select</MenuItem>
                  {custGender && !genderOptions.includes(custGender) ? (
                    <MenuItem value={custGender}>{custGender} (legacy)</MenuItem>
                  ) : null}
                  {genderOptions.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {!editingCustomerId && customers.length > 0 ? (
              <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
                  onClick={() => {
                    const src = firstCustomerForAddress as any;
                    setCustAddr1(String(src?.addressLine1 || ''));
                    setCustAddr2(String(src?.addressLine2 || ''));
                    setCustCity(String(src?.city || ''));
                    setCustState(String(src?.state || ''));
                    setCustPincode(String(src?.pincode || ''));
                  }}
                  sx={{ textTransform: 'none', borderRadius: 999 }}
                >
                  Use address from first customer
                </Button>
              </Box>
            ) : null}
            <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
              <TextField label="Address line 1" value={custAddr1} onChange={(e) => setCustAddr1(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
              <TextField label="Address line 2" value={custAddr2} onChange={(e) => setCustAddr2(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box>
              <TextField label="City" value={custCity} onChange={(e) => setCustCity(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
            <Box>
              <Autocomplete
                options={indiaStatesAndUTs}
                value={indiaStatesAndUTs.includes(custState) ? custState : null}
                inputValue={custState}
                onChange={(_, v) => setCustState(v || '')}
                onInputChange={(_, v) => setCustState(v)}
                renderInput={(params) => <TextField {...params} label="State" size="small" sx={customerFieldSx} />}
              />
            </Box>
            <Box>
              <TextField label="Pincode" value={custPincode} onChange={(e) => setCustPincode(e.target.value)} size="small" fullWidth sx={customerFieldSx} />
            </Box>
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="text" onClick={resetCustomerForm} disabled={savingCustomer}>
              Clear
            </Button>
            <Button variant="outlined" onClick={() => setCustomerDrawerOpen(false)} disabled={savingCustomer}>
              Cancel
            </Button>
            <Button variant="contained" onClick={saveCustomer} disabled={savingCustomer}>
              {savingCustomer ? 'Saving…' : editingCustomerId ? 'Save Changes' : 'Add Customer'}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );

  const resetPaymentForm = () => {
    setPaymentBankAccountId('');
    setPaymentDateYMD(ymdToday());
    setPaymentAmount('');
    setPaymentTxnType('receipt');
    setPaymentReceiptCategory('customer');
    setPaymentMode('');
    setPaymentReferenceNo('');
    setPaymentNotes('');
    setPaymentPartyName(primaryCustomerName || '');
    setPaymentPayerBankName('');
    setPaymentVoucherFile(null);
    setPaymentError(null);
    if (paymentVoucherInputRef.current) paymentVoucherInputRef.current.value = '';
  };

  const openPaymentDrawer = (kind: 'dp' | 'gst' | 'advocateAgreement') => {
    setCustomerDrawerOpen(false);
    setPaymentDrawerOpen(true);
    setPaymentError(null);
    setPaymentTxnType('receipt');
    setPaymentReceiptCategory(kind === 'gst' ? 'gst' : kind === 'advocateAgreement' ? 'advocate' : 'customer');
    setPaymentIsLoanDisbursement(false);
    setPaymentPartyName(primaryCustomerName || '');
    setPaymentPayerBankName(
      kind === 'gst' ? lastPayerBankByCategory.gst : kind === 'advocateAgreement' ? '' : lastPayerBankByCategory.customer
    );
    setPaymentDateYMD(ymdToday());
    setPaymentMode('');
    setPaymentReferenceNo('');
    setPaymentNotes('');
    setPaymentVoucherFile(null);
    if (paymentVoucherInputRef.current) paymentVoucherInputRef.current.value = '';
    if (kind === 'gst') {
      const amt = typeof unit?.unitAmount === 'number' ? computeGstAmount(unit.unitAmount) : 0;
      setPaymentAmount(amt > 0 ? String(amt) : '');
      setPaymentBankAccountId('');
      return;
    }
    if (kind === 'advocateAgreement') {
      setPaymentAmount(projectAdvocateAgreementFee > 0 ? String(projectAdvocateAgreementFee) : '');
      const preferredAdvocateAccount =
        allowedAdvocateAccounts.find((b) => String(b.accountRole || '').toLowerCase() === 'dummy_advocate') ||
        allowedAdvocateAccounts.find((b) =>
          `${String(b.name || '')} ${String(b.bankName || '')}`.toLowerCase().includes('advocate')
        ) ||
        (allowedAdvocateAccounts.length === 1 ? allowedAdvocateAccounts[0] : null);
      setPaymentBankAccountId(preferredAdvocateAccount?.id || '');
      return;
    }
    setPaymentAmount('');
    if (allowedCustomerAccounts.length === 1) {
      setPaymentBankAccountId(allowedCustomerAccounts[0].id);
    } else {
      setPaymentBankAccountId('');
    }
  };
  openPaymentDrawerRef.current = openPaymentDrawer;

  const openGstPaymentUnified = useCallback(
    (stepConfig?: { which: 'main' | 'loan'; key: string }) => {
      openUnifiedPayment('gst', stepConfig);
    },
    [openUnifiedPayment]
  );

  useEffect(() => {
    if (!open || !actionRequest?.type || !actionRequest?.nonce) return;
    if (actionRequest?.unitId && String(actionRequest.unitId || '') !== String(unit?.id || '')) return;
    if (handledActionNonceRef.current === actionRequest.nonce) return;
    if (actionRequest.type === 'booking_form' || actionRequest.type === 'download_receipt' || actionRequest.type === 'start_loan' || actionRequest.type === 'stop_loan' || actionRequest.type === 'regenerate_doc' || actionRequest.type === 'edit_sanction' || actionRequest.type === 'auto_booking_form') return; // handled below
    handledActionNonceRef.current = actionRequest.nonce;
    setError(null);
    if (actionRequest.type === 'add_customer') {
      setCustomerDrawerOpen(true);
      return;
    }
    if (actionRequest.type === 'add_kyc') {
      setKycDialogOpen(true);
      return;
    }
    if (actionRequest.type === 'upload_booking_form') {
      setTimeout(() => quickBookingFormUploadInputRef.current?.click(), 60);
      return;
    }
    if (actionRequest.type === 'add_dp') {
      openUnifiedPayment('dp', { which: 'main', key: OFFICE_MAIN_STEP_KEYS.downpaymentGst });
      return;
    }
    if (actionRequest.type === 'add_gst') {
      openUnifiedPayment('gst', { which: 'main', key: OFFICE_MAIN_STEP_KEYS.downpaymentGst });
      return;
    }
    if (actionRequest.type === 'add_advocate_fee') {
      const targetStep =
        mainWorkflowV2Pending.find((step) => String(step.actionType || '').toLowerCase() === 'advocate_agreement_fee_receipt') ||
        loanWorkflowV2Pending.find((step) => String(step.actionType || '').toLowerCase() === 'advocate_agreement_fee_receipt');
      openUnifiedPayment(
        'advocateAgreement',
        targetStep
          ? { which: mainWorkflowV2Pending.includes(targetStep as any) ? 'main' : 'loan', key: targetStep.key }
          : undefined
      );
      return;
    }
    if (actionRequest.type === 'loan_sanction_form') {
      const targetStep = loanWorkflowV2Pending.find((step) => String(step.actionType || '').toLowerCase() === 'loan_sanction_form');
      if (targetStep) openLoanSanctionDialog(targetStep);
      return;
    }
  }, [actionRequest, loanWorkflowV2Pending, mainWorkflowV2Pending, open, openLoanSanctionDialog, openUnifiedPayment, unit?.id]);

  const skipGstPaymentForNow = async () => {
    setPaymentError(null);
    if (!pendingPaymentStep || pendingPaymentStep.which !== 'main') {
      setPaymentDrawerOpen(false);
      return;
    }
    try {
      await updateStepField('main', pendingPaymentStep.key, 'gstSkippedForNow', true);
      await setStepDone('main', pendingPaymentStep.key, true);
      setPendingPaymentStep(null);
      setPaymentDrawerOpen(false);
      resetPaymentForm();
    } catch (err: any) {
      setPaymentError(err?.message || 'Failed to skip GST for now.');
    }
  };

  const detectPaymentDuplicates = useCallback((input: {
    amount: number;
    date: Date;
  }): PaymentDuplicateMatch[] => {
    const normalizedRef = normalizePaymentDupRef(paymentReferenceNo);
    const normalizedParty = normalizePaymentDupText(paymentPartyName);
    const normalizedPayerBank = normalizePaymentDupText(paymentPayerBankName);
    const normalizedMode = normalizePaymentDupText(paymentMode);
    const selectedTxnType = String(paymentTxnType || '').toLowerCase();
    const selectedReceiptCategory = String(paymentReceiptCategory || '').toLowerCase();
    const selectedBankId = String(paymentBankAccountId || '').trim();
    const selectedBookingId = String(bookingId || '').trim();
    const selectedUnitId = String(unit?.id || '').trim();

    const matches: PaymentDuplicateMatch[] = [];
    paymentDuplicateCandidates.forEach((row: any) => {
      const rowId = String(row?.id || '').trim();
      if (!rowId) return;
      if (String(row?.txnType || '').toLowerCase() !== selectedTxnType) return;
      if (selectedTxnType === 'receipt') {
        if (String(row?.receiptCategory || '').toLowerCase() !== selectedReceiptCategory) return;
      }
      if (String(row?.bankAccountId || '').trim() !== selectedBankId) return;
      if (Math.abs(Number(row?.amount || 0) - input.amount) >= 0.01) return;
      if (selectedUnitId && String(row?.unitId || '').trim() !== selectedUnitId) return;
      if (selectedBookingId && String(row?.bookingId || '').trim() && String(row?.bookingId || '').trim() !== selectedBookingId) {
        return;
      }

      const rowMs =
        coerceTsToMs(row?.chqDate) ||
        coerceTsToMs(row?.txnDate) ||
        coerceTsToMs(row?.createdAt) ||
        coerceTsToMs(row?.updatedAt) ||
        null;
      if (!rowMs) return;
      const rowDate = new Date(rowMs);
      const diff = dayDiffAbsForPayment(input.date, rowDate);
      if (diff > 10) return;

      const rowRef = normalizePaymentDupRef(row?.referenceNo);
      const rowParty = normalizePaymentDupText(row?.partyName);
      const rowPayerBank = normalizePaymentDupText(row?.payerBankName);
      const rowMode = normalizePaymentDupText(row?.mode);
      const hasRefMatch = !!normalizedRef && !!rowRef && normalizedRef === rowRef;
      const hasPartyMatch = !!normalizedParty && !!rowParty && normalizedParty === rowParty;
      const hasPayerBankMatch = !!normalizedPayerBank && !!rowPayerBank && normalizedPayerBank === rowPayerBank;
      const hasModeMatch = !!normalizedMode && !!rowMode && normalizedMode === rowMode;
      if (!hasRefMatch && !hasPartyMatch && !hasPayerBankMatch && !hasModeMatch) return;

      const isExact = diff === 0 && (hasRefMatch || (hasPartyMatch && hasPayerBankMatch) || hasModeMatch);
      matches.push({
        kind: isExact ? 'exact' : 'likely',
        row,
        dateText: formatDateDDMMYYYY(rowDate),
        dayDiff: diff,
      });
    });

    return matches.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'exact' ? -1 : 1;
      return a.dayDiff - b.dayDiff;
    });
  }, [
    paymentReferenceNo,
    paymentPartyName,
    paymentPayerBankName,
    paymentMode,
    paymentTxnType,
    paymentReceiptCategory,
    paymentBankAccountId,
    bookingId,
    unit?.id,
    paymentDuplicateCandidates,
  ]);

  useEffect(() => {
    if (!paymentDrawerOpen || paymentDuplicateDialogOpen) {
      setPaymentLiveDuplicateMatches([]);
      return;
    }
    const amt = Number(String(paymentAmount || '').replace(/,/g, ''));
    const d = dateFromYMD(paymentDateYMD);
    const hasIdentityField =
      !!String(paymentReferenceNo || '').trim() ||
      !!String(paymentPartyName || '').trim() ||
      !!String(paymentPayerBankName || '').trim() ||
      !!String(paymentMode || '').trim();
    if (!paymentBankAccountId || !Number.isFinite(amt) || amt <= 0 || !d || !hasIdentityField) {
      setPaymentLiveDuplicateMatches([]);
      return;
    }
    const timer = window.setTimeout(() => {
      setPaymentLiveDuplicateMatches(detectPaymentDuplicates({ amount: amt, date: d }));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    paymentDrawerOpen,
    paymentDuplicateDialogOpen,
    paymentAmount,
    paymentDateYMD,
    paymentBankAccountId,
    paymentTxnType,
    paymentReceiptCategory,
    paymentReferenceNo,
    paymentPartyName,
    paymentPayerBankName,
    paymentMode,
    bookingId,
    unit?.id,
    paymentDuplicateCandidates,
    detectPaymentDuplicates,
  ]);

  const savePaymentTransaction = async (opts?: { forceDuplicateOverride?: boolean }) => {
    setPaymentError(null);
    if (!projectId) return setPaymentError('Select a project first.');
    if (!unit?.id) return setPaymentError('Select a unit first.');
    if (!currentUser?.email) return setPaymentError('Not signed in.');

    const allowedAccounts =
      paymentTxnType === 'payment'
        ? bankAccounts.filter((b) => {
            const role = String(b.accountRole || '').toLowerCase();
            if (typeof b.allowPayments === 'boolean') return !!b.allowPayments;
            return role === 'operating30' || role === 'other' || !role;
          })
        : paymentReceiptCategory === 'gst'
          ? allowedGstAccounts
          : paymentReceiptCategory === 'advocate'
            ? allowedAdvocateAccounts
            : allowedCustomerAccounts;
    const selectedAccount = allowedAccounts.find((b) => b.id === paymentBankAccountId) || null;
    if (!selectedAccount) return setPaymentError('Select a bank account.');

    const amt = Number(String(paymentAmount || '').replace(/,/g, ''));
    if (!Number.isFinite(amt) || amt <= 0) return setPaymentError('Enter a valid amount.');
    const d = dateFromYMD(paymentDateYMD);
    if (!d) return setPaymentError('Select a valid date.');
    const requiresVoucher =
      paymentTxnType === 'receipt' &&
      (paymentReceiptCategory === 'customer' || paymentReceiptCategory === 'gst' || paymentReceiptCategory === 'advocate');
    if (requiresVoucher && !paymentVoucherFile) return setPaymentError('Chq / Voucher photo is required.');
    if (paymentTxnType === 'receipt' && paymentReceiptCategory === 'customer' && loanToDeveloperAmount > 0) {
      const unitAmount = Math.max(0, Number(unit?.unitAmount || 0) || 0);
      const allowedAmount = paymentIsLoanDisbursement
        ? loanToDeveloperAmount
        : Math.max(0, unitAmount - loanToDeveloperAmount);
      const recordedAmount = paymentIsLoanDisbursement ? loanDisbursementReceiptTotal : customerDpReceiptTotal;
      if (recordedAmount + amt > allowedAmount + 0.009) {
        return setPaymentError(
          `${paymentIsLoanDisbursement ? 'Loan disbursement' : 'Customer DP'} exceeds the allowed amount of ₹${allowedAmount.toLocaleString('en-IN')}.`
        );
      }
    }

    if (!opts?.forceDuplicateOverride) {
      const matches = detectPaymentDuplicates({ amount: amt, date: d });
      if (matches.length) {
        setPaymentDuplicateMatches(matches);
        setPaymentDuplicateDialogOpen(true);
        return;
      }
    }

    setPaymentSaving(true);
    try {
      const docRef = doc(collection(db, 'projects', projectId, 'bankTransactions'));
      const payload: any = {
        txnType: paymentTxnType,
        receiptCategory: paymentTxnType === 'receipt' ? paymentReceiptCategory : null,
        isLoanDisbursement: paymentTxnType === 'receipt' && paymentReceiptCategory === 'customer' && paymentIsLoanDisbursement,
        paymentCategory: paymentTxnType === 'payment' ? null : null,
        bankAccountId: selectedAccount.id,
        unitId: unit.id,
        unitNumber: unit.unitNumber ? String(unit.unitNumber).trim() : null,
        bookingId: String(bookingId || '').trim() || null,
        amount: amt,
        chqDate: Timestamp.fromDate(d),
        txnDate: serverTimestamp(),
        partyName: paymentPartyName ? String(paymentPartyName).trim() : null,
        payerBankName:
          paymentTxnType === 'receipt' &&
          (paymentReceiptCategory === 'customer' || paymentReceiptCategory === 'gst' || paymentReceiptCategory === 'advocate')
            ? (paymentPayerBankName ? String(paymentPayerBankName).trim() : null)
            : null,
        mode: paymentMode ? String(paymentMode).trim() : null,
        referenceNo: paymentReferenceNo ? String(paymentReferenceNo).trim() : null,
        notes: paymentNotes ? String(paymentNotes).trim() : null,
        refundAgainstTxnId: null,
        legalChargeType:
          paymentTxnType === 'receipt' && paymentReceiptCategory === 'advocate' ? 'agreement_fee' : null,
        chargeType:
          paymentTxnType === 'receipt' && paymentReceiptCategory === 'advocate' ? 'agreement_fee' : null,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };

      if (paymentVoucherFile) {
        setPaymentUploading(true);
        const safeName = paymentVoucherFile.name.replace(/[^a-zA-Z0-9._-]+/g, '_');
        const path = `bank-transactions/${projectId}/${docRef.id}/voucher_${Date.now()}_${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, paymentVoucherFile);
        const url = await getDownloadURL(storageRef);
        payload.voucherUrl = url;
        payload.voucherName = paymentVoucherFile.name;
        payload.voucherPath = path;
        payload.voucherUploadedAt = serverTimestamp();
        payload.voucherUploadedBy = currentUser.email;
      }

      await setDoc(docRef, payload);
      setPaymentDrawerOpen(false);
      resetPaymentForm();

      void (async () => {
        try {
          const effectiveBookingId = bookingId || (await ensureBookingForDraft());
          await generatePaymentDetailsV1Draft(effectiveBookingId);

          if (pendingPaymentStep && paymentTxnType === 'receipt' && paymentReceiptCategory !== 'advocate') {
            if (paymentReceiptCategory === 'gst' && pendingPaymentStep.which === 'main') {
              await updateStepField('main', pendingPaymentStep.key, 'gstSkippedForNow', false);
            }
            const nextCustomerTotal = customerReceiptTotal + (paymentReceiptCategory === 'customer' ? amt : 0);
            const nextGstTotal = gstReceiptTotal + (paymentReceiptCategory === 'gst' ? amt : 0);
            const isPaymentStepComplete = isPaymentStepSatisfied(
              pendingPaymentStep.which,
              pendingPaymentStep.key,
              nextCustomerTotal,
              nextGstTotal
            );
            if (isPaymentStepComplete) {
              await setStepDone(pendingPaymentStep.which, pendingPaymentStep.key, true);
            }
          }
        } catch (backgroundErr) {
          console.warn('Payment saved but payment details refresh failed', backgroundErr);
        }
      })();
    } catch (err: any) {
      console.error('Save payment transaction failed', err);
      setPaymentError(err?.message || 'Failed to save payment.');
    } finally {
      setPaymentSaving(false);
      setPaymentUploading(false);
    }
  };

  const confirmPaymentDuplicateAndSave = async () => {
    setPaymentDuplicateProceeding(true);
    try {
      await savePaymentTransaction({ forceDuplicateOverride: true });
      setPaymentDuplicateDialogOpen(false);
      setPaymentDuplicateMatches([]);
    } finally {
      setPaymentDuplicateProceeding(false);
    }
  };

  const paymentLiveExactDuplicateMatches = paymentLiveDuplicateMatches.filter((m) => m.kind === 'exact').slice(0, 3);
  const paymentLiveDuplicateAlertSection = paymentLiveDuplicateMatches.length ? (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: '#b91c1c',
        color: '#ffffff',
        borderColor: '#7f1d1d',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 800, color: '#ffffff' }}>
        {paymentLiveExactDuplicateMatches.length
          ? 'Possible duplicate found before save (exact match).'
          : 'Possible duplicate found before save (likely match within ±10 days).'}
      </Typography>
      {paymentLiveExactDuplicateMatches.length ? (
        <Stack spacing={0.5} sx={{ mt: 0.75 }}>
          {paymentLiveExactDuplicateMatches.map((match) => (
            <Typography key={`${String(match.row?.id || '')}-${match.kind}`} variant="caption" sx={{ color: '#ffffff' }}>
              {`Date: ${match.dateText} • Unit: ${String(match.row?.unitNumber || '—')} • Party: ${String(
                match.row?.partyName || '—'
              )} • Ref: ${String(match.row?.referenceNo || '—')} • Amount: ₹${Number(match.row?.amount || 0).toLocaleString(
                'en-IN'
              )}`}
            </Typography>
          ))}
        </Stack>
      ) : null}
    </Paper>
  ) : null;

  const paymentLoanDisbursementSection =
    paymentTxnType === 'receipt' && paymentReceiptCategory === 'customer' && loanToDeveloperAmount > 0 ? (
      <FormControlLabel
        control={
          <Checkbox
            checked={paymentIsLoanDisbursement}
            onChange={(event) => setPaymentIsLoanDisbursement(event.target.checked)}
          />
        }
        label={`Is this loan disbursement? (Loan to builder: ₹${loanToDeveloperAmount.toLocaleString('en-IN')})`}
      />
    ) : null;

  const paymentDetailLoanDisbursementSection =
    paymentDetailEditMode &&
    paymentDetailTxnType === 'receipt' &&
    paymentDetailReceiptCategory === 'customer' &&
    loanToDeveloperAmount > 0 ? (
      <FormControlLabel
        control={
          <Checkbox
            checked={paymentDetailIsLoanDisbursement}
            onChange={(event) => setPaymentDetailIsLoanDisbursement(event.target.checked)}
          />
        }
        label={`Is this loan disbursement? (Loan to builder: ₹${loanToDeveloperAmount.toLocaleString('en-IN')})`}
      />
    ) : null;

  const savePaymentDetailTransaction = async () => {
    setPaymentDetailError(null);
    if (!projectId) return setPaymentDetailError('Select a project first.');
    if (!unit?.id) return setPaymentDetailError('Select a unit first.');
    if (!currentUser?.email) return setPaymentDetailError('Not signed in.');
    const txnId = String(paymentDetailTxn?.id || '').trim();
    if (!txnId) return setPaymentDetailError('Transaction not found.');
    const selectedAccount = paymentDetailBankAccounts.find((a) => a.id === paymentDetailBankAccountId);
    if (!selectedAccount) return setPaymentDetailError('Select a bank account.');
    const amountValue = Number(paymentDetailAmount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) return setPaymentDetailError('Enter a valid amount.');
    const dateValue = dateFromYMD(paymentDetailDateYMD);
    if (!dateValue) return setPaymentDetailError('Select a valid date.');
    const editingCustomerReceipt =
      String(paymentDetailTxn?.txnType || '').toLowerCase() === 'receipt' &&
      String(paymentDetailTxn?.receiptCategory || '').toLowerCase() === 'customer';
    const existingAmount = Math.max(0, Number(paymentDetailTxn?.amount || 0) || 0);
    const existingIsLoanDisbursement = paymentDetailTxn?.isLoanDisbursement === true;
    const isCustomerReceipt = paymentDetailTxnType === 'receipt' && paymentDetailReceiptCategory === 'customer';
    if (isCustomerReceipt && loanToDeveloperAmount > 0) {
      const unitAmount = Math.max(0, Number(unit?.unitAmount || 0) || 0);
      const allowedAmount = paymentDetailIsLoanDisbursement
        ? loanToDeveloperAmount
        : Math.max(0, unitAmount - loanToDeveloperAmount);
      const recordedAmount = paymentDetailIsLoanDisbursement
        ? loanDisbursementReceiptTotal - (editingCustomerReceipt && existingIsLoanDisbursement ? existingAmount : 0)
        : customerDpReceiptTotal - (editingCustomerReceipt && !existingIsLoanDisbursement ? existingAmount : 0);
      if (recordedAmount + amountValue > allowedAmount + 0.009) {
        return setPaymentDetailError(
          `${paymentDetailIsLoanDisbursement ? 'Loan disbursement' : 'Customer DP'} exceeds the allowed amount of ₹${allowedAmount.toLocaleString('en-IN')}.`
        );
      }
    }
    setPaymentDetailSaving(true);
    try {
      const txnRef = doc(db, 'projects', projectId, 'bankTransactions', txnId);
      await updateDoc(txnRef, {
        txnType: paymentDetailTxnType,
        receiptCategory: paymentDetailTxnType === 'receipt' ? paymentDetailReceiptCategory : null,
        isLoanDisbursement: isCustomerReceipt && paymentDetailIsLoanDisbursement,
        paymentCategory:
          paymentDetailTxnType === 'payment'
            ? (String((paymentDetailTxn as any)?.paymentCategory || '').trim() || null)
            : null,
        bankAccountId: selectedAccount.id,
        bankName: selectedAccount.name || selectedAccount.bankName || null,
        amount: amountValue,
        chqDate: Timestamp.fromDate(dateValue),
        txnDateYMD: paymentDetailDateYMD,
        partyName: String(paymentDetailPartyName || '').trim() || null,
        payerBankName: String(paymentDetailPayerBankName || '').trim() || null,
        mode: String(paymentDetailMode || '').trim() || null,
        referenceNo: String(paymentDetailReferenceNo || '').trim() || null,
        notes: String(paymentDetailNotes || '').trim() || null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      const paymentDetailsBookingId = String(paymentDetailTxn?.bookingId || bookingId || '').trim();
      const shouldRefreshPaymentDetails =
        paymentDetailTxnType === 'receipt' &&
        (paymentDetailReceiptCategory === 'customer' || paymentDetailReceiptCategory === 'gst') &&
        !!paymentDetailsBookingId;
      setPaymentDetailDrawerOpen(false);
      setPaymentDetailTxn(null);
      if (shouldRefreshPaymentDetails) {
        void generatePaymentDetailsV1Draft(paymentDetailsBookingId).catch((backgroundErr) => {
          console.warn('Payment updated but payment details refresh failed', backgroundErr);
        });
      }
    } catch (err: any) {
      console.error('Update payment transaction failed', err);
      setPaymentDetailError(err?.message || 'Failed to update transaction.');
    } finally {
      setPaymentDetailSaving(false);
    }
  };

  const markAdvocateAgreementFeeReceivedWithoutDetails = async () => {
    setPaymentError(null);
    if (!projectId) return setError('Select a project first.');
    if (!unit?.id) return setError('Select a unit first.');
    if (!currentUser?.email) return setError('Not signed in.');
    const pendingAmount = Math.max(0, projectAdvocateAgreementFee - advocateAgreementReceiptTotal);
    if (!pendingAmount) return;
    const preferredAdvocateAccount =
      allowedAdvocateAccounts.find((b) => String(b.accountRole || '').toLowerCase() === 'dummy_advocate') ||
      allowedAdvocateAccounts.find((b) =>
        `${String(b.name || '')} ${String(b.bankName || '')}`.toLowerCase().includes('advocate')
      ) ||
      (allowedAdvocateAccounts.length === 1 ? allowedAdvocateAccounts[0] : null);
    if (!preferredAdvocateAccount?.id) {
      setError('Advocate bank account is required. Please configure/select one first.');
      return;
    }

    try {
      const docRef = doc(collection(db, 'projects', projectId, 'bankTransactions'));
      await setDoc(docRef, {
        txnType: 'receipt',
        receiptCategory: 'advocate',
        bankAccountId: preferredAdvocateAccount.id,
        unitId: unit.id,
        unitNumber: unit.unitNumber ? String(unit.unitNumber).trim() : null,
        bookingId: String(bookingId || '').trim() || null,
        amount: pendingAmount,
        chqDate: null,
        txnDate: serverTimestamp(),
        partyName: null,
        payerBankName: null,
        mode: null,
        referenceNo: '✓',
        notes: 'Marked received without transaction details.',
        legalChargeType: 'agreement_fee',
        chargeType: 'agreement_fee',
        receiptConfirmedWithoutDetails: true,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      const effectiveBookingId = bookingId || (await ensureBookingForDraft());
      try {
        await generatePaymentDetailsV1Draft(effectiveBookingId);
      } catch (draftErr) {
        console.warn('Advocate receipt marked but payment_details_v1 draft refresh failed', draftErr);
      }
      setError(null);
    } catch (err: any) {
      console.error('Mark advocate agreement fee received failed', err);
      setError(err?.message || 'Failed to mark advocate fee received.');
    }
  };

  const paymentAccounts = useMemo(() => {
    if (paymentTxnType === 'payment') {
      return bankAccounts.filter((b) => {
        const role = String(b.accountRole || '').toLowerCase();
        if (typeof b.allowPayments === 'boolean') return !!b.allowPayments;
        return role === 'operating30' || role === 'other' || !role;
      });
    }
    if (paymentReceiptCategory === 'gst') return allowedGstAccounts;
    if (paymentReceiptCategory === 'advocate') return allowedAdvocateAccounts;
    return allowedCustomerAccounts;
  }, [
    allowedAdvocateAccounts,
    allowedCustomerAccounts,
    allowedGstAccounts,
    bankAccounts,
    paymentReceiptCategory,
    paymentTxnType,
  ]);

  const paymentUnitOptions = useMemo(
    () =>
      unit
        ? [
            {
              id: String(unit.id || ''),
              unitNumber: String(unit.unitNumber || unit.id || ''),
              unitAmount: Number(unit.unitAmount || 0) || 0,
            },
          ]
        : [],
    [unit]
  );

  const paymentDetailBankAccounts = useMemo(() => {
    const list = [...bankAccounts];
    const id = String(paymentDetailTxn?.bankAccountId || '').trim();
    if (!id) return list;
    if (list.some((row) => String(row?.id || '').trim() === id)) return list;
    const bankName = String(paymentDetailTxn?.bankName || paymentDetailTxn?.payerBankName || '').trim();
    return [
      ...list,
      {
        id,
        name: bankName || id,
        bankName: bankName || null,
      },
    ];
  }, [bankAccounts, paymentDetailTxn]);

  useEffect(() => {
    if (!onDesktopOffsetChange) return;
    if (isMobile || !open || actionOnly) {
      onDesktopOffsetChange(0);
      return;
    }
    const px =
      bookingPanelWidth +
      (paymentDrawerOpen ? paymentPanelWidth + panelGap : 0);
    onDesktopOffsetChange(px);
    return () => onDesktopOffsetChange(0);
  }, [
    actionOnly,
    bookingPanelWidth,
    isMobile,
    onDesktopOffsetChange,
    open,
    panelGap,
    paymentDrawerOpen,
    paymentPanelWidth,
  ]);

const handlePickFiles = () => {
	    if (!fileInputRef.current) return;
	    fileInputRef.current.click();
	  };

	  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
	    const files = Array.from(e.target.files || []);
	    setKycFiles(files);
	  };

	  const pickAadhaarFront = () => {
	    if (!aadhaarFrontInputRef.current) return;
	    aadhaarFrontInputRef.current.click();
	  };
	  const pickAadhaarBack = () => {
	    if (!aadhaarBackInputRef.current) return;
	    aadhaarBackInputRef.current.click();
	  };
	  const onAadhaarFrontChange = (e: React.ChangeEvent<HTMLInputElement>) => {
	    const file = (e.target.files || [])[0] || null;
	    setAadhaarFrontFile(file);
	  };
	  const onAadhaarBackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
	    const file = (e.target.files || [])[0] || null;
	    setAadhaarBackFile(file);
	  };

  const uploadKyc = async () => {
	    if (!unit?.id) return;
	    if (!projectId || !bookingId) return;
	    if (!selectedCustomerId) {
      setError('Select a customer first.');
      return;
    }
	    if (!currentUser?.email) {
	      setError('Not signed in.');
	      return;
	    }
	    const isAadhaar = kycType === 'AADHAAR';
	    const toUpload: Array<{ file: File; side?: 'front' | 'back' | null }> = [];
	    if (isAadhaar) {
	      if (!aadhaarFrontFile || !aadhaarBackFile) {
	        setError('Select both Aadhaar front and back images.');
	        return;
	      }
	      toUpload.push({ file: aadhaarFrontFile, side: 'front' });
	      toUpload.push({ file: aadhaarBackFile, side: 'back' });
	    } else {
	      if (kycFiles.length === 0) {
	        setError('Select a file to upload.');
	        return;
	      }
	      if (kycFiles.length > 1) {
	        setError('Only one file allowed for PAN.');
	        return;
	      }
	      toUpload.push({ file: kycFiles[0], side: null });
	    }

	    setError(null);
	    setSavingKyc(true);
	    try {
	      const safePart = (s: string) => String(s || '').trim().replace(/[^\w.-]+/g, '_');
	      const by = currentUser.email;
	      const docsCol = collection(
	        db,
	        'projects',
        projectId,
        'units',
        unit.id,
        'bookings',
        bookingId,
        'customers',
        selectedCustomerId,
        'kycDocs'
      );

	      const filesOut: any[] = [];
	      const unitNo = String(unit?.unitNumber || unit?.id || 'Unit').trim();
	      const customerLabel = String(selectedCustomer?.displayName || selectedCustomer?.name || 'Customer').trim();
	      const baseLabel = `${unitNo} - ${String(kycType || 'DOC').toUpperCase()} - ${customerLabel}`;

	      for (let i = 0; i < toUpload.length; i++) {
	        const item = toUpload[i];
	        const file = await shrinkIdFile(item.file);
	        const originalName = file.name;
	        const dot = originalName.lastIndexOf('.');
	        const ext = dot > -1 ? originalName.slice(dot) : '';
	        const sideLabel = item.side ? ` - ${item.side === 'front' ? 'Front' : 'Back'}` : '';
	        const seqLabel = toUpload.length > 1 && !item.side ? ` (${i + 1})` : '';
	        const finalName = `${baseLabel}${sideLabel}${seqLabel}${ext}`;
	        const safeName = safePart(finalName);
	        const uploadedAtMs = Date.now();
	        const path = `bookings/${bookingId}/customers/${selectedCustomerId}/kyc/${kycType}/${uploadedAtMs}_${safeName}`;
	        const storageRef = ref(storage, path);
	        await uploadBytes(storageRef, file);
	        const url = await getDownloadURL(storageRef);
	        filesOut.push({
	          name: finalName,
	          originalName,
	          url,
	          path,
	          uploadedAtMs,
	          uploadedBy: by,
	          side: item.side || null,
	        });
	      }

	      // Keep max two KYC docs per customer by storing under fixed IDs: PAN and AADHAAR.
	      // Uploading again replaces that document's files.
	      const kycDocRef = doc(docsCol, String(kycType || 'DOC').toUpperCase());
	      const existing = await getDoc(kycDocRef);
	      const base: any = {
	        type: String(kycType || 'DOC').toUpperCase(),
	        docNumber: kycDocNumber.trim() || null,
	        status: 'pending',
	        files: filesOut,
	        updatedAt: serverTimestamp(),
	        updatedBy: by,
	      };
	      if (!existing.exists()) {
	        base.createdAt = serverTimestamp();
	        base.createdBy = by;
	      }
	      await setDoc(kycDocRef, base, { merge: true });

	      setKycDocNumber('');
	      setKycFiles([]);
	      setAadhaarFrontFile(null);
	      setAadhaarBackFile(null);
	      if (fileInputRef.current) fileInputRef.current.value = '';
	      if (aadhaarFrontInputRef.current) aadhaarFrontInputRef.current.value = '';
	      if (aadhaarBackInputRef.current) aadhaarBackInputRef.current.value = '';
	    } catch (err: any) {
	      console.error('Upload KYC failed', err);
	      setError(err?.message || 'Failed to upload KYC.');
	    } finally {
      setSavingKyc(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!currentMainStep) return;
    if (currentMainStep.key !== OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter) return;
    if (currentMainStep.status !== 'ready') return;
    if (!unitDataReady) return;
    if (isStepInputsComplete('main', currentMainStep)) return;
    if (autoGeneratingAllotmentRef.current) return;
    autoGeneratingAllotmentRef.current = true;
    runAllotmentDraftGeneration(OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter)
      .catch((err) => {
        console.error('Auto-generate allotment draft failed', err);
      })
      .finally(() => {
        autoGeneratingAllotmentRef.current = false;
      });
  }, [currentMainStep, isStepInputsComplete, open, runAllotmentDraftGeneration, unitDataReady]);

  useEffect(() => {
    if (!open) return;
    if (!currentMainStep) return;
    if (currentMainStep.status !== 'ready') {
      autoFinalizingStepRef.current = '';
      return;
    }
    if (!isStepInputsComplete('main', currentMainStep)) return;
    const stepRefKey = `main:${currentMainStep.key}`;
    if (autoFinalizingStepRef.current === stepRefKey) return;
    autoFinalizingStepRef.current = stepRefKey;
    setStepDone('main', currentMainStep.key, true).catch((err) => {
      autoFinalizingStepRef.current = '';
      console.error('Auto-finalize main step failed', err);
    });
  }, [currentMainStep, isStepInputsComplete, open, setStepDone]);

  const openBookingFormDraftDialog = useCallback(() => {
    const brokerId = String((booking as any)?.refByBrokerId || '').trim();
    const brokerName = String((booking as any)?.refByBrokerName || '').trim();
    const selected =
      (brokerId ? brokers.find((b) => b.id === brokerId) : null) ||
      (brokerName
        ? brokers.find(
            (b) =>
              String(b.brokerName || b.displayName || '').trim().toLowerCase() === brokerName.toLowerCase()
          )
        : null) ||
      null;
    setBookingFormRefByBroker(selected || null);
    const existingRank = (booking as any)?.rank;
    setBookingFormRank(
      typeof existingRank === 'number' && Number.isFinite(existingRank)
        ? String(existingRank)
        : String(existingRank || '').trim()
    );
    const schemeEnabled = Boolean((booking as any)?.schemeEnabled);
    setBookingFormScheme(schemeEnabled ? 'yes' : 'no');
    setBookingFormSchemeRemark(String((booking as any)?.schemeRemark || ''));
    setBookingFormTerms(String((booking as any)?.paymentTerms || ''));
    setBookingFormCondition((booking as any)?.paymentCondition || newPaymentCondition());
    setBookingFormUseLegacyTerms(Boolean((booking as any)?.paymentTerms && !(booking as any)?.paymentCondition));
    const existingFormDate = String(
      (booking as any)?.bookingFormDate ||
      (booking as any)?.requestDate ||
      ''
    ).trim();
    const existingFormTime = String(
      (booking as any)?.bookingFormTime ||
      (booking as any)?.requestTime ||
      ''
    ).trim();
    setBookingFormDraftDate(existingFormDate || ymdNowUsa());
    setBookingFormDraftTime(existingFormTime || hmNowUsa());
    const saved = booking as any;
    setBookingFormTermMonths(saved?.agreementTermMonths ? String(saved.agreementTermMonths) : '');
    setBookingFormEarnest(saved?.earnestAmount ? String(saved.earnestAmount) : '');
    setBookingFormEarnestTouched(!!saved?.earnestAmount);
    setBookingFormCommittee(saved?.societyCommittee === 'yes' || saved?.societyCommittee === 'no' ? saved.societyCommittee : '');
    // Documents: what staff saved, otherwise ticked from the KYC uploads of every buyer.
    const kyc = customers.map((c) => kycSummaryByCustomer[c.id]);
    const allHave = (key: 'hasPAN' | 'hasAADHAAR' | 'hasPHOTO') => kyc.length > 0 && kyc.every((k) => !!k?.[key]);
    setBookingFormDocs(saved?.bookingFormDocs
      ? { photos: !!saved.bookingFormDocs.photos, pan: !!saved.bookingFormDocs.pan, aadhaar: !!saved.bookingFormDocs.aadhaar }
      : { photos: allHave('hasPHOTO'), pan: allHave('hasPAN'), aadhaar: allHave('hasAADHAAR') });
    setBookingFormError('');
    setBookingFormDraftDialogOpen(true);
  }, [booking, brokers, customers, kycSummaryByCustomer]);

  // Earnest money follows the down payment until staff type their own figure.
  useEffect(() => {
    if (!bookingFormDraftDialogOpen || bookingFormEarnestTouched) return;
    const percent = Number(bookingFormCondition?.stages?.[0]?.percent) || 0;
    const price = Number((unit as any)?.unitAmount || 0) || 0;
    setBookingFormEarnest(percent > 0 && price > 0 && !bookingFormUseLegacyTerms ? String(Math.round((price * percent) / 100)) : '');
  }, [bookingFormCondition, bookingFormDraftDialogOpen, bookingFormEarnestTouched, bookingFormUseLegacyTerms, unit]);

  // actionRequest 'start_loan' / 'stop_loan' (unit page Home loan card): needs the main case loaded first.
  useEffect(() => {
    const t = actionRequest?.type;
    if (!open || (t !== 'start_loan' && t !== 'stop_loan') || !actionRequest?.nonce) return;
    if (actionRequest.unitId && String(actionRequest.unitId) !== String(unit?.id || '')) return;
    if (handledActionNonceRef.current === actionRequest.nonce) return;
    if (!mainCase || !workflowHydration.mainCase) return;
    handledActionNonceRef.current = actionRequest.nonce;
    (async () => {
      if (t === 'start_loan') {
        await setLoanRequiredValue('yes');
        await startLoanWorkflowCase(true);
        return;
      }
      // Undo only while no loan step has been done; the started file is closed, not deleted.
      await setLoanRequiredValue('no');
      const pid = String(projectId || '').trim();
      const bid = String(bookingId || '').trim();
      if (loanCase && pid && unit?.id && bid && !(loanCase as any)?.completedStepKeys?.length) {
        await updateDoc(doc(db, 'projects', pid, 'units', unit.id, 'cases', `loan_${bid}`), { status: 'cancelled', updatedAt: serverTimestamp(), updatedBy: currentUser?.email || null });
      }
    })().catch((err: any) => {
      console.error('Loan file change failed', err);
      window.alert?.(err?.message || "Couldn't change the loan file.");
    });
    // setLoanRequiredValue / startLoanWorkflowCase are plain functions of the same values listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionRequest, mainCase, loanCase, open, unit?.id, workflowHydration.mainCase]);

  // actionRequest 'regenerate_doc' (unit page Documents card): same rebuild as the ⟳ icon, once the booking's documents have loaded.
  useEffect(() => {
    if (!open || actionRequest?.type !== 'regenerate_doc' || !actionRequest?.nonce || !actionRequest.docKey) return;
    if (actionRequest.unitId && String(actionRequest.unitId) !== String(unit?.id || '')) return;
    if (handledActionNonceRef.current === actionRequest.nonce) return;
    if (!booking || !workflowHydration.uploaded || !workflowHydration.draft) return;
    handledActionNonceRef.current = actionRequest.nonce;
    const selector = String(actionRequest.docKey);
    regenerateDocumentBySelector(selector).catch((err: any) => {
      console.error('Regenerate document failed', err);
      window.alert?.(err?.message || "Couldn't regenerate the document.");
    });
  }, [actionRequest, booking, open, regenerateDocumentBySelector, unit?.id, workflowHydration.draft, workflowHydration.uploaded]);

  // actionRequest 'edit_sanction' (unit page Home loan / Documents cards): reopen the sanction form with its saved details, once the loan file has loaded.
  useEffect(() => {
    if (!open || actionRequest?.type !== 'edit_sanction' || !actionRequest?.nonce) return;
    if (actionRequest.unitId && String(actionRequest.unitId) !== String(unit?.id || '')) return;
    if (handledActionNonceRef.current === actionRequest.nonce) return;
    if (!loanCase || !loanWorkflowDefinition?.steps || !workflowHydration.uploaded) return;
    handledActionNonceRef.current = actionRequest.nonce;
    openSanctionFormForDocument(String(actionRequest.docKey || 'sanction_letter'));
  }, [actionRequest, loanCase, loanWorkflowDefinition?.steps, open, openSanctionFormForDocument, unit?.id, workflowHydration.uploaded]);

  // actionRequest 'auto_booking_form' (buyer form approval): waits until the approved buyers and saved terms have arrived.
  useEffect(() => {
    if (!open || actionRequest?.type !== 'auto_booking_form' || !actionRequest?.nonce) return;
    if (actionRequest.unitId && String(actionRequest.unitId) !== String(unit?.id || '')) return;
    if (handledActionNonceRef.current === actionRequest.nonce) return;
    if (!unitDataReady || !bookingId || !(booking as any)?.agreementTermMonths) return;
    const approved = customers.filter((c) => !!(c as any).buyerFormApprovedAt).length;
    if (approved < (actionRequest.count || 1)) return;
    handledActionNonceRef.current = actionRequest.nonce;
    generateBookingFormV1Draft(bookingId).catch((err: any) => {
      console.error('Create booking form after approval failed', err);
      window.alert?.(err?.message || "Couldn't create the booking form. Use Create booking form to try again.");
    });
  }, [actionRequest, booking, bookingId, customers, generateBookingFormV1Draft, open, unit?.id, unitDataReady]);

  // actionRequest 'download_receipt': make the payment receipt PDF for one customer receipt, once it has loaded.
  useEffect(() => {
    if (!open || actionRequest?.type !== 'download_receipt' || !actionRequest?.nonce || !actionRequest.txnId) return;
    if (actionRequest.unitId && String(actionRequest.unitId) !== String(unit?.id || '')) return;
    if (handledActionNonceRef.current === actionRequest.nonce) return;
    const row = [...(customerReceiptRows || []), ...(gstReceiptRows || [])].find(
      (r: any) => String(r?.id || '') === String(actionRequest.txnId)
    );
    if (!row) return;
    handledActionNonceRef.current = actionRequest.nonce;
    setReceiptGeneratingTxnId(String(row.id));
    generateDocumentFromTemplateId('payment_receipt_v1', { transactionRow: row, downloadOnly: true })
      .catch((err: any) => {
        console.error('Download payment receipt failed', err);
        window.alert?.(err?.message || 'Could not make the payment receipt.');
      })
      .finally(() => setReceiptGeneratingTxnId(''));
  }, [actionRequest, customerReceiptRows, gstReceiptRows, generateDocumentFromTemplateId, open, unit?.id]);

  // actionRequest 'booking_form': the dialog prefills from the booking and its broker, so wait until both have loaded.
  useEffect(() => {
    if (!open || actionRequest?.type !== 'booking_form' || !actionRequest?.nonce) return;
    if (actionRequest.unitId && String(actionRequest.unitId) !== String(unit?.id || '')) return;
    if (handledActionNonceRef.current === actionRequest.nonce) return;
    if (bookingId && !booking) return;
    if (((booking as any)?.refByBrokerId || (booking as any)?.refByBrokerName) && !brokers.length) return;
    handledActionNonceRef.current = actionRequest.nonce;
    openBookingFormDraftDialog();
  }, [actionRequest, booking, bookingId, brokers.length, open, openBookingFormDraftDialog, unit?.id]);

  // Expose action triggers to parent page via ref
  useImperativeHandle(drawerRef, () => ({
    triggerAddPayment: () => {
      openAddPayment({
        unitId:     unit?.id     ?? undefined,
        unitNumber: unit?.unitNumber ?? undefined,
        bookingId:  booking?.id  ?? undefined,
        partyName:  primaryCustomerName || undefined,
        receiptCategory: 'customer',
      });
    },
    triggerBookingForm: () => {
      openBookingFormDraftDialog();
    },
    triggerDownloadBookingForm: () => {
      if (headerBookingFormFile?.url) {
        triggerNamedDownload(
          String(headerBookingFormFile.url),
          'BOOKING FORM DRAFT',
          String(headerBookingFormFile.originalName || headerBookingFormFile.name || 'BOOKING_FORM_DRAFT.pdf'),
        );
      }
    },
    triggerEditStatus: () => {
      setUnitStatusDraft(currentUnitStatus);
      setUnitStatusDialogOpen(true);
    },
    triggerAdminMenu: (anchor: HTMLElement) => {
      setHeaderEditAnchorEl(anchor);
    },
  }), [openAddPayment, openBookingFormDraftDialog, triggerNamedDownload, headerBookingFormFile, currentUnitStatus, unit, booking, primaryCustomerName]);

  const bookingFormTermsError = useMemo(() => validatePaymentTerms(bookingFormTerms), [bookingFormTerms, validatePaymentTerms]);
  // Saves the terms without making the PDF, so the buyer details form can go out first.
  const saveBookingTermsOnly = async () => {
    if (bookingFormDraftGenerating || bookingFormDoc?.files?.length) return;
    setBookingFormDraftGenerating(true);
    setBookingFormError('');
    try {
      const conditionError = bookingFormUseLegacyTerms ? bookingFormTermsError : validatePaymentCondition(bookingFormCondition);
      if (conditionError) throw new Error(conditionError);
      const effectiveBookingId = await ensureBookingForDraft();
      await saveBookingFormMetadata(effectiveBookingId);
      setBookingFormDraftDialogOpen(false);
    } catch (err: any) {
      setBookingFormError(err?.message || 'Failed to save the booking terms.');
    } finally {
      setBookingFormDraftGenerating(false);
    }
  };
  const submitBookingFormDraft = async () => {
    if (bookingFormDraftGenerating || bookingFormDoc?.files?.length) return;
    setBookingFormDraftGenerating(true);
    setError(null);
    setBookingFormError('');
    try {
      const conditionError = bookingFormUseLegacyTerms ? bookingFormTermsError : validatePaymentCondition(bookingFormCondition);
      if (conditionError) throw new Error(conditionError);
      if (!/^[1-9]\d{0,2}$/.test(bookingFormTermMonths.trim())) throw new Error('Enter the agreement term as a whole number of months.');
      if (!(Number(bookingFormEarnest) > 0)) throw new Error('Enter the earnest money.');
      if (!bookingFormCommittee) throw new Error('Choose Yes or No for the society committee.');
      const effectiveBookingId = await ensureBookingForDraft();
      await saveBookingFormMetadata(effectiveBookingId);
      await generateBookingFormV1Draft(effectiveBookingId, bookingFormUseLegacyTerms ? null : bookingFormCondition);
      setBookingFormDraftDialogOpen(false);
    } catch (err: any) {
      setBookingFormError(err?.message || 'Failed to create booking form.');
    } finally {
      setBookingFormDraftGenerating(false);
    }
  };
  const unitStatusForHeader = String((unit as any)?.bookingStatus || (unit as any)?.status || '')
    .trim()
    .toLowerCase();
  const isUnitBookedOrSold =
    unitStatusForHeader === 'booked' || unitStatusForHeader === 'sold';
  const canShowDraftDownloadHeaderButton =
    unitStatusForHeader !== 'booked' && unitStatusForHeader !== 'sold';
  const createBookingFormTooltipTitle = useMemo(() => {
    if (!bookingFormCreateHoverDetails) return 'Create Booking Form (booking_form_v1)';
    return (
      <Box sx={{ maxWidth: 360 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>
          Booking Form Details
        </Typography>
        <Typography sx={{ fontSize: 11.5 }}>File: {bookingFormCreateHoverDetails.fileName}</Typography>
        {bookingFormCreateHoverDetails.enteredDateTime ? (
          <Typography sx={{ fontSize: 11.5 }}>
            Date/Time: {bookingFormCreateHoverDetails.enteredDateTime}
          </Typography>
        ) : null}
        {bookingFormCreateHoverDetails.refBy ? (
          <Typography sx={{ fontSize: 11.5 }}>Ref By: {bookingFormCreateHoverDetails.refBy}</Typography>
        ) : null}
        {bookingFormCreateHoverDetails.rank ? (
          <Typography sx={{ fontSize: 11.5 }}>Rank: {bookingFormCreateHoverDetails.rank}</Typography>
        ) : null}
        <Typography sx={{ fontSize: 11.5 }}>Scheme: {bookingFormCreateHoverDetails.scheme}</Typography>
        {bookingFormCreateHoverDetails.schemeRemark ? (
          <Typography sx={{ fontSize: 11.5 }}>Scheme Remark: {bookingFormCreateHoverDetails.schemeRemark}</Typography>
        ) : null}
        {bookingFormCreateHoverDetails.terms ? (
          <Typography sx={{ fontSize: 11.5 }}>
            Terms: {bookingFormCreateHoverDetails.terms}
          </Typography>
        ) : null}
      </Box>
    );
  }, [bookingFormCreateHoverDetails]);
  const canShowReceivePaymentHeaderButton = useMemo(() => {
    const received = Math.max(0, Number(customerReceiptTotal || 0));
    return received > 0 && customerPaymentPendingAmount > 0;
  }, [customerPaymentPendingAmount, customerReceiptTotal]);
  useEffect(() => {
    let cancelled = false;
    const loadAvailableUnitDraftHeaderFile = async () => {
      if (!open || !projectId || !unit?.id) {
        setAvailableUnitDraftHeaderFile(null);
        return;
      }
      if (bookingFormDraftFirstFile?.url) {
        setAvailableUnitDraftHeaderFile(null);
        return;
      }
      try {
        const bookingsSnap = await getDocs(
          query(collection(db, 'projects', projectId, 'units', unit.id, 'bookings'), limit(20))
        );
        let found: any = null;
        for (const bookingDoc of bookingsSnap.docs) {
          const draftSnap = await getDoc(
            doc(db, 'projects', projectId, 'units', unit.id, 'bookings', bookingDoc.id, 'draft', 'booking_form_v1')
          );
          if (!draftSnap.exists()) continue;
          const first = (draftSnap.data() as any)?.files?.[0] || null;
          if (first?.url) {
            found = first;
            break;
          }
        }
        if (!cancelled) setAvailableUnitDraftHeaderFile(found);
      } catch {
        if (!cancelled) setAvailableUnitDraftHeaderFile(null);
      }
    };
    loadAvailableUnitDraftHeaderFile();
    return () => {
      cancelled = true;
    };
  }, [bookingFormDraftFirstFile?.url, db, open, projectId, unit?.id]);
  const _drawerInnerRootSx = pageMode
    ? ({ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', bgcolor: '#f4f2ee', overflow: 'hidden' } as const)
    : isMobile
      ? ({ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#faf9f7', overflow: 'hidden' } as const)
      : ({ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#faf9f7', overflow: 'hidden' } as const);
  const drawerInner = (
    <Box sx={_drawerInnerRootSx}>
      <Box
        sx={{
          display: pageMode ? 'none' : undefined,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: '#ffffff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderTop: isMobile ? 'none' : '3px solid #9d6f32',
          px: pageMode ? 'max(24px, calc(50% - 430px))' : 2,
          py: 1.5,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
              <Typography
                noWrap
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 900,
                  fontSize: 20,
                  color: '#9d6f32',
                  lineHeight: 1.2,
                }}
              >
                {unitTitle}
              </Typography>
              {statusChip}
            </Stack>
            {primaryCustomerHeaderName ? (
              <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 600, color: '#1a1a1a' }} noWrap>
                {primaryCustomerHeaderName}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} alignItems="center" flexWrap="wrap" rowGap={0.75}>
              {bookingId ? null : (
                <Chip size="small" label="No active booking" variant="outlined" sx={{ px: 0.3, height: 20, fontWeight: 900 }} />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {!pageMode && onOpenUnitPage ? (
              <Tooltip title="Open full unit page">
                <IconButton onClick={onOpenUnitPage} aria-label="Open full unit page"><OpenInNewOutlinedIcon /></IconButton>
              </Tooltip>
            ) : null}
            {isAdmin || repeatableWorkflowActions.length > 0 ? (
              <Tooltip title="Edit options">
                <IconButton
                  aria-label="Edit options"
                  color={adminDocumentActionMode || unitStatusDialogOpen ? 'primary' : 'default'}
                  onClick={(e) => setHeaderEditAnchorEl(e.currentTarget)}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            {canShowReceivePaymentHeaderButton ? (
              <Tooltip title="Receive Payment">
                <IconButton
                  aria-label="Receive payment"
                  color="primary"
                  onClick={() => {
                    openUnifiedPayment('dp');
                  }}
                >
                  <CurrencyRupeeOutlinedIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title={createBookingFormTooltipTitle}>
              <span>
                <IconButton
                  aria-label="Create booking form"
                  onClick={openBookingFormDraftDialog}
                  disabled={bookingFormDraftGenerating}
                >
                  <DescriptionOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            {canShowDraftDownloadHeaderButton && headerBookingFormFile ? (
              <Tooltip title="Download Booking Form">
                <span>
                  <IconButton
                    aria-label="Download booking form"
                    disabled={!headerBookingFormFile?.url}
                    onClick={() =>
                      triggerNamedDownload(
                        String(headerBookingFormFile?.url || ''),
                        'BOOKING FORM DRAFT',
                        String(
                          headerBookingFormFile?.originalName ||
                            headerBookingFormFile?.name ||
                            'BOOKING_FORM_DRAFT.pdf'
                        )
                      )
                    }
                  >
                    <DownloadOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {!pageMode ? <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton> : null}
          </Stack>
        </Stack>
      </Box>

      <Menu
        anchorEl={headerEditAnchorEl}
        open={Boolean(headerEditAnchorEl)}
        onClose={() => setHeaderEditAnchorEl(null)}
      >
        {repeatableWorkflowActions.length > 0 ? (
          [
            <ListSubheader key="repeatable-header" disableSticky>
              Repeatable Actions
            </ListSubheader>,
            ...repeatableWorkflowActions.map(({ which, step }) => (
              <MenuItem
                key={`repeatable_${which}_${step.key}`}
                onClick={() => {
                  setHeaderEditAnchorEl(null);
                  runRepeatableWorkflowAction(which, step).catch((err: any) =>
                    setError(err?.message || 'Failed to open workflow action.')
                  );
                }}
              >
                {getRepeatableWorkflowActionLabel(which, step)}
              </MenuItem>
            )),
            <Divider key="repeatable-divider" />,
          ]
        ) : null}
        {isAdmin && onRequestUnitStatusChange ? (
          <MenuItem
            onClick={() => {
              setHeaderEditAnchorEl(null);
              setUnitStatusDraft(currentUnitStatus);
              setUnitStatusDialogOpen(true);
            }}
          >
            Change Unit Status
          </MenuItem>
        ) : null}
        {isAdmin && bookingId && loanCase ? (
          <MenuItem
            onClick={() => {
              setHeaderEditAnchorEl(null);
              openSwitchLoanBankDialog();
            }}
          >
            Switch Loan Bank
          </MenuItem>
        ) : null}
        {isAdmin ? (
          <MenuItem
            onClick={() => {
              setHeaderEditAnchorEl(null);
              setAdminDocumentActionMode((prev) => !prev);
            }}
          >
            {adminDocumentActionMode ? 'Hide Document Edit Actions' : 'Show Document Edit Actions'}
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog
        open={unitStatusDialogOpen}
        onClose={() => {
          if (unitStatusSaving) return;
          setUnitStatusDialogOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Change Unit Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={unitStatusDraft}
              onChange={(e) => setUnitStatusDraft(String(e.target.value || 'available') as UnitBookingStatus)}
            >
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
              <MenuItem value="booked">Booked</MenuItem>
              <MenuItem value="sold">Sold</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitStatusDialogOpen(false)} disabled={unitStatusSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveUnitStatusFromDrawer} disabled={unitStatusSaving}>
            {unitStatusSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={switchLoanBankDialogOpen}
        onClose={() => {
          if (switchLoanBankSaving) return;
          setSwitchLoanBankDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Switch Loan Bank</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {switchLoanBankError ? <Alert severity="error">{switchLoanBankError}</Alert> : null}
            <Alert severity="warning">
              This archives the current loan attempt and starts a fresh bank workflow for the same booking. Loan-specific
              documents will be archived from the active view.
            </Alert>
            <TextField
              label="Current Bank"
              value={String((latestLoanSanctionData as any)?.sanctionBankName || '').trim() || '—'}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <Autocomplete
              freeSolo
              options={loanSanctionHistory.bankNames}
              value={switchLoanBankName}
              onChange={(_, value) => setSwitchLoanBankName(String(value || ''))}
              onInputChange={(_, value) => setSwitchLoanBankName(String(value || ''))}
              renderInput={(params) => <TextField {...params} label="New Bank" fullWidth />}
            />
            <TextField
              label="Reason for switch"
              value={switchLoanBankReason}
              onChange={(e) => setSwitchLoanBankReason(String(e.target.value || ''))}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwitchLoanBankDialogOpen(false)} disabled={switchLoanBankSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={switchLoanBankSaving}
            onClick={async () => {
              try {
                await switchLoanBankAttempt();
              } catch {
                // handled in dialog
              }
            }}
          >
            {switchLoanBankSaving ? 'Switching…' : 'Switch Bank'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bookingFormDraftDialogOpen}
        onClose={() => {
          if (bookingFormDraftGenerating) return;
          setBookingFormDraftDialogOpen(false);
        }}
        fullScreen={isMobile}
        maxWidth={false}
        PaperProps={{
          sx: isMobile ? { borderRadius: 0 } : { width: 'min(1060px, calc(100% - 32px))', maxHeight: 'calc(100% - 48px)', borderRadius: '22px', overflow: 'hidden' },
          onKeyDown: (e: React.KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              submitBookingFormDraft();
            }
          },
        }}
        aria-labelledby="bf-title"
      >
        <div className="bf" style={{ height: isMobile ? '100%' : undefined }}>
          <div className="bf-head">
            <div>
              <h2 id="bf-title">Create booking form</h2>
              <p>{[String(unit?.unitNumber || unit?.id || ''), primaryCustomerName].filter(Boolean).join(' · ')}</p>
            </div>
            <button type="button" className="bf-x" aria-label="Close" disabled={bookingFormDraftGenerating} onClick={() => setBookingFormDraftDialogOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="bf-body">
            <div className="bf-left">
              {bookingFormDoc?.files?.length ? (
                <div className="bf-note warn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
                  <span>The signed booking form is uploaded, so its payment plan is locked and cannot be changed here.</span>
                </div>
              ) : null}
              {(() => {
                // Buyers without checked Gujarati print as empty blocks; never unchecked or out-of-date Gujarati.
                const notReady = customers.filter((c) => gujaratiStatus(c) !== 'ok');
                if (customers.length && !notReady.length) return null;
                return (
                  <div className="bf-note warn" role="status">
                    <span>
                      {customers.length
                        ? `No checked Gujarati for ${notReady.map((c) => `${String(c.displayName || c.name)}${gujaratiStatus(c) === 'stale' ? ' (details changed after approval)' : ''}`).join(', ')}. Their block prints empty, to fill by hand.`
                        : 'No buyers yet. The form prints an empty buyer block to fill by hand.'}
                      {bookingId && projectId && unit?.id ? <> <button type="button" className="bf-link" onClick={() => window.location.assign(`/buyer-form/office/${projectId}/${unit.id}/${bookingId}`)}>Fill with buyer here</button></> : null}
                    </span>
                  </div>
                );
              })()}
              <section className="bf-sec" aria-label="Booking">
                <div className="bf-sec-h"><b>Booking</b><span>When and through whom</span></div>
                <div className="bf-grid3">
                  <div className="bf-f"><label htmlFor="bf-unit">Unit</label><input id="bf-unit" className="bf-inp" value={String(unit?.unitNumber || unit?.id || '')} readOnly /></div>
                  <div className="bf-f"><label htmlFor="bf-date">Form date</label><input id="bf-date" className="bf-inp" type="date" value={bookingFormDraftDate} onChange={(e) => setBookingFormDraftDate(e.target.value)} /></div>
                  <div className="bf-f"><label htmlFor="bf-time">Time</label><input id="bf-time" className="bf-inp" type="time" value={bookingFormDraftTime} onChange={(e) => setBookingFormDraftTime(e.target.value)} /></div>
                </div>
                <div className="bf-grid2" style={{ gridTemplateColumns: isMobile ? undefined : 'minmax(0, 2fr) minmax(0, 1fr)' }}>
                  <div className="bf-f">
                    <label htmlFor="bf-ref">Ref by</label>
                    <Autocomplete
                      id="bf-ref"
                      className="bf-ac"
                      size="small"
                      options={brokers}
                      value={bookingFormRefByBroker}
                      onChange={(_, value) => setBookingFormRefByBroker(value)}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      getOptionLabel={(option) => {
                        const brokerName = String(option?.brokerName || option?.displayName || '').trim();
                        const agencyName = String(option?.agencyName || '').trim();
                        return agencyName ? `${brokerName} (${agencyName})` : brokerName;
                      }}
                      componentsProps={{ popper: { style: { zIndex: 1400 } } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Choose a broker, or leave empty"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                <button type="button" className="bf-addbroker" onClick={() => setBrokerCreateDrawerOpen(true)}>+ Broker</button>
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="bf-f">
                    <label htmlFor="bf-rank">Rank</label>
                    <input id="bf-rank" className="bf-inp num" type="number" step="0.1" value={bookingFormRank} onChange={(e) => setBookingFormRank(e.target.value)} />
                  </div>
                </div>
              </section>
              {bookingFormUseLegacyTerms ? (
                <section className="bf-sec" aria-label="Payment plan">
                  <div className="bf-sec-h"><b>Payment plan</b><span>Older terms</span></div>
                  <div className="bf-note info">This booking has older payment terms. They stay as they are unless you convert them.</div>
                  <div className="bf-f">
                    <label htmlFor="bf-legacy">Payment terms</label>
                    <input id="bf-legacy" className={`bf-inp num${bookingFormTermsError ? ' bad' : ''}`} value={bookingFormTerms} onChange={(e) => setBookingFormTerms(e.target.value)} />
                    <span className={`bf-hint${bookingFormTermsError ? ' bad' : ''}`}>{bookingFormTermsError || 'Format: 30-90|30-150|40-PO'}</span>
                  </div>
                  <div><button type="button" className="bf-btn sm" onClick={() => setBookingFormUseLegacyTerms(false)}>Convert to Basic or Advanced plan</button></div>
                </section>
              ) : (
                <PaymentConditionEditor value={bookingFormCondition} onChange={setBookingFormCondition} bookingDate={bookingFormDraftDate} unitAmount={Number(unit?.unitAmount || 0) || 0} />
              )}
              <section className="bf-sec" aria-label="Agreement details">
                <div className="bf-sec-h"><b>Agreement details</b><span>Print on the form</span></div>
                <div className="bf-grid2">
                  <div className="bf-f">
                    <label htmlFor="bf-term">Agreement term (months)</label>
                    <input id="bf-term" className="bf-inp num" inputMode="numeric" value={bookingFormTermMonths} onChange={(e) => setBookingFormTermMonths(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                  </div>
                  <div className="bf-f">
                    <label htmlFor="bf-earnest">Earnest money</label>
                    <div className="bf-pre"><span>₹</span><input id="bf-earnest" className="bf-inp num" inputMode="numeric" value={bookingFormEarnest} onChange={(e) => { setBookingFormEarnestTouched(true); setBookingFormEarnest(e.target.value.replace(/\D/g, '')); }} /></div>
                    {!bookingFormEarnestTouched && bookingFormEarnest ? <span className="bf-hint">From the down payment. Change it if needed.</span> : null}
                  </div>
                </div>
                <div className="bf-toggle">
                  <span>Interested in the society committee</span>
                  <div className="bf-seg" role="group" aria-label="Society committee">
                    <button type="button" aria-pressed={bookingFormCommittee === 'yes'} onClick={() => setBookingFormCommittee('yes')}>Yes</button>
                    <button type="button" aria-pressed={bookingFormCommittee === 'no'} onClick={() => setBookingFormCommittee('no')}>No</button>
                  </div>
                </div>
                <div className="bf-f">
                  <span className="bf-lbl">Documents submitted</span>
                  {([['photos', '2 passport photos of each buyer'], ['pan', 'PAN copy (3 copies)'], ['aadhaar', 'Aadhaar copy (3 copies)']] as const).map(([key, label]) => (
                    <label key={key} className="bf-check"><input type="checkbox" checked={bookingFormDocs[key]} onChange={(e) => setBookingFormDocs((prev) => ({ ...prev, [key]: e.target.checked }))} />{label}</label>
                  ))}
                  <span className="bf-hint">Ticked from the KYC uploads. Change them to match what was handed in.</span>
                </div>
              </section>
              <section className="bf-sec" aria-label="Scheme">
                <div className="bf-toggle">
                  <b style={{ fontSize: 13, fontWeight: 600 }}>Booked under a scheme</b>
                  <div className="bf-seg" role="group" aria-label="Scheme">
                    <button type="button" aria-pressed={bookingFormScheme === 'yes'} onClick={() => setBookingFormScheme('yes')}>Yes</button>
                    <button type="button" aria-pressed={bookingFormScheme === 'no'} onClick={() => setBookingFormScheme('no')}>No</button>
                  </div>
                </div>
                {bookingFormScheme === 'yes' ? (
                  <div className="bf-f">
                    <label htmlFor="bf-scheme">Scheme remark</label>
                    <textarea id="bf-scheme" className="bf-inp" rows={2} value={bookingFormSchemeRemark} onChange={(e) => setBookingFormSchemeRemark(e.target.value)} />
                  </div>
                ) : null}
              </section>
            </div>
            <aside className="bf-right" aria-label="Live payment plan">
              <BookingPlanPanel
                condition={bookingFormCondition}
                bookingDate={bookingFormDraftDate}
                unitAmount={Number(unit?.unitAmount || 0) || 0}
                legacyTerms={bookingFormUseLegacyTerms ? bookingFormTerms : null}
                locked={Boolean(bookingFormDoc?.files?.length)}
              />
            </aside>
          </div>
          {bookingFormError ? <div className="bf-note bad" role="alert" style={{ margin: '0 22px 10px' }}>{bookingFormError}</div> : null}
          <div className="bf-foot">
            {isAdmin ? (
              <Tooltip title={bookingId ? 'Reset booking' : 'Force-clear stale booking data'}>
                <span>
                  <button type="button" className="bf-ibtn" onClick={handleFullBookingWipe} disabled={bookingFormDraftGenerating} aria-label={bookingId ? 'Reset booking' : 'Force-clear stale booking data'}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
                  </button>
                </span>
              </Tooltip>
            ) : null}
            <span className="grow"><span className="bf-kbd"><kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>Enter</kbd> to create</span></span>
            <button type="button" className="bf-btn ghost" onClick={() => setBookingFormDraftDialogOpen(false)} disabled={bookingFormDraftGenerating}>Cancel</button>
            <button type="button" className="bf-btn" onClick={saveBookingTermsOnly} disabled={bookingFormDraftGenerating || Boolean(bookingFormDoc?.files?.length)} title="Save the payment terms without making the PDF">Save terms</button>
            <button type="button" className="bf-btn primary" disabled={bookingFormDraftGenerating || Boolean(bookingFormDoc?.files?.length)} onClick={submitBookingFormDraft}>
              {bookingFormDraftGenerating ? 'Creating…' : 'Create booking form'}
            </button>
          </div>
        </div>
      </Dialog>

      <BrokerDrawer
        open={brokerCreateDrawerOpen}
        onClose={() => setBrokerCreateDrawerOpen(false)}
        editing={null}
        elevateOverModal
        onSaved={(saved) => {
          setBrokerCreateDrawerOpen(false);
          if (!saved?.id) return;
          const created: BrokerOption = {
            id: String(saved.id),
            brokerName: String(saved.brokerName || '').trim(),
            agencyName: String(saved.agencyName || '').trim(),
            displayName: String(saved.brokerName || '').trim(),
          };
          setBookingFormRefByBroker(created);
        }}
      />
      <input
        ref={quickBookingFormUploadInputRef}
        type="file"
        hidden
        accept="image/*,application/pdf"
        onChange={async (e) => {
          const file = (e.target.files || [])[0] || null;
          if (!file) return;
          const input = e.target as HTMLInputElement;
          if (input) input.value = '';
          await uploadStepRequiredDoc(BOOKING_FORM_SIGNED_DOC_KEY, 'Signed booking form', file);
        }}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', overscrollBehavior: 'contain',
        px: pageMode ? { xs: 1.25, sm: 2 } : 2,
        pt: pageMode ? { xs: 2, sm: 1 } : 2,
        pb: pageMode ? '32px' : 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
        bgcolor: pageMode ? '#f4f2ee' : undefined,
      }}>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading booking…</Typography>
          </Stack>
        )}

        {!bookingId ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={sectionSx}>
              {(() => {
                const unitAmount = typeof unit?.unitAmount === 'number' ? unit.unitAmount : 0;
                const ceilToHundred = (value: number) => Math.ceil(value / 100) * 100;
                const gstAmount = computeGstAmount(unitAmount);
                const stampDutyAmount = unitAmount > 0 ? ceilToHundred(ceilToHundred(unitAmount) * 0.049) : 0;
                const registrationChargesAmount = unitAmount > 0 ? ceilToHundred(ceilToHundred(unitAmount) * 0.01) + 1200 : 0;
                const bookingStatusLabel = String((unit as any)?.bookingStatus || 'available');
                return (
                  <Stack spacing={0}>
                    {amountBreakupRow(unitAmount)}
                    {gstBreakupRow(gstAmount)}
                    {chargeBreakupRow('Stamp Duty', stampDutyAmount, stampDutyReceiptTotal)}
                    {chargeBreakupRow('Registration Charges', registrationChargesAmount, registrationReceiptTotal)}
                    {renderSaleAgreementDateRow()}
                    {renderSaleDeedDateRow()}
                    {detailRow(<AssignmentTurnedInOutlinedIcon fontSize="small" />, 'Status', bookingStatusLabel)}
                  </Stack>
                );
              })()}
            </Box>
            <Alert severity="info">
              No active booking yet for this unit.
            </Alert>
          </Box>
        ) : (
          <>
            <Box
              id="drawer-section-overview"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: pageMode && isMobile ? 0 : 2,
                ...(pageMode && isMobile ? {
                  '& > #drawer-section-collection': { mb: 2 },
                  '& > div:not(.page-section-group):not(#drawer-section-collection)': { mb: 2 },
                  '& > .page-section-group > div:not([id])': { mb: 2 },
                } : {}),
              }}
            >
	            {pageMode && (pageModeActiveSection === 'payment-roadmap' || pageModeActiveSection === 'overview') && <>
                  <Box id="drawer-section-collection" sx={{ ...sectionSx, order: 0 }}>
                    {collectionSnapshot(unit?.unitAmount)}
                  </Box>
                  <Box sx={{ ...sectionSx, order: 0 }}>
	                <PaymentRoadmap
	                  condition={booking?.paymentCondition}
	                  bookingDate={String(booking?.bookingFormDate || '')}
	                  legacyTerms={booking?.paymentTerms}
	                />
                  </Box>
                </>}
	            <Box className="page-section-group" sx={pageMode ? { display: 'contents' } : { order: 2 }}>
              <Box
                sx={{
                  ...sectionSx,
                  ...(pageMode ? { order: 2 } : {}),
                  ...((pageMode || !!focusSection) && (
                    !isPageSectionVisible('customers') ||
                    (pageModeActiveSection === 'overview' && customers.length === 0)
                  ) ? { display: 'none' } : {}),
                }}
                data-section="customers"
              >
              {!pageMode && (<>
              {(() => {
                const unitAmount =
                  typeof unit?.unitAmount === 'number' ? unit.unitAmount : 0;
                const ceilToHundred = (value: number) =>
                  Math.ceil(value / 100) * 100;
                const gstAmount = computeGstAmount(unitAmount);
                const stampDutyAmount =
                  unitAmount > 0
                    ? ceilToHundred(ceilToHundred(unitAmount) * 0.049)
                    : 0;
                const hasMaleCustomer = customers.some(
                  (customer) =>
                    String(customer?.gender || '')
                      .trim()
                      .toLowerCase() === 'male'
                );
                const registrationChargesAmount =
                  unitAmount <= 0
                    ? 0
                    : customers.length === 0 || hasMaleCustomer
                    ? ceilToHundred(ceilToHundred(unitAmount) * 0.01) + 1200
                    : 1200;

                return (
                  <Stack spacing={0} sx={{ mb: 1 }}>
                    {amountBreakupRow(unit?.unitAmount)}
                    {gstBreakupRow(gstAmount)}
                    {chargeBreakupRow('Stamp Duty', stampDutyAmount, stampDutyReceiptTotal)}
                    {chargeBreakupRow('Registration Charges', registrationChargesAmount, registrationReceiptTotal)}
                    {renderSaleAgreementDateRow()}
                    {renderSaleDeedDateRow()}
                    {collectionSnapshot(unit?.unitAmount)}
                  </Stack>
                );
              })()}

              {(() => {
                const washArea =
                  typeof (unit as any)?.exclusiveWashArea === 'number'
                    ? Number((unit as any).exclusiveWashArea)
                    : typeof (unit as any)?.areaOfExclusiveWash === 'number'
                      ? Number((unit as any).areaOfExclusiveWash)
                      : typeof (unit as any)?.area_of_exclusive_wash === 'number'
                        ? Number((unit as any).area_of_exclusive_wash)
                        : null;
                const totalSqm =
                  (typeof unit?.carpetArea === 'number' ? unit.carpetArea : 0) +
                  (typeof unit?.exclusiveBalconyArea === 'number' ? unit.exclusiveBalconyArea : 0) +
                  (typeof washArea === 'number' ? washArea : 0) +
                  (typeof unit?.openTerraceArea === 'number' ? unit.openTerraceArea : 0);
                const hasAny =
                  typeof unit?.carpetArea === 'number' ||
                  typeof unit?.exclusiveBalconyArea === 'number' ||
                  typeof washArea === 'number' ||
                  typeof unit?.openTerraceArea === 'number';
                return (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
                      Areas
                    </Typography>
	                    {hasAny ? (
	                      <Typography
	                        variant="caption"
	                        color="text.secondary"
	                        sx={{ fontWeight: 800, whiteSpace: 'nowrap', fontSize: '0.75rem' }}
	                      >
	                        {(() => {
	                          const totalSqftRaw = sqmToSqft(totalSqm);
	                          const totalSqft = Math.ceil(totalSqftRaw);
	                          const sbPercent = Math.max(0, Number(currentSbPercentForUnit || 0) || 0);
	                          if (!(sbPercent > 0 && sbPercent < 100)) {
	                            return `Total: ${totalSqft} sq ft`;
	                          }
	                          const sbDerivedSqft = Math.ceil(totalSqftRaw / (1 - sbPercent / 100));
	                          return `Total: ${totalSqft} sq ft (${sbDerivedSqft} sq ft)`;
	                        })()}
	                      </Typography>
	                    ) : null}
                  </Stack>
                );
              })()}
              <Stack spacing={1} sx={{ mb: 1 }}>
                {areaRow({
                  icon: <SquareFootOutlinedIcon fontSize="small" />,
                  label: 'Carpet',
                  sqm: typeof unit?.carpetArea === 'number' ? unit.carpetArea : null,
                })}
                {areaRow({
                  icon: <BalconyOutlinedIcon fontSize="small" />,
                  label: 'Excl. Balcony',
                  sqm: typeof unit?.exclusiveBalconyArea === 'number' ? unit.exclusiveBalconyArea : null,
                })}
                {areaRow({
                  icon: <BalconyOutlinedIcon fontSize="small" />,
                  label: 'Excl. Wash',
                  sqm:
                    typeof (unit as any)?.exclusiveWashArea === 'number'
                      ? Number((unit as any).exclusiveWashArea)
                      : typeof (unit as any)?.areaOfExclusiveWash === 'number'
                        ? Number((unit as any).areaOfExclusiveWash)
                        : typeof (unit as any)?.area_of_exclusive_wash === 'number'
                          ? Number((unit as any).area_of_exclusive_wash)
                          : null,
                })}
                {typeof unit?.openTerraceArea === 'number' ? (
                  areaRow({
                    icon: <BalconyOutlinedIcon fontSize="small" />,
                    label: 'Open Terrace',
                    sqm: unit.openTerraceArea,
                  })
                ) : null}
              </Stack>
              </>)}

              {isUnitBookedOrSold ? (
                <>
                  {customers.length > 0 ? (
                    <>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
                          Customers
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="Add / edit customer">
                            <IconButton
                              size="small"
                              onClick={() => {
                                resetCustomerForm();
                                setEditingCustomerId(null);
                                setCustomerDrawerOpen(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="KYC documents">
                            <IconButton size="small" onClick={() => setKycDialogOpen(true)}>
                              <DescriptionOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
	                <Stack spacing={1}>
	                  {customers.map((c) => {
                    const roleLabel = String(c.role || '').toLowerCase() === 'primary' ? 'Primary' : 'Secondary';
                    const mobile = String(c.mobile || '');
                    const digits = normalizePhoneDigits(mobile);
                    return (
	                      <Box
	                        key={c.id}
	                        sx={{
	                          display: 'flex',
	                          alignItems: 'center',
	                          justifyContent: 'space-between',
	                          gap: 1,
	                          px: 1,
	                          py: 0.875,
	                          borderBottom: '1px solid #f0f0f0',
	                        }}
	                      >
	                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
	                          <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
	                            <PersonOutlineOutlinedIcon fontSize="small" />
	                          </Box>
	                          <Box sx={{ minWidth: 0 }}>
	                            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
	                              {c.displayName || c.name || 'Customer'}
	                            </Typography>
	                            <Stack
	                              direction="row"
	                              spacing={1}
	                              alignItems="center"
	                              sx={{ mt: 0.25 }}
	                              flexWrap="wrap"
	                              rowGap={0.5}
	                            >
	                              <Typography variant="body2" color="text.secondary">
	                                {formatPhoneDisplay(mobile) || '—'}
	                              </Typography>
	                              {customers.length > 1 ? <Chip size="small" label={roleLabel} variant="outlined" /> : null}
	                            </Stack>
	                          </Box>
	                        </Stack>
	                        <Stack direction="row" spacing={0.5} alignItems="center">
	                          <Tooltip title="Call">
	                            <span>
	                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (!digits) return;
                                  window.open(`tel:${digits}`, '_self');
                                }}
                                disabled={!digits}
                              >
                                <PhoneOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="WhatsApp">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (!digits) return;
                                  window.open(`https://wa.me/${digits}`, '_blank', 'noopener,noreferrer');
                                }}
                                disabled={!digits}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Copy">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(mobile)}
                                disabled={!mobile}
                              >
                                <ContentCopyOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
	                      </Box>
	                    );
	                  })}
	                </Stack>
                    </>
                  ) : null}

              </>
            ) : null}
            </Box>
            {((relatedChangeOrders.length > 0 && (pageMode || isPageSectionVisible('change-orders'))) || (pageMode && pageModeActiveSection === 'change-orders')) ? (
              <Box
                sx={{
                  ...sectionSx,
                  p: { xs: 1.5, sm: 2 },
                  order: 2.75,
                  ...(pageModeActiveSection !== 'overview' && pageModeActiveSection !== 'change-orders'
                    ? { display: 'none' }
                    : {}),
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25, gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
                      Related Change Orders
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: { xs: 11.5, sm: 12 } }}>
                      Change requests for this unit
                    </Typography>
                  </Box>
                  {relatedChangeOrders.length > 0 ? (
                    <Chip
                      size="small"
                      label={`${relatedChangeOrders.length} ${relatedChangeOrders.length === 1 ? 'item' : 'items'}`}
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 800 }}
                    />
                  ) : null}
                </Stack>
                {relatedChangeOrders.length === 0 ? (
                  detailRow(<InfoOutlinedIcon fontSize="small" />, 'Change Orders', '—')
                ) : (
                  (() => {
                    const titleGroups = relatedChangeOrders.reduce((map, row) => {
                      const key = String(row.title || 'Change Order').trim() || 'Change Order';
                      const rows = map.get(key) || [];
                      rows.push(row);
                      map.set(key, rows);
                      return map;
                    }, new Map<string, RelatedChangeOrderRow[]>());
                    const titles = Array.from(titleGroups.keys()).sort((left, right) => left.localeCompare(right));
                    const renderRelatedChangeOrderCard = (row: RelatedChangeOrderRow) => {
                      const updatedMs = tsToMs(row.updatedAt) || tsToMs(row.createdAt) || null;
                      const updatedLabel = updatedMs ? formatDDMMYYYY(updatedMs) : '—';
                      const openStatus = isOpenChangeOrderStatus(row.status);
                      const rejectedStatus = row.status === 'rejected';
                      const cardColor = rejectedStatus ? '#d32f2f' : openStatus ? '#9d6f32' : '#2e7d32';
                      const cardBorder = rejectedStatus
                        ? 'rgba(211,47,47,0.20)'
                        : openStatus
                          ? 'rgba(157,111,50,0.24)'
                          : 'rgba(46,125,50,0.20)';
                      const approvalLabel = relatedChangeOrderApprovalLabel(row.customerApproval);
                      const requestText =
                        String(row.description || '').trim() ||
                        String(row.title || '').trim() ||
                        String(row.agency || '').trim() ||
                        'Change request';
                      const metaText = [
                        String(row.agency || row.category || '').trim(),
                        `Updated ${updatedLabel}`,
                      ].filter(Boolean).join(' • ');

                      return (
                        <Box
                          key={`related_co_${row.id}`}
                          sx={{
                            border: `1px solid ${cardBorder}`,
                            borderLeft: `3px solid ${cardColor}`,
                            borderRadius: 1.5,
                            p: { xs: 1, sm: 1.1 },
                            bgcolor: 'transparent',
                          }}
                        >
                          <Stack spacing={0.55} sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: 13, sm: 13.5 },
                                lineHeight: 1.3,
                                fontWeight: 800,
                                color: '#1f1f1f',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {requestText}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10.5, sm: 11.5 } }} noWrap>
                              {metaText}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              flexWrap="nowrap"
                              sx={{ minWidth: 0, overflowX: 'auto', pt: 0.15, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
                            >
                              <Chip
                                size="small"
                                label={relatedChangeOrderStatusLabel(row.status)}
                                color={rejectedStatus ? 'error' : openStatus ? 'warning' : 'success'}
                                variant="outlined"
                                sx={{ height: 19, fontSize: '0.62rem', fontWeight: 800, flexShrink: 0 }}
                              />
                              {row.costImpact ? (
                                <Chip
                                  size="small"
                                  label="Cost"
                                  variant="outlined"
                                  sx={{ height: 19, fontSize: '0.62rem', fontWeight: 800, flexShrink: 0, bgcolor: '#fff' }}
                                />
                              ) : null}
                              <Chip
                                size="small"
                                label={approvalLabel}
                                variant="outlined"
                                sx={{ height: 19, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#fff', flexShrink: 0 }}
                              />
                            </Stack>
                          </Stack>
                        </Box>
                      );
                    };

                    if (isMobile) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            overflowX: 'auto',
                            pb: 0.5,
                            scrollSnapType: 'x mandatory',
                            WebkitOverflowScrolling: 'touch',
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                          }}
                        >
                          {titles.map((title) => {
                            const rows = titleGroups.get(title) || [];
                            return (
                              <Box
                                key={`related_group_${title}`}
                                sx={{
                                  flex: '0 0 calc(100% - 32px)',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  border: '1px solid #e7e1d8',
                                  borderRadius: 1.75,
                                  bgcolor: 'transparent',
                                  scrollSnapAlign: 'start',
                                  alignSelf: 'flex-start',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={1}
                                  sx={{ px: 1.1, py: 0.75, borderBottom: '1px solid #ece7df' }}
                                >
                                  <Typography sx={{ minWidth: 0, fontSize: 12, lineHeight: 1.25, fontWeight: 800, color: '#5f4427' }}>
                                    {title}
                                  </Typography>
                                  <Typography variant="caption" sx={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, color: '#7a6a59' }}>
                                    {rows.length} {rows.length === 1 ? 'item' : 'items'}
                                  </Typography>
                                </Stack>
                                <Stack spacing={0.75} sx={{ p: 0.85 }}>
                                  {rows.map(renderRelatedChangeOrderCard)}
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    }
                    return (
                      <Box
                        sx={{
                          overflowX: 'auto',
                          border: '1px solid #ece7df',
                          borderRadius: 2,
                          bgcolor: '#fff',
                        }}
                      >
                        <Table
                          size="small"
                          sx={{
                            minWidth: Math.max(480, titles.length * 240),
                            tableLayout: 'fixed',
                            '& th, & td': { borderColor: '#eee8de' },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              {titles.map((title) => (
                                <TableCell key={title} sx={{ width: 240, bgcolor: '#f7f4ef' }}>
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Typography variant="body2" sx={{ fontWeight: 900, color: '#5f4427' }} noWrap>
                                      {title}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={titleGroups.get(title)?.length || 0}
                                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 900, bgcolor: '#fff', color: '#5f4427' }}
                                    />
                                  </Stack>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              {titles.map((title) => {
                                const rows = titleGroups.get(title) || [];
                                return (
                                  <TableCell key={`related_matrix_${title}`} sx={{ verticalAlign: 'top', p: 1, bgcolor: '#fff' }}>
                                    <Stack spacing={0.75}>
                                      {rows.map(renderRelatedChangeOrderCard)}
                                    </Stack>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    );
                  })()
                )}
              </Box>
            ) : null}
            <Box id="drawer-section-documents" sx={{ height: 0, ...(pageMode ? { display: 'none' } : {}) }} />

            {isUnitBookedOrSold && (visibleMainDocumentDefs.length > 0 ||
              ((bookingDocsByKey.paymentDetailsDraft?.files || []).some((f) => !!f?.url) &&
                getStoredDocDisplaySection(bookingDocsByKey.paymentDetailsDraft) === 'main_documents')) ? (
              <Box sx={{ ...sectionSx, mt: pageMode ? 0 : 1.25, ...(pageMode ? { order: 3 } : {}), ...((pageMode || !!focusSection) && !isPageSectionVisible('documents') ? { display: 'none' } : {}) }} data-section="documents">
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase', mb: 0.75 }}>
                  Documents
                </Typography>
	                    <Stack spacing={1}>
	                      {visibleMainDocumentDefs.map((d) => {
	                    const row = uploadedDocsByKey[d.key] || null;
	                    const docKeyForUpload = String(
	                      d.key || (row as any)?.type || (row as any)?.id || ''
	                    ).trim();
	                    const files = (row?.files || []).filter((f) => !!f?.url);
	                    const first = files[0] || null;
	                    const atMs =
	                      first?.uploadedAtMs ||
	                      tsToMs((row as any)?.updatedAt) ||
	                      tsToMs((row as any)?.createdAt) ||
	                      null;
	                    const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : null;
	                    return (
	                      <Box
	                        key={`doc_${d.key}`}
	                        sx={{
	                          display: 'flex',
	                          alignItems: 'center',
	                          justifyContent: 'space-between',
	                          gap: 1,
	                          px: 1,
	                          py: 0.875,
	                          borderBottom: '1px solid #f0f0f0',
	                        }}
	                      >
	                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
	                          <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
	                            <DescriptionOutlinedIcon fontSize="small" />
	                          </Box>
	                          <Box sx={{ minWidth: 0 }}>
	                            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
	                              {String((row as any)?.displayName || (uploadTypeByKey.get(String(d.key || '').trim()) as any)?.label || d.label || d.key)}
	                            </Typography>
	                            <Typography variant="body2" color="text.secondary" noWrap>
	                              {when || '—'}
	                            </Typography>
	                          </Box>
	                        </Stack>
	                        <Stack direction="row" spacing={0.5} alignItems="center">
	                          {docKeyForUpload === BOOKING_FORM_SIGNED_DOC_KEY && (
	                            <>
	                              <Tooltip title={signedBookingFormPdf ? 'Send signed booking form PDF to the primary unit holder' : 'Upload the signed booking form as a PDF to send it'}>
	                                <span>
	                                  <IconButton size="small"
	                                    disabled={signedBookingWhatsAppSending || templateStatuses.loading || !signedBookingFormPdf || !signedBookingPrimaryMobile}
	                                    onClick={(event) => { setSignedBookingWhatsAppError(null); setSignedBookingWhatsAppAnchor(event.currentTarget); }}
	                                    aria-label="Send signed booking form on WhatsApp"
	                                    sx={{ color: '#128c7e', border: '1px solid rgba(18,140,126,0.22)', borderRadius: 1.5, p: 0.55 }}>
	                                    {signedBookingWhatsAppSending ? <CircularProgress size={18} /> : <WhatsAppIcon fontSize="small" />}
	                                  </IconButton>
	                                </span>
	                              </Tooltip>
	                              <Menu anchorEl={signedBookingWhatsAppAnchor} open={Boolean(signedBookingWhatsAppAnchor)} onClose={() => setSignedBookingWhatsAppAnchor(null)}>
	                                {([['en', 'English'], ['gu', 'ગુજરાતી'], ['hi', 'हिंदी']] as Array<[AutoReplyLang, string]>).map(([lang, label]) => {
	                                  const approved = !!templateStatuses.versionsFor('swarna_signed_booking_form', lang).sending;
	                                  return <MenuItem key={lang} disabled={!approved || signedBookingWhatsAppSending}
	                                    onClick={() => void sendSignedBookingFormWhatsApp(lang)}>
	                                    {label}{!approved ? ' · Awaiting Meta approval' : ''}
	                                  </MenuItem>;
	                                })}
	                              </Menu>
	                            </>
	                          )}
	                          {isAdmin && adminDocumentActionMode ? (
	                            <Tooltip title="Re-upload">
	                              <span>
	                                <IconButton
	                                  size="small"
	                                  component="label"
	                                  disabled={!docKeyForUpload || isDocUploadBusy(docKeyForUpload)}
	                                >
	                                  <UploadFileOutlinedIcon fontSize="small" />
	                                  <input
	                                    hidden
	                                    type="file"
	                                    accept=".pdf,image/*"
	                                    onChange={async (e) => {
	                                      const file = e.target.files?.[0] || null;
	                                      e.currentTarget.value = '';
	                                      if (!file || !docKeyForUpload) return;
	                                      try {
	                                        await uploadStepRequiredDoc(
	                                          docKeyForUpload,
	                                          String(
	                                            (row as any)?.displayName ||
	                                              (uploadTypeByKey.get(String(d.key || '').trim()) as any)?.label ||
	                                              d.label ||
	                                              docKeyForUpload
	                                          ),
	                                          file
	                                        );
	                                      } catch (err: any) {
	                                        setError(err?.message || 'Failed to re-upload document.');
	                                      }
	                                    }}
	                                  />
	                                </IconButton>
	                              </span>
	                            </Tooltip>
	                          ) : null}
	                          {first?.url ? (
	                            <Tooltip title="Open">
	                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openDocumentInNewTab(String(first?.url || ''))}
                                >
                                  <OpenInNewOutlinedIcon fontSize="small" />
                                </IconButton>
	                              </span>
	                            </Tooltip>
	                          ) : null}
	                          {files.length > 1 ? <Chip size="small" label={`${files.length} files`} variant="outlined" /> : null}
	                        </Stack>
	                      </Box>
	                    );
	                  })}
                    {signedBookingWhatsAppError && <Alert severity="error">{signedBookingWhatsAppError}</Alert>}
                    {(() => {
                      const row = (bookingDocsByKey.paymentDetailsDraft as any) || null;
                      const first = row?.files?.[0] || null;
                      if (!first?.url) return null;
                      if (getStoredDocDisplaySection(row) !== 'main_documents') return null;
                      const atMs =
                        first?.uploadedAtMs ||
                        tsToMs((row as any)?.updatedAt) ||
                        tsToMs((row as any)?.createdAt) ||
                        null;
                      const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : null;
                      return (
                        <Box
                          key="doc_payment_details_v1_draft"

                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, py: 0.875, borderBottom: '1px solid #f0f0f0' }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>

                            <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
                              <DescriptionOutlinedIcon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                PAYMENT DETAILS
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {when || '—'}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {isAdmin ? (
                              <Tooltip title={paymentDetailsDraftGenerating ? 'Generating…' : 'Regenerate'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={paymentDetailsDraftGenerating}
                                    onClick={async () => {
                                      setPaymentDetailsDraftGenerating(true);
                                      setError(null);
                                      try {
                                        const effectiveBookingId = bookingId || (await ensureBookingForDraft());
                                        await generatePaymentDetailsV1Draft(effectiveBookingId);
                                      } catch (err: any) {
                                        setError(err?.message || 'Failed to regenerate payment details.');
                                      } finally {
                                        setPaymentDetailsDraftGenerating(false);
                                      }
                                    }}
                                  >
                                    <AutorenewOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                            <Tooltip title="Open">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openDocumentInNewTab(String(first?.url || ''))}
                                >
                                  <OpenInNewOutlinedIcon fontSize="small" />
                                  </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Box>
                      );
		                    })()}
		                  </Stack>
              </Box>
            ) : null}

            {isUnitBookedOrSold && (visibleLoanDocumentDefs.length > 0 ||
              visibleLoanSectionDraftRows.some((row) => (row?.files || []).some((file) => !!file?.url)) ||
              ((bookingDocsByKey.paymentDetailsDraft?.files || []).some((file) => !!file?.url) &&
                getStoredDocDisplaySection(bookingDocsByKey.paymentDetailsDraft) === 'loan_documents')) ? (
              <Box sx={{ ...sectionSx, mt: pageMode ? 0 : 1.25, ...(pageMode ? { order: 4 } : {}), ...((pageMode || !!focusSection) && !isPageSectionVisible('loan-documents') ? { display: 'none' } : {}) }} data-section="loan-documents">
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase', mb: 0.75 }}>
                        Loan Documents
                      </Typography>
                      <Stack spacing={1}>
                        {visibleLoanDocumentDefs.map((d) => {
                          const row = uploadedDocsByKey[d.key] || null;
                          const docKeyForUpload = String(
                            d.key || (row as any)?.type || (row as any)?.id || ''
                          ).trim();
                          const isSanctionLetter = docKeyForUpload.toLowerCase().replace(/[^a-z0-9]/g, '') === 'sanctionletter';
                          const files = (row?.files || []).filter((f) => !!f?.url);
                          const first = files[0] || null;
                          const atMs =
                            first?.uploadedAtMs ||
                            tsToMs((row as any)?.updatedAt) ||
                            tsToMs((row as any)?.createdAt) ||
                            null;
                          const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : null;
                          return (
                            <Box
                              key={`loan_doc_${d.key}`}
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, py: 0.875, borderBottom: '1px solid #f0f0f0' }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>

                                <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}><DescriptionOutlinedIcon fontSize="small" /></Box>

                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                    {String((row as any)?.displayName || (uploadTypeByKey.get(String(d.key || '').trim()) as any)?.label || d.label || d.key)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {when || '—'}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {isAdmin ? (
                                  <Tooltip title={isSanctionLetter ? 'Edit sanction details and letter' : 'Re-upload'}>
                                    <span>
                                      {isSanctionLetter ? (
                                        <IconButton
                                          size="small"
                                          disabled={!docKeyForUpload || isDocUploadBusy(docKeyForUpload)}
                                          aria-label="Edit sanction details and letter"
                                          onClick={() => openSanctionFormForDocument(docKeyForUpload)}
                                        >
                                          <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          size="small"
                                          component="label"
                                          disabled={!docKeyForUpload || isDocUploadBusy(docKeyForUpload)}
                                        >
                                          <UploadFileOutlinedIcon fontSize="small" />
                                          <input
                                            hidden
                                            type="file"
                                            accept=".pdf,image/*"
                                            onChange={async (e) => {
                                              const file = e.target.files?.[0] || null;
                                              e.currentTarget.value = '';
                                              if (!file || !docKeyForUpload) return;
                                              try {
                                                await uploadStepRequiredDoc(
                                                  docKeyForUpload,
                                                  String(
                                                    (row as any)?.displayName ||
                                                      (uploadTypeByKey.get(String(d.key || '').trim()) as any)?.label ||
                                                      d.label ||
                                                      docKeyForUpload
                                                  ),
                                                  file
                                                );
                                              } catch (err: any) {
                                                setError(err?.message || 'Failed to re-upload document.');
                                              }
                                            }}
                                          />
                                        </IconButton>
                                      )}
                                    </span>
                                  </Tooltip>
                                ) : null}
                                {first?.url ? (
                                  <Tooltip title="Open">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => openDocumentInNewTab(String(first?.url || ''))}
                                      >
                                        <OpenInNewOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : null}
                                {files.length > 1 ? <Chip size="small" label={`${files.length} files`} variant="outlined" /> : null}
                              </Stack>
                            </Box>
                          );
                        })}
                        {(() => {
                          const row = (bookingDocsByKey.paymentDetailsDraft as any) || null;
                          const first = row?.files?.[0] || null;
                          if (!first?.url) return null;
                          if (getStoredDocDisplaySection(row) !== 'loan_documents') return null;
                          const atMs =
                            first?.uploadedAtMs ||
                            tsToMs((row as any)?.updatedAt) ||
                            tsToMs((row as any)?.createdAt) ||
                            null;
                          const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : null;
                          return (
                            <Box
                              key="loan_doc_payment_details_v1_draft"
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, py: 0.875, borderBottom: '1px solid #f0f0f0' }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
                                  <DescriptionOutlinedIcon fontSize="small" />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                    PAYMENT DETAILS
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {when || '—'}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {isAdmin && adminDocumentActionMode ? (
                                  <Tooltip title={paymentDetailsDraftGenerating ? 'Generating…' : 'Regenerate'}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={paymentDetailsDraftGenerating}
                                        onClick={async () => {
                                          setPaymentDetailsDraftGenerating(true);
                                          setError(null);
                                          try {
                                            const effectiveBookingId = bookingId || (await ensureBookingForDraft());
                                            await generatePaymentDetailsV1Draft(effectiveBookingId);
                                          } catch (err: any) {
                                            setError(err?.message || 'Failed to regenerate payment details.');
                                          } finally {
                                            setPaymentDetailsDraftGenerating(false);
                                          }
                                        }}
                                      >
                                        <AutorenewOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : null}
                                <Tooltip title="Open">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => openDocumentInNewTab(String(first?.url || ''))}
                                    >
                                      <OpenInNewOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </Box>
                          );
                        })()}
                        {visibleLoanSectionDraftRows
                          .filter((row) => (row?.files || []).some((f) => !!f?.url))
                          .map((row) => {
                            const rowKey = String((row as any)?.id || Math.random());
                            const files = (row?.files || []).filter((f) => !!f?.url);
                            const first = files[0] || null;
                            const atMs =
                              first?.uploadedAtMs ||
                              tsToMs((row as any)?.updatedAt) ||
                              tsToMs((row as any)?.createdAt) ||
                              null;
                            const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : null;
                            const label = toDraftDisplayLabel(
                              String(first?.name || first?.originalName || (row as any)?.type || (row as any)?.templateId || rowKey),
                              String((row as any)?.type || (row as any)?.templateId || rowKey)
                            );
                            return (
                              <Box
                                key={`loan_draft_doc_${rowKey}`}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, py: 0.875, borderBottom: '1px solid #f0f0f0' }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
                                    <DescriptionOutlinedIcon fontSize="small" />
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                      {label || 'Draft Document'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {when || '—'}
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  {isAdmin && adminDocumentActionMode ? (
                                    <Tooltip title="Regenerate">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={async () => {
                                            setError(null);
                                            try {
                                              const selector = String(
                                                (row as any)?.templateId || (row as any)?.id || (row as any)?.type || ''
                                              ).trim();
                                              await regenerateDocumentBySelector(selector);
                                            } catch (err: any) {
                                              setError(err?.message || 'Failed to regenerate document.');
                                            }
                                          }}
                                        >
                                          <AutorenewOutlinedIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  ) : null}
                                  {first?.url ? (
                                    <Tooltip title="Open">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => openDocumentInNewTab(String(first?.url || ''))}
                                        >
                                          <OpenInNewOutlinedIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  ) : null}
                                  {files.length > 1 ? <Chip size="small" label={`${files.length} files`} variant="outlined" /> : null}
                                </Stack>
                              </Box>
                            );
                          })}
                      </Stack>
              </Box>
            ) : null}

            <Box id="drawer-section-loan" sx={{ height: 0, ...(pageMode ? { display: 'none' } : {}) }} />
            {isUnitBookedOrSold && (!pageMode || visibleDraftSectionRows.some((row) => (row?.files || []).some((file) => !!file?.url))) ? (
              <Box sx={{ ...sectionSx, mt: pageMode ? 0 : 1.25, ...(pageMode ? { order: 5 } : {}), ...((pageMode || !!focusSection) && !isPageSectionVisible('drafts') ? { display: 'none' } : {}) }} data-section="drafts">
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase', mb: 0.75 }}>
                  Draft Documents
                </Typography>
	              {visibleDraftSectionRows.filter((row) => (row?.files || []).some((f) => !!f?.url)).length === 0 ? (
	                detailRow(<InfoOutlinedIcon fontSize="small" />, 'Draft Documents', '—')
	              ) : (
	                <Stack spacing={1}>
	                  {visibleDraftSectionRows
	                    .filter((row) => (row?.files || []).some((f) => !!f?.url))
	                    .map((row) => {
	                      const rowKey = String((row as any)?.id || Math.random());
	                      const files = (row?.files || []).filter((f) => !!f?.url);
	                      const first = files[0] || null;
	                      const atMs =
	                        first?.uploadedAtMs ||
	                        tsToMs((row as any)?.updatedAt) ||
	                        tsToMs((row as any)?.createdAt) ||
	                        null;
	                      const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : null;
	                      const label = toDraftDisplayLabel(
                          String(
                            first?.name ||
                            first?.originalName ||
                            (row as any)?.type ||
                            (row as any)?.templateId ||
                            rowKey
                          ),
                          String((row as any)?.type || (row as any)?.templateId || rowKey)
                        );
	                      return (
	                        <Box
	                          key={`draft_doc_${rowKey}`}
	                          sx={{
	                            display: 'flex',
	                            alignItems: 'center',
	                            justifyContent: 'space-between',
	                            gap: 1,
	                            px: 1,
	                            py: 0.875,
	                            borderBottom: '1px solid #f0f0f0',
	                          }}
	                        >
	                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
	                            <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex' }}>
	                              <DescriptionOutlinedIcon fontSize="small" />
	                            </Box>
	                            <Box sx={{ minWidth: 0 }}>
	                              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
	                                {label || 'Draft Document'}
	                              </Typography>
	                              <Typography variant="body2" color="text.secondary" noWrap>
	                                {when || '—'}
	                              </Typography>
	                            </Box>
	                          </Stack>
	                          <Stack direction="row" spacing={0.5} alignItems="center">
	                            {isAdmin ? (
	                              <Tooltip title={paymentDetailsDraftGenerating && ['payment_details_v1', 'payment_details_v2'].includes(String((row as any)?.templateId || (row as any)?.id || '').trim()) ? 'Generating…' : 'Regenerate'}>
	                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      paymentDetailsDraftGenerating &&
                                      ['payment_details_v1', 'payment_details_v2'].includes(String((row as any)?.templateId || (row as any)?.id || '').trim())
                                    }
                                    onClick={async () => {
                                      setError(null);
                                      const selector = String(
                                        (row as any)?.templateId || (row as any)?.id || (row as any)?.type || ''
                                      ).trim();
                                      const isPaymentDetails = selector === 'payment_details_v1' || selector === 'payment_details_v2';
                                      try {
                                        if (isPaymentDetails) setPaymentDetailsDraftGenerating(true);
                                        await regenerateDocumentBySelector(selector);
                                      } catch (err: any) {
                                        setError(err?.message || 'Failed to regenerate document.');
                                      } finally {
                                        if (isPaymentDetails) setPaymentDetailsDraftGenerating(false);
                                      }
                                    }}
                                  >
                                    {paymentDetailsDraftGenerating &&
                                    ['payment_details_v1', 'payment_details_v2'].includes(String((row as any)?.templateId || (row as any)?.id || '').trim()) ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <AutorenewOutlinedIcon fontSize="small" />
                                    )}
                                  </IconButton>
	                                </span>
	                              </Tooltip>
	                            ) : null}
	                            {first?.url ? (
	                              <Tooltip title="Open">
	                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => openDocumentInNewTab(String(first?.url || ''))}
                                  >
                                    <OpenInNewOutlinedIcon fontSize="small" />
                                  </IconButton>
	                                </span>
	                              </Tooltip>
	                            ) : null}
	                            {files.length > 1 ? <Chip size="small" label={`${files.length} files`} variant="outlined" /> : null}
	                          </Stack>
	                        </Box>
	                      );
	                    })}
	                </Stack>
		              )}
              </Box>
            ) : null}

                <Box id="drawer-section-payments" sx={{ height: 0, ...(pageMode ? { display: 'none' } : {}) }} />

                <Box sx={{ ...sectionSx, mt: pageMode ? 0 : 1.25, ...(pageMode ? { order: 5 } : {}), ...((pageMode || !!focusSection) && !isPageSectionVisible('payments') ? { display: 'none' } : {}) }} data-section="payments">
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
                        Payment Transactions
                      </Typography>
                      <Tooltip title={templateStatuses.loading ? 'Checking Meta template approval…' : 'Send the payment-details PDF to the primary unit holder'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={paymentWhatsAppSending || templateStatuses.loading || !bookingDocsByKey.paymentDetailsDraft?.files?.some((file: any) => !!file?.url) || !primaryCustomerMobile}
                            onClick={(event) => {
                              setPaymentWhatsAppError(null);
                              setPaymentWhatsAppAnchor(event.currentTarget);
                            }}
                            aria-label="Send payment details on WhatsApp"
                            sx={{ color: '#128c7e', border: '1px solid rgba(18,140,126,0.22)', borderRadius: 1.5, p: 0.55 }}
                          >
                            {paymentWhatsAppSending ? <CircularProgress size={18} /> : <WhatsAppIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Menu
                        anchorEl={paymentWhatsAppAnchor}
                        open={Boolean(paymentWhatsAppAnchor)}
                        onClose={() => setPaymentWhatsAppAnchor(null)}
                      >
                        {([
                          ['en', 'English'],
                          ['gu', 'ગુજરાતી'],
                          ['hi', 'हिंदी'],
                        ] as Array<[AutoReplyLang, string]>).map(([lang, label]) => {
                          const approved = !!templateStatuses.versionsFor('swarna_unit_payment_details', lang).sending;
                          return (
                            <MenuItem
                              key={lang}
                              disabled={!approved || paymentWhatsAppSending}
                              onClick={() => void sendPaymentDetailsWhatsApp(lang)}
                            >
                              {label}{!approved ? ' · Awaiting Meta approval' : ''}
                            </MenuItem>
                          );
                        })}
                      </Menu>
                      {downloadableReceiptRows.length > 1 && !isMultiSelectReceipts ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setIsMultiSelectReceipts(true)}
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            py: 0.1,
                            px: 0.8,
                            minWidth: 0,
                            color: GOLD,
                            textTransform: 'none',
                            bgcolor: 'rgba(157,111,50,0.06)',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'rgba(157,111,50,0.12)' },
                          }}
                        >
                          Select
                        </Button>
                      ) : null}
                    </Stack>
                    {customerPaymentPendingAmount > 0 ? (
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }} noWrap>
                        Pending: ₹ {customerPaymentPendingAmount.toLocaleString('en-IN')}
                      </Typography>
                    ) : null}
                  </Stack>

                  {isMultiSelectReceipts ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1,
                        mb: 1.25,
                        bgcolor: 'rgba(157,111,50,0.04)',
                        borderColor: 'rgba(157,111,50,0.3)',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                downloadableReceiptRows.length > 0 &&
                                downloadableReceiptRows.every((r: any) => selectedReceiptTxnIds.has(String(r.id || '')))
                              }
                              indeterminate={
                                selectedReceiptTxnIds.size > 0 &&
                                selectedReceiptTxnIds.size < downloadableReceiptRows.length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedReceiptTxnIds(new Set(downloadableReceiptRows.map((r: any) => String(r.id || ''))));
                                } else {
                                  setSelectedReceiptTxnIds(new Set());
                                }
                              }}
                              sx={{ p: 0.5, color: GOLD, '&.Mui-checked': { color: GOLD } }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 700, userSelect: 'none' }}>
                              {selectedReceiptTxnIds.size > 0
                                ? `${selectedReceiptTxnIds.size} of ${downloadableReceiptRows.length} selected`
                                : 'Select All'}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Button
                          size="small"
                          variant="contained"
                          disabled={selectedReceiptTxnIds.size === 0 || multiReceiptDownloading}
                          onClick={handleDownloadCombinedReceipts}
                          startIcon={
                            multiReceiptDownloading ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <DownloadOutlinedIcon fontSize="small" />
                            )
                          }
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            bgcolor: GOLD,
                            '&:hover': { bgcolor: '#875d27' },
                          }}
                        >
                          {multiReceiptDownloading
                            ? multiReceiptProgressText || 'Downloading…'
                            : `Download 1 PDF (${selectedReceiptTxnIds.size})`}
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          disabled={multiReceiptDownloading}
                          onClick={() => {
                            setIsMultiSelectReceipts(false);
                            setSelectedReceiptTxnIds(new Set());
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                          }}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Paper>
                  ) : null}

                  {customerReceiptRows.length === 0 ? (
                    detailRow(<InfoOutlinedIcon fontSize="small" />, 'Payment Transactions', '—')
                  ) : (
                    <Stack spacing={0}>
                      {customerReceiptRows.map((row: any) => {
                        const atMs =
                          tsToMs(row?.chqDate) ||
                          tsToMs(row?.txnDate) ||
                          tsToMs(row?.createdAt) ||
                          tsToMs(row?.updatedAt) ||
                          null;
                        const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : '—';
                        const bank = String(row?.payerBankName || row?.bankName || '—');
                        const refNo = String(row?.referenceNo || '—');
                        const amount = Number(row?.amount || 0);
                        const amountLabel = Number.isFinite(amount) ? amount.toLocaleString('en-IN') : '0';
                        const refundedAmount = Number(customerRefundAmountByReceiptId.get(String(row?.id || '')) || 0);
                        const refundStatusLabel =
                          refundedAmount > 0
                            ? refundedAmount + 0.5 >= amount
                              ? 'Refunded'
                              : 'Partly Refunded'
                            : '';
                        const isCustomerReceipt =
                          String(row?.txnType || '').toLowerCase() === 'receipt' &&
                          String(row?.receiptCategory || '').toLowerCase() === 'customer';
                        const isGenerating = receiptGeneratingTxnId === String(row?.id || '');
                        const isSelected = selectedReceiptTxnIds.has(String(row?.id || ''));
                        return (
                          <Box
                            key={`pay_tx_${row.id}`}
                            onClick={() => {
                              if (isMultiSelectReceipts) {
                                if (isCustomerReceipt) {
                                  toggleReceiptSelection(String(row.id || ''));
                                }
                                return;
                              }
                              openPaymentDetail(String(row.id || ''));
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1.25,
                              px: 1,
                              py: 1.25,
                              borderBottom: '1px solid #f0f0f0',
                              cursor: 'pointer',
                              bgcolor: isSelected ? 'rgba(157,111,50,0.06)' : 'transparent',
                              borderRadius: isSelected ? 1 : 0,
                              transition: 'background-color 0.15s ease',
                            }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              {isMultiSelectReceipts && isCustomerReceipt ? (
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleReceiptSelection(String(row.id || ''));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0.25, mr: 0.25, color: GOLD, '&.Mui-checked': { color: GOLD } }}
                                />
                              ) : (
                                <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex', flexShrink: 0 }}>
                                  <CurrencyRupeeOutlinedIcon fontSize="small" />
                                </Box>
                              )}
                              <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{bank}</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                                  {`${when} • ${refNo}`}
                                </Typography>
                              </Stack>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                              {refundStatusLabel ? (
                                <Chip
                                  size="small"
                                  label={refundStatusLabel}
                                  color={refundStatusLabel === 'Refunded' ? 'error' : 'warning'}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800 }}
                                />
                              ) : null}
                              <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>₹ {amountLabel}</Typography>
                              {isCustomerReceipt ? (
                                <Tooltip title={isGenerating ? 'Generating receipt…' : 'Download payment receipt'}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={isGenerating || multiReceiptDownloading}
                                      onClick={async (event) => {
                                        event.stopPropagation();
                                        if (!unit?.id) return;
                                        const txnId = String(row?.id || '').trim();
                                        if (!txnId) return;
                                        try {
                                          setReceiptGeneratingTxnId(txnId);
                                          await generateDocumentFromTemplateId('payment_receipt_v1', {
                                            transactionRow: row,
                                            downloadOnly: true,
                                          });
                                        } catch (err: any) {
                                          setError(err?.message || 'Failed to generate payment receipt.');
                                        } finally {
                                          setReceiptGeneratingTxnId('');
                                        }
                                      }}
                                    >
                                      {isGenerating ? (
                                        <CircularProgress size={15} />
                                      ) : (
                                        <DownloadOutlinedIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>

                {customerRefundRows.length > 0 ? (
                  <Box sx={{ ...sectionSx, mt: pageMode ? 0 : 1.25, ...(pageMode ? { order: 6 } : {}), ...((pageMode || !!focusSection) && !isPageSectionVisible('payments') ? { display: 'none' } : {}) }} data-section="payments">
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase', mb: 0.75 }}>
                      Refund Transactions
                    </Typography>
                    <Stack spacing={0}>
                      {customerRefundRows.map((row: any) => {
                        const atMs =
                          tsToMs(row?.chqDate) ||
                          tsToMs(row?.txnDate) ||
                          tsToMs(row?.createdAt) ||
                          tsToMs(row?.updatedAt) ||
                          null;
                        const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : '—';
                        const bank = String(row?.bankName || row?.payerBankName || '—');
                        const refNo = String(row?.referenceNo || '—');
                        const amount = Number(row?.amount || 0);
                        const amountLabel = Number.isFinite(amount) ? amount.toLocaleString('en-IN') : '0';
                        return (
                          <Box
                            key={`refund_tx_${row.id}`}
                            onClick={() => openPaymentDetail(String(row.id || ''))}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, px: 1, py: 1.25, borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ color: 'rgba(211,47,47,0.75)', display: 'flex', flexShrink: 0 }}>
                                <CurrencyRupeeOutlinedIcon fontSize="small" />
                              </Box>
                              <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{bank}</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                                  {`${when} • ${refNo}`}
                                </Typography>
                              </Stack>
                            </Stack>
                            <Typography variant="body2" color="error.main" sx={{ fontWeight: 900, flexShrink: 0 }} noWrap>
                              - ₹ {amountLabel}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}

                <Box sx={{ ...sectionSx, mt: pageMode ? 0 : 1.25, ...(pageMode ? { order: 7 } : {}), ...((pageMode || !!focusSection) && !isPageSectionVisible('gst') ? { display: 'none' } : {}) }} data-section="gst">
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
                      GST Transactions
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {Math.max(0, computeGstAmount(Number(unit?.unitAmount || 0) || 0) - gstReceiptTotal) > 0 ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }} noWrap>
                            Pending GST: ₹ {Math.max(0, computeGstAmount(Number(unit?.unitAmount || 0) || 0) - gstReceiptTotal).toLocaleString('en-IN')}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openGstPaymentUnified({ which: 'main', key: OFFICE_MAIN_STEP_KEYS.downpaymentGst })}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Add GST payment
                          </Button>
                        </>
                      ) : null}
                    </Stack>
                  </Stack>
                  {gstReceiptRows.length === 0 ? (
                    detailRow(<InfoOutlinedIcon fontSize="small" />, 'GST Transactions', '—')
                  ) : (
                    <Stack spacing={0}>
                      {gstReceiptRows.map((row: any) => {
                        const atMs =
                          tsToMs(row?.chqDate) ||
                          tsToMs(row?.txnDate) ||
                          tsToMs(row?.createdAt) ||
                          tsToMs(row?.updatedAt) ||
                          null;
                        const when = typeof atMs === 'number' ? formatDDMMYYYY(atMs) : '—';
                        const bank = String(row?.payerBankName || row?.bankName || '—');
                        const refNo = String(row?.referenceNo || '—');
                        const amount = Number(row?.amount || 0);
                        const amountLabel = Number.isFinite(amount) ? amount.toLocaleString('en-IN') : '0';
                        const isGenerating = receiptGeneratingTxnId === String(row?.id || '');
                        return (
                          <Box
                            key={`gst_tx_${row.id}`}
                            onClick={() => openPaymentDetail(String(row.id || ''))}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, px: 1, py: 1.25, borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ color: 'rgba(157,111,50,0.55)', display: 'flex', flexShrink: 0 }}>
                                <CurrencyRupeeOutlinedIcon fontSize="small" />
                              </Box>
                              <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{bank}</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                                  {`${when} • ${refNo}`}
                                </Typography>
                              </Stack>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>₹ {amountLabel}</Typography>
                              <Tooltip title={isGenerating ? 'Generating receipt…' : 'Download GST receipt'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isGenerating || multiReceiptDownloading}
                                    onClick={async (event) => {
                                      event.stopPropagation();
                                      if (!unit?.id) return;
                                      const txnId = String(row?.id || '').trim();
                                      if (!txnId) return;
                                      try {
                                        setReceiptGeneratingTxnId(txnId);
                                        await generateDocumentFromTemplateId('payment_receipt_v1', {
                                          transactionRow: row,
                                          downloadOnly: true,
                                        });
                                      } catch (err: any) {
                                        setError(err?.message || 'Failed to generate GST receipt.');
                                      } finally {
                                        setReceiptGeneratingTxnId('');
                                      }
                                    }}
                                  >
                                    {isGenerating ? <CircularProgress size={15} /> : <DownloadOutlinedIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>

	              <Dialog open={kycDialogOpen} onClose={() => setKycDialogOpen(false)} fullWidth maxWidth="sm">
	                <DialogTitle>{`${String(unit?.unitNumber || unit?.id || 'Unit')} : KYC Documents`}</DialogTitle>
	                <DialogContent dividers>
                  {customers.length > 0 ? (
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Customer</InputLabel>
                      <Select
                        label="Customer"
                        value={selectedCustomerId || ''}
                        onChange={(e) => setSelectedCustomerId(String(e.target.value))}
                      >
                        {customers.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.displayName || c.name || 'Customer'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}

                  {!selectedCustomerId ? (
                    <Alert severity="info">Select a customer to view KYC documents.</Alert>
                  ) : (
		                    <Stack spacing={1}>
                      {(() => {
                        const hasPAN = kycDocs.some(
                          (document) =>
                            document.id === 'PAN' ||
                            String(document.type || '').toUpperCase() === 'PAN'
                        );
                        const hasAADHAAR = kycDocs.some(
                          (document) =>
                            document.id === 'AADHAAR' ||
                            String(document.type || '').toUpperCase() === 'AADHAAR'
                        );
                        return (
                          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                            <Chip
                              size="small"
                              label={`PAN: ${hasPAN ? 'Uploaded' : 'Pending'}`}
                              color={hasPAN ? 'success' : 'warning'}
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={`AADHAAR: ${hasAADHAAR ? 'Uploaded' : 'Pending'}`}
                              color={hasAADHAAR ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </Stack>
                        );
                      })()}

                      {(() => {
                        const uploadedDocsCount = kycDocs.filter(
                          (document) => (document.files || []).length > 0
                        ).length;
                        const hideUploadControls = uploadedDocsCount >= 2 && !showExtraKycUpload;

                        if (hideUploadControls) {
                          return (
                            <Stack direction="row" justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setShowExtraKycUpload(true)}
                                sx={{ minWidth: 30, px: 0.75, fontWeight: 900 }}
                              >
                                +
                              </Button>
                            </Stack>
                          );
                        }

                        return (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Upload PAN (single file) or Aadhaar (front + back).
                            </Typography>

                            <FormControl size="small" fullWidth>
                              <InputLabel>KYC Type</InputLabel>
                              <Select
                                label="KYC Type"
                                value={kycType}
                                onChange={(e) => setKycType(String(e.target.value))}
                              >
                                {kycTypeOptions.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <TextField
                              size="small"
                              fullWidth
                              label="Document Number (optional)"
                              value={kycDocNumber}
                              onChange={(e) => setKycDocNumber(e.target.value)}
                            />

                            {kycType === 'AADHAAR' ? (
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button size="small" variant="outlined" onClick={pickAadhaarFront}>
                                  Select Front
                                </Button>
                                <Button size="small" variant="outlined" onClick={pickAadhaarBack}>
                                  Select Back
                                </Button>
                                {(aadhaarFrontFile || aadhaarBackFile) ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                    {aadhaarFrontFile?.name || 'Front not selected'} | {aadhaarBackFile?.name || 'Back not selected'}
                                  </Typography>
                                ) : null}
                              </Stack>
                            ) : (
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button size="small" variant="outlined" onClick={handlePickFiles}>
                                  Select PAN File
                                </Button>
                                {kycFiles[0] ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                    {kycFiles[0].name}
                                  </Typography>
                                ) : null}
                              </Stack>
                            )}
                          </>
                        );
                      })()}

                      <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={onFileChange}
                      />
                      <input
                        ref={aadhaarFrontInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={onAadhaarFrontChange}
                      />
                      <input
                        ref={aadhaarBackInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={onAadhaarBackChange}
                      />

                      {(() => {
                        const uploadedDocsCount = kycDocs.filter(
                          (document) => (document.files || []).length > 0
                        ).length;
                        const hideUploadControls = uploadedDocsCount >= 2 && !showExtraKycUpload;
                        if (hideUploadControls) return null;
                        return (
                          <Stack direction="row" justifyContent="flex-end">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={uploadKyc}
                              disabled={savingKyc}
                            >
                              {savingKyc ? 'Uploading…' : 'Upload KYC'}
                            </Button>
                          </Stack>
                        );
                      })()}

                      {kycDocs.length === 0 ? (
                        <Chip label="No KYC uploaded yet" variant="outlined" />
                      ) : (
                        <Stack spacing={1}>
                          {kycDocs.map((d) => {
                        const hasFiles = Array.isArray(d.files) && d.files.length > 0;
                        const status = ((d.status || 'pending') as any) === 'pending' && hasFiles
                          ? 'uploaded'
                          : (d.status || 'pending') as any;
                        const color: any =
                          status === 'verified' ? 'success' :
                          status === 'uploaded' ? 'success' :
                          status === 'rejected' ? 'error' :
                          'warning';
                        return (
                          <Box
                            key={d.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 1,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                              <Chip size="small" label={String(d.type || 'DOC')} sx={{ fontWeight: 900 }} />
                              <Chip size="small" label={status} color={color} variant="outlined" sx={{ fontWeight: 900 }} />
                              {d.docNumber ? <Chip size="small" label={`No: ${d.docNumber}`} variant="outlined" /> : null}
                              <Box sx={{ flex: 1 }} />
                              {d.files?.length ? <Chip size="small" label={`${d.files.length} file(s)`} variant="outlined" /> : null}
                            </Stack>
                            {d.files?.length ? (
                              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mt: 1 }}>
                                {d.files.map((f, idx) => (
                                  <Button
                                    key={`${d.id}_${idx}`}
                                    size="small"
                                    variant="text"
                                    component="a"
                                    href={f.url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Open: {f.name || 'file'}
                                  </Button>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No files attached.
                              </Typography>
                            )}
                          </Box>
                        );
                          })}
                        </Stack>
                      )}
                    </Stack>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setKycDialogOpen(false)}>Close</Button>
                </DialogActions>
              </Dialog>

              {isMobile ? (
                <Drawer
                  anchor="right"
                  open={customerDrawerOpen}
                  onClose={() => setCustomerDrawerOpen(false)}
                  ModalProps={{ keepMounted: true }}
                  PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, maxWidth: '100vw' } }}
                >
                  {customerPanelContent}
                </Drawer>
              ) : null}
              {isMobile ? (
                <BankTransactionDrawer
                  open={paymentDrawerOpen}
                  onClose={() => setPaymentDrawerOpen(false)}
                  title="Add Transaction"
                  saveError={paymentError}
                  saving={paymentSaving}
                  voucherUploading={paymentUploading}
                  onSave={savePaymentTransaction}
                  txnType={paymentTxnType}
                  setTxnType={setPaymentTxnType}
                  receiptCategory={paymentReceiptCategory}
                  setReceiptCategory={setPaymentReceiptCategory}
                  eligibleBankAccounts={paymentAccounts}
                  bankAccountId={paymentBankAccountId}
                  setBankAccountId={setPaymentBankAccountId}
                  txnDateYMD={paymentDateYMD}
                  setTxnDateYMD={setPaymentDateYMD}
                  amount={paymentAmount}
                  setAmount={setPaymentAmount}
                  showUnitPicker={
                    paymentTxnType === 'receipt' &&
                    (paymentReceiptCategory === 'customer' || paymentReceiptCategory === 'gst' || paymentReceiptCategory === 'advocate')
                  }
                  eligibleUnitOptions={paymentUnitOptions}
                  unitId={unit?.id || ''}
                  setUnitId={() => {}}
                  unitNumber={unit?.unitNumber || ''}
                  setUnitNumber={() => {}}
                  pendingAmountText={
                    paymentTxnType === 'receipt' && (typeof unit?.unitAmount === 'number' || paymentReceiptCategory === 'advocate')
                      ? `${paymentReceiptCategory === 'gst' ? 'GST Pending: ' : paymentReceiptCategory === 'advocate' ? 'Advocate Fee Pending: ' : 'Pending: '}${Math.max(
                          0,
                          paymentReceiptCategory === 'gst'
                            ? computeGstAmount(Number(unit?.unitAmount || 0) || 0) - gstReceiptTotal
                            : paymentReceiptCategory === 'advocate'
                              ? projectAdvocateAgreementFee - advocateAgreementReceiptTotal
                              : customerPaymentPendingAmount
                        ).toLocaleString('en-IN')}`
                      : null
                  }
                  partyName={paymentPartyName}
                  setPartyName={setPaymentPartyName}
                  partyNameOptions={[]}
                  payerBankName={paymentPayerBankName}
                  setPayerBankName={setPaymentPayerBankName}
                  payerBankNameOptions={paymentPayerBankNameOptions}
                  onPayerBankClear={() => setPaymentPayerBankName('')}
                  paymentModeOptions={paymentModeOptions}
                  mode={paymentMode}
                  setMode={setPaymentMode}
                  referenceNo={paymentReferenceNo}
                  setReferenceNo={setPaymentReferenceNo}
                  notes={paymentNotes}
                  setNotes={setPaymentNotes}
                  customPostAmountSection={paymentLoanDisbursementSection}
                  voucherInputRef={paymentVoucherInputRef}
                  voucherFile={paymentVoucherFile}
                  setVoucherFile={setPaymentVoucherFile}
                  voucherRequired={
                    paymentTxnType === 'receipt' &&
                    (paymentReceiptCategory === 'customer' || paymentReceiptCategory === 'gst' || paymentReceiptCategory === 'advocate')
                  }
                  showSkipButton={paymentReceiptCategory === 'gst'}
                  skipButtonLabel="Skip GST for now"
                  onSkip={skipGstPaymentForNow}
                  showExtraFooterAction={paymentReceiptCategory === 'advocate'}
                  extraFooterActionLabel="Mark received without details"
                  onExtraFooterAction={markAdvocateAgreementFeeReceivedWithoutDetails}
                  drawerWidth={paymentPanelWidth}
                />
              ) : null}
              {isMobile ? (
                <BankTransactionDrawer
                  open={paymentDetailDrawerOpen}
                  onClose={() => {
                    setPaymentDetailDrawerOpen(false);
                    setPaymentDetailTxn(null);
                    setPaymentDetailEditMode(false);
                  }}
                  title={`${String(unit?.unitNumber || unit?.id || 'Unit')} : Payment Detail`}
                  saveError={paymentDetailError}
                  saving={paymentDetailSaving}
                  onSave={() => {
                    if (!paymentDetailEditMode) return;
                    savePaymentDetailTransaction();
                  }}
                  txnType={paymentDetailTxnType}
                  setTxnType={setPaymentDetailTxnType}
                  receiptCategory={paymentDetailReceiptCategory}
                  setReceiptCategory={setPaymentDetailReceiptCategory}
                  hideTxnType={!paymentDetailEditMode}
                  hideReceiptCategory={!paymentDetailEditMode}
                  eligibleBankAccounts={paymentDetailBankAccounts}
                  bankAccountId={paymentDetailBankAccountId}
                  setBankAccountId={setPaymentDetailBankAccountId}
                  txnDateYMD={paymentDetailDateYMD}
                  setTxnDateYMD={setPaymentDetailDateYMD}
                  amount={paymentDetailAmount}
                  setAmount={setPaymentDetailAmount}
                  showUnitPicker={false}
                  eligibleUnitOptions={[]}
                  unitId={unit?.id || ''}
                  setUnitId={() => {}}
                  unitNumber={unit?.unitNumber || ''}
                  setUnitNumber={() => {}}
                  partyName={paymentDetailPartyName}
                  setPartyName={setPaymentDetailPartyName}
                  partyNameOptions={[]}
                  payerBankName={paymentDetailPayerBankName}
                  setPayerBankName={setPaymentDetailPayerBankName}
                  payerBankNameOptions={paymentPayerBankNameOptions}
                  paymentModeOptions={paymentModeOptions}
                  mode={paymentDetailMode}
                  setMode={setPaymentDetailMode}
                  referenceNo={paymentDetailReferenceNo}
                  setReferenceNo={setPaymentDetailReferenceNo}
                  notes={paymentDetailNotes}
                  setNotes={setPaymentDetailNotes}
                  customPostAmountSection={paymentDetailLoanDisbursementSection}
                  voucherInputRef={paymentVoucherInputRef}
                  voucherFile={null}
                  setVoucherFile={() => {}}
                  voucherRequired={false}
                  existingVoucherUrl={String(paymentDetailTxn?.voucherUrl || '')}
                  drawerWidth={paymentPanelWidth}
                  onHeaderAction={
                    isAdmin
                      ? () => {
                          if (paymentDetailEditMode) {
                            hydratePaymentDetailForm(paymentDetailTxn);
                            setPaymentDetailEditMode(false);
                            setPaymentDetailError(null);
                            return;
                          }
                          setPaymentDetailEditMode(true);
                        }
                      : null
                  }
                  headerActionIcon={<EditOutlinedIcon fontSize="small" />}
                  headerActionTitle={paymentDetailEditMode ? 'Cancel Edit' : 'Edit'}
                  showExtraFooterAction={false}
                  onExtraFooterAction={() => {
                    if (paymentDetailEditMode) {
                      hydratePaymentDetailForm(paymentDetailTxn);
                      setPaymentDetailEditMode(false);
                      setPaymentDetailError(null);
                      return;
                    }
                    setPaymentDetailEditMode(true);
                  }}
                  readOnly={!paymentDetailEditMode}
                />
              ) : null}

              {booking?.notes ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {booking.notes}
                </Typography>
              ) : null}
		            </Box>

		              <Box
                    sx={{
                      ...sectionSx,
                      order: 1,
                      display:
                        isUnitBookedOrSold && ((!pageMode && !focusSection) || isPageSectionVisible('pending-work'))
                          ? 'block'
                          : 'none',
                      '& .pending-work-body': { gap: 1 },
                      '& .pending-work-body .MuiPaper-root': {
                        p: '10px !important',
                        borderRadius: 2,
                        boxShadow: 'none',
                      },
                      '& .pending-work-body .MuiButton-root': {
                        minHeight: 34,
                        py: '4px !important',
                        px: '12px !important',
                        fontSize: '0.875rem',
                      },
                      '& .pending-work-body .MuiChip-root': {
                        height: 28,
                      },
                      '& .pending-work-body .MuiPaper-root .MuiStack-root': {
                        gap: '4px !important',
                      },
                      '& .pending-work-body .MuiTypography-root': {
                        fontSize: '0.875rem',
                        lineHeight: 1.3,
                      },
                      '& .pending-work-body .MuiTypography-body2, & .pending-work-body .MuiTypography-caption': {
                        fontWeight: 500,
                      },
                      '& .pending-work-body .MuiTypography-subtitle2': {
                        fontWeight: 800,
                      },
                      '& .pending-work-body .MuiPaper-root .MuiTypography-root': {
                        mb: '0 !important',
                      },
                      '& .pending-work-body .MuiDivider-root': {
                        my: 0.5,
                      },
                    }}
                  >
                  {(() => {
                    const gstPendingAmount = Math.max(
                      0,
                      computeGstAmount(Number(unit?.unitAmount || 0) || 0) - gstReceiptTotal
                    );
                    if (!gstPendingAmount) return null;
                    const hasGstPaymentStepVisible = mainWorkflowV2Pending.some(
                      (step) =>
                        String(step?.actionType || '').toLowerCase() === 'add_payment' &&
                        String((step as any)?.paymentType || '').toLowerCase() === 'gst'
                    );
                    // If GST step is already visible in Pending Work, don't show GST in two places.
                    if (hasGstPaymentStepVisible) return null;
                    return (
                      <Paper
                        variant="outlined"
                        sx={{
                          mb: 1,
                          px: 1.25,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: '#fff7ed',
                          borderColor: '#fdba74',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            GST pending: ₹ {gstPendingAmount.toLocaleString('en-IN')}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openGstPaymentUnified({ which: 'main', key: OFFICE_MAIN_STEP_KEYS.downpaymentGst })}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Add GST payment
                          </Button>
                        </Stack>
                      </Paper>
                    );
                  })()}
		                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 0.75 }}>
		                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
		                    Pending Work
		                  </Typography>
	                  <Box sx={{ flex: 1 }} />
	                  {mainCase ? (
	                    <Stack direction="row" spacing={0.75} alignItems="center">
	                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
	                        Loan?
	                      </Typography>
	                      <Tooltip title="Yes">
	                        <span>
	                          <IconButton
	                            size="small"
	                            onClick={() => setLoanRequiredValue('yes')}
	                            sx={{
	                              p: 0.35,
	                              width: 26,
	                              height: 26,
	                              borderRadius: 2,
	                              border: '1px solid',
	                              borderColor:
	                                String(loanRequired || '').toLowerCase() === 'yes'
	                                  ? 'rgba(25,118,210,0.35)'
	                                  : 'rgba(15,23,42,0.12)',
	                              bgcolor:
	                                String(loanRequired || '').toLowerCase() === 'yes'
	                                  ? 'rgba(25,118,210,0.10)'
	                                  : '#f8fafc',
	                            }}
	                            aria-label="Loan required: yes"
	                          >
	                            <CheckOutlinedIcon
	                              sx={{
	                                fontSize: 16,
	                                color: String(loanRequired || '').toLowerCase() === 'yes' ? 'primary.main' : 'text.secondary',
	                              }}
	                            />
	                          </IconButton>
	                        </span>
	                      </Tooltip>
	                      <Tooltip title="No">
	                        <span>
	                          <IconButton
	                            size="small"
	                            onClick={() => setLoanRequiredValue('no')}
	                            sx={{
	                              p: 0.35,
	                              width: 26,
	                              height: 26,
	                              borderRadius: 2,
	                              border: '1px solid',
	                              borderColor:
	                                String(loanRequired || '').toLowerCase() === 'no'
	                                  ? 'rgba(25,118,210,0.35)'
	                                  : 'rgba(15,23,42,0.12)',
	                              bgcolor:
	                                String(loanRequired || '').toLowerCase() === 'no'
	                                  ? 'rgba(25,118,210,0.10)'
	                                  : '#f8fafc',
	                            }}
	                            aria-label="Loan required: no"
	                          >
	                            <CloseIcon
	                              sx={{
	                                fontSize: 16,
	                                color: String(loanRequired || '').toLowerCase() === 'no' ? 'primary.main' : 'text.secondary',
	                              }}
	                            />
	                          </IconButton>
	                        </span>
	                      </Tooltip>
	                      {String(loanRequired || '').toLowerCase() === 'yes' && !loanCase ? (
	                        <Button
	                          size="small"
	                          variant="contained"
	                          onClick={() => void startLoanWorkflowCase()}
	                          sx={{ fontWeight: 700, textTransform: 'none' }}
	                        >
	                          Start Loan
	                        </Button>
	                      ) : null}
	                    </Stack>
	                  ) : null}
	                </Stack>

                {mainWorkflowDefinition && !!bookingDocsByKey?.sale_agreement?.files?.length && nearFullPayment(unit?.unitAmount, customerReceiptTotal + loanDisbursementReceiptTotal) ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    About 10% or less remains on the unit price. You may invite the customer to complete the balance for sale deed preparation; follow the agreed payment due dates.
                  </Alert>
                ) : null}
                {mainWorkflowDefinition || (String(loanRequired || '').toLowerCase() === 'yes' && loanWorkflowDefinition) ? (
                  !pendingHydrationReady ? (
                    <Alert severity="info">Loading pending workflow…</Alert>
                  ) : !mainCase ? (
                    <Alert severity="warning">
                      Starting workflow automatically for this booked unit...
                    </Alert>
                  ) : drawerWorkflowV2Sections.length === 0 ? (
                    <Alert severity="success">All new workflow steps are completed.</Alert>
                  ) : (
                    <Stack spacing={1}>
                      {drawerWorkflowV2Sections.map((section) => (
                        <Stack key={`drawer_section_${section.key}`} spacing={1}>
                          {section.title ? (
                            <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.25 }}>
                              {section.title}
                            </Typography>
                          ) : null}
                          {section.steps.map((step) => {
                            const action = section.which === 'loan' ? mapLoanV2StepAction(step) : mapMainV2StepAction(step);
                            const isUploadDoc = String(step.actionType || '').toLowerCase() === 'upload_document';
                            const isGenerateDoc = String(step.actionType || '').toLowerCase() === 'generate_document';
                            const isChecklist = String(step.actionType || '').toLowerCase() === 'checklist';
                            const isYesNo = String(step.actionType || '').toLowerCase() === 'toggle_yes_no';
                            const answerFieldKey = String(step.fieldKey || step.key).trim();
                            const savedAnswer = getCaseStepData(section.which, step.key)?.[answerFieldKey];
                            const isInputValue = String(step.actionType || '').toLowerCase() === 'input_value';
                            const isAddBankLead =
                              section.which === 'loan' && String(step.actionType || '').toLowerCase() === 'add_bank_lead';
                            const v2DocKey = String((step as any)?.docKey || '').trim();
                            const v2Uploaded = v2DocKey ? !!bookingDocsByKey[v2DocKey]?.files?.length : false;
                            const v2Uploading = v2DocKey ? isDocUploadBusy(v2DocKey) : false;
                            const v2UploadLabel = v2DocKey ? getDocUploadActionLabel(v2DocKey, v2Uploaded) : 'Upload';
                            const v2GenerateDocKey = isGenerateDoc ? String((step as any)?.docKey || '').trim() : '';
                            const v2AlreadyGenerated = v2GenerateDocKey
                              ? !!(bookingDocsByKey[v2GenerateDocKey]?.files || []).some((f) => !!f?.url)
                              : false;
                            const v2IsRecentDone = (step as any).status === 'recent_done';
                            const v2HideInMs = v2IsRecentDone
                              ? Math.max(0, Number((step as any)?.hideAtMs || 0) - Date.now())
                              : 0;
                            const v2HideInMinutes = Math.floor(v2HideInMs / 60000);
                            const v2HideInSeconds = Math.floor((v2HideInMs % 60000) / 1000);
                            const v2HideCountdownLabel =
                              v2IsRecentDone && v2HideInMs > 0
                                ? `${String(v2HideInMinutes).padStart(2, '0')}:${String(v2HideInSeconds).padStart(2, '0')} left`
                                : '';
                            const checklistItems = getChecklistItemsForStep(section.which, step);
                            const checklistFieldKey = String((step as any)?.fieldKey || '').trim();
                            const checklistStepData = getCaseStepData(section.which, step.key);
                            const checklistState = checklistFieldKey
                              ? (((checklistStepData as any)?.[checklistFieldKey] || {}) as Record<string, boolean>)
                              : {};
                            const inputFieldKey = String((step as any)?.fieldKey || step.key || '').trim();
                            const inputFieldType = String((step as any)?.inputType || 'text').trim().toLowerCase();
                            const inputStepData = getCaseStepData(section.which, step.key);
                            const inputDraftKey = inputFieldKey ? makeDraftKey(section.which, step.key, inputFieldKey) : '';
                            const inputRemoteValue = inputFieldKey ? (inputStepData as any)?.[inputFieldKey] : '';
                            const inputRawValue = inputDraftKey
                              ? (stepFieldDrafts[inputDraftKey] ??
                                (inputRemoteValue == null ? '' : String(inputRemoteValue)))
                              : '';
                            const inputValueChanged = inputDraftKey && String(inputRawValue) !== String(inputRemoteValue ?? '');
                            const bankLeadStepData = getCaseStepData(section.which, step.key);
                            const bankLeadRows = isAddBankLead && Array.isArray((bankLeadStepData as any)?.otherBankLeads)
                              ? (((bankLeadStepData as any)?.otherBankLeads || []) as Array<Record<string, any>>)
                              : [];
                            const bankLeadHistory = (() => {
                              const bankSet = new Set<string>();
                              const contactNameSet = new Set<string>();
                              const contactNumberSet = new Set<string>();
                              const nameToNumber = new Map<string, string>();
                              const numberToName = new Map<string, string>();
                              for (const row of bankLeadRows) {
                                const bankName = String(row?.bankName || '').trim();
                                const contactName = String(row?.contactName || '').trim();
                                const contactNumber = String(row?.contactNumber || '').trim();
                                if (bankName) bankSet.add(bankName);
                                if (contactName) contactNameSet.add(contactName);
                                if (contactNumber) contactNumberSet.add(contactNumber);
                                if (contactName && contactNumber) {
                                  const nameKey = contactName.toLowerCase();
                                  if (!nameToNumber.has(nameKey)) nameToNumber.set(nameKey, contactNumber);
                                  if (!numberToName.has(contactNumber)) numberToName.set(contactNumber, contactName);
                                }
                              }
                              return {
                                bankNames: Array.from(bankSet).sort((a, b) => a.localeCompare(b)),
                                contactNames: Array.from(contactNameSet).sort((a, b) => a.localeCompare(b)),
                                contactNumbers: Array.from(contactNumberSet).sort((a, b) => a.localeCompare(b)),
                                nameToNumber,
                                numberToName,
                              };
                            })();
                            const normalizedBankLeadContactNumber = normalizeBankContactNumber(loanOtherBankContactNumber);
                            const isBankLeadContactValid = isValidBankContactNumber(normalizedBankLeadContactNumber);
                            return (
                              <Paper
                                key={`v2_${section.key}_${step.key}`}
                                variant="outlined"
                                sx={{
                                  p: 1,
                                  borderRadius: 2,
                                  bgcolor: v2IsRecentDone ? '#ecfdf5' : undefined,
                                  borderColor: v2IsRecentDone ? '#86efac' : 'divider',
                                  '&:hover .loan-collect-done-btn': {
                                    opacity: 1,
                                  },
                                }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                  <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {resolveWorkflowStepTitle(step, section.which)}
                                      </Typography>
                                      {v2IsRecentDone && v2HideCountdownLabel ? (
                                        <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.disabled' }}>
                                          {v2HideCountdownLabel}
                                        </Typography>
                                      ) : null}
                                      {section.which === 'main' &&
                                      String(step.actionType || '').toLowerCase() === 'add_customer' &&
                                      step.status === 'ready' ? (
                                        <Tooltip title={customers.length > 0 ? 'Mark done' : 'Add at least one customer first'}>
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={customers.length <= 0}
                                              onClick={() =>
                                                setStepDone('main', step.key, true).catch((err: any) =>
                                                  setError(err?.message || 'Failed to mark step done.')
                                                )
                                              }
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                border: '1px solid',
                                                borderColor: customers.length > 0 ? '#86efac' : 'divider',
                                                bgcolor: customers.length > 0 ? '#dcfce7' : '#f8fafc',
                                                color: customers.length > 0 ? '#16a34a' : 'text.secondary',
                                                borderRadius: '50%',
                                              }}
                                            >
                                              <CheckOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : null}
                                      {section.which === 'loan' &&
                                      isChecklist &&
                                      step.status === 'ready' ? (
                                        <Tooltip title="Mark done">
                                          <span>
                                            <IconButton
                                              size="small"
                                              className="loan-collect-done-btn"
                                              onClick={() =>
                                                setStepDone('loan', step.key, true).catch((err: any) =>
                                                  setError(err?.message || 'Failed to mark step done.')
                                                )
                                              }
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                border: '1px solid',
                                                borderColor: '#86efac',
                                                bgcolor: '#dcfce7',
                                                color: '#16a34a',
                                                borderRadius: '50%',
                                                opacity: 0,
                                                transition: 'opacity 0.15s ease',
                                              }}
                                            >
                                              <CheckOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : null}
                                    </Stack>
                                    {step.helpText ? (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                        {resolveWorkflowStepHelpText(step, section.which)}
                                      </Typography>
                                    ) : null}
                                    {step.status === 'blocked' && step.blockedBy?.length ? (
                                      <Typography variant="caption" color="text.secondary">
                                        Blocked by: {step.blockedBy.join(', ')}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    {isUploadDoc && v2DocKey ? (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color={v2Uploaded ? 'secondary' : 'primary'}
                                        component="label"
                                        disabled={v2Uploading}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                      >
                                        {v2UploadLabel === 'Upload' ? 'Upload' : v2UploadLabel}
                                        <input
                                          hidden
                                          id={section.which === 'loan' ? `loan-v2-upload-${step.key}` : undefined}
                                          type="file"
                                          accept="image/*,application/pdf"
                                          onChange={async (e) => {
                                            const f = (e.target.files || [])[0] || null;
                                            if (!f) return;
                                            const input = e.target as HTMLInputElement;
                                            if (input) input.value = '';
                                            setStepDocPickedFiles((prev) => ({ ...prev, [v2DocKey]: f }));
                                            setStepDocUploadPhase((prev) => ({ ...prev, [v2DocKey]: 'idle' }));
                                            await uploadStepRequiredDoc(v2DocKey, resolveWorkflowStepTitle(step, section.which), f);
                                          }}
                                        />
                                      </Button>
                                    ) : isGenerateDoc ? (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color={
                                          stepGeneratePhase[`${section.which}:${step.key}`] === 'done' || v2AlreadyGenerated
                                            ? 'secondary'
                                            : 'primary'
                                        }
                                        disabled={stepGeneratePhase[`${section.which}:${step.key}`] === 'processing'}
                                        onClick={() => openGenerateWorkflowStep(section.which, step)}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                      >
                                        {stepGeneratePhase[`${section.which}:${step.key}`] === 'processing'
                                          ? 'Generating…'
                                          : stepGeneratePhase[`${section.which}:${step.key}`] === 'done'
                                            ? 'Generated'
                                            : v2AlreadyGenerated
                                              ? 'Regenerate'
                                              : 'Generate'}
                                      </Button>
                                    ) : isAddBankLead ? (
                                      <Tooltip title="Add bank lead">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => setLoanOtherBankFormOpen((prev) => !prev)}
                                            sx={{
                                              p: 0.25,
                                              minWidth: 0,
                                              color: 'text.secondary',
                                              bgcolor: 'transparent',
                                              border: 'none',
                                              '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                                            }}
                                          >
                                            <AddOutlinedIcon sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : isYesNo ? (
                                      <Stack direction="row" spacing={0.5} aria-label={`${step.title} answer`}>
                                        {([true, false] as const).map((answer) => (
                                          <Button
                                            key={String(answer)}
                                            size="small"
                                            variant={savedAnswer === answer ? 'contained' : 'outlined'}
                                            onClick={() => saveYesNoAnswer(section.which, step, answer).catch((err: any) => setError(err?.message || 'Failed to save answer.'))}
                                            sx={{ textTransform: 'none', fontWeight: 700 }}
                                          >
                                            {answer ? 'Yes' : 'No'}
                                          </Button>
                                        ))}
                                      </Stack>
                                    ) : isInputValue ? (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!inputFieldKey || !inputValueChanged || !String(inputRawValue || '').trim()}
                                        onClick={async () => {
                                          await saveInputValueStep({
                                            which: section.which,
                                            stepKey: step.key,
                                            fieldKey: inputFieldKey,
                                            inputType: inputFieldType,
                                            rawValue: inputRawValue,
                                            draftKey: inputDraftKey,
                                          });
                                        }}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                      >
                                        Save
                                      </Button>
                                    ) : action ? (
                                      <Button size="small" variant="contained" onClick={action.run} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                        {action.label}
                                      </Button>
                                    ) : null}
                                  </Stack>
                                </Stack>
                                {isChecklist && checklistItems.length ? (
                                  <Stack spacing={0.5} sx={{ mt: 0.75, px: 0.25 }}>
                                    {checklistItems.map((item) => {
                                      const checked = Boolean(checklistState[item.key]);
                                      return (
                                        <Stack
                                          key={`v2_${section.key}_${step.key}_${item.key}`}
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          justifyContent="space-between"
                                          sx={{ pl: 1 }}
                                        >
                                          <Typography variant="body2">• {item.label}</Typography>
                                          <IconButton
                                            size="small"
                                            disabled={v2IsRecentDone || !checklistFieldKey}
                                            onClick={() =>
                                              updateStepField(section.which, step.key, checklistFieldKey, {
                                                ...checklistState,
                                                [item.key]: !checked,
                                              })
                                            }
                                            sx={{
                                              border: '1px solid',
                                              borderColor: checked ? '#86efac' : 'divider',
                                              bgcolor: checked ? '#dcfce7' : '#f8fafc',
                                              color: checked ? '#16a34a' : 'text.secondary',
                                              borderRadius: 1.5,
                                            }}
                                          >
                                            <CheckOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                ) : null}
                                {isInputValue ? (
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1, px: 0.25 }}>
                                    <TextField
                                      size="small"
                                      type={inputFieldType === 'date' ? 'date' : inputFieldType === 'number' ? 'number' : 'text'}
                                      multiline={inputFieldType === 'textarea'}
                                      minRows={inputFieldType === 'textarea' ? 2 : undefined}
                                      value={inputRawValue}
                                      onChange={(e) => {
                                        if (!inputDraftKey) return;
                                        const nextValue = String(e.target.value || '');
                                        setStepFieldDrafts((prev) => ({ ...prev, [inputDraftKey]: nextValue }));
                                      }}
                                      onBlur={async () => {
                                        if (!inputDraftKey || !inputFieldKey || !inputValueChanged) return;
                                        try {
                                          await saveInputValueStep({
                                            which: section.which,
                                            stepKey: step.key,
                                            fieldKey: inputFieldKey,
                                            inputType: inputFieldType,
                                            rawValue: inputRawValue,
                                            draftKey: inputDraftKey,
                                          });
                                        } catch {
                                          // no-op
                                        }
                                      }}
                                      fullWidth
                                      label={resolveWorkflowStepTitle(step, section.which)}
                                      InputLabelProps={inputFieldType === 'date' ? { shrink: true } : undefined}
                                    />
                                  </Stack>
                                ) : null}
                                {section.which === 'main' && step.key === 'send_deed_package' ? (
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                    {([
                                      ['Signed booking form', bookingDocsByKey[BOOKING_FORM_SIGNED_DOC_KEY]?.files?.[0] || bookingDocsByKey.bookingFormSigned?.files?.[0]],
                                      ['Payment details', bookingDocsByKey.paymentDetailsDraft?.files?.[0]],
                                      ['Registered sale agreement', bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.[0] || bookingDocsByKey.sale_agreement?.files?.[0]],
                                    ] as Array<[string, any]>).map(([label, file]) => (
                                      <Button key={label} size="small" disabled={!file?.url} onClick={() => triggerNamedDownload(String(file.url), label, String(file.originalName || file.name || ''))}>
                                        {label}
                                      </Button>
                                    ))}
                                  </Stack>
                                ) : null}
                                {section.which === 'main' && step.key === 'fitout_access_letter' ? (
                                  <Button size="small" sx={{ alignSelf: 'flex-start', mt: 0.5 }} onClick={() => downloadFitoutAccessForm({
                                    project: projectName,
                                    building: String((unit as any)?.buildingNo || (unit as any)?.building_no || ''),
                                    unit: String(unit?.unitNumber || unit?.id || ''),
                                    customer: primaryCustomerHeaderName,
                                  }).catch((err: any) => setError(err?.message || 'Could not create fit-out form.'))}>
                                    Download fit-out form and checklist
                                  </Button>
                                ) : null}
                                {section.which === 'main' && String(step.actionType || '') === 'deed_charges_paid' ? (
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Stamp ₹{stampDutyReceiptTotal.toLocaleString('en-IN')} · Registration ₹{registrationReceiptTotal.toLocaleString('en-IN')} · Deed advocate ₹{advocateDeedReceiptTotal.toLocaleString('en-IN')}
                                    </Typography>
                                    <Button size="small" onClick={() => openAddPayment({ unitId: unit?.id, unitNumber: unit?.unitNumber || '', bookingId, receiptCategory: 'advocate' })}>
                                      Add charge receipt
                                    </Button>
                                  </Stack>
                                ) : null}
                                {isAddBankLead ? (
                                  <Stack spacing={0.75} sx={{ mt: 0.75, px: 0.25 }}>
                                    {loanOtherBankFormOpen ? (
                                      <>
                                        <Autocomplete
                                          freeSolo
                                          options={mergeBankNameOptions(officeDocumentLinkedBankNames, bankLeadHistory.bankNames)}
                                          value={loanOtherBankName}
                                          onChange={(_, value) => setLoanOtherBankName(String(value || ''))}
                                          onInputChange={(_, value) => setLoanOtherBankName(String(value || ''))}
                                          sx={{ width: '100%' }}
                                          renderInput={(params) => <TextField {...params} size="small" label="Bank name" fullWidth />}
                                        />
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                          <Autocomplete
                                            freeSolo
                                            options={bankLeadHistory.contactNames}
                                            value={loanOtherBankContactName}
                                            onChange={(_, value) => {
                                              const next = String(value || '');
                                              setLoanOtherBankContactName(next);
                                              const mapped = bankLeadHistory.nameToNumber.get(next.trim().toLowerCase());
                                              if (mapped) setLoanOtherBankContactNumber(mapped);
                                            }}
                                            onInputChange={(_, value) => {
                                              const next = String(value || '');
                                              setLoanOtherBankContactName(next);
                                              const mapped = bankLeadHistory.nameToNumber.get(next.trim().toLowerCase());
                                              if (mapped) setLoanOtherBankContactNumber(mapped);
                                            }}
                                            sx={{ flex: 1 }}
                                            renderInput={(params) => <TextField {...params} size="small" label="Bank person name" />}
                                          />
                                          <Autocomplete
                                            freeSolo
                                            options={bankLeadHistory.contactNumbers}
                                            value={loanOtherBankContactNumber}
                                            onChange={(_, value) => {
                                              const next = normalizeBankContactNumber(String(value || ''));
                                              setLoanOtherBankContactNumber(next);
                                              const mapped = bankLeadHistory.numberToName.get(next.trim());
                                              if (mapped) setLoanOtherBankContactName(mapped);
                                            }}
                                            onInputChange={(_, value) => {
                                              const next = normalizeBankContactNumber(String(value || ''));
                                              setLoanOtherBankContactNumber(next);
                                              const mapped = bankLeadHistory.numberToName.get(next.trim());
                                              if (mapped) setLoanOtherBankContactName(mapped);
                                            }}
                                            sx={{ flex: 1 }}
                                            renderInput={(params) => (
                                              <TextField
                                                {...params}
                                                size="small"
                                                label="Contact number"
                                                error={!!normalizedBankLeadContactNumber && !isBankLeadContactValid}
                                              />
                                            )}
                                          />
                                        </Stack>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                          <TextField
                                            size="small"
                                            label="Remarks"
                                            value={loanOtherBankRemarks}
                                            onChange={(e) => setLoanOtherBankRemarks(e.target.value)}
                                            sx={{ flex: 1 }}
                                          />
                                          <Button
                                            size="small"
                                            variant="contained"
                                            disabled={
                                              !String(loanOtherBankName || '').trim() ||
                                              !String(loanOtherBankContactName || '').trim() ||
                                              (!!String(loanOtherBankContactNumber || '').trim() && !isBankLeadContactValid)
                                            }
                                            onClick={async () => {
                                              try {
                                                const name = String(loanOtherBankName || '').trim();
                                                const contactName = String(loanOtherBankContactName || '').trim();
                                                const contactNumber = normalizeBankContactNumber(loanOtherBankContactNumber);
                                                if (!name || !contactName) return;
                                                if (contactNumber && !isValidBankContactNumber(contactNumber)) return;
                                                const current = Array.isArray((bankLeadStepData as any)?.otherBankLeads)
                                                  ? ([...(bankLeadStepData as any).otherBankLeads] as Array<Record<string, any>>)
                                                  : [];
                                                current.push({
                                                  id: `lead_${Date.now()}`,
                                                  bankName: name,
                                                  contactName,
                                                  contactNumber: contactNumber || '',
                                                  sentOn: String(loanOtherBankSentOn || ymdToday()),
                                                  remarks: String(loanOtherBankRemarks || '').trim(),
                                                });
                                                await updateStepField('loan', step.key, 'otherBankLeads', current);
                                                setLoanOtherBankName('');
                                                setLoanOtherBankContactName('');
                                                setLoanOtherBankContactNumber('');
                                                setLoanOtherBankSentOn(ymdToday());
                                                setLoanOtherBankRemarks('');
                                                setLoanOtherBankFormOpen(false);
                                              } catch (err: any) {
                                                setError(err?.message || 'Failed to add bank lead.');
                                              }
                                            }}
                                            sx={{ textTransform: 'none', fontWeight: 700 }}
                                          >
                                            Add Bank
                                          </Button>
                                        </Stack>
                                      </>
                                    ) : null}
                                    {bankLeadRows.length ? (
                                      <Stack spacing={0.5}>
                                        {bankLeadRows.map((lead, index) => {
                                          const leadId = String(lead?.id || `lead_${index}`);
                                          const isEditingRemark = loanOtherBankRemarkEditId === leadId;
                                          const leadStatus = String(lead?.status || 'sent');
                                          const followupTimeline = Array.isArray((lead as any)?.followupTimeline)
                                            ? ((lead as any).followupTimeline as Array<Record<string, any>>)
                                            : [];
                                          const orderedTimeline = followupTimeline.slice().reverse();
                                          const hasMoreTimeline = orderedTimeline.length > 3;
                                          const isTimelineExpanded = !!loanOtherBankTimelineExpanded[leadId];
                                          const visibleTimeline =
                                            hasMoreTimeline && !isTimelineExpanded ? orderedTimeline.slice(0, 3) : orderedTimeline;
                                          const leadStatusLabel =
                                            leadStatus === 'follow_up'
                                              ? 'Follow-up'
                                              : leadStatus === 'not_responding'
                                                ? 'Not Responding'
                                                : leadStatus === 'rejected_by_bank'
                                                  ? 'Rejected by Bank'
                                                  : leadStatus === 'rejected_by_buyer'
                                                    ? 'Rejected by Buyer'
                                                    : 'Sent';
                                          const submitBankFollowup = async () => {
                                            try {
                                              const next = Array.isArray((bankLeadStepData as any)?.otherBankLeads)
                                                ? ([...(bankLeadStepData as any).otherBankLeads] as Array<Record<string, any>>)
                                                : [];
                                              const row = { ...(next[index] || {}) };
                                              const timeline = Array.isArray(row?.followupTimeline)
                                                ? [...row.followupTimeline]
                                                : [];
                                              timeline.push({
                                                atMs: Date.now(),
                                                status: String(loanOtherBankStatusEdit || 'sent'),
                                                remark: String(loanOtherBankRemarkDraft || '').trim(),
                                                by: currentUser?.email || null,
                                              });
                                              row.followupTimeline = timeline;
                                              row.status = String(loanOtherBankStatusEdit || 'sent');
                                              next[index] = row;
                                              await updateStepField('loan', step.key, 'otherBankLeads', next);
                                              setLoanOtherBankRemarkEditId('');
                                              setLoanOtherBankStatusEdit('sent');
                                              setLoanOtherBankRemarkDraft('');
                                            } catch (err: any) {
                                              setError(err?.message || 'Failed to update remark.');
                                            }
                                          };
                                          return (
                                            <Stack key={leadId} spacing={0.5} sx={{ pl: 0.5 }}>
                                              <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={1}
                                                alignItems={{ sm: 'center' }}
                                                justifyContent="space-between"
                                              >
                                                <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>
                                                  {index + 1}. {String(lead?.bankName || 'Bank')}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                  <Chip size="small" variant="outlined" label={leadStatusLabel} />
                                                  <Tooltip title="Edit remark">
                                                    <span>
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                          setLoanOtherBankRemarkEditId(leadId);
                                                          setLoanOtherBankStatusEdit('follow_up');
                                                          setLoanOtherBankRemarkDraft('');
                                                        }}
                                                        sx={{
                                                          p: 0.25,
                                                          minWidth: 0,
                                                          color: 'text.secondary',
                                                          bgcolor: 'transparent',
                                                          border: 'none',
                                                          '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                                                        }}
                                                      >
                                                        <EditOutlinedIcon sx={{ fontSize: 14 }} />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                  {canRemoveOtherBankLead && !isEditingRemark ? (
                                                    <Tooltip title="Remove">
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          onClick={async () => {
                                                            try {
                                                              const ok = window.confirm('Are you sure you want to remove this bank lead entry?');
                                                              if (!ok) return;
                                                              const next = Array.isArray((bankLeadStepData as any)?.otherBankLeads)
                                                                ? ([...(bankLeadStepData as any).otherBankLeads] as Array<Record<string, any>>)
                                                                : [];
                                                              next.splice(index, 1);
                                                              await updateStepField('loan', step.key, 'otherBankLeads', next);
                                                            } catch (err: any) {
                                                              setError(err?.message || 'Failed to remove bank lead.');
                                                            }
                                                          }}
                                                          sx={{
                                                            p: 0.25,
                                                            minWidth: 0,
                                                            color: 'error.main',
                                                            bgcolor: 'transparent',
                                                            border: 'none',
                                                            '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                                                          }}
                                                        >
                                                          <CloseIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                  ) : null}
                                                </Stack>
                                              </Stack>
                                              <Stack sx={{ minWidth: 0, flex: 1, pl: 1.25 }} spacing={0.35}>
                                                <Typography variant="caption" color="text.secondary">
                                                  {String(lead?.contactName || '-')} · {String(lead?.contactNumber || '-')}
                                                </Typography>
                                                {String(lead?.remarks || '').trim() ? (
                                                  <Typography variant="caption" color="text.secondary">
                                                    {String(lead?.remarks || '').trim()}
                                                  </Typography>
                                                ) : null}
                                                {visibleTimeline.map((entry, entryIndex) => {
                                                  const status = String(entry?.status || 'sent');
                                                  const statusLabel =
                                                    status === 'follow_up'
                                                      ? 'Follow-up'
                                                      : status === 'not_responding'
                                                        ? 'Not Responding'
                                                        : status === 'rejected_by_bank'
                                                          ? 'Rejected by Bank'
                                                          : status === 'rejected_by_buyer'
                                                            ? 'Rejected by Buyer'
                                                            : 'Sent';
                                                  const remark = String(entry?.remark || '').trim();
                                                  const showExpandOnRow =
                                                    hasMoreTimeline && !isTimelineExpanded && entryIndex === 2;
                                                  return (
                                                    <Stack
                                                      key={`${leadId}_entry_${entryIndex}`}
                                                      direction="row"
                                                      alignItems="center"
                                                      justifyContent="space-between"
                                                      spacing={0.5}
                                                      sx={{ pl: 0.5 }}
                                                    >
                                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                                                        {formatDDMMYYYY(Number(entry?.atMs || 0))} · {statusLabel}
                                                        {remark ? ` · ${remark}` : ''}
                                                      </Typography>
                                                      {showExpandOnRow ? (
                                                        <IconButton
                                                          size="small"
                                                          onClick={() =>
                                                            setLoanOtherBankTimelineExpanded((prev) => ({ ...prev, [leadId]: true }))
                                                          }
                                                          sx={{ p: 0.25, minWidth: 0, color: 'text.secondary' }}
                                                        >
                                                          <ExpandMoreIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                      ) : null}
                                                    </Stack>
                                                  );
                                                })}
                                                {hasMoreTimeline && isTimelineExpanded ? (
                                                  <Stack direction="row" justifyContent="flex-end">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        setLoanOtherBankTimelineExpanded((prev) => ({ ...prev, [leadId]: false }))
                                                      }
                                                      sx={{ p: 0.25, minWidth: 0, color: 'text.secondary' }}
                                                    >
                                                      <ExpandMoreIcon sx={{ fontSize: 14, transform: 'rotate(180deg)' }} />
                                                    </IconButton>
                                                  </Stack>
                                                ) : null}
                                              </Stack>
                                              {isEditingRemark ? (
                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                                                  <FormControl size="small" sx={{ minWidth: 170 }}>
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                      label="Status"
                                                      value={loanOtherBankStatusEdit}
                                                      onChange={(e) => setLoanOtherBankStatusEdit(String(e.target.value || 'sent'))}
                                                    >
                                                      <MenuItem value="sent">Sent</MenuItem>
                                                      <MenuItem value="follow_up">Follow-up</MenuItem>
                                                      <MenuItem value="not_responding">Not Responding</MenuItem>
                                                      <MenuItem value="rejected_by_bank">Rejected by Bank</MenuItem>
                                                      <MenuItem value="rejected_by_buyer">Rejected by Buyer</MenuItem>
                                                    </Select>
                                                  </FormControl>
                                                  <TextField
                                                    size="small"
                                                    value={loanOtherBankRemarkDraft}
                                                    onChange={(e) => setLoanOtherBankRemarkDraft(e.target.value)}
                                                    onKeyDown={async (e) => {
                                                      if (e.key !== 'Enter') return;
                                                      e.preventDefault();
                                                      await submitBankFollowup();
                                                    }}
                                                    placeholder="Add remark"
                                                    sx={{ flex: 1 }}
                                                  />
                                                  <IconButton
                                                    size="small"
                                                    onClick={submitBankFollowup}
                                                    sx={{ p: 0.25, minWidth: 0, color: 'success.main', bgcolor: 'transparent', border: 'none' }}
                                                  >
                                                    <CheckOutlinedIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                      setLoanOtherBankRemarkEditId('');
                                                      setLoanOtherBankStatusEdit('sent');
                                                      setLoanOtherBankRemarkDraft('');
                                                    }}
                                                    sx={{ p: 0.25, minWidth: 0, color: 'text.secondary', bgcolor: 'transparent', border: 'none' }}
                                                  >
                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Stack>
                                              ) : null}
                                            </Stack>
                                          );
                                        })}
                                      </Stack>
                                    ) : null}
                                  </Stack>
                                ) : null}
                                {section.which === 'main' && String(step.key || '').trim().toLowerCase() === 'send_document_to_advocate' ? (
                                  <Box
                                    sx={{
                                      mt: 0.5,
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 1,
                                      flexWrap: 'nowrap',
                                      overflowX: 'auto',
                                      '& .MuiButton-root': { flexShrink: 0, whiteSpace: 'nowrap' },
                                    }}
                                  >
                                    {(() => {
                                      const file = bookingDocsByKey.allotmentLetterDraft?.files?.[0];
                                      if (!file?.url) return <Box sx={{ flex: 1 }} />;
                                      return (
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<DownloadOutlinedIcon fontSize="small" />}
                                          onClick={() =>
                                            triggerNamedDownload(
                                              String(file.url),
                                              'Allotment Letter Draft',
                                              String(file.originalName || file.name || '')
                                            )
                                          }
                                          sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
                                        >
                                          Allotment draft
                                        </Button>
                                      );
                                    })()}
                                    {(() => {
                                      const file = bookingDocsByKey.paymentDetailsDraft?.files?.[0];
                                      if (!file?.url) return <Box sx={{ flex: 1 }} />;
                                      return (
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<DownloadOutlinedIcon fontSize="small" />}
                                          onClick={() =>
                                            triggerNamedDownload(
                                              String(file.url),
                                              'PAYMENT DETAILS',
                                              String(file.originalName || file.name || '')
                                            )
                                          }
                                          sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
                                        >
                                          Payment details
                                        </Button>
                                      );
                                    })()}
                                    {(() => {
                                      const file =
                                        bookingDocsByKey[BOOKING_FORM_SIGNED_DOC_KEY]?.files?.[0] ||
                                        bookingDocsByKey.bookingFormSigned?.files?.[0];
                                      if (!file?.url) return <Box sx={{ flex: 1 }} />;
                                      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
                                      const downloadName = unitNo ? `${unitNo}_BOOKING_FORM_SIGNED` : 'BOOKING_FORM_SIGNED';
                                      return (
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<DownloadOutlinedIcon fontSize="small" />}
                                          onClick={() =>
                                            triggerNamedDownload(
                                              String(file.url),
                                              downloadName,
                                              String(file.originalName || file.name || '')
                                            )
                                          }
                                          sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
                                        >
                                          Signed booking form
                                        </Button>
                                      );
                                    })()}
                                  </Box>
                                ) : null}
                                {section.which === 'loan' && String(step.key || '').trim().toLowerCase() === 'send_sbi_noc_request' ? (
                                  <Box
                                    sx={{
                                      mt: 0.5,
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 1,
                                      flexWrap: 'nowrap',
                                      overflowX: 'auto',
                                      '& .MuiButton-root': { flexShrink: 0, whiteSpace: 'nowrap' },
                                    }}
                                  >
                                    {(() => {
                                      const file =
                                        bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.[0] ||
                                        bookingDocsByKey[LEGACY_SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.[0];
                                      if (!file?.url) return <Box sx={{ flex: 1 }} />;
                                      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
                                      const downloadName = unitNo ? `${unitNo}_SALE_AGREEMENT` : 'SALE_AGREEMENT';
                                      return (
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<DownloadOutlinedIcon fontSize="small" />}
                                          onClick={() =>
                                            triggerNamedDownload(
                                              String(file.url),
                                              downloadName,
                                              String(file.originalName || file.name || '')
                                            )
                                          }
                                          sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
                                        >
                                          Sale agreement
                                        </Button>
                                      );
                                    })()}
                                    {(() => {
                                      const file = bookingDocsByKey['sbi_noc_request_v1']?.files?.[0];
                                      if (!file?.url) return <Box sx={{ flex: 1 }} />;
                                      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
                                      const downloadName = unitNo ? `${unitNo}_SBI_NOC_REQUEST` : 'SBI_NOC_REQUEST';
                                      return (
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<DownloadOutlinedIcon fontSize="small" />}
                                          onClick={() =>
                                            triggerNamedDownload(
                                              String(file.url),
                                              downloadName,
                                              String(file.originalName || file.name || '')
                                            )
                                          }
                                          sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
                                        >
                                          SBI NOC Request
                                        </Button>
                                      );
                                    })()}
                                    {(() => {
                                      const sanctionDocKey = String((step as any)?.docKey || '').trim() || 'sanction_letter';
                                      const file =
                                        [sanctionDocKey, 'sanctionLetter', 'sanction_letter']
                                          .map((key) => String(key || '').trim())
                                          .filter(Boolean)
                                          .map((key) => bookingDocsByKey[key]?.files?.[0])
                                          .find((candidate) => !!candidate?.url) || null;
                                      if (!file?.url) return <Box sx={{ flex: 1 }} />;
                                      const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
                                      const downloadName = unitNo ? `${unitNo}_SANCTION_LETTER` : 'SANCTION_LETTER';
                                      return (
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<DownloadOutlinedIcon fontSize="small" />}
                                          onClick={() =>
                                            triggerNamedDownload(
                                              String(file.url),
                                              downloadName,
                                              String(file.originalName || file.name || '')
                                            )
                                          }
                                          sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
                                        >
                                          Sanction letter
                                        </Button>
                                      );
                                    })()}
                                  </Box>
                                ) : null}
                              </Paper>
                            );
                          })}
                        </Stack>
                      ))}
                    </Stack>
                  )
                ) : (
                  <Alert severity="info">No new workflow is assigned to this project yet. Configure it in Settings.</Alert>
                )}

                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{ bgcolor: 'transparent', '&:before': { display: 'none' }, display: 'none' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Legacy workflow
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0.5, pb: 0 }}>
                {!mainCase ? (
                  <Alert severity="warning">
                    Starting workflow automatically for this booked unit...
                  </Alert>
	                ) : (
	                  <>
	                    <Stack spacing={1.25}>

                      {!currentMainStep ? (
                        <Alert severity="success">All main workflow tasks are completed.</Alert>
                      ) : (() => {
                        const s = currentMainStep;
                        const isGate = s.key === OFFICE_MAIN_STEP_KEYS.loanGateSanction;
                        const isStep1 = s.key === OFFICE_MAIN_STEP_KEYS.customerKycBookingForm;
                        const isAutoGenerateAllotment =
                          s.key === OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter;
                        const isSendToAdvocate = s.key === OFFICE_MAIN_STEP_KEYS.sendToAdvocate;
                        const isSignAtAdvocate = s.key === OFFICE_MAIN_STEP_KEYS.signAtAdvocate;
                        const isUploadSaleAgreement = s.key === OFFICE_MAIN_STEP_KEYS.uploadSignedDocs;
                        const isPayment = s.kind === 'payment';
                        const details = getStepDetails('main', s.key);
                        const canMarkDone = s.status === 'ready' && isStepInputsComplete('main', s) && !isGate && !isStep1;
                        const canMarkInProgress = canMarkDone;
                        const showAuto = isGate || isStep1 || (s.key === OFFICE_MAIN_STEP_KEYS.loanGateSanction && String(loanRequired || '').toLowerCase() !== 'yes');
                        const stepData = getCaseStepData('main', s.key);
                        const isInProgress = Boolean(stepData?.inProgress);

                        return (
	                          <Paper key={s.key} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
	                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              {stepStatusChip(s)}
		                              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
		                                {s.title}
		                              </Typography>
                              {showAuto || isAutoGenerateAllotment ? (
		                                <Chip size="small" label="Auto" variant="outlined" sx={{ fontWeight: 700 }} />
                              ) : null}
                              {canMarkInProgress ? (
                                <Tooltip title={isInProgress ? 'Clear in progress' : 'Mark in progress'}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => updateStepField('main', s.key, 'inProgress', !isInProgress)}
                                      sx={{
                                        border: '1px solid',
                                        borderColor: isInProgress ? '#f59e0b' : 'divider',
                                        bgcolor: isInProgress ? '#ffedd5' : '#f8fafc',
                                        color: isInProgress ? '#d97706' : 'text.secondary',
                                        borderRadius: 1.5,
                                      }}
                                    >
                                      <ScheduleOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {canMarkDone ? (
                                <Tooltip title="Mark done">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => setStepDone('main', s.key, true)}
                                      sx={{
                                        border: '1px solid',
                                        borderColor: '#93c5fd',
                                        bgcolor: '#dbeafe',
                                        color: 'primary.main',
                                        borderRadius: 1.5,
                                      }}
                                    >
                                      <CheckOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Stack>

                            {!isPayment && !isStep1 && details.length ? (
                              <Stack spacing={0.5} sx={{ mb: 1 }}>
                                {details.map((line, idx) => (
                                  <Typography key={`${s.key}-detail-${idx}`} variant="caption" color="text.secondary">
                                    {line}
                                  </Typography>
                                ))}
                              </Stack>
                            ) : null}

                            {!isPayment && s.status === 'blocked' && s.blockedBy?.length ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Blocked by: {s.blockedBy.join(', ')}
                              </Typography>
                            ) : null}

		                    <Stack spacing={0.75} className="pending-work-body">
                              {isStep1 ? (
                                <>
                                  {step1Data.hasApplicantsCount && !step1ApplicantsEditMode ? (
                                    <Paper sx={qaCardCompactSx}>
                                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
	                                        <Box>
	                                          <Typography sx={{ fontWeight: 700, mb: 0.25 }}>How many applicants?</Typography>
	                                          <Typography variant="body2">{step1Data.expectedApplicantsCount}</Typography>
		              </Box>
	                                        <Tooltip title="Edit">
                                          <span>
                                            <IconButton size="small" onClick={() => setStep1ApplicantsEditMode(true)}>
                                              <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </Stack>
                                    </Paper>
                                  ) : (
                                    renderOptionsQuestion({
                                      title: 'How many applicants?',
                                      value: String(step1Data.expectedApplicantsCount),
                                      options: [1, 2, 3, 4, 5].map((n) => ({ key: String(n), label: String(n) })),
                                      onSelect: (k) => {
                                        updateStep1Data({ expectedApplicantsCount: Number(k || 1) || 1 });
                                        setStep1ApplicantsEditMode(false);
                                      },
                                      compact: true,
                                      inlineOptions: true,
                                    })
                                  )}
                                  <Paper sx={qaCardCompactSx}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Box sx={{ minWidth: 0 }}>
		                                            <Typography sx={{ fontWeight: 900, lineHeight: 1.2, mb: '2px' }}>Customers</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0, mb: 0, lineHeight: 1.2 }}>
		                                   Added: {step1CustomersCount} / Required: {step1Data.expectedApplicantsCount}
                                        </Typography>
                                      </Box>
                                      <Button
                                        size="small"
                                        variant={step1CustomersOk ? 'outlined' : 'contained'}
                                        onClick={() => setCustomerDrawerOpen(true)}
                                        sx={step1CustomersOk ? qaOptionButtonCompactSx : { textTransform: 'none', fontWeight: 700 }}
                                      >
                                        {step1CustomersOk ? 'Edit' : 'Add'}
                                      </Button>
                                    </Stack>
                                    {customers.length ? (
                                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {customers.map((customer) => (
                                          <Typography key={`step1_customer_${customer.id}`} variant="caption" color="text.secondary">
                                            {String(customer.displayName || customer.name || 'Customer')} • {String(customer.mobile || '—')} • DOB:{' '}
                                            {String((customer as any)?.dateOfBirth || (customer as any)?.dob || (customer as any)?.birthDate || '').trim() || 'Pending'}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    ) : null}
                                  </Paper>

                                  {step1CustomersCount > 0 && !step1DobOk ? (
                                    <Paper sx={qaCardCompactSx}>
                                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Box sx={{ minWidth: 0 }}>
		                                        <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>Date of birth</Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.125, mb: 0, lineHeight: 1.2 }}>
                                            DOB pending for {step1CustomersMissingDob.length} customer(s).
                                          </Typography>
                                        </Box>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => setCustomerDrawerOpen(true)}
                                          sx={{ textTransform: 'none', fontWeight: 700 }}
                                        >
                                          Add DOB
                                        </Button>
                                      </Stack>
                                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {step1CustomersMissingDob.map((customer) => (
                                          <Typography key={`step1_dob_pending_${customer.id}`} variant="caption" color="text.secondary">
                                            {String(customer.displayName || customer.name || 'Customer')}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    </Paper>
                                  ) : null}

                                  {!step1KycOk ? (
                                    <Paper sx={qaCardCompactSx}>
                                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Box sx={{ minWidth: 0 }}>
		                                        <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>KYC documents</Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.125, mb: 0, lineHeight: 1.2 }}>
                                            {step1KycOk ? 'KYC OK for all customers.' : 'KYC pending for one or more customers.'}
                                          </Typography>
                                        </Box>
                                        <Button size="small" variant="contained" onClick={() => setKycDialogOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                          Add
                                        </Button>
                                      </Stack>
                                    </Paper>
                                  ) : null}

                                  {!step1BookingFormOk
                                    ? renderUploadQuestion({
                                        title: 'Signed booking form',
                                        docKey: BOOKING_FORM_SIGNED_DOC_KEY,
                                        existing: bookingDocsByKey[BOOKING_FORM_SIGNED_DOC_KEY] || bookingDocsByKey[LEGACY_BOOKING_FORM_SIGNED_DOC_KEY] || null,
                                        compact: true,
                                        hideStatusChip: true,
                                        instantUpload: true,
                                        subtitle: 'Upload signed booking form',
                                      })
                                    : null}

                                </>
                              ) : isAutoGenerateAllotment ? (
                                <Paper sx={qaCardCompactSx}>
                                  {bookingDocsByKey.allotmentLetterDraft?.files?.length ? (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                                        {bookingDocsByKey.allotmentLetterDraft.files.map((file: any, fileIdx: number) => (
                                          <Button
                                            key={`allotment-draft-auto-${fileIdx}`}
                                            size="small"
                                            variant="text"
                                            onClick={() =>
                                              file?.url
                                                ? triggerNamedDownload(
                                                    file.url,
                                                    toDraftDisplayLabel(
                                                      String(
                                                        file?.name ||
                                                          file?.originalName ||
                                                          bookingDocsByKey.allotmentLetterDraft?.type ||
                                                          'Allotment Letter Draft'
                                                      ),
                                                      String(bookingDocsByKey.allotmentLetterDraft?.type || 'allotment_letter_v1')
                                                    ),
                                                    String(file?.originalName || file?.name || '')
                                                  )
                                                : undefined
                                            }
                                            sx={{ textTransform: 'none', fontWeight: 800 }}
                                          >
                                            Download Draft
                                        </Button>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      Draft will be auto-generated.
                                    </Typography>
                                  )}
                                  {draftGenerationStatus === 'processing' ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      Processing draft generation...
                                    </Typography>
                                  ) : null}
                                  {draftGenerationStatus === 'done' ? (
                                    <Chip size="small" color="success" variant="outlined" label="Draft regenerated" sx={{ mb: 1 }} />
                                  ) : null}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      runAllotmentDraftGeneration(OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter).catch((err) =>
                                        setError(err?.message || 'Failed to regenerate draft.')
                                      )
                                    }
                                    disabled={draftGenerationStatus === 'processing'}
                                    sx={{ textTransform: 'none', fontWeight: 800 }}
                                  >
                                    {draftGenerationStatus === 'processing' ? 'Processing…' : 'Regenerate Draft'}
                                  </Button>
                                </Paper>
                              ) : isGate ? (
                                <Paper sx={qaCardCompactSx}>
                                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Loan sanction gate</Typography>
                                  {String(loanRequired || '').toLowerCase() === 'yes' ? (
                                    <Typography variant="body2" color="text.secondary">
                                      This step is auto-completed when the loan workflow reaches “Sanction received”.
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      Loan not required. This step is auto-skipped.
                                    </Typography>
                                  )}
                                </Paper>
                              ) : isSendToAdvocate ? (
                                <>
                                <Paper sx={qaCardCompactSx}>
                                  <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontWeight: 500 }}>Send draft allotment letter</Typography>
                                      <Stack direction="row" spacing={0.5}>
                                        {bookingDocsByKey.allotmentLetterDraft?.files?.length ? (
                                          <Tooltip title="Download">
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  const file = bookingDocsByKey.allotmentLetterDraft?.files?.[0];
                                                  if (!file?.url) return;
                                                  triggerNamedDownload(
                                                    file.url,
                                                    toDraftDisplayLabel(
                                                      String(
                                                        file?.name ||
                                                          file?.originalName ||
                                                          bookingDocsByKey.allotmentLetterDraft?.type ||
                                                          'Allotment Letter Draft'
                                                      ),
                                                      String(bookingDocsByKey.allotmentLetterDraft?.type || 'allotment_letter_v1')
                                                    ),
                                                    String(file?.originalName || file?.name || '')
                                                  );
                                                }}
                                              >
                                                <DownloadOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        ) : null}
                                        <Tooltip title="Regenerate">
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                runAllotmentDraftGeneration(s.key).catch((err) =>
                                                  setError(err?.message || 'Failed to regenerate draft.')
                                                )
                                              }
                                              disabled={draftGenerationStatus === 'processing'}
                                            >
                                              <AutorenewOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </Stack>
                                    </Stack>
                                    {!bookingDocsByKey.allotmentLetterDraft?.files?.length ? (
                                      <Alert severity="warning">Draft not found yet. Generate it now.</Alert>
                                    ) : null}
                                    <Divider />
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontWeight: 500 }}>Send booking form</Typography>
                                      {bookingFormDoc?.files?.length ? (
                                        <Tooltip title="Download">
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                const file = bookingFormDoc.files?.[0];
                                                if (!file?.url) return;
                                                const unitNo = String(unit?.unitNumber || unit?.id || '').trim();
                                                const downloadName = unitNo ? `${unitNo}_BOOKING_FORM_SIGNED` : 'BOOKING_FORM_SIGNED';
                                                triggerNamedDownload(
                                                  file.url,
                                                  downloadName,
                                                  String(file?.originalName || file?.name || '')
                                                );
                                              }}
                                            >
                                              <DownloadOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : null}
                                    </Stack>
                                    {!bookingFormDoc?.files?.length ? (
                                      <Alert severity="warning">Signed booking form is not uploaded yet.</Alert>
                                    ) : null}
                                    <Divider />
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontWeight: 500 }}>Send payment details</Typography>
                                      <Stack direction="row" spacing={0.5}>
                                        {bookingDocsByKey.paymentDetailsDraft?.files?.length ? (
                                          <Tooltip title="Download">
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  const file = bookingDocsByKey.paymentDetailsDraft?.files?.[0];
                                                  if (!file?.url) return;
                                                  triggerNamedDownload(
                                                    file.url,
                                                    toDraftDisplayLabel(
                                                      String(
                                                          file?.name ||
                                                          file?.originalName ||
                                                          (bookingDocsByKey.paymentDetailsDraft as any)?.templateId ||
                                                          bookingDocsByKey.paymentDetailsDraft?.type ||
                                                          'PAYMENT DETAILS'
                                                      ),
                                                      String(
                                                        (bookingDocsByKey.paymentDetailsDraft as any)?.templateId ||
                                                          bookingDocsByKey.paymentDetailsDraft?.type ||
                                                          'payment_details_v2'
                                                      )
                                                    ),
                                                    String(file?.originalName || file?.name || '')
                                                  );
                                                }}
                                              >
                                                <DownloadOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        ) : null}
                                        <Tooltip title="Regenerate">
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={paymentDetailsDraftGenerating}
                                              onClick={async () => {
                                                try {
                                                  setPaymentDetailsDraftGenerating(true);
                                                  const effectiveBookingId = bookingId || (await ensureBookingForDraft());
                                                  await generatePaymentDetailsV1Draft(effectiveBookingId);
                                                } catch (err: any) {
                                                  setError(err?.message || 'Failed to regenerate payment details.');
                                                } finally {
                                                  setPaymentDetailsDraftGenerating(false);
                                                }
                                              }}
                                            >
                                              <AutorenewOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </Stack>
                                    </Stack>
                                    {paymentWhatsAppError ? <Alert severity="error" sx={{ mt: 1 }}>{paymentWhatsAppError}</Alert> : null}
                                    {!templateStatuses.loading && !(['en', 'gu', 'hi'] as AutoReplyLang[]).some((lang) => !!templateStatuses.versionsFor('swarna_unit_payment_details', lang).sending) ? (
                                      <Alert severity="info" sx={{ mt: 1 }}>The payment-details WhatsApp template must be approved by Meta before it can be sent.</Alert>
                                    ) : null}
                                    {!bookingDocsByKey.paymentDetailsDraft?.files?.length ? (
                                      <Alert severity="warning">Payment details draft is not generated yet.</Alert>
                                    ) : null}
                                  </Stack>
                                </Paper>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
                                  <Typography sx={{ fontWeight: 500 }}>Sent to advocate?</Typography>
                                  <Stack direction="row" spacing={1}>
                                    {[
                                      { key: 'no', icon: <CloseIcon fontSize="small" /> },
                                      { key: 'yes', icon: <CheckOutlinedIcon fontSize="small" /> },
                                    ].map((option) => {
                                      const selected = (stepData?.sentToAdvocate ? 'yes' : 'no') === option.key;
                                      return (
                                        <Tooltip
                                          key={option.key}
                                          title={option.key === 'yes' ? 'Yes' : 'No'}
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={async () => {
                                                const yes = option.key === 'yes';
                                                await updateStepField('main', s.key, 'sentToAdvocate', yes);
                                                if (!yes) return;
                                                const uploaded = !!bookingDocsByKey.allotmentLetterDraft?.files?.length;
                                                if (uploaded) await setStepDone('main', s.key, true);
                                              }}
                                              sx={{
                                                border: '1px solid',
                                                borderColor: selected ? '#93c5fd' : 'divider',
                                                bgcolor: selected ? '#dbeafe' : '#f8fafc',
                                                color: selected ? 'primary.main' : 'text.secondary',
                                                borderRadius: 1.5,
                                              }}
                                            >
                                              {option.icon}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      );
                                    })}
                                  </Stack>
                                </Stack>
                                </>
                              ) : isSignAtAdvocate ? (
                                <Paper sx={qaCardCompactSx}>
                                  <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontWeight: 500 }}>Customer sent for sale agreement sign?</Typography>
                                      <Stack direction="row" spacing={1}>
                                        {[
                                          { key: 'no', icon: <CloseIcon fontSize="small" /> },
                                          { key: 'yes', icon: <CheckOutlinedIcon fontSize="small" /> },
                                        ].map((option) => {
                                          const selected = (stepData?.customerSentForSign ? 'yes' : 'no') === option.key;
                                          return (
                                            <Tooltip key={option.key} title={option.key === 'yes' ? 'Yes' : 'No'}>
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => updateStepField('main', s.key, 'customerSentForSign', option.key === 'yes')}
                                                  sx={{
                                                    border: '1px solid',
                                                    borderColor: selected ? '#93c5fd' : 'divider',
                                                    bgcolor: selected ? '#dbeafe' : '#f8fafc',
                                                    color: selected ? 'primary.main' : 'text.secondary',
                                                    borderRadius: 1.5,
                                                  }}
                                                >
                                                  {option.icon}
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          );
                                        })}
                                      </Stack>
                                    </Stack>
                                  </Stack>
                                </Paper>
                              ) : isUploadSaleAgreement ? (
                                <Paper sx={qaCardCompactSx}>
                                  <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontWeight: 500 }}>
                                        Signed sale agreement
                                      </Typography>
                                      {bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.length ? (
                                        <Chip size="small" color="success" variant="outlined" label="Uploaded" />
                                      ) : (
                                        <Chip size="small" color="warning" variant="outlined" label="Pending" />
                                      )}
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadFileOutlinedIcon />}
                                        sx={qaOptionButtonCompactSx}
                                        disabled={isDocUploadBusy(SALE_AGREEMENT_SIGNED_DOC_KEY)}
                                      >
                                        Choose file
                                        <input
                                          hidden
                                          type="file"
                                          accept="image/*,application/pdf"
                                          onChange={async (e) => {
                                            const file = (e.target.files || [])[0] || null;
                                            setStepDocPickedFiles((prev) => ({
                                              ...prev,
                                              [SALE_AGREEMENT_SIGNED_DOC_KEY]: file,
                                            }));
                                            setStepDocUploadPhase((prev) => ({
                                              ...prev,
                                              [SALE_AGREEMENT_SIGNED_DOC_KEY]: 'idle',
                                            }));
                                            if (file) setError(null);
                                            if (!file) return;
                                            const input = e.target as HTMLInputElement;
                                            if (input) input.value = '';
                                            await uploadStepRequiredDoc(SALE_AGREEMENT_SIGNED_DOC_KEY, 'Signed sale agreement', file);
                                          }}
                                        />
                                      </Button>
                                      {stepDocPickedFiles[SALE_AGREEMENT_SIGNED_DOC_KEY] ? (
                                        <Typography variant="body2" color="text.secondary">
                                          {stepDocPickedFiles[SALE_AGREEMENT_SIGNED_DOC_KEY]?.name}
                                        </Typography>
                                      ) : null}
                                      <Box sx={{ flex: 1 }} />
                                      {bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.[0]?.url ? (
                                        <Tooltip title="Download">
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                const file = bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.[0];
                                                if (!file?.url) return;
                                                triggerNamedDownload(
                                                  file.url,
                                                  String((bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY] as any)?.displayName || 'Signed Sale Agreement'),
                                                  String(file?.originalName || file?.name || '')
                                                );
                                              }}
                                            >
                                              <DownloadOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : null}
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          uploadStepRequiredDoc(SALE_AGREEMENT_SIGNED_DOC_KEY, 'Signed sale agreement')
                                        }
                                        disabled={
                                          isDocUploadBusy(SALE_AGREEMENT_SIGNED_DOC_KEY) ||
                                          !stepDocPickedFiles[SALE_AGREEMENT_SIGNED_DOC_KEY]
                                        }
                                        sx={qaOptionButtonCompactSx}
                                      >
                                        {getDocUploadActionLabel(
                                          SALE_AGREEMENT_SIGNED_DOC_KEY,
                                          !!bookingDocsByKey[SALE_AGREEMENT_SIGNED_DOC_KEY]?.files?.length
                                        )}
                                      </Button>
                                    </Stack>
                                    <Divider />
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontWeight: 500 }}>
                                        Signed allotment letter
                                      </Typography>
                                      {bookingDocsByKey[ALLOTMENT_LETTER_SIGNED_DOC_KEY]?.files?.length ? (
                                        <Chip size="small" color="success" variant="outlined" label="Uploaded" />
                                      ) : (
                                        <Chip size="small" color="warning" variant="outlined" label="Pending" />
                                      )}
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadFileOutlinedIcon />}
                                        sx={qaOptionButtonCompactSx}
                                        disabled={isDocUploadBusy(ALLOTMENT_LETTER_SIGNED_DOC_KEY)}
                                      >
                                        Choose file
                                        <input
                                          hidden
                                          type="file"
                                          accept="image/*,application/pdf"
                                          onChange={async (e) => {
                                            const file = (e.target.files || [])[0] || null;
                                            setStepDocPickedFiles((prev) => ({
                                              ...prev,
                                              [ALLOTMENT_LETTER_SIGNED_DOC_KEY]: file,
                                            }));
                                            setStepDocUploadPhase((prev) => ({
                                              ...prev,
                                              [ALLOTMENT_LETTER_SIGNED_DOC_KEY]: 'idle',
                                            }));
                                            if (file) setError(null);
                                            if (!file) return;
                                            const input = e.target as HTMLInputElement;
                                            if (input) input.value = '';
                                            await uploadStepRequiredDoc(ALLOTMENT_LETTER_SIGNED_DOC_KEY, 'Signed allotment letter', file);
                                          }}
                                        />
                                      </Button>
                                      {stepDocPickedFiles[ALLOTMENT_LETTER_SIGNED_DOC_KEY] ? (
                                        <Typography variant="body2" color="text.secondary">
                                          {stepDocPickedFiles[ALLOTMENT_LETTER_SIGNED_DOC_KEY]?.name}
                                        </Typography>
                                      ) : null}
                                      <Box sx={{ flex: 1 }} />
                                      {bookingDocsByKey[ALLOTMENT_LETTER_SIGNED_DOC_KEY]?.files?.[0]?.url ? (
                                        <Tooltip title="Download">
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                const file = bookingDocsByKey[ALLOTMENT_LETTER_SIGNED_DOC_KEY]?.files?.[0];
                                                if (!file?.url) return;
                                                triggerNamedDownload(
                                                  file.url,
                                                  String((bookingDocsByKey[ALLOTMENT_LETTER_SIGNED_DOC_KEY] as any)?.displayName || 'Signed Allotment Letter'),
                                                  String(file?.originalName || file?.name || '')
                                                );
                                              }}
                                            >
                                              <DownloadOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : null}
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          uploadStepRequiredDoc(ALLOTMENT_LETTER_SIGNED_DOC_KEY, 'Signed allotment letter')
                                        }
                                        disabled={
                                          isDocUploadBusy(ALLOTMENT_LETTER_SIGNED_DOC_KEY) ||
                                          !stepDocPickedFiles[ALLOTMENT_LETTER_SIGNED_DOC_KEY]
                                        }
                                        sx={qaOptionButtonCompactSx}
                                      >
                                        {getDocUploadActionLabel(
                                          ALLOTMENT_LETTER_SIGNED_DOC_KEY,
                                          !!bookingDocsByKey[ALLOTMENT_LETTER_SIGNED_DOC_KEY]?.files?.length
                                        )}
                                      </Button>
                                    </Stack>
                                    <Divider />
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontWeight: 500 }}>
                                        Advocate sale agreement fee
                                      </Typography>
                                      {advocateAgreementReceiptTotal > 0 ? (
                                        <Chip
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          label={`Received ₹${Math.round(advocateAgreementReceiptTotal).toLocaleString('en-IN')}`}
                                        />
                                      ) : (
                                        <Chip size="small" color="warning" variant="outlined" label="Pending" />
                                      )}
                                    </Stack>
                                    {advocateAgreementReceiptTotal <= 0 ? (
                                      <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => openUnifiedPayment('advocateAgreement')}
                                          startIcon={<CurrencyRupeeOutlinedIcon fontSize="small" />}
                                          sx={qaOptionButtonCompactSx}
                                        >
                                          Add sale agreement fee receipt
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => markAdvocateAgreementFeeReceivedWithoutDetails()}
                                          startIcon={<CheckOutlinedIcon fontSize="small" />}
                                          sx={qaOptionButtonCompactSx}
                                        >
                                          Mark received (no details)
                                        </Button>
                                      </Stack>
                                    ) : null}
                                    {error ? (
                                      <Alert severity="warning">{String(error)}</Alert>
                                    ) : null}
                                  </Stack>
                                </Paper>
                              ) : isPayment ? (
                                <>
                                  {s.kind === 'payment' ? (
                                    <Paper sx={qaCardCompactSx}>
                                      <Typography sx={{ fontWeight: 700, mb: 1 }}>Payments</Typography>
                                      {(() => {
                                        const receiptType = String(
                                          (s as any).paymentReceiptType || 'both'
                                        ).toLowerCase();
                                        const allowCustomer =
                                          receiptType === 'both' || receiptType === 'customer';
                                        const allowGst =
                                          receiptType === 'both' || receiptType === 'gst';
                                        const customerDone = customerReceiptTotal > 0;
                                        const gstDone = gstReceiptTotal > 0;
                                        return (
                                      <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                                        {allowCustomer && !customerDone ? (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                              openUnifiedPayment('dp', { which: 'main', key: s.key });
                                            }}
                                            sx={{ textTransform: 'none', fontWeight: 800 }}
                                          >
                                            Add DP payment
                                          </Button>
                                        ) : null}
                                        {allowCustomer && customerDone ? (
                                          <Chip size="small" color="success" variant="outlined" label="DP payment added" />
                                        ) : null}
                                        {allowGst && !gstDone ? (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => openGstPaymentUnified({ which: 'main', key: s.key })}
                                            sx={{ textTransform: 'none', fontWeight: 800 }}
                                          >
                                            Add GST payment
                                          </Button>
                                        ) : null}
                                        {allowGst && gstDone ? (
                                          <Chip size="small" color="success" variant="outlined" label="GST payment added" />
                                        ) : null}
                                      </Stack>
                                        );
                                      })()}
                                    </Paper>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  {(s.fields || []).map((f) => {
                                    const k = makeDraftKey('main', s.key, f.key);
                                    const remote = stepData?.[f.key];
                                    const raw =
                                      stepFieldDrafts[k] ??
                                      (f.type === 'boolean'
                                        ? typeof remote === 'boolean'
                                          ? remote
                                            ? 'yes'
                                            : 'no'
                                          : ''
                                        : remote == null
                                          ? ''
                                          : String(remote));

                                    if (f.type === 'boolean') {
                                      return renderOptionsQuestion({
                                        title: f.label,
                                        value: raw,
                                        options: [
                                          { key: 'yes', label: 'Yes' },
                                          { key: 'no', label: 'No' },
                                        ],
                                        onSelect: async (next) => {
                                          await updateStepField('main', s.key, f.key, next === 'yes');
                                          setStepFieldDrafts((prev) => {
                                            const nextDrafts = { ...prev };
                                            delete nextDrafts[k];
                                            return nextDrafts;
                                          });
                                        },
                                        compact: true,
                                      });
                                    }

                                    if (f.type === 'select' && f.options?.length) {
                                      return renderOptionsQuestion({
                                        title: f.label,
                                        value: raw,
                                        options: f.options.map((o) => ({ key: o, label: o })),
                                        onSelect: async (next) => {
                                          await updateStepField('main', s.key, f.key, next);
                                          setStepFieldDrafts((prev) => {
                                            const nextDrafts = { ...prev };
                                            delete nextDrafts[k];
                                            return nextDrafts;
                                          });
                                        },
                                        compact: true,
                                      });
                                    }

                                    if (f.type === 'number' && f.options?.length) {
                                      return renderOptionsQuestion({
                                        title: f.label,
                                        value: raw,
                                        options: f.options.map((o) => ({ key: o, label: o })),
                                        onSelect: async (next) => {
                                          const n = Number(next);
                                          await updateStepField('main', s.key, f.key, Number.isFinite(n) ? n : null);
                                          setStepFieldDrafts((prev) => {
                                            const nextDrafts = { ...prev };
                                            delete nextDrafts[k];
                                            return nextDrafts;
                                          });
                                        },
                                        compact: true,
                                      });
                                    }

                                    return renderTextQuestion({
                                      title: f.label,
                                      value: raw,
                                      multiline: f.type === 'textarea',
                                      type: f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text',
                                      onChange: (v) => setStepFieldDrafts((prev) => ({ ...prev, [k]: v })),
                                      onBlur: () => commitDraftField({ which: 'main', stepKey: s.key, fieldKey: f.key, fieldType: f.type }),
                                      compact: true,
                                    });
                                  })}

                                  {(s.requiredDocs || []).map((d) => (
                                    <Box key={`doc_${s.key}_${d.key}`}>
                                      {renderUploadQuestion({
                                        title: d.label,
                                        docKey: d.key,
                                        existing: bookingDocsByKey[d.key] || null,
                                        compact: true,
                                      })}
                                    </Box>
                                  ))}

                                  {(!s.fields?.length && !s.requiredDocs?.length) ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No questions/documents configured for this step.
                                    </Typography>
                                  ) : null}
                                </>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })()}

                      {String(loanRequired || '').toLowerCase() === 'yes' ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.5 }}>
                            Loan Workflow
                          </Typography>
                          {!loanCase ? (
                            <Alert severity="info">Start the loan workflow to track bank steps.</Alert>
                          ) : (
                            !currentLoanStep ? (
                              <Alert severity="success">All loan workflow tasks are completed.</Alert>
                            ) : (() => {
                              const s = currentLoanStep;
                              const isPayment = s.kind === 'payment';
                              const details = getStepDetails('loan', s.key);
                              const canMarkDone = s.status === 'ready' && isStepInputsComplete('loan', s);
                              const sendToSbiStep =
                                loanSteps.find((step) => step.key === OFFICE_LOAN_STEP_KEYS.sendToBanks) || null;
                              const collectDocsStep =
                                loanSteps.find((step) => step.key === OFFICE_LOAN_STEP_KEYS.collectDocs) || null;
                              const sanctionReceivedDone =
                                loanSteps.find((step) => step.key === OFFICE_LOAN_STEP_KEYS.sanctionReceived)?.status === 'done';
                              const showPrimaryLoanTaskGroup =
                                (s.key === OFFICE_LOAN_STEP_KEYS.sendToBanks && !sanctionReceivedDone) ||
                                (s.key === OFFICE_LOAN_STEP_KEYS.collectDocs &&
                                  (!!sendToSbiStep || !!collectDocsStep) &&
                                  (sendToSbiStep?.status !== 'done' || collectDocsStep?.status !== 'done'));
                              const canMarkInProgress =
                                !showPrimaryLoanTaskGroup &&
                                s.status === 'ready' &&
                                (s.key === OFFICE_LOAN_STEP_KEYS.collectDocs ||
                                  (canMarkDone && s.key !== OFFICE_LOAN_STEP_KEYS.sendToBanks));
                              const stepData = getCaseStepData('loan', s.key);
                              const isInProgress = Boolean(stepData?.inProgress);
                              const sendToSbiData = getCaseStepData('loan', OFFICE_LOAN_STEP_KEYS.sendToBanks);
                              const otherBankLeads = Array.isArray((sendToSbiData as any)?.otherBankLeads)
                                ? ((sendToSbiData as any).otherBankLeads as Array<Record<string, any>>)
                                : [];
                              const lockOtherBankLeads = sanctionReceivedDone;
                              const normalizedOtherBankContactNumber = normalizeBankContactNumber(loanOtherBankContactNumber);
                              const isOtherBankContactNumberValid = isValidBankContactNumber(normalizedOtherBankContactNumber);
                              const collectDocsData = getCaseStepData('loan', OFFICE_LOAN_STEP_KEYS.collectDocs);
                              const collectDocsChecklist = (((collectDocsData as any)?.loanDocChecklist || {}) as Record<string, boolean>);
                              const collectDocsInProgress = Boolean((collectDocsData as any)?.inProgress);
                              const canMarkCollectInProgress = collectDocsStep?.status === 'ready';
                              const canMarkCollectDone =
                                collectDocsStep?.status === 'ready' &&
                                LOAN_COLLECT_DOC_SUBTASKS.every((item) => Boolean(collectDocsChecklist[item.key]));
                              const collectDocsDone = collectDocsStep?.status === 'done';
                              const canMarkSendToSbiDone = sendToSbiStep?.status === 'ready';
                              const sendToSbiDone = sendToSbiStep?.status === 'done';
                              const showTopDoneButton = !showPrimaryLoanTaskGroup && canMarkDone;
                              return (
                                <Paper key={s.key} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                  {!showPrimaryLoanTaskGroup ? (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                                      {stepStatusChip(s)}
                                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 900 }}>
                                        {s.title}
                                      </Typography>
                                      {canMarkInProgress ? (
                                        <Tooltip title={isInProgress ? 'Clear in progress' : 'Mark in progress'}>
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => updateStepField('loan', s.key, 'inProgress', !isInProgress)}
                                              sx={{
                                                border: '1px solid',
                                                borderColor: isInProgress ? '#f59e0b' : 'divider',
                                                bgcolor: isInProgress ? '#ffedd5' : '#f8fafc',
                                                color: isInProgress ? '#d97706' : 'text.secondary',
                                                borderRadius: 1.5,
                                              }}
                                            >
                                              <ScheduleOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : null}
                                      {showTopDoneButton ? (
                                        <Tooltip title="Mark done">
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => setStepDone('loan', s.key, true)}
                                              sx={{
                                                border: '1px solid',
                                                borderColor: '#93c5fd',
                                                bgcolor: '#dbeafe',
                                                color: 'primary.main',
                                                borderRadius: 1.5,
                                              }}
                                            >
                                              <CheckOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : null}
                                    </Stack>
                                  ) : null}

                                  {!isPayment && details.length ? (
                                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                                      {details.map((line, idx) => (
                                        <Typography key={`${s.key}-detail-${idx}`} variant="caption" color="text.secondary">
                                          {line}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  ) : null}

                                  {!isPayment && s.status === 'blocked' && s.blockedBy?.length ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      Blocked by: {s.blockedBy.join(', ')}
                                    </Typography>
                                  ) : null}

                                  <Stack spacing={1}>
                                    {showPrimaryLoanTaskGroup ? (
                                      <Stack spacing={1} sx={{ px: 0.25 }}>
                                        <Stack spacing={1} sx={{ px: 0.25 }}>
                                            {!sendToSbiDone ? (
                                              <>
                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Send Lead to SBI</Typography>
                                                  {canMarkSendToSbiDone || sendToSbiDone ? (
                                                    <IconButton
                                                      size="small"
                                                      onClick={async () => {
                                                        try {
                                                          if (!sendToSbiDone) {
                                                            const hasSbiLead = otherBankLeads.some(
                                                              (lead) =>
                                                                String(lead?.bankName || '').trim().toLowerCase() ===
                                                                'state bank of india'
                                                            );
                                                          if (!hasSbiLead) {
                                                              setSendToSbiHint(
                                                                'To mark Send Lead to SBI as done, first add an entry in "Send Lead to Bank" with Bank name: State Bank of India.'
                                                              );
                                                              return;
                                                            }
                                                          }
                                                          await setStepDone(
                                                            'loan',
                                                            OFFICE_LOAN_STEP_KEYS.sendToBanks,
                                                            !sendToSbiDone
                                                          );
                                                          setSendToSbiHint('');
                                                          setError(null);
                                                        } catch (err: any) {
                                                          setError(err?.message || 'Failed to update Send Lead to SBI.');
                                                        }
                                                      }}
                                                      sx={{
                                                        border: '1px solid',
                                                        borderColor: sendToSbiDone ? '#86efac' : 'divider',
                                                        bgcolor: sendToSbiDone ? '#dcfce7' : '#f8fafc',
                                                        color: sendToSbiDone ? '#16a34a' : 'text.secondary',
                                                        borderRadius: 1.5,
                                                      }}
                                                    >
                                                      <CheckOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                  ) : null}
                                                </Stack>
                                                {sendToSbiHint ? (
                                                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                                                    {sendToSbiHint}
                                                  </Alert>
                                                ) : null}
                                                <Divider />
                                              </>
                                            ) : null}
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                Send Lead to Bank
                                              </Typography>
                                              <Tooltip title="Add bank lead">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    disabled={lockOtherBankLeads}
                                                    onClick={() => setLoanOtherBankFormOpen((prev) => !prev)}
                                                    sx={{
                                                      p: 0.25,
                                                      minWidth: 0,
                                                      color: 'text.secondary',
                                                      bgcolor: 'transparent',
                                                      border: 'none',
                                                      '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                                                    }}
                                                  >
                                                    <AddOutlinedIcon sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            </Stack>
                                            {loanOtherBankFormOpen ? (
                                              <>
                                                  <Autocomplete
                                                    freeSolo
                                                    options={mergeBankNameOptions(officeDocumentLinkedBankNames, loanOtherBankHistory.bankNames)}
                                                    value={loanOtherBankName}
                                                  onChange={(_, value) => setLoanOtherBankName(String(value || ''))}
                                                  onInputChange={(_, value) => setLoanOtherBankName(String(value || ''))}
                                                  disabled={lockOtherBankLeads}
                                                  sx={{ width: '100%' }}
                                                  renderInput={(params) => <TextField {...params} size="small" label="Bank name" fullWidth />}
                                                />
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                                  <Autocomplete
                                                    freeSolo
                                                    options={loanOtherBankHistory.contactNames}
                                                    value={loanOtherBankContactName}
                                                    onChange={(_, value) => {
                                                      const next = String(value || '');
                                                      setLoanOtherBankContactName(next);
                                                      const mapped = loanOtherBankHistory.nameToNumber.get(next.trim().toLowerCase());
                                                      if (mapped) setLoanOtherBankContactNumber(mapped);
                                                    }}
                                                    onInputChange={(_, value) => {
                                                      const next = String(value || '');
                                                      setLoanOtherBankContactName(next);
                                                      const mapped = loanOtherBankHistory.nameToNumber.get(next.trim().toLowerCase());
                                                      if (mapped) setLoanOtherBankContactNumber(mapped);
                                                    }}
                                                    disabled={lockOtherBankLeads}
                                                    sx={{ flex: 1 }}
                                                    renderInput={(params) => <TextField {...params} size="small" label="Bank person name" />}
                                                  />
                                                  <Autocomplete
                                                    freeSolo
                                                    options={loanOtherBankHistory.contactNumbers}
                                                    value={loanOtherBankContactNumber}
                                                    onChange={(_, value) => {
                                                      const next = normalizeBankContactNumber(String(value || ''));
                                                      setLoanOtherBankContactNumber(next);
                                                      const mapped = loanOtherBankHistory.numberToName.get(next.trim());
                                                      if (mapped) setLoanOtherBankContactName(mapped);
                                                    }}
                                                    onInputChange={(_, value) => {
                                                      const next = normalizeBankContactNumber(String(value || ''));
                                                      setLoanOtherBankContactNumber(next);
                                                      const mapped = loanOtherBankHistory.numberToName.get(next.trim());
                                                      if (mapped) setLoanOtherBankContactName(mapped);
                                                    }}
                                                    disabled={lockOtherBankLeads}
                                                    sx={{ flex: 1 }}
                                                    renderInput={(params) => (
                                                      <TextField
                                                        {...params}
                                                        size="small"
                                                        label="Contact number"
                                                        error={!!normalizedOtherBankContactNumber && !isOtherBankContactNumberValid}
                                                      />
                                                    )}
                                                  />
                                                </Stack>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                                  <TextField
                                                    size="small"
                                                    label="Remarks"
                                                    value={loanOtherBankRemarks}
                                                    onChange={(e) => setLoanOtherBankRemarks(e.target.value)}
                                                    disabled={lockOtherBankLeads}
                                                    sx={{ flex: 1 }}
                                                  />
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={
                                                      lockOtherBankLeads ||
                                                      !String(loanOtherBankName || '').trim() ||
                                                      !String(loanOtherBankContactName || '').trim() ||
                                                      (!!String(loanOtherBankContactNumber || '').trim() && !isOtherBankContactNumberValid)
                                                    }
                                                    onClick={async () => {
                                                      try {
                                                        const name = String(loanOtherBankName || '').trim();
                                                        const contactName = String(loanOtherBankContactName || '').trim();
                                                        const contactNumber = normalizeBankContactNumber(loanOtherBankContactNumber);
                                                        if (!name || !contactName) return;
                                                        if (contactNumber && !isValidBankContactNumber(contactNumber)) return;
                                                        const current = Array.isArray((sendToSbiData as any)?.otherBankLeads)
                                                          ? ([...(sendToSbiData as any).otherBankLeads] as Array<Record<string, any>>)
                                                          : [];
                                                        current.push({
                                                          id: `lead_${Date.now()}`,
                                                          bankName: name,
                                                          contactName,
                                                          contactNumber: contactNumber || '',
                                                          sentOn: String(loanOtherBankSentOn || ymdToday()),
                                                          remarks: String(loanOtherBankRemarks || '').trim(),
                                                        });
                                                        await updateStepField(
                                                          'loan',
                                                          OFFICE_LOAN_STEP_KEYS.sendToBanks,
                                                          'otherBankLeads',
                                                          current
                                                        );
                                                        setLoanOtherBankName('');
                                                        setLoanOtherBankContactName('');
                                                        setLoanOtherBankContactNumber('');
                                                        setLoanOtherBankSentOn(ymdToday());
                                                        setLoanOtherBankRemarks('');
                                                        setLoanOtherBankFormOpen(false);
                                                      } catch (err: any) {
                                                        setError(err?.message || 'Failed to add other bank lead.');
                                                      }
                                                    }}
                                                    sx={{ textTransform: 'none' }}
                                                  >
                                                    Add Bank
                                                  </Button>
                                                </Stack>
                                              </>
                                            ) : null}
                                            {otherBankLeads.length ? (
                                              <Stack spacing={0.5}>
                                                {otherBankLeads.map((lead, index) => {
                                                  const leadId = String(lead?.id || `lead_${index}`);
                                                  const isEditingRemark = loanOtherBankRemarkEditId === leadId;
                                                  const leadStatus = String(lead?.status || 'sent');
                                                  const followupTimeline = Array.isArray((lead as any)?.followupTimeline)
                                                    ? ((lead as any).followupTimeline as Array<Record<string, any>>)
                                                    : [];
                                                  const orderedTimeline = followupTimeline.slice().reverse();
                                                  const hasMoreTimeline = orderedTimeline.length > 3;
                                                  const isTimelineExpanded = !!loanOtherBankTimelineExpanded[leadId];
                                                  const visibleTimeline = hasMoreTimeline && !isTimelineExpanded ? orderedTimeline.slice(0, 3) : orderedTimeline;
                                                  const leadStatusLabel =
                                                    leadStatus === 'follow_up'
                                                      ? 'Follow-up'
                                                      : leadStatus === 'not_responding'
                                                        ? 'Not Responding'
                                                        : leadStatus === 'rejected_by_bank'
                                                          ? 'Rejected by Bank'
                                                          : leadStatus === 'rejected_by_buyer'
                                                            ? 'Rejected by Buyer'
                                                          : 'Sent';
                                                  const submitBankFollowup = async () => {
                                                    try {
                                                      const next = Array.isArray((sendToSbiData as any)?.otherBankLeads)
                                                        ? ([...(sendToSbiData as any).otherBankLeads] as Array<Record<string, any>>)
                                                        : [];
                                                      const row = { ...(next[index] || {}) };
                                                      const timeline = Array.isArray(row?.followupTimeline)
                                                        ? [...row.followupTimeline]
                                                        : [];
                                                      timeline.push({
                                                        atMs: Date.now(),
                                                        status: String(loanOtherBankStatusEdit || 'sent'),
                                                        remark: String(loanOtherBankRemarkDraft || '').trim(),
                                                        by: currentUser?.email || null,
                                                      });
                                                      row.followupTimeline = timeline;
                                                      next[index] = row;
                                                      await updateStepField(
                                                        'loan',
                                                        OFFICE_LOAN_STEP_KEYS.sendToBanks,
                                                        'otherBankLeads',
                                                        next
                                                      );
                                                      setLoanOtherBankRemarkEditId('');
                                                      setLoanOtherBankStatusEdit('sent');
                                                      setLoanOtherBankRemarkDraft('');
                                                    } catch (err: any) {
                                                      setError(err?.message || 'Failed to update remark.');
                                                    }
                                                  };
                                                  return (
                                                    <Stack
                                                      key={leadId}
                                                      spacing={0.5}
                                                      sx={{ pl: 0.5 }}
                                                    >
                                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                                                        <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>
                                                          {index + 1}. {String(lead?.bankName || 'Bank')}
                                                        </Typography>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                          <Chip size="small" variant="outlined" label={leadStatusLabel} />
                                                          {!lockOtherBankLeads ? (
                                                            <Tooltip title="Edit remark">
                                                              <span>
                                                                <IconButton
                                                                  size="small"
                                                                  onClick={() => {
                                                                    setLoanOtherBankRemarkEditId(leadId);
                                                                    setLoanOtherBankStatusEdit('follow_up');
                                                                    setLoanOtherBankRemarkDraft('');
                                                                  }}
                                                                  sx={{
                                                                    p: 0.25,
                                                                    minWidth: 0,
                                                                    color: 'text.secondary',
                                                                    bgcolor: 'transparent',
                                                                    border: 'none',
                                                                    '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                                                                  }}
                                                                >
                                                                  <EditOutlinedIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                              </span>
                                                            </Tooltip>
                                                          ) : null}
                                                        {canRemoveOtherBankLead && !lockOtherBankLeads && !isEditingRemark ? (
                                                          <Tooltip title="Remove">
                                                            <span>
                                                              <IconButton
                                                                size="small"
                                                                onClick={async () => {
                                                                  try {
                                                                    const ok = window.confirm('Are you sure you want to remove this bank lead entry?');
                                                                    if (!ok) return;
                                                                    const next = Array.isArray((sendToSbiData as any)?.otherBankLeads)
                                                                      ? ([...(sendToSbiData as any).otherBankLeads] as Array<Record<string, any>>)
                                                                      : [];
                                                                    next.splice(index, 1);
                                                                    await updateStepField(
                                                                      'loan',
                                                                      OFFICE_LOAN_STEP_KEYS.sendToBanks,
                                                                      'otherBankLeads',
                                                                      next
                                                                    );
                                                                  } catch (err: any) {
                                                                    setError(err?.message || 'Failed to remove bank lead.');
                                                                  }
                                                                }}
                                                                sx={{
                                                                  p: 0.25,
                                                                  minWidth: 0,
                                                                  color: 'error.main',
                                                                  bgcolor: 'transparent',
                                                                  border: 'none',
                                                                  '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                                                                }}
                                                              >
                                                                <CloseIcon sx={{ fontSize: 14 }} />
                                                              </IconButton>
                                                            </span>
                                                          </Tooltip>
                                                        ) : null}
                                                        </Stack>
                                                      </Stack>
                                                      <Stack sx={{ minWidth: 0, flex: 1, pl: 1.25 }} spacing={0.35}>
                                                          <Typography variant="caption" color="text.secondary">
                                                            {String(lead?.contactName || '-')} · {String(lead?.contactNumber || '-')}
                                                          </Typography>
                                                          {visibleTimeline.map((entry, entryIndex) => {
                                                              const status = String(entry?.status || 'sent');
                                                              const statusLabel =
                                                                status === 'follow_up'
                                                                  ? 'Follow-up'
                                                                  : status === 'not_responding'
                                                                    ? 'Not Responding'
                                                                    : status === 'rejected_by_bank'
                                                                      ? 'Rejected by Bank'
                                                                      : status === 'rejected_by_buyer'
                                                                        ? 'Rejected by Buyer'
                                                                        : 'Sent';
                                                              const remark = String(entry?.remark || '').trim();
                                                              const showExpandOnRow =
                                                                hasMoreTimeline && !isTimelineExpanded && entryIndex === 2;
                                                              return (
                                                                <Stack
                                                                  key={`${leadId}_entry_${entryIndex}`}
                                                                  direction="row"
                                                                  alignItems="center"
                                                                  justifyContent="space-between"
                                                                  spacing={0.5}
                                                                  sx={{ pl: 0.5 }}
                                                                >
                                                                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                                                                    {formatDDMMYYYY(Number(entry?.atMs || 0))} · {statusLabel}{remark ? ` · ${remark}` : ''}
                                                                  </Typography>
                                                                  {showExpandOnRow ? (
                                                                    <IconButton
                                                                      size="small"
                                                                      onClick={() =>
                                                                        setLoanOtherBankTimelineExpanded((prev) => ({ ...prev, [leadId]: true }))
                                                                      }
                                                                      sx={{ p: 0.25, minWidth: 0, color: 'text.secondary' }}
                                                                    >
                                                                      <ExpandMoreIcon sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                  ) : null}
                                                                </Stack>
                                                              );
                                                            })}
                                                          {hasMoreTimeline && isTimelineExpanded ? (
                                                            <Stack direction="row" justifyContent="flex-end">
                                                              <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                  setLoanOtherBankTimelineExpanded((prev) => ({ ...prev, [leadId]: false }))
                                                                }
                                                                sx={{ p: 0.25, minWidth: 0, color: 'text.secondary' }}
                                                              >
                                                                <ExpandMoreIcon sx={{ fontSize: 14, transform: 'rotate(180deg)' }} />
                                                              </IconButton>
                                                            </Stack>
                                                          ) : null}
                                                        </Stack>
                                                      {isEditingRemark ? (
                                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                                                          <FormControl size="small" sx={{ minWidth: 170 }}>
                                                            <InputLabel>Status</InputLabel>
                                                            <Select
                                                              label="Status"
                                                              value={loanOtherBankStatusEdit}
                                                              onChange={(e) => setLoanOtherBankStatusEdit(String(e.target.value || 'sent'))}
                                                            >
                                                              <MenuItem value="sent">Sent</MenuItem>
                                                              <MenuItem value="follow_up">Follow-up</MenuItem>
                                                              <MenuItem value="not_responding">Not Responding</MenuItem>
                                                              <MenuItem value="rejected_by_bank">Rejected by Bank</MenuItem>
                                                              <MenuItem value="rejected_by_buyer">Rejected by Buyer</MenuItem>
                                                            </Select>
                                                          </FormControl>
                                                          <TextField
                                                            size="small"
                                                            value={loanOtherBankRemarkDraft}
                                                            onChange={(e) => setLoanOtherBankRemarkDraft(e.target.value)}
                                                            onKeyDown={async (e) => {
                                                              if (e.key !== 'Enter') return;
                                                              e.preventDefault();
                                                              await submitBankFollowup();
                                                            }}
                                                            placeholder="Add remark"
                                                            sx={{ flex: 1 }}
                                                          />
                                                          <IconButton
                                                            size="small"
                                                            onClick={submitBankFollowup}
                                                            sx={{ p: 0.25, minWidth: 0, color: 'success.main', bgcolor: 'transparent', border: 'none' }}
                                                          >
                                                            <CheckOutlinedIcon sx={{ fontSize: 14 }} />
                                                          </IconButton>
                                                          <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                              setLoanOtherBankRemarkEditId('');
                                                              setLoanOtherBankStatusEdit('sent');
                                                              setLoanOtherBankRemarkDraft('');
                                                            }}
                                                            sx={{ p: 0.25, minWidth: 0, color: 'text.secondary', bgcolor: 'transparent', border: 'none' }}
                                                          >
                                                            <CloseIcon sx={{ fontSize: 14 }} />
                                                          </IconButton>
                                                        </Stack>
                                                      ) : null}
                                                    </Stack>
                                                  );
                                                })}
                                              </Stack>
                                            ) : null}
                                            {lockOtherBankLeads ? (
                                              <Typography variant="caption" color="text.secondary">
                                                Other bank leads are locked after sanction received.
                                              </Typography>
                                            ) : null}
                                          </Stack>
                                        <Divider />
                                        <Stack spacing={0.5} sx={{ px: 0.25 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                              <Typography variant="body2" sx={{ fontWeight: 700 }}>Collect Documents for Loan</Typography>
                                              <Stack direction="row" spacing={0.5}>
                                                {collectDocsDone ? (
                                                  <Chip size="small" color="success" variant="outlined" label="Documents received" />
                                                ) : null}
                                                {canMarkCollectInProgress ? (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                      updateStepField(
                                                        'loan',
                                                        OFFICE_LOAN_STEP_KEYS.collectDocs,
                                                        'inProgress',
                                                        !collectDocsInProgress
                                                      )
                                                    }
                                                    sx={{
                                                      border: '1px solid',
                                                      borderColor: collectDocsInProgress ? '#f59e0b' : 'divider',
                                                      bgcolor: collectDocsInProgress ? '#ffedd5' : '#f8fafc',
                                                      color: collectDocsInProgress ? '#d97706' : 'text.secondary',
                                                      borderRadius: 1.5,
                                                    }}
                                                  >
                                                    <ScheduleOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                                ) : null}
                                                {canMarkCollectDone ? (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => setStepDone('loan', OFFICE_LOAN_STEP_KEYS.collectDocs, true)}
                                                    sx={{
                                                      border: '1px solid',
                                                      borderColor: '#93c5fd',
                                                      bgcolor: '#dbeafe',
                                                      color: 'primary.main',
                                                      borderRadius: 1.5,
                                                    }}
                                                  >
                                                    <CheckOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                                ) : null}
                                              </Stack>
                                            </Stack>
                                            {!collectDocsDone ? LOAN_COLLECT_DOC_SUBTASKS.map((item) => {
                                              const checked = Boolean(collectDocsChecklist[item.key]);
                                              return (
                                                <Stack
                                                  key={`loan_collect_doc_${item.key}`}
                                                  direction="row"
                                                  spacing={1}
                                                  alignItems="center"
                                                  justifyContent="space-between"
                                                  sx={{ pl: 1 }}
                                                >
                                                  <Typography variant="body2">• {item.label}</Typography>
                                                  <IconButton
                                                    size="small"
                                                    disabled={collectDocsStep?.status !== 'ready'}
                                                    onClick={() =>
                                                      updateStepField('loan', OFFICE_LOAN_STEP_KEYS.collectDocs, 'loanDocChecklist', {
                                                        ...collectDocsChecklist,
                                                        [item.key]: !checked,
                                                      })
                                                    }
                                                    sx={{
                                                      border: '1px solid',
                                                      borderColor: checked ? '#86efac' : 'divider',
                                                      bgcolor: checked ? '#dcfce7' : '#f8fafc',
                                                      color: checked ? '#16a34a' : 'text.secondary',
                                                      borderRadius: 1.5,
                                                    }}
                                                  >
                                                    <CheckOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                                </Stack>
                                              );
                                            }) : null}
                                          </Stack>
                                      </Stack>
                                    ) : s.key === OFFICE_LOAN_STEP_KEYS.collectDocs ? (
                                      <Stack spacing={0.5} sx={{ px: 0.25 }}>
                                        {LOAN_COLLECT_DOC_SUBTASKS.map((item) => {
                                          const checklist = ((stepData as any)?.loanDocChecklist || {}) as Record<string, boolean>;
                                          const checked = Boolean(checklist[item.key]);
                                          return (
                                            <Stack
                                              key={`loan_collect_doc_${item.key}`}
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              justifyContent="space-between"
                                              sx={{ pl: 1 }}
                                            >
                                              <Typography variant="body2">• {item.label}</Typography>
                                              <IconButton
                                                size="small"
                                                disabled={s.status !== 'ready'}
                                                onClick={() =>
                                                  updateStepField('loan', s.key, 'loanDocChecklist', {
                                                    ...checklist,
                                                    [item.key]: !checked,
                                                  })
                                                }
                                                sx={{
                                                  border: '1px solid',
                                                  borderColor: checked ? '#86efac' : 'divider',
                                                  bgcolor: checked ? '#dcfce7' : '#f8fafc',
                                                  color: checked ? '#16a34a' : 'text.secondary',
                                                  borderRadius: 1.5,
                                                }}
                                              >
                                                <CheckOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </Stack>
                                          );
                                        })}
                                      </Stack>
                                    ) : null}
                                    {isPayment ? (
                                      <Paper sx={qaCardCompactSx}>
                                        <Typography sx={{ fontWeight: 900, mb: 1 }}>Payments</Typography>
                                        {(() => {
                                          const receiptType = String(
                                            (s as any).paymentReceiptType || 'both'
                                          ).toLowerCase();
                                          const allowCustomer =
                                            receiptType === 'both' || receiptType === 'customer';
                                          const allowGst =
                                            receiptType === 'both' || receiptType === 'gst';
                                          const customerDone = customerReceiptTotal > 0;
                                          const gstDone = gstReceiptTotal > 0;
                                          return (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                                              {allowCustomer && !customerDone ? (
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={() => {
                                                    openUnifiedPayment('dp', { which: 'loan', key: s.key });
                                                  }}
                                                  sx={{ textTransform: 'none', fontWeight: 800 }}
                                                >
                                                  Add DP payment
                                                </Button>
                                              ) : null}
                                              {allowCustomer && customerDone ? (
                                                <Chip size="small" color="success" variant="outlined" label="DP payment added" />
                                              ) : null}
                                              {allowGst && !gstDone ? (
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={() => openGstPaymentUnified({ which: 'loan', key: s.key })}
                                                  sx={{ textTransform: 'none', fontWeight: 800 }}
                                                >
                                                  Add GST payment
                                                </Button>
                                              ) : null}
                                              {allowGst && gstDone ? (
                                                <Chip size="small" color="success" variant="outlined" label="GST payment added" />
                                              ) : null}
                                            </Stack>
                                          );
                                        })()}
                                      </Paper>
                                    ) : null}

                                    {!isPayment && (s.fields || []).map((f) => {
                                      const k = makeDraftKey('loan', s.key, f.key);
                                      const remote = stepData?.[f.key];
                                      const raw =
                                        stepFieldDrafts[k] ??
                                        (f.type === 'boolean'
                                          ? typeof remote === 'boolean'
                                            ? remote
                                              ? 'yes'
                                              : 'no'
                                            : ''
                                          : remote == null
                                            ? ''
                                            : String(remote));

                                      if (f.type === 'boolean') {
                                        return renderOptionsQuestion({
                                          title: f.label,
                                          value: raw,
                                          options: [
                                            { key: 'yes', label: 'Yes' },
                                            { key: 'no', label: 'No' },
                                          ],
                                          onSelect: async (next) => {
                                            await updateStepField('loan', s.key, f.key, next === 'yes');
                                            setStepFieldDrafts((prev) => {
                                              const nextDrafts = { ...prev };
                                              delete nextDrafts[k];
                                              return nextDrafts;
                                            });
                                          },
                                          compact: true,
                                        });
                                      }

                                      if (f.type === 'select' && f.options?.length) {
                                        return renderOptionsQuestion({
                                          title: f.label,
                                          value: raw,
                                          options: f.options.map((o) => ({ key: o, label: o })),
                                          onSelect: async (next) => {
                                            await updateStepField('loan', s.key, f.key, next);
                                            setStepFieldDrafts((prev) => {
                                              const nextDrafts = { ...prev };
                                              delete nextDrafts[k];
                                              return nextDrafts;
                                            });
                                          },
                                          compact: true,
                                        });
                                      }

                                      if (f.type === 'number' && f.options?.length) {
                                        return renderOptionsQuestion({
                                          title: f.label,
                                          value: raw,
                                          options: f.options.map((o) => ({ key: o, label: o })),
                                          onSelect: async (next) => {
                                            const n = Number(next);
                                            await updateStepField('loan', s.key, f.key, Number.isFinite(n) ? n : null);
                                            setStepFieldDrafts((prev) => {
                                              const nextDrafts = { ...prev };
                                              delete nextDrafts[k];
                                              return nextDrafts;
                                            });
                                          },
                                          compact: true,
                                        });
                                      }

                                      return renderTextQuestion({
                                        title: f.label,
                                        value: raw,
                                        multiline: f.type === 'textarea',
                                        type: f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text',
                                        onChange: (v) => setStepFieldDrafts((prev) => ({ ...prev, [k]: v })),
                                        onBlur: () => commitDraftField({ which: 'loan', stepKey: s.key, fieldKey: f.key, fieldType: f.type }),
                                        compact: true,
                                      });
                                    })}

                                    {!isPayment && (s.requiredDocs || []).map((d) => (
                                      <Box key={`doc_${s.key}_${d.key}`}>
                                        {renderUploadQuestion({
                                          title: d.label,
                                          docKey: d.key,
                                          existing: bookingDocsByKey[d.key] || null,
                                          compact: true,
                                        })}
                                      </Box>
                                    ))}

                                    {!isPayment &&
                                    s.key !== OFFICE_LOAN_STEP_KEYS.collectDocs &&
                                    s.key !== OFFICE_LOAN_STEP_KEYS.sendToBanks &&
                                    (!s.fields?.length && !s.requiredDocs?.length) ? (
                                      <Typography variant="body2" color="text.secondary">
                                        No questions/documents configured for this step.
                                      </Typography>
                                    ) : null}
                                  </Stack>
                                </Paper>
                              );
                            })()
                          )}
                        </>
                      ) : null}

                      {repeatableWorkflowActions.some((action) => action.which === 'loan') ? (
                        <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                          <Divider />
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
                            Repeatable Loan Actions
                          </Typography>
                          {repeatableWorkflowActions
                            .filter((action) => action.which === 'loan')
                            .map(({ which, step }) => {
                              const actionType = String(step.actionType || '').toLowerCase();
                              const docKey = String((step as any)?.docKey || '').trim();
                              const hasDocument = !!docKey && !!bookingDocsByKey?.[docKey]?.files?.length;
                              const buttonLabel =
                                actionType === 'generate_document'
                                  ? hasDocument ? 'Generate again' : 'Generate'
                                  : actionType === 'send_email'
                                    ? 'Send again'
                                    : actionType === 'upload_document'
                                      ? hasDocument ? 'Upload again' : 'Upload'
                                      : actionType === 'loan_sanction_form'
                                        ? 'Open'
                                        : 'Open action';
                              return (
                                <Paper
                                  key={`repeatable_pending_${which}_${step.key}`}
                                  variant="outlined"
                                  sx={{ px: 1, py: 0.75, borderRadius: 2, bgcolor: '#fffbeb', borderColor: '#fde68a' }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {resolveWorkflowStepTitle(step, which)}
                                      </Typography>
                                      {step.helpText ? (
                                        <Typography variant="caption" color="text.secondary">
                                          {resolveWorkflowStepHelpText(step, which)}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() =>
                                        runRepeatableWorkflowAction(which, step).catch((err: any) =>
                                          setError(err?.message || 'Failed to open workflow action.')
                                        )
                                      }
                                      sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 700 }}
                                    >
                                      {buttonLabel}
                                    </Button>
                                  </Stack>
                                </Paper>
                              );
                            })}
                        </Stack>
                      ) : null}
	                    </Stack>
		                  </>
		                )}
                  </AccordionDetails>
                </Accordion>
		              </Box>

            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  if (!isMobile || pageMode) {
    if (!open && !pageMode) return null;
    const closeTopmostDrawer = () => {
      if (paymentDetailDrawerOpen) {
        setPaymentDetailDrawerOpen(false);
        setPaymentDetailTxn(null);
        setPaymentDetailEditMode(false);
        return;
      }
      if (paymentDrawerOpen) {
        setPaymentDrawerOpen(false);
        return;
      }
      onClose();
    };
    return (
      <>
        {paymentDrawerOpen ? (
          <BankTransactionDrawer
            open={paymentDrawerOpen}
            onClose={() => setPaymentDrawerOpen(false)}
                  title="Add Transaction"
            saveError={paymentError}
            saving={paymentSaving}
            voucherUploading={paymentUploading}
            onSave={savePaymentTransaction}
            txnType={paymentTxnType}
            setTxnType={setPaymentTxnType}
            receiptCategory={paymentReceiptCategory}
            setReceiptCategory={setPaymentReceiptCategory}
            eligibleBankAccounts={paymentAccounts}
            bankAccountId={paymentBankAccountId}
            setBankAccountId={setPaymentBankAccountId}
            txnDateYMD={paymentDateYMD}
            setTxnDateYMD={setPaymentDateYMD}
            amount={paymentAmount}
            setAmount={setPaymentAmount}
            showUnitPicker={
              paymentTxnType === 'receipt' &&
              (paymentReceiptCategory === 'customer' || paymentReceiptCategory === 'gst' || paymentReceiptCategory === 'advocate')
            }
            eligibleUnitOptions={paymentUnitOptions}
            unitId={unit?.id || ''}
            setUnitId={() => {}}
            unitNumber={unit?.unitNumber || ''}
            setUnitNumber={() => {}}
                  pendingAmountText={
                    paymentTxnType === 'receipt' && (typeof unit?.unitAmount === 'number' || paymentReceiptCategory === 'advocate')
                      ? `${paymentReceiptCategory === 'gst' ? 'GST Pending: ' : paymentReceiptCategory === 'advocate' ? 'Advocate Fee Pending: ' : 'Pending: '}${Math.max(
                          0,
                          paymentReceiptCategory === 'gst'
                      ? computeGstAmount(Number(unit?.unitAmount || 0) || 0) - gstReceiptTotal
                      : paymentReceiptCategory === 'advocate'
                        ? projectAdvocateAgreementFee - advocateAgreementReceiptTotal
                        : customerPaymentPendingAmount
                  ).toLocaleString('en-IN')}`
                : null
            }
            partyName={paymentPartyName}
            setPartyName={setPaymentPartyName}
            partyNameOptions={[]}
            payerBankName={paymentPayerBankName}
            setPayerBankName={setPaymentPayerBankName}
            payerBankNameOptions={paymentPayerBankNameOptions}
            onPayerBankClear={() => setPaymentPayerBankName('')}
            paymentModeOptions={paymentModeOptions}
            mode={paymentMode}
            setMode={setPaymentMode}
            referenceNo={paymentReferenceNo}
            setReferenceNo={setPaymentReferenceNo}
            notes={paymentNotes}
            setNotes={setPaymentNotes}
            customPostAmountSection={paymentLoanDisbursementSection}
            customAfterAmountSection={paymentLiveDuplicateAlertSection}
            voucherInputRef={paymentVoucherInputRef}
            voucherFile={paymentVoucherFile}
            setVoucherFile={setPaymentVoucherFile}
            voucherRequired={
              paymentTxnType === 'receipt' &&
              (paymentReceiptCategory === 'customer' || paymentReceiptCategory === 'gst' || paymentReceiptCategory === 'advocate')
            }
            showSkipButton={paymentReceiptCategory === 'gst'}
            skipButtonLabel="Skip GST for now"
            onSkip={skipGstPaymentForNow}
            showExtraFooterAction={paymentReceiptCategory === 'advocate'}
            extraFooterActionLabel="Mark received without details"
            onExtraFooterAction={markAdvocateAgreementFeeReceivedWithoutDetails}
            drawerWidth={paymentPanelWidth}
            variant="panel"
            panelSx={{
              position: 'fixed',
              right: 0,
              top: 'var(--office-appbar-height, 56px)',
              height: 'calc(100vh - var(--office-appbar-height, 56px))',
              width: paymentPanelWidth,
              maxWidth: '100%',
              overflow: 'hidden',
              bgcolor: '#ffffff',
              borderLeft: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              zIndex: 5,
            }}
          />
        ) : null}
        {paymentDetailDrawerOpen ? (
          <BankTransactionDrawer
            open={paymentDetailDrawerOpen}
            onClose={() => {
              setPaymentDetailDrawerOpen(false);
              setPaymentDetailTxn(null);
              setPaymentDetailEditMode(false);
            }}
            title={`${String(unit?.unitNumber || unit?.id || 'Unit')} : Payment Detail`}
            saveError={paymentDetailError}
            saving={paymentDetailSaving}
            onSave={() => {
              if (!paymentDetailEditMode) return;
              savePaymentDetailTransaction();
            }}
            txnType={paymentDetailTxnType}
            setTxnType={setPaymentDetailTxnType}
            receiptCategory={paymentDetailReceiptCategory}
            setReceiptCategory={setPaymentDetailReceiptCategory}
            hideTxnType={!paymentDetailEditMode}
            hideReceiptCategory={!paymentDetailEditMode}
            eligibleBankAccounts={paymentDetailBankAccounts}
            bankAccountId={paymentDetailBankAccountId}
            setBankAccountId={setPaymentDetailBankAccountId}
            txnDateYMD={paymentDetailDateYMD}
            setTxnDateYMD={setPaymentDetailDateYMD}
            amount={paymentDetailAmount}
            setAmount={setPaymentDetailAmount}
            showUnitPicker={false}
            eligibleUnitOptions={[]}
            unitId={unit?.id || ''}
            setUnitId={() => {}}
            unitNumber={unit?.unitNumber || ''}
            setUnitNumber={() => {}}
            partyName={paymentDetailPartyName}
            setPartyName={setPaymentDetailPartyName}
            partyNameOptions={[]}
            payerBankName={paymentDetailPayerBankName}
            setPayerBankName={setPaymentDetailPayerBankName}
            payerBankNameOptions={paymentPayerBankNameOptions}
            paymentModeOptions={paymentModeOptions}
            mode={paymentDetailMode}
            setMode={setPaymentDetailMode}
            referenceNo={paymentDetailReferenceNo}
            setReferenceNo={setPaymentDetailReferenceNo}
            notes={paymentDetailNotes}
            setNotes={setPaymentDetailNotes}
            customPostAmountSection={paymentDetailLoanDisbursementSection}
            voucherInputRef={paymentVoucherInputRef}
            voucherFile={null}
            setVoucherFile={() => {}}
            voucherRequired={false}
            existingVoucherUrl={String(paymentDetailTxn?.voucherUrl || '')}
            drawerWidth={paymentPanelWidth}
            onHeaderAction={
              isAdmin
                ? () => {
                    if (paymentDetailEditMode) {
                      hydratePaymentDetailForm(paymentDetailTxn);
                      setPaymentDetailEditMode(false);
                      setPaymentDetailError(null);
                      return;
                    }
                    setPaymentDetailEditMode(true);
                  }
                : null
            }
            headerActionIcon={<EditOutlinedIcon fontSize="small" />}
            headerActionTitle={paymentDetailEditMode ? 'Cancel Edit' : 'Edit'}
            showExtraFooterAction={false}
            onExtraFooterAction={() => {
              if (paymentDetailEditMode) {
                hydratePaymentDetailForm(paymentDetailTxn);
                setPaymentDetailEditMode(false);
                setPaymentDetailError(null);
                return;
              }
              setPaymentDetailEditMode(true);
            }}
            readOnly={!paymentDetailEditMode}
            variant="panel"
            panelSx={{
              position: 'fixed',
              right: 0,
              top: 'var(--office-appbar-height, 56px)',
              height: 'calc(100vh - var(--office-appbar-height, 56px))',
              width: paymentPanelWidth,
              maxWidth: '100%',
              overflow: 'hidden',
              bgcolor: '#ffffff',
              borderLeft: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              zIndex: 5,
            }}
          />
        ) : null}
        <Dialog
          open={emailConfirmOpen}
          onClose={() => {
            if (emailConfirmSending) return;
            setEmailConfirmOpen(false);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Confirm Email</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25}>
              {emailConfirmError ? <Alert severity="error">{emailConfirmError}</Alert> : null}
              {emailConfirmLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading preview…</Typography>
                </Stack>
              ) : null}
              {!emailConfirmLoading && emailConfirmPreview ? (
                <>
                  <Typography variant="body2">
                    <strong>Template:</strong> {emailConfirmPreview.templateName || '—'}
                  </Typography>
                  <TextField
                    size="small"
                    label="To (comma-separated emails)"
                    value={emailConfirmToDraft}
                    onChange={(e) => setEmailConfirmToDraft(e.target.value)}
                    fullWidth
                  />
                  <Typography variant="body2">
                    <strong>Subject:</strong> {emailConfirmPreview.subject || '—'}
                  </Typography>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Attachments</Typography>
                    {emailConfirmPreview.attachments.length ? (
                      <Stack spacing={0.5}>
                        {emailConfirmPreview.attachments.map((row, idx) => (
                          <Typography key={`${row.key}-${idx}`} variant="body2">
                            {idx + 1}. {row.name || row.key || 'Attachment'} ({row.source})
                          </Typography>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No attachments.</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Email Preview</Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        height: 340,
                      }}
                    >
                      <iframe
                        title="Workflow Email Preview"
                        srcDoc={emailConfirmPreview.bodyHtml || ''}
                        style={{ width: '100%', height: '100%', border: 0 }}
                      />
                    </Box>
                  </Box>
                </>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (emailConfirmSending) return;
                setEmailConfirmOpen(false);
              }}
              disabled={emailConfirmSending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                confirmAndSendWorkflowEmail().catch((err: any) =>
                  setEmailConfirmError(err?.message || 'Failed to send email.')
                );
              }}
              disabled={emailConfirmLoading || emailConfirmSending || !emailConfirmPreview}
            >
              {emailConfirmSending ? 'Sending…' : 'Confirm & Send'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={loanSanctionDialogOpen}
          onClose={() => {
            if (loanSanctionSaving) return;
            setLoanSanctionDialogOpen(false);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 2,
              bgcolor: '#ffffff',
            },
          }}
        >
          <DialogTitle>{`${String(unit?.unitNumber || unit?.id || 'Unit')} : ${loanSanctionStepTitle || 'Loan Sanction'}`}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  label="Date of Sanction"
                  value={loanSanctionDate}
                  onChange={(e) => setLoanSanctionDate(e.target.value)}
                  sx={{ flex: 1 }}
                  required
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
                <Autocomplete
                  freeSolo
                  options={mergeBankNameOptions(officeDocumentLinkedBankNames, loanSanctionHistory.bankNames, paymentPayerBankNameOptions)}
                  value={loanSanctionBankName}
                  onChange={(_, value) => setLoanSanctionBankName(String(value || ''))}
                  onInputChange={(_, value) => setLoanSanctionBankName(String(value || ''))}
                  sx={{ flex: 1 }}
                  renderInput={(params) => <TextField {...params} size="small" label="Bank" required />}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <Autocomplete
                  freeSolo
                  options={loanSanctionHistory.agentNames}
                  value={loanSanctionAgentName}
                  onChange={(_, value) => {
                    const next = String(value || '');
                    setLoanSanctionAgentName(next);
                    const mapped = loanSanctionHistory.nameToMobile.get(next.trim().toLowerCase());
                    if (mapped) setLoanSanctionAgentMobile(mapped);
                  }}
                  onInputChange={(_, value) => {
                    const next = String(value || '');
                    setLoanSanctionAgentName(next);
                    const mapped = loanSanctionHistory.nameToMobile.get(next.trim().toLowerCase());
                    if (mapped) setLoanSanctionAgentMobile(mapped);
                  }}
                  sx={{ flex: 1 }}
                  renderInput={(params) => <TextField {...params} size="small" label="Agent Name" />}
                />
                <Autocomplete
                  freeSolo
                  options={loanSanctionHistory.agentMobiles}
                  value={loanSanctionAgentMobile}
                  onChange={(_, value) => {
                    const next = normalizeBankContactNumber(String(value || ''));
                    setLoanSanctionAgentMobile(next);
                    const mapped = loanSanctionHistory.mobileToName.get(next.trim());
                    if (mapped) setLoanSanctionAgentName(mapped);
                  }}
                  onInputChange={(_, value) => {
                    const next = normalizeBankContactNumber(String(value || ''));
                    setLoanSanctionAgentMobile(next);
                    const mapped = loanSanctionHistory.mobileToName.get(next.trim());
                    if (mapped) setLoanSanctionAgentName(mapped);
                  }}
                  sx={{ flex: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Agent Mobile"
                      error={!!loanSanctionAgentMobile && !isValidBankContactNumber(loanSanctionAgentMobile)}
                    />
                  )}
                />
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.45, whiteSpace: 'normal' }}
              >
                Pending: ₹ {Number(loanToDeveloperPendingAmount || 0).toLocaleString('en-IN')} • 90%: ₹{' '}
                {Number(loanToDeveloperNinetyPercentAmount || 0).toLocaleString('en-IN')}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    size="small"
                    label="Loan to Unit Amount"
                    value={loanSanctionLoanToUnitAmount}
                    onChange={(e) => setLoanSanctionLoanToUnitAmount(e.target.value)}
                    fullWidth
                    required
                    type="number"
                  />
                </Box>
                <TextField
                  size="small"
                  label="Sanction Amount"
                  value={loanSanctionAmount}
                  onChange={(e) => setLoanSanctionAmount(e.target.value)}
                  sx={{ flex: 1, minWidth: 0 }}
                  fullWidth
                  required
                  type="number"
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                {String(loanSanctionBankName || '').trim().toLowerCase() !== 'state bank of india' &&
                String(loanSanctionBankName || '').trim() ? (
                  <TextField
                    size="small"
                    label="Reason not taking loan from SBI"
                    value={loanSanctionNonSbiReason}
                    onChange={(e) => setLoanSanctionNonSbiReason(e.target.value)}
                    fullWidth
                    required
                  />
                ) : (
                  <Box sx={{ flex: 1 }} />
                )}
              </Stack>
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>Upload sanction letter</Typography>
                    {bookingDocsByKey[loanSanctionDocKey]?.files?.[0]?.name ? (
                      <Typography variant="body2" color="text.secondary">
                        Current: {String(bookingDocsByKey[loanSanctionDocKey]?.files?.[0]?.name || '')}
                      </Typography>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} sx={{ textTransform: 'none' }}>
                      Choose file
                      <input
                        hidden
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          const f = (e.target.files || [])[0] || null;
                          const input = e.target as HTMLInputElement;
                          if (input) input.value = '';
                          setStepDocPickedFiles((prev) => ({ ...prev, [loanSanctionDocKey]: f }));
                        }}
                      />
                    </Button>
                    {stepDocPickedFiles[loanSanctionDocKey]?.name ? (
                      <Typography variant="body2" color="text.secondary">
                        {stepDocPickedFiles[loanSanctionDocKey]?.name}
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLoanSanctionDialogOpen(false)} disabled={loanSanctionSaving}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                saveLoanSanctionForm().catch((err: any) =>
                  setError(err?.message || 'Failed to save sanction details.')
                )
              }
              disabled={loanSanctionSaving}
            >
              {loanSanctionSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={demandGenerateDialogOpen}
          onClose={() => {
            if (demandGenerateSubmitting) return;
            setDemandGenerateDialogOpen(false);
            setDemandGenerateContext(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Generate Demand Letter</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Both fields are optional. If left blank, underline space will be shown in PDF.
              </Typography>
              <TextField
                size="small"
                label="Construction Stage Status"
                value={demandConstructionStageInput}
                onChange={(e) => setDemandConstructionStageInput(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Demand Amount"
                value={demandAmountInput}
                onChange={(e) => setDemandAmountInput(e.target.value)}
                fullWidth
                placeholder="Example: 12,50,000"
              />
              <TextField
                size="small"
                type="date"
                label="Payment Due Date"
                value={demandDueDateInput}
                onChange={(e) => setDemandDueDateInput(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Optional. With a date, the buyer gets WhatsApp reminders 7 days and 1 day before."
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (demandGenerateSubmitting) return;
                setDemandGenerateDialogOpen(false);
                setDemandGenerateContext(null);
              }}
              disabled={demandGenerateSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                confirmGenerateDemandLetter().catch((err: any) =>
                  setError(err?.message || 'Failed to generate demand letter.')
                );
              }}
              disabled={demandGenerateSubmitting || !demandGenerateContext}
            >
              {demandGenerateSubmitting ? 'Generating…' : 'Generate'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={paymentDuplicateDialogOpen}
          onClose={() => {
            if (paymentDuplicateProceeding) return;
            setPaymentDuplicateDialogOpen(false);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Possible Duplicate Transaction</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25}>
              <Alert severity={paymentDuplicateMatches.some((m) => m.kind === 'exact') ? 'error' : 'warning'}>
                {paymentDuplicateMatches.some((m) => m.kind === 'exact')
                  ? 'Exact duplicate match found.'
                  : 'Likely duplicate matches found within ±10 days.'}
              </Alert>
              <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Match</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Party</TableCell>
                      <TableCell>Ref No</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentDuplicateMatches.map((m) => (
                      <TableRow key={`${String(m.row?.id || '')}-${m.kind}`}>
                        <TableCell>
                          <Chip
                            size="small"
                            color={m.kind === 'exact' ? 'error' : 'warning'}
                            label={m.kind === 'exact' ? 'Exact' : `Likely (${m.dayDiff}d)`}
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.dateText}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{String(m.row?.unitNumber || '—')}</TableCell>
                        <TableCell>{String(m.row?.partyName || '—')}</TableCell>
                        <TableCell>{String(m.row?.referenceNo || '—')}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                          ₹{Number(m.row?.amount || 0).toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPaymentDuplicateDialogOpen(false);
                setPaymentDuplicateMatches([]);
              }}
              disabled={paymentDuplicateProceeding}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={confirmPaymentDuplicateAndSave}
              disabled={paymentDuplicateProceeding}
            >
              {paymentDuplicateProceeding ? 'Saving…' : 'Save Anyway'}
            </Button>
          </DialogActions>
        </Dialog>
        {customerDrawerOpen ? (
          <Dialog
            open={customerDrawerOpen}
            onClose={() => setCustomerDrawerOpen(false)}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: 2,
                bgcolor: '#ffffff',
                maxHeight: '82vh',
              },
            }}
          >
            {customerPanelContent}
          </Dialog>
        ) : null}
        {!actionOnly && !pageMode ? (
          <Box
            onClick={closeTopmostDrawer}
            sx={{
              position: 'fixed',
              top: 'var(--office-appbar-height, 56px)',
              left: 0,
              right: 0,
              height: 'calc(100vh - var(--office-appbar-height, 56px))',
              zIndex: 2,
              bgcolor: 'transparent',
            }}
          />
        ) : null}
        {actionOnly ? (
          <Box sx={{ display: 'none' }}>{drawerInner}</Box>
        ) : pageMode ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: 'hidden',
              bgcolor: '#ffffff',
            }}
          >
            {drawerInner}
          </Box>
        ) : (
          <Box
            sx={{
              position: 'fixed',
              right: paymentDrawerOpen ? paymentPanelWidth + panelGap : 0,
              top: 'var(--office-appbar-height, 56px)',
              height: 'calc(100vh - var(--office-appbar-height, 56px))',
              left: paymentDrawerOpen ? 0 : 'auto',
              width: paymentDrawerOpen ? 'auto' : bookingPanelWidth,
              maxWidth: '100%',
              overflow: 'hidden',
              bgcolor: '#ffffff',
              borderLeft: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              zIndex: 3,
              transition: 'right 0.2s ease, width 0.2s ease, left 0.2s ease',
            }}
          >
            {drawerInner}
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={emailConfirmOpen}
        onClose={() => {
          if (emailConfirmSending) return;
          setEmailConfirmOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Confirm Email</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {emailConfirmError ? <Alert severity="error">{emailConfirmError}</Alert> : null}
            {emailConfirmLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Loading preview…</Typography>
              </Stack>
            ) : null}
            {!emailConfirmLoading && emailConfirmPreview ? (
              <>
                <Typography variant="body2">
                  <strong>Template:</strong> {emailConfirmPreview.templateName || '—'}
                </Typography>
                <TextField
                  size="small"
                  label="To (comma-separated emails)"
                  value={emailConfirmToDraft}
                  onChange={(e) => setEmailConfirmToDraft(e.target.value)}
                  fullWidth
                />
                <Typography variant="body2">
                  <strong>Subject:</strong> {emailConfirmPreview.subject || '—'}
                </Typography>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Attachments</Typography>
                  {emailConfirmPreview.attachments.length ? (
                    <Stack spacing={0.5}>
                      {emailConfirmPreview.attachments.map((row, idx) => (
                        <Typography key={`${row.key}-${idx}`} variant="body2">
                          {idx + 1}. {row.name || row.key || 'Attachment'} ({row.source})
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No attachments.</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Email Preview</Typography>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#fff',
                      height: 340,
                    }}
                  >
                    <iframe
                      title="Workflow Email Preview"
                      srcDoc={emailConfirmPreview.bodyHtml || ''}
                      style={{ width: '100%', height: '100%', border: 0 }}
                    />
                  </Box>
                </Box>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (emailConfirmSending) return;
              setEmailConfirmOpen(false);
            }}
            disabled={emailConfirmSending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              confirmAndSendWorkflowEmail().catch((err: any) =>
                setEmailConfirmError(err?.message || 'Failed to send email.')
              );
            }}
            disabled={emailConfirmLoading || emailConfirmSending || !emailConfirmPreview}
          >
            {emailConfirmSending ? 'Sending…' : 'Confirm & Send'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={loanSanctionDialogOpen}
        onClose={() => {
          if (loanSanctionSaving) return;
          setLoanSanctionDialogOpen(false);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: '#ffffff',
          },
        }}
      >
        <DialogTitle>{`${String(unit?.unitNumber || unit?.id || 'Unit')} : ${loanSanctionStepTitle || 'Loan Sanction'}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField
                size="small"
                label="Date of Sanction"
                value={loanSanctionDate}
                onChange={(e) => setLoanSanctionDate(e.target.value)}
                sx={{ flex: 1 }}
                required
                type="date"
                InputLabelProps={{ shrink: true }}
              />
              <Autocomplete
                freeSolo
                options={mergeBankNameOptions(officeDocumentLinkedBankNames, loanSanctionHistory.bankNames, paymentPayerBankNameOptions)}
                value={loanSanctionBankName}
                onChange={(_, value) => setLoanSanctionBankName(String(value || ''))}
                onInputChange={(_, value) => setLoanSanctionBankName(String(value || ''))}
                sx={{ flex: 1 }}
                renderInput={(params) => <TextField {...params} size="small" label="Bank" required />}
              />
            </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <Autocomplete
                freeSolo
                options={loanSanctionHistory.agentNames}
                value={loanSanctionAgentName}
                onChange={(_, value) => {
                  const next = String(value || '');
                  setLoanSanctionAgentName(next);
                  const mapped = loanSanctionHistory.nameToMobile.get(next.trim().toLowerCase());
                  if (mapped) setLoanSanctionAgentMobile(mapped);
                }}
                onInputChange={(_, value) => {
                  const next = String(value || '');
                  setLoanSanctionAgentName(next);
                  const mapped = loanSanctionHistory.nameToMobile.get(next.trim().toLowerCase());
                  if (mapped) setLoanSanctionAgentMobile(mapped);
                }}
                sx={{ flex: 1 }}
                renderInput={(params) => <TextField {...params} size="small" label="Agent Name" />}
              />
              <Autocomplete
                freeSolo
                options={loanSanctionHistory.agentMobiles}
                value={loanSanctionAgentMobile}
                onChange={(_, value) => {
                  const next = normalizeBankContactNumber(String(value || ''));
                  setLoanSanctionAgentMobile(next);
                  const mapped = loanSanctionHistory.mobileToName.get(next.trim());
                  if (mapped) setLoanSanctionAgentName(mapped);
                }}
                onInputChange={(_, value) => {
                  const next = normalizeBankContactNumber(String(value || ''));
                  setLoanSanctionAgentMobile(next);
                  const mapped = loanSanctionHistory.mobileToName.get(next.trim());
                  if (mapped) setLoanSanctionAgentName(mapped);
                }}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Agent Mobile"
                    error={!!loanSanctionAgentMobile && !isValidBankContactNumber(loanSanctionAgentMobile)}
                  />
                )}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.45, whiteSpace: 'normal' }}
            >
              Pending: ₹ {Number(loanToDeveloperPendingAmount || 0).toLocaleString('en-IN')} • 90%: ₹{' '}
              {Number(loanToDeveloperNinetyPercentAmount || 0).toLocaleString('en-IN')}
            </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  size="small"
                  label="Loan to Unit Amount"
                  value={loanSanctionLoanToUnitAmount}
                  onChange={(e) => setLoanSanctionLoanToUnitAmount(e.target.value)}
                  fullWidth
                  required
                  type="number"
                />
              </Box>
              <TextField
                size="small"
                label="Sanction Amount"
                value={loanSanctionAmount}
                onChange={(e) => setLoanSanctionAmount(e.target.value)}
                sx={{ flex: 1, minWidth: 0 }}
                fullWidth
                required
                type="number"
              />
            </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              {String(loanSanctionBankName || '').trim().toLowerCase() !== 'state bank of india' &&
              String(loanSanctionBankName || '').trim() ? (
                <TextField
                  size="small"
                  label="Reason not taking loan from SBI"
                  value={loanSanctionNonSbiReason}
                  onChange={(e) => setLoanSanctionNonSbiReason(e.target.value)}
                  fullWidth
                  required
                />
              ) : (
                <Box sx={{ flex: 1 }} />
              )}
            </Stack>
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }}>Upload sanction letter</Typography>
                  {bookingDocsByKey[loanSanctionDocKey]?.files?.[0]?.name ? (
                    <Typography variant="body2" color="text.secondary">
                      Current: {String(bookingDocsByKey[loanSanctionDocKey]?.files?.[0]?.name || '')}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} sx={{ textTransform: 'none' }}>
                    Choose file
                    <input
                      hidden
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        const f = (e.target.files || [])[0] || null;
                        const input = e.target as HTMLInputElement;
                        if (input) input.value = '';
                        setStepDocPickedFiles((prev) => ({ ...prev, [loanSanctionDocKey]: f }));
                      }}
                    />
                  </Button>
                  {stepDocPickedFiles[loanSanctionDocKey]?.name ? (
                    <Typography variant="body2" color="text.secondary">
                      {stepDocPickedFiles[loanSanctionDocKey]?.name}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoanSanctionDialogOpen(false)} disabled={loanSanctionSaving}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              saveLoanSanctionForm().catch((err: any) =>
                setError(err?.message || 'Failed to save sanction details.')
              )
            }
            disabled={loanSanctionSaving}
          >
            {loanSanctionSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={demandGenerateDialogOpen}
        onClose={() => {
          if (demandGenerateSubmitting) return;
          setDemandGenerateDialogOpen(false);
          setDemandGenerateContext(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generate Demand Letter</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Both fields are optional. If left blank, underline space will be shown in PDF.
            </Typography>
            <TextField
              size="small"
              label="Construction Stage Status"
              value={demandConstructionStageInput}
              onChange={(e) => setDemandConstructionStageInput(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Demand Amount"
              value={demandAmountInput}
              onChange={(e) => setDemandAmountInput(e.target.value)}
              fullWidth
              placeholder="Example: 12,50,000"
            />
            <TextField
              size="small"
              type="date"
              label="Payment Due Date"
              value={demandDueDateInput}
              onChange={(e) => setDemandDueDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Optional. With a date, the buyer gets WhatsApp reminders 7 days and 1 day before."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (demandGenerateSubmitting) return;
              setDemandGenerateDialogOpen(false);
              setDemandGenerateContext(null);
            }}
            disabled={demandGenerateSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              confirmGenerateDemandLetter().catch((err: any) =>
                setError(err?.message || 'Failed to generate demand letter.')
              );
            }}
            disabled={demandGenerateSubmitting || !demandGenerateContext}
          >
            {demandGenerateSubmitting ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={paymentDuplicateDialogOpen}
        onClose={() => {
          if (paymentDuplicateProceeding) return;
          setPaymentDuplicateDialogOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Possible Duplicate Transaction</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Alert severity={paymentDuplicateMatches.some((m) => m.kind === 'exact') ? 'error' : 'warning'}>
              {paymentDuplicateMatches.some((m) => m.kind === 'exact')
                ? 'Exact duplicate match found.'
                : 'Likely duplicate matches found within ±10 days.'}
            </Alert>
            <Paper variant="outlined" sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Match</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Party</TableCell>
                    <TableCell>Ref No</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentDuplicateMatches.map((m) => (
                    <TableRow key={`${String(m.row?.id || '')}-${m.kind}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          color={m.kind === 'exact' ? 'error' : 'warning'}
                          label={m.kind === 'exact' ? 'Exact' : `Likely (${m.dayDiff}d)`}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.dateText}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{String(m.row?.unitNumber || '—')}</TableCell>
                      <TableCell>{String(m.row?.partyName || '—')}</TableCell>
                      <TableCell>{String(m.row?.referenceNo || '—')}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                        ₹{Number(m.row?.amount || 0).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPaymentDuplicateDialogOpen(false);
              setPaymentDuplicateMatches([]);
            }}
            disabled={paymentDuplicateProceeding}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmPaymentDuplicateAndSave}
            disabled={paymentDuplicateProceeding}
          >
            {paymentDuplicateProceeding ? 'Saving…' : 'Save Anyway'}
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={onClose}
        hideBackdrop={!isMobile}
        PaperProps={{
          sx: isMobile ? {
            bottom: 0,
            height: '88dvh',
            maxHeight: 'calc(100dvh - 16px)',
            borderRadius: '16px 16px 0 0',
            bgcolor: '#faf9f7',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          } : {
            width: 520,
            maxWidth: '100vw',
            bgcolor: '#ffffff',
          },
        }}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: false,
        }}
        sx={isMobile ? {} : {
          '& .MuiDrawer-paper': {
            top: 'var(--office-appbar-height, 56px)',
            mt: 0,
            height: 'calc(100% - var(--office-appbar-height, 56px))',
            position: 'fixed',
          },
        }}
      >
        {isMobile && (
          <Box sx={{ pt: 1.25, pb: 0.5, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#ddd' }} />
          </Box>
        )}
        {drawerInner}
      </Drawer>
    </>
  );
});

export default OfficeBookingDrawer;
